// console logs marked with: // *Latest Debugging*

import axios from 'axios';
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import './App.css';
import PianoKey from './components/PianoKey';
// import PCDisplay from './components/PCDisplay';
import { signOut } from 'firebase/auth';
import { collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import * as Tone from 'tone';
import KeyCenterPanelKey from './components/KeyCenterPanelKey';
// import { colors } from './theme/colors';
import { useColors, ColorProvider, ColorScheme } from './theme/colorThemeContext';
// import Bar from './components/Bar';
import { auth, firestore } from './firebase';
import {
  getSpellings,
  midiToName,
  midiToSingleNote, midiBassToName, midiToScaleDegree, velocityToDb,
  midiToRomanNumeral,
  midiToNashvilleNumber,
  getOvertoneNotes,
  getOvertoneInfo,
  checkApprovedChord,
  getModeFromPcSet
} from './utils';
import Modal from 'react-modal';
import HelpPopup from './components/HelpPopup';
import Settings from './components/Settings';
import StaffView from './components/StaffView';
import StatusBanner from './components/StatusBanner';
import ChordBatchReview from './components/ChordBatchReview';
import {
  ChartResolution,
  ChartResolutionResult,
  applyResolutions,
  changesOnly,
  respellChartByOrdering,
  resolveChart,
  resolveChord,
} from './utils/chordRespell';
import { createCandidateCache, createCandidateProvider, prefetchCandidates } from './utils/chordCandidates';
import {
  KeyCenter,
  KeyCenterRegion,
  keyCenterForChord,
  keyCentersEqual,
  regionCoveringChord,
  reconcileRegions,
  regionsInChartOrder,
  clearKeyCenterAtChord,
  setKeyCenterAtChord,
  setNoKeyCenterAtChord,
} from './utils/keyCenterRegions';
import KeyCenterPicker, { PickerAnchor, keyCenterColor, keyCenterLabel } from './components/KeyCenterPicker';
import { ToggleSwitch } from './components/SettingsElements';
// import { Sampler, loaded } from 'tone';
// import sentisonicslogo from '.images/sentisonics-logo.jpg';
import SentimentCustomizer from './components/SentimentCustomizer';
import { MidiEventMerger } from './utils/midiEventMerger';
import { MidiAudioHandler } from './utils/midiAudioHandler';
import MidiController, { MidiControllerRef } from './components/MidiController';
import { ChordRegion, shownSegment, isEnabled, regionVoicing } from './utils/midiAnalysis';
import { FileKeys, RegionChange, applyRegionChanges, divergentChartKeys, holdsNotesOf, isCharted, isOrphan, linkedTo, placePromoted, respellFollowed, returnToFileKeyAt, syncFileKeyRegions } from './utils/chartFileLink';
import { buildIrealProUrl, openInIrealPro } from './utils/chordChartExport';
import ChartDropdown from './components/ChartDropdown';
import PanelRow from './components/PanelRow';
import Panel from './components/Panel';
import LuxTonnetz from './components/LuxTonnetz';
import { PanelId, PanelLayout, PANELS, DEFAULT_ORDER, PANEL_GAP, rowWidth, panelStack, stackPositions, meterLineup, hidePanel, showPanel, stepPanel, placePanel, scrollStack, canLockPanel, lockPanel, canUnlockPanel, unlockPanel } from './panels/registry';
import { useStackMotion } from './panels/useStackMotion';
import { loadLayout, saveLayout } from './panels/layoutStorage';
import StackScroller from './components/StackScroller';
import CompassChartStrip from './components/CompassChartStrip';
import FollowToggle from './components/FollowToggle';
import { log } from 'console';

Modal.setAppElement('#root'); // matches root element in index.html file


// MAIN COMPONENT wrapper
// Scale previews (playScale) only ever span MIDI 48-83: baseNote 48-59 plus
// intervals up to 11, each doubled an octave up, closing with the root two
// octaves up. So the scale sampler only needs these C3-C6 samples (13 of the
// 29 files); Tone.Sampler pitch-shifts from the nearest sample to fill the
// gaps. File names are identical in every instrument's directory.
const scaleSampleUrls = {
  "C3": "C3.mp3", "C4": "C4.mp3", "C5": "C5.mp3", "C6": "C6.mp3",
  "D#3": "Ds3.mp3", "D#4": "Ds4.mp3", "D#5": "Ds5.mp3",
  "F#3": "Fs3.mp3", "F#4": "Fs4.mp3", "F#5": "Fs5.mp3",
  "A3": "A3.mp3", "A4": "A4.mp3", "A5": "A5.mp3",
};

const App: React.FC = () => {
  return (
    <ColorProvider>
      <AppContent />
    </ColorProvider>
  );
}


// Comment out the local server /sentiserver' for production, online server for dev:
// export const server = 'https://sentisonics.pythonanywhere.com/sentiserver'
// Server URL comes from .env.development (npm start) or .env.production (npm run build)
export const server = process.env.REACT_APP_SERVER_URL || 'http://127.0.0.1:5001/sentiserver'

// export const server = '/sentiserver' // old method

// export const server = process.env.NODE_ENV === 'development'
//   ? 'http://localhost:5000/sentiserver'
//   : '/sentiserver'

// const server = process.env.NODE_ENV === "development" ? "/sentiserver" : "http://localhost:5000/sentiserver";
// fetch("http://localhost:5000/sentiserver", {...}) // Use to test build



// Pie rotation
// -1.832 Root & 5th centered top, -1.573 Root top, -0.26 old,
// -3.4 Root & 5th left for horizon display, -3.14 Horizontal with Root straddling horizon. -2.88 Horizontal with Root above horizon.
const rotation = -3.14; // Pie is 3.14159265358979323846;

// How many entries the up and down arrows jump in the chart. The Compass's
// chart strip draws one chord further than this either side, so every
// keyboard step is plain sliding (see CompassChartStrip).
const CHART_JUMP = 8;
// Milliseconds an arrow press that cannot move leaves its chord deselected
// before selecting it again (see chartNavigation): long enough for the browser
// to draw the keys going out (a frame is ~16ms), so the reselect visibly
// relights them rather than only replaying the sound.
const CHART_RESELECT_GAP = 25;

// The strip key of a chord not in the chart yet (see chartChordPending).
const COMPASS_PENDING_ID = 'compass-pending';
// gravityThreshold (18) now controlled by settings.secondaryChordThreshold

function getPieSlicePath(index: number, total: number, showPieSentiments: boolean): string {
  // To rotate counterclockwise left three slices for horizon display option
  // const sliceAngle = 2 * Math.PI / total;
  // const rotation = -3 * sliceAngle; // Rotate counter-clockwise by 3 slices

  // const startAngle = ((index - 0.5) / total) * 2 * Math.PI + rotation;
  // const endAngle = ((index + 0.5) / total) * 2 * Math.PI + rotation;
  const startAngle = ((index - 0.5) / total) * 2 * Math.PI + rotation;
  const endAngle = ((index + 0.5) / total) * 2 * Math.PI + rotation;

  const largeArcFlag = endAngle - startAngle <= Math.PI ? 0 : 1;

  const radius = showPieSentiments ? 20 : 25; // Doubled the radius

  const startX = radius * Math.cos(startAngle);
  const startY = radius * Math.sin(startAngle);
  const endX = radius * Math.cos(endAngle);
  const endY = radius * Math.sin(endAngle);

  return `M 0 0 L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;
}

// Unused function for pie chart/chord compass donut paths - trying to separate gradients
function getPieSliceDonutPath(index: number, total: number, rInner: number, rOuter: number): string {
  const sliceAngle = (2 * Math.PI) / total;
  const start = (index - 0.5) * sliceAngle + rotation;
  const end = (index + 0.5) * sliceAngle + rotation;
  const large = end - start <= Math.PI ? 0 : 1;

  const sxo = rOuter * Math.cos(start), syo = rOuter * Math.sin(start);
  const exo = rOuter * Math.cos(end), eyo = rOuter * Math.sin(end);
  const sxi = rInner * Math.cos(end), syi = rInner * Math.sin(end);
  const exi = rInner * Math.cos(start), eyi = rInner * Math.sin(start);

  return `M ${sxo} ${syo} A ${rOuter} ${rOuter} 0 ${large} 1 ${exo} ${eyo} L ${sxi} ${syi} A ${rInner} ${rInner} 0 ${large} 0 ${exi} ${eyi} Z`;
}



// export const defaultEmoClassLabelsMajor = ['', '', 'Hope', 'Peace', 'Brightness', 'Yearning', 'Enchantment', 'Eeriness', 'Mysticism', 'Fortitude', 'Anticipation', 'Suspense']
// export const defaultEmoClassLabelsMinor = ['', '', 'Longing', 'Bewitchment', 'Rebellion', 'Despair', 'Dread', 'Gloom', 'Apprehension', 'Sadness', 'Pensiveness', 'Wistfulness']


// Emotion Descriptors:

// root Major: Existence and Prominence Major root and 5th
// root Minor: Perseverance and Solemnity Minor root and 5th

// Perf 5 Major: Stability, Assertiveness, Permanence, Vigor, Fervor, Ardor, Zeal, Avidity, Eminence, Ardency, Potency, Salience, Saliency or Prominence
// Perf 5 Minor: Solemnity, Seriousness

// Maj 2: Ascension for Hope
// Maj 6: Comfort, Calm, Reassurance, Hope for Peace

// Mystery or Enigmatism or Suspicion for Suspicion
// Hopelessness for Despair, and Depression, Darkness, Sunken-ness for Worry

// Min 2: Discomfort, Aggravation, Unsettled, Unease, Disturbing, Anxiety, Sinisterness for Eeriness
// Foreboding for Dread
// Malevolence or Dismay or Discomfort or Haunt or Disconcertment for Eeriness

// Perf 4: Aspiration, Expectation, Elevation, Ascension, Transcendence, Suspension, Suspense, Postponement, Weightlessness, Respite, Levitation, Stasis, Flotation, Buoyancy
// Min 7: Stasis, Anticipation
// Min 3: Defiance or Rebellion for Fortitude
// Min 2: Discomfort, Aggravation, Unsettled, Unease, Disturbing, Anxiety, Sinisterness for Eeriness
// Foreboding for Dread
// Maj 3: Optimism, Contentment, Gratification, Pleasure, Fulfillment, Triumph, Joy, Sanctuary, Jubilation, Positivity, Brightness, Radiance, Calmness, Comfort, Composure
// Maj 3 in minor: Distress, Discomfort, Aggitation
// try Vigor
// try Bewilderment


// export const defaultEmoClassLabelsMajor = ['', '', 'hope', 'reassurance', 'positivity', 'yearning', 'enchantment', 'unease', 'eeriness', 'defiance', 'anticipation', 'expectation']
// export const defaultEmoClassLabelsMinor = ['', '', 'longing', 'bewitchment', 'discomfort', 'despair', 'dread', 'gloom', 'worry', 'sadness', 'pensiveness', 'wistfulness']

// From Durham study prelim test of 11 people, Major Root and P5: '(confidence)', '(stability)', Minor Root and P5: '(authority)', '(solemnity)'
export const defaultEmoClassLabelsMajor = ['', '', 'hope', 'tranquility', 'positivity', 'yearning', 'enchantment', 'distress', 'mystery', 'defiance', 'anticipation', 'expectation']
export const defaultEmoClassLabelsMinor = ['', '', 'heartache', 'intrigue', 'tension', 'despair', 'malevolence', 'doom', 'apprehension', 'melancholy', 'reflection', 'contemplation']

// export const defaultEmoClassLabelsMajor = ['', '', 'hope', 'reassurance', 'positivity', 'yearning', 'enchantment', 'unease', 'eeriness', 'defiance', 'anticipation', 'expectation']
// export const defaultEmoClassLabelsMinor = ['', '', 'longing', 'bewitchment', 'discomfort', 'despair', 'dread', 'gloom', 'worry', 'sadness', 'pensiveness', 'wistfulness']

const defaultEmoClassLabelsKeyMajor = ['', 'Confidence', 'Hope', 'Reassurance', 'Tranquility', 'Passion', 'Mystery', 'Awe', 'Lament', 'Solemnity', 'Nobility', 'Fleetingness']
const defaultEmoClassLabelsKeyMinor = ['', 'Gravitas', 'Longing', 'Bewitchment', 'Softness', 'Desire', 'Intrigue', 'Concern', 'Worry', 'Sadness', 'Pensiveness', 'Wistfulness']

// Lowercase default
// const defaultEmoClassLabelsKeyMajor = ['', 'Confidence', 'Hope', 'Reassurance', 'Tranquility', 'Passion', 'Mystery', 'Awe', 'Lament', 'Bravery', 'Nobility', 'Fleetingness']
// const defaultEmoClassLabelsKeyMinor = ['', 'Gravitas', 'Longing', 'Bewitchment', 'Alarm', 'Desire', 'Intrigue', 'Concern', 'Worry', 'Sadness', 'Pensiveness', 'Wistfulness']

// const defaultEmoClassLabelsKeyMajor = ['', 'CONFIDENCE', 'HOPE', 'REASSURANCE', 'TRANQUILITY', 'PASSION', 'MYSTERY', 'AWE', 'LAMENT', 'BRAVERY', 'NOBILITY', 'FLEETINGNESS']
// const defaultEmoClassLabelsKeyMinor = ['', 'GRAVITAS', 'LONGING', 'BEWITCHMENT', 'ALARM', 'DESIRE', 'INTRIGUE', 'CONCERN', 'WORRY', 'SADNESS', 'PENSIVENESS', 'WISTFULNESS']

const defaultEmoClassLabelsSent2Major = ['', '', 'encouragement', 'tranquility', 'gratefulness', 'romance', 'adventure', 'disgust', 'mystery', 'rebellion', 'expectation', 'weightlessness']
const defaultEmoClassLabelsSent2Minor = ['', '', 'wistfulness', 'spookiness', 'annoyance', 'devastation', 'evil', 'brokenness', 'apprehension', 'despair', 'co-existance', 'indifference']

const defaultEmoClassLabelsMusicianMajor = ['purity', 'stability', 'hope', 'tranquility', 'brightness', 'nostalgia', 'enchantment', 'disturbance', 'mystery', 'defiance', 'anticipation', 'expectation']
const defaultEmoClassLabelsMusicianMinor = ['authority', 'solemnity', 'lament', 'mystique', 'tension', 'misfortune', 'sinister', 'doom', 'ominous', 'melancholy', 'ponder', 'transition']

const defaultEmoClassLabelsImgMajor = ['', '', 'bright horizon', 'warm light', 'radiance', 'bittersweet tears', 'enchanting glow', 'mystical fog', 'supernatural glow', 'brave warrior', 'slow motion', 'zero gravity']
const defaultEmoClassLabelsImgMinor = ['', '', 'sad tears', 'magic', 'pain', 'dark horizon', 'evil spirit', 'looming darkness', 'eerie clouds', 'shadow', 'friend', 'elevation']

const defaultEmoClassLabelsCharMajor = ['The Creator', 'The Father', 'The Brother', 'The Mother', 'The Best Friend', 'The Lover', 'The Explorer', 'The Bully', 'The Wizard', 'The Rebel', 'The Prophet', 'The Pioneer']
const defaultEmoClassLabelsCharMinor = ['The Ruler', 'The Architect', 'The Dreamer', 'The Mystic', 'The Pest', 'The Mourner', 'The Villian', 'The Widow', 'The Worrywort', 'The Pessimist', 'The Philosopher', 'The Enlightened']

const defaultEmoClassLabelsSymMajor = ['', '', 'sunrise', 'fireplace', 'home', 'sweetheart', 'fairy dust', 'knife', 'magic wand', 'clenched fist', 'stopwatch', 'light bulb']
const defaultEmoClassLabelsSymMinor = ['', '', 'tombstone', 'ghost', 'nails on chalkboard', 'storm clouds', 'witch\'s brew', 'pitch darkness', 'alien', 'rain', 'hand in hand', 'ladder']

const defaultEmoClassLabelsAdjMajor = ['', '', 'hopeful', 'tranquil', 'successful', 'romantic', 'mystical', 'uneasy', 'mysterious', 'defiant', 'anticipatory', 'expectant']
const defaultEmoClassLabelsAdjMinor = ['', '', 'lamentable', 'intrigued', 'unpleasant', 'hopeless', 'sinister', 'doomed', 'ominous', 'melancholic', 'contemplative', 'transitional']

// const defaultEmoClassLabelsAdjMajor = ['', '', 'hopeful', 'reassured', 'content', 'romantic', 'Enchanted', 'Uneasey', 'Eerie', 'Rebellious', 'Anticipatory', 'Weightless']
// const defaultEmoClassLabelsAdjMinor = ['', '', 'Regretful', 'Bewitched', 'Aggravated', 'Hopeless', 'Dreadful', 'Gloomy', 'Worried', 'Sad', 'Pensive', 'Elevated']

interface Palette {
  label: string,
  majorLabels: string[],
  minorLabels: string[],
}

const defaultPalettes: Palette[] = [
  {
    label: "Chord Emotions",
    majorLabels: defaultEmoClassLabelsMajor,
    minorLabels: defaultEmoClassLabelsMinor,
  },
  {
    label: "Key-Center Emotions",
    majorLabels: defaultEmoClassLabelsKeyMajor,
    minorLabels: defaultEmoClassLabelsKeyMinor,
  },
  {
    label: "More Chord Emotions",
    majorLabels: defaultEmoClassLabelsSent2Major,
    minorLabels: defaultEmoClassLabelsSent2Minor,
  },
  {
    label: "Musician Emotions",
    majorLabels: defaultEmoClassLabelsMusicianMajor,
    minorLabels: defaultEmoClassLabelsMusicianMinor,
  },
  {
    label: "Imagery",
    majorLabels: defaultEmoClassLabelsImgMajor,
    minorLabels: defaultEmoClassLabelsImgMinor,
  },
  {
    label: "Characters",
    majorLabels: defaultEmoClassLabelsCharMajor,
    minorLabels: defaultEmoClassLabelsCharMinor,
  },
  {
    label: "Symbols",
    majorLabels: defaultEmoClassLabelsSymMajor,
    minorLabels: defaultEmoClassLabelsSymMinor,
  },
  {
    label: "Adjectives",
    majorLabels: defaultEmoClassLabelsAdjMajor,
    minorLabels: defaultEmoClassLabelsAdjMinor,
  }
  // {
  //   label: "Emotions 1 (Adjectives)",
  //   majorLabels: defaultEmoClassLabelsAdjMajor,
  //   minorLabels: defaultEmoClassLabelsAdjMinor,
  // }
]

// NEW ADDITION ACRYLICODE
// ///////////////////////
// const defaultSongs = [
//   {
//     songTitle: "Demo Song",
//     composer: "Sentisonics",
//     timeSigBeats: 3,
//     timeSigDuration: 4,
//     chordChart: [
//       { name: "F∆7", voicing: [45, 56, 74], isSeparator: false },
//       { name: "G7", voicing: [30, 53, 71], isSeparator: false },
//     ]
//   },
//   {
//     songTitle: "Song Title",
//     composer: "Composer",
//     timeSigBeats: 4,
//     timeSigDuration: 4,
//     tempo: 120,
//     chordChart: [
//       { name: "/", voicing: [], isSeparator: true },
//     ]
//   }
// ]

// type ChordChartEntry = {
//   name: string;
//   voicing: number[];
//   isSeparator: boolean;
// };
// type Song = {
//   songTitle: string,
//   composer: string,
//   timeSigBeats: number,
//   timeSigDuration: number,
//   chordChart: ChordChartEntry[]
// };


/**
 * Mints a stable ID for a chord chart entry.
 *
 * An ID is unique, never reused, and never derived from position — key center
 * regions anchor to these IDs, so an ID has to survive inserts, deletes, moves,
 * and edits to the entry's own chord. Position stays derived from array order.
 *
 * The session prefix keeps IDs distinct from those of a chart saved in an
 * earlier session, since the counter restarts with the app.
 */
const ENTRY_ID_PREFIX = Math.random().toString(36).slice(2, 8);
let entryIdCounter = 0;
// The guitar and bass keyboard layout's strings: each row of keys, low
// string first, and each key's column along it (its fret)
const STRING_KEY_ROWS = ['zxcvbnm,./', 'asdfghjkl;\'', 'qwertyuiop[]\\', '1234567890-='];
const STRING_KEY_POSITIONS: { [key: string]: { row: number; column: number } } = {};
STRING_KEY_ROWS.forEach((keys, row) => keys.split('').forEach((key, column) => {
  STRING_KEY_POSITIONS[key] = { row, column };
}));

const newEntryId = (): string => `${ENTRY_ID_PREFIX}-${(entryIdCounter++).toString(36)}`;

// MAIN COMPONENT
const AppContent: React.FC = () => {
  const [activeNotes, setActiveNotes] = useState<number[]>([]);
  const [heldNotes, setHeldNotes] = useState<number[]>([]);
  const [toggledNotes, setToggledNotes] = useState<number[]>([]);
  const activeNotesRef = useRef(activeNotes);
  const toggledNotesRef = useRef(toggledNotes);
  const heldNotesRef = useRef(heldNotes);
  // Synchronous mirror of activeNotes for MIDI events only: which notes have had
  // a note-on with no note-off yet. activeNotesRef is written in an effect, so it
  // lags a render - too late for a note-off that arrives in the same tick as its
  // note-on (file playback's 50ms lookahead does that for any short note), and
  // for deciding whether a note-off belongs to a note that is actually sounding.
  const midiActiveNotesRef = useRef<Set<number>>(new Set());
  const audioHandlerRef = useRef<MidiAudioHandler | null>(null);
  const [sustain, setSustain] = useState<boolean>(false);
  const sustainRef = useRef(sustain);
  const showOvertonesRef = useRef(false);
  const midiControllerRef = useRef<MidiControllerRef>(null);
  const [midiAccess, setMidiAccess] = useState<WebMidi.MIDIAccess | null>(null);
  const [selectedInputId, setSelectedInputId] = useState<string | null>(null);
  const selectedInputIdRef = useRef(selectedInputId);
  const [selectedInstrument, setSelectedInstrument] = useState<keyof InstrumentConfigs>('PianoGrand');
  const selectedInstrumentRef = useRef(selectedInstrument);
  const [currentSampler, setCurrentSampler] = useState<Tone.Sampler | null>(null); // use for props to child components (i.e. Key.tsx)
  const [samplerLoaded, setSamplerLoaded] = useState<boolean>(true); // For UI reactivity; use samplerRef.current.loaded for actual sound checks
  const [midiNotes, setMidiNotes] = useState<number[]>([]);
  const [keyflash, setKeyflash] = useState<number[]>([]);
  // const [labelFlashes, setLabelFlashes] = useState({});
  const [labelFlashes, setLabelFlashes] = useState<Record<number, boolean>>({});
  // const [pcLabelFlashes, setPcLabelFlashes] = useState({});
  const [pcLabelFlashes, setPcLabelFlashes] = useState<Record<number, boolean>>({
    0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 7: false, 8: false, 9: false, 10: false, 11: false
  });
  // Add state for primary and secondary label flashes
  const [primaryLabelFlashes, setPrimaryLabelFlashes] = useState<Record<number, boolean>>({});
  const [secondaryLabelFlashes, setSecondaryLabelFlashes] = useState<Record<number, boolean>>({});
  const [mode, setMode] = useState<number>(0);
  const [modes, setModes] = useState<number[]>([]);
  const [chordQual, setChordQual] = useState<number>(0);
  const [chordQuals, setChordQuals] = useState<number[]>([]);
  const [rootPc, setRootPc] = useState<number | null>(null);
  const [pieChordModes, setPieChordModes] = useState<number[]>([0]);
  const [pieSliceMode, setPieSliceMode] = useState<number>(0);
  const [pcWeightsSorted, setPcWeightsSorted] = useState<number[]>([]);
  const [pcSorted, setPcSorted] = useState<number[]>([]);
  const [tonicScales, setTonicScales] = useState<string[]>([]);
  const [tonicScalesNotes, setTonicScalesNotes] = useState<number[][]>([]);
  const [relativeChordScales, setRelativeChordScales] = useState<string[]>([]);
  const [relativeChordScalesNotes, setRelativeChordScalesNotes] = useState<number[][]>([]);
  const [chordScales, setChordScales] = useState<string[]>([]);
  const [chordScalesNotes, setChordScalesNotes] = useState<number[][]>([]);
  const [activeEmoLabels, setActiveEmoLabels] = useState<string[]>([]);
  const [activeEmoLabelsSecondRoot, setActiveEmoLabelsSecondRoot] = useState<string[]>([]);
  const [emoClassLabelsSecondRoot, setEmoClassLabelsSecondRoot] = useState<string[]>([]);
  const rootNoteRef = useRef<number>(0); // default value
  const sentimentCustomizerRef = useRef<{ openPopup: () => void } | null>(null);
  const blackKeyOffsetsOneOctave = [10, 30, 50, 70, 90];
  const [soundOn, setSoundOn] = useState<boolean>(false);
  const [midiMenuExpanded, setMidiMenuExpanded] = useState<boolean>(false);
  const [instrumentMenuExpanded, setInstrumentMenuExpanded] = useState<boolean>(false);
  const samplerRef = useRef<Tone.Sampler | null>(null);
  const samplerLoudRef = useRef<Tone.Sampler | null>(null); // For loud velocity layer (Grand Piano only)
  const scaleSamplerRef = useRef<Tone.Sampler | null>(null); // Dedicated sampler for scale playback (independent of chord notes)
  // const silentAudioRef = useRef<HTMLAudioElement | null>(null); // for iOS silent mode workaround for iOS 16 or older
  const [audioInitialized, setAudioInitialized] = useState<boolean>(false);
  // const audioInitializedRef = useRef(audioInitialized);
  const [shouldPlaySound, setShouldPlaySound] = useState(false);
  const shouldPlaySoundRef = useRef(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showBatchMenu, setShowBatchMenu] = useState(false);
  // Pending batch-edit review, or null when no batch operation is awaiting
  // confirmation. `apply` closes over how the accepted changes get applied, so
  // the dialog stays generic across both respell operations.
  const [batchReview, setBatchReview] = useState<{
    title: string;
    summary: string;
    changes: ChartResolution[];
    warning?: string;
    applyLabel?: string;
    keepLabel?: string;
    width?: 'wide' | 'narrow';
    apply: (accepted: ChartResolution[]) => void;
    // Runs on the secondary button. Omitted means "do nothing but close" (the
    // in-chart Cancel); the export flow supplies one so "Keep Originals &
    // Export" still exports, unmodified.
    keepAll?: () => void;
    // Rect of the button that opened it, so the dialog appears below that
    // button like its dropdown menu does.
    anchor?: { bottom: number; right: number } | null;
  } | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);

  // Instrument configurations
  type InstrumentConfig = {
    name: string;
    baseUrl: string;
    loudBaseUrl?: string; // Optional URL for loud velocity layer samples
    release: number;
  };

  type InstrumentConfigs = {
    [key in 'PianoGrand' | 'PianoFelt' | 'PianoUpright' | 'Rhodes' | 'AcousticGuitar' | 'BassSixString']: InstrumentConfig;
  };

  const instrumentConfigs: InstrumentConfigs = {
    'PianoGrand': { name: 'Grand Piano', baseUrl: '/samples/instruments/PianoGrand/', loudBaseUrl: '/samples/instruments/PianoGrand-mf/', release: 0.5 },
    'PianoFelt': { name: 'Felt Piano', baseUrl: '/samples/instruments/PianoFelt/', release: 0.5 },
    'PianoUpright': { name: 'Upright Piano', baseUrl: '/samples/instruments/PianoUpright/', release: 0.5 },
    'Rhodes': { name: 'Rhodes', baseUrl: '/samples/instruments/Rhodes/', release: 0.1 },
    'AcousticGuitar': { name: 'Guitar', baseUrl: '/samples/instruments/AcousticGuitar/', release: 0.1 },
    'BassSixString': { name: 'Six-String Bass', baseUrl: '/samples/instruments/BassSixString/', release: 0.1 },
    // 'BassFretless': { name: 'Fretless Bass', baseUrl: '/samples/instruments/BassFretless/', release: 0.1 }
  };

  const [octaveOffset, setOctaveOffset] = useState<number>(0);
  // The user's own key center and lock. Only user actions write the lock — the
  // lock icon, a key center panel key, the Major/Minor toggle, the key center
  // menu. The app reads lockedTonicNote / lockedTonicMode / keyCenter instead,
  // derived below the chart state, which let the chart's key centers lock the
  // view while the user has not.
  const [baseKeyCenter, setKeyCenter] = useState<number>(0);
  const [userLockedTonicNote, setLockedTonicNote] = useState<number | null>(null); // null when no note is locked
  const [userLockedTonicMode, setLockedTonicMode] = useState<number>(0); // 0 for Major as default
  const [keyNameDisplay, setKeyNameDisplay] = useState('');
  const [isScale, setIsScale] = useState<boolean>(false);
  const [scaleCode, setScaleCode] = useState<string>("");
  const [scaleUrl, setScaleUrl] = useState<string>("");
  const [activePcs, setActivePcs] = useState<number[]>([]);
  const [activePlusOvertonePcs, setActivePlusOvertonePcs] = useState<number[]>([]);
  const [isImpliedScale, setIsImpliedScale] = useState<boolean>(false);
  const [isArpeggiateOn, setIsArpeggiateOn] = useState<boolean>(false);
  const [scaleDescending, setScaleDescending] = useState<boolean>(false);
  const [hoveredScaleBtn, setHoveredScaleBtn] = useState<'tonic' | 'relative' | 'chord' | null>(null);
  const [hoveredPlayBtn, setHoveredPlayBtn] = useState<'tonic' | 'relative' | 'chord' | null>(null);
  const [playingScale, setPlayingScale] = useState<'tonic' | 'relative' | 'chord' | null>(null);
  const [playIconFlash, setPlayIconFlash] = useState<boolean>(true);
  // Which scale-preview button is waiting on the scale sampler download (shows a spinner)
  const [loadingScaleKey, setLoadingScaleKey] = useState<'tonic' | 'relative' | 'chord' | null>(null);
  // Same spinner as the MIDI File Player's "Analyzing File…" indicator (.loading-spinner in App.css)
  const scaleLoadingSpinner = <span className="loading-spinner" />;
  const scalePlayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scaleNoteTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const isInputtingTextRef = useRef(false);
  const { colors, followModeSoftColors, transformPalette, softColors, hardColors } = useColors();
  const [currentCirclePosition, setCurrentCirclePosition] = useState<number>(0);
  const [previousRootNote, setPreviousRootNote] = useState<number>(0);
  const [prevActualRotation, setPrevActualRotation] = useState<number>(0);
  // const [isMidiPlayerEnabled, setIsMidiPlayerEnabled] = useState(false);
  const midiEventMergerRef = useRef<MidiEventMerger | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const isScrubbingRef = useRef(isScrubbing);
  const [isMidiFilePaused, setIsMidiFilePaused] = useState(false);
  const isMidiFilePausedRef = useRef(isMidiFilePaused);



  // Add settings state
  // Whether loading a MIDI file makes a chord chart from it without asking
  // (Settings → Chord Chart). Settings live only in memory so far, so this
  // one is kept in localStorage, as the chart hint's dismissal is: the popup
  // that sets it promises "every time".
  const CHART_MIDI_ON_LOAD_KEY = 'harmona.chartMidiOnLoad';
  const storedChartMidiOnLoad = (() => {
    try {
      return window.localStorage.getItem(CHART_MIDI_ON_LOAD_KEY) === 'true';
    } catch (e) {
      return false;
    }
  })();

  const [settings, setSettings] = useState({
    preferNotationLegible: false,
    showScaleDegrees: true,
    showTonicLabel: false,
    // Panel visibility lives in panelLayout below, not here — what is on
    // screen depends on the stack's order and where it is scrolled to, which a
    // plain boolean cannot say.
    // showMidiPlayer: true, // MIDI Player always shown, not turned off with settings.showMidiPlayer
    showIntervalLabels: true,
    showPieSentiments: true,
    selectedPalette: "softMajMinColors",
    showOvertones: false,
    showNashvilleNumber: false,
    showTonicScale: false,
    showRelativeChordScale: true,
    showChordScale: false,
    preferModeBorrowing: true,
    preferWhiteStaffPaper: true,
    // Draws the chart's key center lines, and lets the selected chord's key
    // center lock the view (see lockedTonicNote). The data itself is maintained
    // by every edit either way.
    showKeyCenterRegions: true,
    appTheme: 'warm',
    showChordQualityColors: true,
    secondaryChordThreshold: 18,
    showRootFifthSentiment: false,
    // Whether the Chord Compass and Sentiment panels read the chord against a
    // locked key center (see anchorTonicNote) or against the chord's own root.
    anchorToChordRoot: true,
    chartMidiOnLoad: storedChartMidiOnLoad,
    // showSelectedSentiments: true, // Possible to implement in future, if the chord chart selected root is preferred
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(CHART_MIDI_ON_LOAD_KEY, settings.chartMidiOnLoad ? 'true' : 'false');
    } catch (e) {
      // Storage unavailable: the setting lasts this session only
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.chartMidiOnLoad]);

  // Add settings handler
  const handleSettingChange = (setting: string, value: boolean | string | number) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // The panel stack's arrangement (see the stack functions in panels/registry):
  // the order panels sit in, which ones the user has switched off, which are
  // locked on the left, and the panel the view starts at. Panels read their place from the view rather than
  // from where they sit in the JSX below, so moving a panel on screen does not
  // mean moving hundreds of lines of markup.
  // `start` is always a panel in the stack: hiding it hands the start to a
  // neighbour. The layout is restored from the last session when there is one
  // (see panels/layoutStorage).
  const [panelLayout, setPanelLayout] = useState<PanelLayout>(() => {
    const saved = loadLayout();
    if (saved !== null) {
      return saved;
    }
    return {
      order: DEFAULT_ORDER,
      hidden: ['sentiment', 'luxTonnetz', 'chordChart'],
      locked: [],
      start: 'chordSymbol',
    };
  });

  // Save the layout as it changes.
  useEffect(() => {
    saveLayout(panelLayout);
  }, [panelLayout]);

  // The hint under the Chord Symbol panel that says how to get a chord into the
  // chart. It has done its job once it has been read, so it can be dismissed for
  // good. Nothing else in settings is persisted yet, so this is kept on its own
  // in localStorage rather than waiting for the user-settings store — one key,
  // read once, and a browser that refuses it (private mode, storage disabled)
  // just means the hint comes back.
  const CHART_HINT_KEY = 'harmona.chartHintDismissed';
  const [chartHintDismissed, setChartHintDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(CHART_HINT_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });

  const dismissChartHint = () => {
    setChartHintDismissed(true);
    try {
      window.localStorage.setItem(CHART_HINT_KEY, 'true');
    } catch (e) {
      // Storage unavailable — it stays dismissed for this session only.
    }
  };

  const stack = panelStack(panelLayout);
  const placed = stackPositions(panelLayout);
  const visiblePanels = placed.view;
  // Panels sliding out of the view, still drawn until they have faded.
  const leavingPanels = useStackMotion(panelLayout);

  /** In the view — on screen and live. A panel scrolled out of view is not rendered. */
  const isPanelVisible = (id: PanelId) => visiblePanels.indexOf(id) !== -1;
  /** Drawn at all: in the view, or on its way out of it. */
  const isPanelRendered = (id: PanelId) =>
    isPanelVisible(id) || leavingPanels.some(l => l.id === id);
  /** Where a drawn panel sits, and whether it is live. A leaving panel is frozen at its last place. */
  const panelPlacement = (id: PanelId) => {
    const leaving = leavingPanels.find(l => l.id === id);
    if (isPanelVisible(id) || leaving === undefined) {
      return { x: placed.x[id] as number, frozen: false };
    }
    return { x: leaving.x, frozen: true };
  };
  /** Switched on, whether or not it is scrolled into view. */
  const isPanelOn = (id: PanelId) => panelLayout.hidden.indexOf(id) === -1;

  // Rearranging the stack. The rules — what a move does at the edge of the
  // view, how a meter click maps onto the stack — are with the stack
  // arithmetic in panels/registry.
  const movePanel = (id: PanelId, direction: number) => {
    setPanelLayout(prev => stepPanel(prev, id, direction));
  };

  // Settings' meter calls this: its blocks are the stack drawn to scale, so
  // clicking a block puts the panel there.
  const movePanelTo = (id: PanelId, index: number) => {
    setPanelLayout(prev => placePanel(prev, id, index));
  };

  // The scroll controls at the row's ends, and the panels they name.
  const scrollPanels = (direction: number) => {
    setPanelLayout(prev => scrollStack(prev, direction));
  };
  // Everything waiting beyond each edge, for the scroll controls' miniatures.
  const offLeft = stack.filter(p => placed.side[p] === 'left');
  const offRight = stack.filter(p => placed.side[p] === 'right');

  // The move/hide buttons and padlock each panel carries in the tab under its
  // bottom edge. The first panel in the stack has nowhere to go left, the last
  // nowhere right, and a locked panel does not move. The padlock goes on the
  // leftmost panel of the view, when locking it would still leave room for
  // every panel, and on the innermost locked panel (see Panel for where it
  // sits beside the left arrow).
  const panelControls = (id: PanelId) => {
    const at = stack.indexOf(id);
    let lock: 'unlocked' | 'locked' | undefined;
    if (canUnlockPanel(panelLayout, id)) {
      lock = 'locked';
    } else if (canLockPanel(panelLayout, id)) {
      lock = 'unlocked';
    }
    return {
      onMoveLeft: () => movePanel(id, -1),
      onMoveRight: () => movePanel(id, 1),
      onHide: () => togglePanel(id),
      canMoveLeft: at > 0,
      canMoveRight: at !== -1 && at < stack.length - 1,
      lock: lock,
      onToggleLock: () => setPanelLayout(prev =>
        (prev.locked.indexOf(id) === -1 ? lockPanel(prev, id) : unlockPanel(prev, id))),
    };
  };

  // Drives the little meter beside each panel's name in Settings. The meter is
  // the whole row, left to right — the locked panels, then the stack — each
  // panel to scale against the others, so it answers "where is this panel" and
  // "is it in view". A panel that is off is included in the slot it would
  // return to, so the meter previews the result of the toggle.
  const panelMeter = (id: PanelId) => {
    const shown = meterLineup(panelLayout, id);
    const total = rowWidth(shown);

    // The real gap is a hairline at this size. Draw it wider, and take the
    // extra out of the blocks so they still fill the track exactly.
    const gaps = Math.max(0, shown.length - 1);
    const trueGap = PANEL_GAP / total;
    const drawnGap = Math.max(trueGap, 0.045);
    const scale = (1 - drawnGap * gaps) / (1 - trueGap * gaps);

    return {
      segments: shown.map(p => ({
        id: p,
        fraction: (PANELS[p].width / total) * scale,
        self: p === id,
        inView: isPanelVisible(p),
      })),
      gapFraction: drawnGap,
    };
  };

  // Hiding takes the panel out of the stack. Showing puts it back in its slot
  // and scrolls it into view — nothing is ever refused or pushed off for want
  // of room.
  const togglePanel = (id: PanelId) => {
    setPanelLayout(prev => (prev.hidden.indexOf(id) === -1 ? hidePanel(prev, id) : showPanel(prev, id)));
  };


  const appStyle = {
    maxHeight: '2000px', // Adjust the value as needed
    overflow: 'auto', // Adds scrollbars if content exceeds max height
  };


  const handleInputFocus = () => { isInputtingTextRef.current = true; };
  const handleInputBlur = () => { isInputtingTextRef.current = false; };

  // Define a type that includes the favored mode
  type FifthsOrderedEntry = {
    pitchClass: number;
    label: string;
    showInModes: number[]; // array of modes where this should appear (e.g., [0, 1] for both, [0] for major only)
  };

  const fifthsOrderedPitchClasses: FifthsOrderedEntry[] = [
    { pitchClass: 1, label: 'C♯', showInModes: [1] },  // C♯ in minor only
    { pitchClass: 6, label: 'F♯', showInModes: [1] },  // F♯ in minor only
    { pitchClass: 11, label: 'B', showInModes: [0, 1] },  // B in both modes
    { pitchClass: 4, label: 'E', showInModes: [0, 1] },   // E in both modes
    { pitchClass: 9, label: 'A', showInModes: [0, 1] },   // A in both modes
    { pitchClass: 2, label: 'D', showInModes: [0, 1] },   // D in both modes
    { pitchClass: 7, label: 'G', showInModes: [0, 1] },   // G in both modes
    { pitchClass: 0, label: 'C', showInModes: [0, 1] },   // C in both modes
    { pitchClass: 5, label: 'F', showInModes: [0, 1] },   // F in both modes
    { pitchClass: 10, label: 'B♭', showInModes: [0, 1] }, // B♭ in both modes
    { pitchClass: 3, label: 'E♭', showInModes: [0, 1] },  // E♭ in both modes
    { pitchClass: 8, label: 'A♭', showInModes: [0, 1] },  // A♭ in both modes
    { pitchClass: 1, label: 'D♭', showInModes: [0] },  // D♭ in both modes
    { pitchClass: 6, label: 'G♭', showInModes: [0] }      // G♭ in major only
  ];

  // Written by the user's lock actions, and by the effect that follows the
  // analysed key center while unlocked. The menu reads selectedKeyCenterIndex,
  // derived below the chart state.
  const [baseKeyCenterIndex, setSelectedKeyCenterIndex] = useState<number>(7);
  const [keyCenterMenuExpanded, setKeyCenterMenuExpanded] = useState<boolean>(false);

  const handleKeyCenterSelect = (index: number) => {
    const selectedEntry = fifthsOrderedPitchClasses[index];
    // Picking the key that is already locked would toggle the lock off (the old
    // <select> simply fired no change event for it), so just close the menu.
    // Picking the key a chart lock shows makes it the user's lock.
    if (userLockedTonicNote !== selectedEntry.pitchClass) {
      handleToggleLock(selectedEntry.pitchClass);
      setSelectedKeyCenterIndex(index);
    }
    setKeyCenterMenuExpanded(false);
  };

  // NEW ADDITION ACRYLICODE
  // ///////////////////
  // BEGIN Chord Chart handlers
  // const [chordChart, setChordChart] = useState<ChordChartEntry[]>([]);
  // const [userSongs, setUserSongs] = useState<Song[]>([]);
  // const [allSongs, setAllSongs] = useState<Song[]>(defaultSongs);
  // const [selectedSong, setSelectedSong] = useState<Song>(defaultSongs[0]);

  // BEGIN Chord Chart handlers
  // const [chordChart, setChordChart] = useState<{
  //   name: string,
  //   voicing: number[],
  //   isSeparator: boolean
  // }[]>([]);


  // const [chordChart, setChordChart] = useState<{ name: string, voicing: number[], isSeparator: boolean, isRehearsalMark: boolean, isTimeSig: boolean}[]>([]);

  // const [chordChart, setChordChart] = useState<{
  //   type: string // either 'chord', 'separator', 'marker', 'timesig', 'keysig', or 'systembreak'
  //   name: string, // chord (quality) m9b5, ∆13#11, etc., SEPARATORS.barline etc., marker 
  //   root: number, // 0-11 pitch class
  //   voicing: number[], // array of midi note numbers
  //   keySig: number[], // [0-11 pitch class, 0 for major 1 for minor]
  //   altBass: [boolean, number], // is alternate bass note, pitch class
  // }[]>([]);

  const [chartLength, setChartLength] = useState<number>(0);
  const [songTitle, setSongTitle] = useState({ title: "Song Title", isEditable: false });
  const [songTitleLength, setSongTitleLength] = useState<number>(0);
  const [composer, setComposer] = useState({ name: "Composer", isEditable: false });
  const [tempo, setTempo] = useState({ value: "120", isEditable: false });
  const [timeSigBeats, setTimeSigBeats] = useState(4);
  const [timeSigDuration, setTimeSigDuration] = useState(4);
  const beatOptions = [2, 3, 4, 5, 6, 7, 9, 12];
  const durationOptions = [2, 4, 8];
  const [selectedChordIndex, setSelectedChordIndex] = useState<number | null>(null);
  const [hoveredChordIndex, setHoveredChordIndex] = useState<number | null>(null);
  const [chordEditMode, setChordEditMode] = useState<boolean>(false);
  const chordEditModeRef = useRef(false);
  const [editModeOriginalVoicing, setEditModeOriginalVoicing] = useState<number[]>([]);
  const editModeOriginalVoicingRef = useRef<number[]>([]);
  const [selectedChordOptionIndex, setSelectedChordOptionIndex] = useState<number>(0);
  const selectedChordOptionIndexRef = useRef(0);
  const [separatorEditMode, setSeparatorEditMode] = useState<boolean>(false);
  const separatorEditModeRef = useRef(false);
  const [selectedSeparatorTypeIndex, setSelectedSeparatorTypeIndex] = useState<number>(0);
  const selectedSeparatorTypeIndexRef = useRef(0);
  const [separatorMenuExpanded, setSeparatorMenuExpanded] = useState<boolean>(false);
  const selectedChordIndexRef = useRef(selectedChordIndex);
  const [playSongOn, setPlaySongOn] = useState<boolean>(false);
  const playSongOnRef = useRef(playSongOn);
  const [currentPlaySongIndex, setCurrentPlaySongIndex] = useState<number | null>(null);
  // type ChordChartEntry = { name: string; voicing: number[]; isSeparator: boolean; }; // NEW REMOVAL ACRYLICODE
  type ChordChartEntry = {
    id: string;
    // The chord symbol as text (see chordSymbolText); a separator holds its own
    // glyph and a blank chord holds the em quad (U+2001). Never a React
    // element: an element cannot be stored, and the drawn symbol is rebuilt
    // from `segment` at draw time anyway, which is what keeps it current with
    // the locked tonic.
    name: string;
    voicing: number[];
    isSeparator: boolean;
    segment?: ChordSegment;
    // Where in a MIDI file the chord came from (CHORD-TIMESTAMP-SPEC.md):
    // the file's SHA-256 and the start of its chord region, in the file's own
    // time. Provenance, not position: it stays through edits and moves, and
    // moves only with its region's start. Chord entries only.
    midiSource?: MidiSource;
  };
  type MidiSource = { fileKey: string; seconds: number };
  const [chordChart, setChordChart] = useState<ChordChartEntry[]>([]);

  // The MIDI files the chart's timestamps came from, with the name each had
  // when its first chord was added: display only, so a viewer knows which
  // file to load to follow along. A record is added with a file's first
  // chord and never removed here, so undo needs nothing; saving the chart
  // writes only the records some entry still uses.
  type ChartMidiFile = { fileKey: string; name: string; storageId?: string };
  const [chartMidiFiles, setChartMidiFiles] = useState<ChartMidiFile[]>([]);
  const rememberChartMidiFile = (fileKey: string, name: string) => {
    setChartMidiFiles(prev => (prev.some(f => f.fileKey === fileKey) ? prev : [...prev, { fileKey, name }]));
  };

  // Whether the notes on the keys came from the MIDI file: set when the
  // player puts them there, cleared when the chart puts a chord's own notes
  // there. A chord added while it is set is timestamped to the region at the
  // playhead, toggled corrections and all: it still came from that moment.
  const notesFromFileRef = useRef(false);

  // Follow (FollowToggle): the chart and the MIDI file share one place.
  // `followTime` is the latest chart timestamp the file's place (playhead or
  // clicked region) has reached, as the player reports it. While the file
  // drives, the first chord dated to it is the chart's selection, wherever it
  // sits in the chart (CHORD-TIMESTAMP-SPEC.md, follow mode).
  const [chartFollowOn, setChartFollowOn] = useState(true);
  const [followTime, setFollowTime] = useState<{ fileKey: string; seconds: number } | null>(null);
  const isFollowedEntry = (entry: ChordChartEntry) => followTime !== null && !entry.isSeparator && !!entry.midiSource
    && entry.midiSource.fileKey === followTime.fileKey && Math.abs(entry.midiSource.seconds - followTime.seconds) < 0.001;
  const firstFollowedIndex = followTime === null ? -1 : chordChart.findIndex(isFollowedEntry);
  // Set when the chart selects a chord itself (promotion), not the user, so
  // Follow does not move the playhead to it
  const skipFollowSeekRef = useRef(false);
  // Whether the file is playing, for one driver at a time
  const [midiFilePlaying, setMidiFilePlaying] = useState(false);
  // The chord the file has reached stays in view while the file drives
  useEffect(() => {
    if (chartFollowOn && fileDrivesKey && firstFollowedIndex >= 0) scrollChartEntryIntoView(firstFollowedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstFollowedIndex]);

  /**
   * The chart's own key centers — document data, saved with the chart, and not
   * to be confused with lockedTonicNote/lockedTonicMode, which are view state
   * saying what key the app is rendering in. See KEY-CENTER-TRACKING-SPEC.md;
   * the rules that keep regions anchored live in utils/keyCenterRegions.ts.
   */
  const [globalKeyCenter, setGlobalKeyCenter] = useState<KeyCenter | null>(null);
  const [keyCenterRegions, setKeyCenterRegions] = useState<KeyCenterRegion[]>([]);

  /**
   * The key center the app renders in. A tonic the user locked comes first.
   * Otherwise, with Show Key Center Regions on, the selected chart chord's key
   * center locks the view: its region's key, or the chart's global key where no
   * region covers it. Every panel then treats it exactly as a locked tonic.
   * Nothing stores the chart lock, so it follows the selection and lets go the
   * moment the selection or the setting does.
   */
  const selectedChartEntry = selectedChordIndex !== null && chordChart[selectedChordIndex] !== undefined
    ? chordChart[selectedChordIndex]
    : null;
  // A chord with notes. Separators and blank chords (no notes, the em quad as
  // their name) are not: they are passed over by the Key Center panel's chords
  // and the Compass, and selecting one leaves the last chord's notes sounding.
  const isChartChord = (entry: ChordChartEntry) => !entry.isSeparator && entry.name !== ' ';
  // Selecting a separator or blank chord leaves the last selected chord's notes
  // sounding, so every panel goes on showing that chord; the chart lock keeps
  // its key too. Cleared on deselect, so one selected afresh borrows nothing.
  const [lastSelectedChordId, setLastSelectedChordId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedChartEntry === null) setLastSelectedChordId(null);
    else if (isChartChord(selectedChartEntry)) setLastSelectedChordId(selectedChartEntry.id);
  }, [selectedChartEntry]);
  const chartKeyChordId = (() => {
    if (selectedChartEntry === null) return null;
    if (isChartChord(selectedChartEntry)) return selectedChartEntry.id;
    // The remembered chord may have been deleted since
    return chordChart.some(entry => entry.id === lastSelectedChordId) ? lastSelectedChordId : null;
  })();
  // The document lock: the key of whichever document holds the current
  // place, the MIDI file's key at its playhead when the file moved last
  // (playing, scrubbing, << >>, a region click), else the selected chart
  // chord's key. Both are document keys, drawn with the chart-locked icon, and
  // the user's own lock overrides either, to audition another key.
  const [fileKeyCenter, setFileKeyCenter] = useState<KeyCenter | null>(null);
  const [fileDrivesKey, setFileDrivesKey] = useState(false);
  const isFileLocked = settings.showKeyCenterRegions && userLockedTonicNote === null
    && fileDrivesKey && fileKeyCenter !== null;
  const chartKeyCenter = (() => {
    if (!settings.showKeyCenterRegions || userLockedTonicNote !== null) return null;
    if (isFileLocked) return fileKeyCenter;
    return chartKeyChordId !== null ? keyCenterForChord(chordChart, keyCenterRegions, globalKeyCenter, chartKeyChordId) : null;
  })();
  const isChartLocked = chartKeyCenter !== null;
  const lockedTonicNote = chartKeyCenter !== null ? chartKeyCenter.pitchClass : userLockedTonicNote;
  const lockedTonicMode = chartKeyCenter !== null ? chartKeyCenter.mode : userLockedTonicMode;
  // The lock as the Chord Compass and Sentiment panels see it. By default they
  // stay anchored on the chord's root while a key is locked, so their wheel,
  // labels and sentiments don't switch with the key; the Key-Center Anchor
  // setting has them read the chord against the locked key instead. Read the
  // mode from lockedTonicMode wherever this is non-null.
  const anchorTonicNote = settings.anchorToChordRoot ? null : lockedTonicNote;
  const keyCenter = chartKeyCenter !== null ? chartKeyCenter.pitchClass : baseKeyCenter;
  // The key center menu's selection. A chart lock names its own entry; every
  // pitch class has an entry in each mode.
  const selectedKeyCenterIndex = chartKeyCenter !== null
    ? fifthsOrderedPitchClasses.findIndex(entry =>
      entry.pitchClass === chartKeyCenter.pitchClass && entry.showInModes.includes(chartKeyCenter.mode))
    : baseKeyCenterIndex;

  // The Lux Tonnetz centers on the key center as the menu spells it, and lights the
  // played pitch classes as degrees above it. Memoized so the grid only rebuilds when
  // the degrees change, not on every render.
  const tonnetzKeyCenterEntry = fifthsOrderedPitchClasses[selectedKeyCenterIndex];
  const tonnetzKeyCenter = tonnetzKeyCenterEntry ? tonnetzKeyCenterEntry.label : 'C';
  const tonnetzDegrees = useMemo(
    () => activePcs.map(pc => (pc - keyCenter + 12) % 12).sort((a, b) => a - b),
    [activePcs, keyCenter]
  );

  /**
   * One step of chart history. The regions travel with the entries because an
   * edit changes both at once: an anchor that slid or a region that was removed
   * has to come back with the chords that caused it.
   */
  type ChartSnapshot = {
    entries: ChordChartEntry[];
    regions: KeyCenterRegion[];
    globalKeyCenter: KeyCenter | null;
  };
  const [chordChartHistory, setChordChartHistory] = useState<ChartSnapshot[]>([]);

  // The chart opens itself when it gains entries, and scrolls itself into
  // view. From then on it is a normal panel: emptying it leaves it open, and
  // only the user closes it.
  const hasChartEntries = chordChart.length > 0;
  // Also called on every entry added, so adding to a chart that is off, or
  // scrolled out of view, brings it back to show what was added.
  const autoOpenChartPanel = () => {
    setPanelLayout(prev => showPanel(prev, 'chordChart'));
  };
  // A panel that sends something to the chart — the Compass's staff lines and
  // add plus, a chord clicked in the Chord Symbol panel — opens the chart just
  // before itself and starts the view at it, so the panel stays in view to the
  // chart's right, and closing the chart hands the view's start back to the
  // panel (hidePanel) whatever either one's width. Keys and the chart's own
  // controls use autoOpenChartPanel instead. A chart already on screen, locked
  // or not, stays where it is; a locked panel never leaves the screen, so the
  // chart only needs showing.
  const openChartBefore = (anchor: PanelId) => {
    setPanelLayout(prev => {
      if (stackPositions(prev).view.indexOf('chordChart') !== -1) return prev;
      if (prev.locked.indexOf(anchor) !== -1) return showPanel(prev, 'chordChart');
      const order = prev.order.filter(p => p !== 'chordChart');
      order.splice(order.indexOf(anchor), 0, 'chordChart');
      return {
        order: order,
        hidden: prev.hidden.filter(p => p !== 'chordChart'),
        locked: prev.locked,
        start: 'chordChart',
      };
    });
  };
  useEffect(() => {
    if (hasChartEntries) {
      autoOpenChartPanel();
    }
  }, [hasChartEntries]);

  // Whether the chart has chords past its bottom edge. The chart is a
  // fixed-height panel, so the overflow is cut off cleanly by the panel border
  // and a chart that continues looks exactly like a chart that ends. This drives
  // the fade and the arrow at the foot of the panel, which say otherwise.
  const chartBodyRef = useRef<HTMLDivElement>(null);
  const [chartHasMoreBelow, setChartHasMoreBelow] = useState(false);
  const [chartHasMoreAbove, setChartHasMoreAbove] = useState(false);
  const chartPanelShown = isPanelVisible('chordChart');
  const chordSymbolPanelShown = isPanelVisible('chordSymbol');

  // A pixel of slack at each end: fractional heights leave scrollTop and
  // scrollTop + clientHeight a hair off their limits.
  const measureChartOverflow = (body: HTMLDivElement) => {
    setChartHasMoreAbove(body.scrollTop > 1);
    setChartHasMoreBelow(body.scrollHeight - body.scrollTop - body.clientHeight > 1);
  };

  // The chart's paper, matching the Staff's black paper by theme.
  const chartPaperAlpha = () => {
    if (settings.appTheme === 'light') {
      return 0.75;
    }
    if (settings.appTheme === 'dark') {
      return 0.7;
    }
    return 0.83;
  };

  // Re-measure whenever the chords change, whenever the font size derived from
  // chartLength changes (the same chords can occupy a different height than
  // they did a moment ago), when the panel comes back on screen — there is
  // nothing to measure while it is away — and when the first key center goes
  // in or the last comes out, which opens or closes the gaps between systems.
  const chartHasKeyCenters = globalKeyCenter !== null || keyCenterRegions.length > 0;
  useEffect(() => {
    const body = chartBodyRef.current;
    if (body) {
      measureChartOverflow(body);
    }
  }, [chordChart, chartLength, chartPanelShown, chartHasKeyCenters, settings.showKeyCenterRegions]);

  // Ease the chart's scroll instead of jumping it. The duration barely grows
  // with the distance, so the SPEED is what scales: a jump to the far end of the
  // chart is covered very fast, a step to the next bar moves gently — both land
  // in about a fifth of a second, which is quick enough not to be waited on but
  // slow enough to see which way the chart went.
  //
  // Hand-rolled rather than scrollTo({ behavior: 'smooth' }) precisely because
  // of that: the browser's smooth scroll picks its own duration, and for a long
  // move it is a slow glide.
  const chartScrollFrameRef = useRef<number | null>(null);

  const easeChartScrollTo = (body: HTMLDivElement, top: number) => {
    // A held-down arrow key retargets mid-flight; drop the old animation or the
    // two fight over scrollTop.
    if (chartScrollFrameRef.current !== null) {
      cancelAnimationFrame(chartScrollFrameRef.current);
      chartScrollFrameRef.current = null;
    }

    const from = body.scrollTop;
    const distance = top - from;
    if (Math.abs(distance) < 2) {
      body.scrollTop = top;
      return;
    }

    const duration = Math.min(450, 140 + Math.abs(distance) * 0.2);
    const start = performance.now();

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic: leaves at once, settles softly.
      const eased = 1 - Math.pow(1 - t, 3);
      body.scrollTop = from + distance * eased;
      chartScrollFrameRef.current = t < 1 ? requestAnimationFrame(step) : null;
    };

    chartScrollFrameRef.current = requestAnimationFrame(step);
  };

  // Keep the selected entry in view. Everything that moves the selection goes
  // through selectedChordIndex — the arrow keys (chartNavigation), the return
  // key and a click in the Chord Symbol panel (both via addEntryToChart),
  // a click in the chart itself, and play-along — so one effect covers them all.
  //
  // It follows the SELECTION rather than what is sounding on the piano. A
  // separator or an empty-voicing entry can be selected without setting
  // toggledNotes, and arrow keys walk onto separators outside chord-edit mode;
  // scrolling only for entries that reach the keyboard would leave the cursor
  // off screen while stepping across a run of barlines.
  //
  // chordChart is a dependency as well as the index: adding a chord at the end
  // can leave the index unchanged while the entry it names is a new one, sitting
  // a row further down.
  // Also used by Follow, for the chord the MIDI file has reached
  const scrollChartEntryIntoView = (index: number) => {
    const body = chartBodyRef.current;
    if (!body) {
      return;
    }
    const entry = body.querySelector(`[data-chart-index="${index}"]`);
    if (!entry) {
      return;
    }

    const view = body.getBoundingClientRect();
    const box = entry.getBoundingClientRect();
    // The bottom of the view is spoken for: the fade, and under it the row of
    // chords it dims. Treat that strip as out of sight, or the selection lands
    // in it and reads as greyed out. 1dvw of breathing room at the top.
    const dvw = window.innerWidth / 100;
    const above = box.top - (view.top + 1 * dvw);
    const below = box.bottom - (view.bottom - 3 * dvw);

    if (above < 0) {
      easeChartScrollTo(body, body.scrollTop + above);
    } else if (below > 0) {
      easeChartScrollTo(body, body.scrollTop + below);
    }
  };
  useEffect(() => {
    if (selectedChordIndex !== null) scrollChartEntryIntoView(selectedChordIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChordIndex, chordChart]);

  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const openingBarSelectRef = useRef<HTMLSelectElement>(null);
  const closingBarSelectRef = useRef<HTMLSelectElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const batchButtonRef = useRef<HTMLButtonElement>(null);
  // Shared across batch operations so an ordering respell after an iReal Pro respell (or a second
  // ordering respell) reuses the analysis results already fetched for each voicing.
  const candidateCacheRef = useRef(createCandidateCache());


  const SEPARATORS = {
    // spacer: "\u00A0", // ⬚ \u00A0\u00A0\u00A0
    // spacer: "⬚", // ⬚ \u00A0\u00A0\u00A0
    barline: "|", // "𝄀"
    doubleBarline: "𝄁",
    repeatStart: "𝄆",
    repeatEnd: "𝄇",
    repeatMeasure: "𝄎",
    doubleRepeatMeasure: "𝄏",
    slash: "/" // "𝄍"
  };
  const SEPARATOR_TYPES = Object.values(SEPARATORS);


  const MARKERS = {
    a: "Ⓐ",
    b: "Ⓑ",
    c: "Ⓒ",
    d: "Ⓓ",
    v: "Ⓥ",
    i: "Ⓘ",
    s: "𝄋",
    q: "𝄌",
    f: "𝄐"
  }


  // Tempo Slider
  // type RangeChangeEvent = React.ChangeEvent<HTMLInputElement>;
  // const [tempoSliderValue, onChange] = useState<number>(1);
  // useEffect(() => {
  //   const ele = document.querySelector('.buble') as HTMLElement | null;
  //   if (ele) {
  //     ele.style.bottom = `${Number(tempoSliderValue / 4)}px`;
  //   }
  // }, [tempoSliderValue]); // Added value as a dependency
  // const handleSliderChange = (event: RangeChangeEvent) => {
  //   onChange(Number(event.target.value));
  // };


  // State for managing tempo value and slider visibility
  // const [tempo, setTempo] = useState({ value: 120, isEditable: false }); ABOVE
  const [showSlider, setShowSlider] = useState(false);

  // const handleTempoChange = (event: React.ChangeEvent<HTMLInputElement>) => { BELOW
  //   setTempo({ ...tempo, value: event.target.value });
  // };

  const toggleEditTempo = () => { // Replaced the BELOW
    setShowSlider(!showSlider);
    // Additional logic if needed when toggling
  };

  // Update tempo value from slider
  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempo({ ...tempo, value: event.target.value });
  };


  useEffect(() => {
    selectedChordIndexRef.current = selectedChordIndex;
  }, [selectedChordIndex]);

  // The selection whose chord's notes this effect last put on the keyboard.
  // Selecting a chord renders once before they arrive, with the last chord's
  // notes still sounding; anything that compares the two has to wait for this
  // to catch up rather than read that render as a change of voicing.
  const [notesAppliedFor, setNotesAppliedFor] = useState<number | null>(null);

  useEffect(() => {
    if (keepNotesOnReselectRef.current) {
      keepNotesOnReselectRef.current = false;
      return;
    }
    if (selectedChordIndex !== null && selectedChordIndex >= 0 && selectedChordIndex <= chordChart.length) {
      const selectedChord = chordChart[selectedChordIndex];
      if (selectedChord && !selectedChord.isSeparator && selectedChord.voicing.length !== 0) { // selectedChord.name !== '\u2001') {
        releaseToggledNotes();
        // The chart's notes must never land in a region being corrected
        if (midiControllerRef.current) midiControllerRef.current.stopEditingRegion();
        setToggledNotes(selectedChord.voicing);
        notesFromFileRef.current = false;
        setFileDrivesKey(false);
        // Follow: the file's playhead to where this chord came from
        if (chartFollowOn && !skipFollowSeekRef.current && selectedChord.midiSource && midiControllerRef.current
          && fileRegions !== null && fileRegions.fileKey === selectedChord.midiSource.fileKey) {
          midiControllerRef.current.seekSilently(selectedChord.midiSource.seconds);
        }
        skipFollowSeekRef.current = false;
        setNotesAppliedFor(selectedChordIndex);
        toggledNotesRef.current = selectedChord.voicing;
        if (chordEditModeRef.current) {
          const v = [...new Set(selectedChord.voicing)].sort((a, b) => a - b);
          editModeOriginalVoicingRef.current = v;
          setEditModeOriginalVoicing(v);
        }
        playChartChord();
      }
    } else {
      setChordEditMode(false);
      setSeparatorEditMode(false);
      releaseToggledNotes();
      setToggledNotes([]);
    }
  }, [selectedChordIndex]);





  // Undo Redo functions
  const restoreChartSnapshot = (snapshot: ChartSnapshot) => {
    setChordChart(snapshot.entries);
    setKeyCenterRegions(snapshot.regions);
    setGlobalKeyCenter(snapshot.globalKeyCenter);
  };
  useEffect(() => {
    // If the current state is different from the latest in history, update history.
    // One edit writes one step: the chart's entries and its key centers are set
    // together at every edit site, so they arrive here in the same render.
    const snapshot: ChartSnapshot = { entries: chordChart, regions: keyCenterRegions, globalKeyCenter };
    if (JSON.stringify(snapshot) !== JSON.stringify(chordChartHistory[currentHistoryIndex])) {
      const updatedHistory = [...chordChartHistory.slice(0, currentHistoryIndex + 1), snapshot];
      setChordChartHistory(updatedHistory);
      setCurrentHistoryIndex(updatedHistory.length - 1);
    }
  }, [chordChart, keyCenterRegions, globalKeyCenter]);
  const undo = () => {
    if (currentHistoryIndex > 0) {
      setCurrentHistoryIndex(currentHistoryIndex - 1);
      restoreChartSnapshot(chordChartHistory[currentHistoryIndex - 1]);
    }
  };
  const redo = () => {
    if (currentHistoryIndex < chordChartHistory.length - 1) {
      setCurrentHistoryIndex(currentHistoryIndex + 1);
      restoreChartSnapshot(chordChartHistory[currentHistoryIndex + 1]);
    }
  };


  useEffect(() => {
    playSongOnRef.current = playSongOn;
  }, [playSongOn]);



  const handleChordSelection = (index: number) => {
    if (index >= 0 && index < chordChart.length) {
      const selectedEntry = chordChart[index];
      if (!selectedEntry.isSeparator) {
        if (chordEditModeRef.current && selectedChordIndexRef.current !== index) {
          replaceChordInChart(selectedChordOptionIndexRef.current, false);
          setSelectedChordOptionIndex(-1);
        }
        releaseToggledNotes();

        // If clicking the same chord, don't toggle selection off
        if (selectedChordIndexRef.current !== index) {
          setSelectedChordIndex(index);
        } else {
          toggledNotesRef.current = selectedEntry.voicing;
          playChartChord();
        }
        if (midiControllerRef.current) midiControllerRef.current.stopEditingRegion();
        setToggledNotes(selectedEntry.voicing);
        toggledNotesRef.current = selectedEntry.voicing;
        notesFromFileRef.current = false;
        setFileDrivesKey(false);
        // playChartChord(); // Letting the UseEffect handle this
      }
    }
  };


  // const handleChordNavigation = (direction: string) => {
  //   // Logic to determine the next index based on the direction
  //   const newIndex = calculateNewIndex(direction, selectedChordIndex, chordChart);
  //   setSelectedChordIndex(newIndex);
  // };



  // const toggleChordSelection = (index: number) => {
  //   setSelectedChordIndex(prevIndex => prevIndex === index ? null : index);
  // };

  const handleGlobalClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    // Check if click is on or inside any relevant elements
    const isRelevantTarget = target.matches('.chord') ||
      target.matches('.delete-chord') ||
      target.matches('.color-key') ||
      target.matches('.mode-select') ||
      target.closest('[data-actionable="true"]') ||
      target.closest('.chord-chart') ||  // Add this to prevent deselection when clicking chart area
      target.closest('.chord-display') ||   // Add this to prevent deselection when clicking display area
      target.closest('.midi-player');    // Prevent deselection when clicking MIDI player controls

    // Only while the chart is on screen: with it hidden, the selection is what
    // the Key Center panel's chords show, and a stray click shouldn't lose it.
    if (chartPanelShown && !isRelevantTarget && selectedChordIndexRef.current != null) {
      setSelectedChordIndex(null);
      releaseToggledNotes();
      setToggledNotes([]);
      // Also clear any lingering chord displays
      setCsOptionsSegments([]);
    }
  };

  /**
   * Clicking empty space inside the chart deselects the selected chord.
   *
   * Anything that is not an entry or a control counts as blank paper. The chart
   * handles this itself because handleGlobalClick above exempts everything
   * inside .chord-chart, so that its own controls never drop the selection.
   */
  const handleChartBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const onSomething = target.closest('[data-chart-index]') ||
      target.closest('[data-actionable="true"]') ||
      target.closest('.chord-chart-controls') ||
      target.closest('input') ||
      target.closest('select');
    if (onSomething) return;
    if (selectedChordIndexRef.current === null) return;
    setSelectedChordIndex(null);
    releaseToggledNotes();
    setToggledNotes([]);
    setCsOptionsSegments([]);
  };

  const chartNavigation = (direction: string) => {
    // Nothing to move to in an empty chart. Without this, forward lands on
    // index -1, which reads as a deselect and clears the keyboard.
    if (chordChart.length === 0) return;
    if (!playSongOnRef.current) {
      const prevIndex = selectedChordIndexRef.current;
      const defaultIndexForBackward = 0;
      const defaultIndexForForward = chordChart.length - 1;

      let newIndex = prevIndex !== null
        ? (direction === "forward"
          ? Math.min(prevIndex + 1, chordChart.length - 1)
          : direction === "jump-forward"
            ? Math.min(prevIndex + CHART_JUMP, chordChart.length - 1)
            : direction === "jump-backward"
              ? Math.max(prevIndex - CHART_JUMP, 0)
              : Math.max(prevIndex - 1, 0))
        : (direction === "forward" || direction === "jump-forward"
          ? defaultIndexForForward
          : defaultIndexForBackward);

      // Separators are skipped while a chord is being edited, and while the
      // chart is hidden: the Key Center panel shows only chords, so landing on
      // a separator there would look like the key did nothing. A jump always
      // skips them: a separator eight entries off sits between two chords, and
      // the display would go on showing the chord the jump started from. So
      // does the first move with nothing selected, which lands on the first or
      // last chord rather than on a separator at the chart's end.
      const isJump = direction === "jump-forward" || direction === "jump-backward";
      if (chordEditModeRef.current || !chartPanelShown || isJump || prevIndex === null) {
        const step = (direction === "backward" || direction === "jump-backward") ? -1 : 1;
        let candidate = newIndex;
        while (candidate >= 0 && candidate < chordChart.length && chordChart[candidate].isSeparator) {
          candidate += step;
        }
        // Past the last chord in that direction — a jump clamped onto the
        // chart's closing barline, say — take the nearest chord back toward
        // where the move started, so a jump near the end still lands.
        if (candidate < 0 || candidate >= chordChart.length) {
          candidate = newIndex;
          while (candidate !== prevIndex && candidate >= 0 && candidate < chordChart.length && chordChart[candidate].isSeparator) {
            candidate -= step;
          }
        }
        // Only move if that found a chord; otherwise stay put
        if (candidate >= 0 && candidate < chordChart.length && !chordChart[candidate].isSeparator) {
          newIndex = candidate;
        } else {
          newIndex = prevIndex !== null ? prevIndex : newIndex;
        }
        // Only reset selectedChordOptionIndex (for flicker prevention) if we're actually moving
        if (chordEditModeRef.current && newIndex !== prevIndex) setSelectedChordOptionIndex(-1);
      }

      // A step or jump with the first or last chord already selected has
      // nowhere to go, so it deselects the chord and selects it again, which
      // sounds it and lights its keys as a move would. Not in edit mode, where
      // the deselect would close the editor, and not on a separator or blank
      // chord, where it would drop the notes they leave sounding.
      if (newIndex === prevIndex && !chordEditModeRef.current && !separatorEditModeRef.current
        && isChartChord(chordChart[newIndex])) {
        const reselectIndex = newIndex;
        setSelectedChordIndex(null);
        setTimeout(() => setSelectedChordIndex(reselectIndex), CHART_RESELECT_GAP);
        return;
      }

      // The logic to play the chord will be handled by the useEffect
      setSelectedChordIndex(newIndex);
    }
  };



  useEffect(() => {
    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [chartPanelShown]);



  function playChartChord() {
    playChordNotes(toggledNotesRef.current);
  }

  // A chord's notes struck as the chart strikes them: rolled for piano,
  // strummed for guitar, arpeggiated when that is on. The MIDI player's chord
  // regions sound through here too.
  function playChordNotes(notes: number[]) {
    // let sortedMidiNotes = toggledNotesRef.current.sort((a, b) => a - b);
    let uniqueMidiNotes = Array.from(new Set(notes));
    let sortedMidiNotes = uniqueMidiNotes.sort((a, b) => a - b);

    // let arpSpeed = 10000 / parseInt(tempo.value, 10) / (1 + sortedMidiNotes.length / 50); // larger chords arpeggiate faster

    let arpSpeed = (isScale ?
      100000 : // for scales base tempo is slower: default 100000, higher number is slower
      isArpeggiateOn ?
        30000 : // Arpeggio speed here, default 30000, lower number is faster
        selectedInstrument == 'AcousticGuitar' ?
          6000 : // strummed chords
          selectedInstrument == 'BassSixString' ?
            3500 : // finger picking chords
            1500 // Piano and Rhodes slightly imperfect block chords
    ) / parseInt(tempo.value, 10) / (1 + sortedMidiNotes.length / 2) // more notes arpeggiate/strum faster, parseInt(tempo.value, 10) is 120 for default tempo

    if (!shouldPlaySoundRef.current) return;
    startChordNotes(sortedMidiNotes, arpSpeed);
  }

  /**
   * Starts notes arpSpeed ms apart, for playChartChord and playChord.
   *
   * Block chords and strums are scheduled up front on the audio clock: their
   * notes are a few ms apart, and a timer per note let a render that blocks
   * the page push a note late and roll the chord. Arpeggios and scales keep a
   * timer per note, each checking that sound is still on, so Sound Off stops
   * the notes not yet played: the sampler cannot cancel a note already
   * scheduled on the audio clock (releaseAll only fades it, over the release
   * time, and it can still start inside that fade).
   */
  // The timers of an arpeggio still playing out. A new chord, or the release
  // of the one sounding, cancels them: a note started after its chord was
  // released would never be released, and would ring on under the next chord.
  const pendingNoteTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  function cancelPendingNotes() {
    pendingNoteTimersRef.current.forEach(timer => clearTimeout(timer));
    pendingNoteTimersRef.current = [];
  }
  function startChordNotes(notes: number[], arpSpeed: number) {
    cancelPendingNotes();
    if (isArpeggiateOn || isScale) {
      pendingNoteTimersRef.current = notes.map((note, index) =>
        setTimeout(() => {
          if (shouldPlaySoundRef.current && samplerRef.current && samplerRef.current.loaded) {
            samplerRef.current.triggerAttack(Tone.Midi(note).toNote(), undefined, 0.65);
          }
        }, index * arpSpeed));
      return;
    }
    if (!samplerRef.current || !samplerRef.current.loaded) return;
    const sampler = samplerRef.current;
    const start = Tone.now();
    notes.forEach((note, index) => {
      sampler.triggerAttack(Tone.Midi(note).toNote(), start + (index * arpSpeed) / 1000, 0.65);
    });
  }


  function releaseToggledNotes() {
    cancelPendingNotes();
    if (shouldPlaySoundRef.current && samplerRef.current) {
      toggledNotesRef.current.forEach(note => {
        samplerRef.current!.triggerRelease(Tone.Midi(note).toNote());
      });
    }
  }

  /**
   * Writes a chart whose entries were added or removed, bringing the key center
   * regions with it.
   *
   * Every structural edit goes through here, so an anchor can never be left
   * pointing at a chord that is gone. Edits that only rewrite an entry in place
   * \u2014 replaceChordInChart, updateSeparator, applyToChart \u2014 keep the entry's ID
   * and so need no reconciling; they call setChordChart directly.
   *
   * Callers pass the next chart as a value, built from `chordChart`, so the
   * regions reconcile against the same "before" the entries were built from.
   */
  const applyChartEdit = (nextChart: ChordChartEntry[]) => {
    const prevChart = chordChart;
    setChordChart(nextChart);
    setKeyCenterRegions(prevRegions => reconcileRegions(prevChart, nextChart, prevRegions));
  };

  /**
   * Which key center the picker is editing: the chart's global key, or the
   * region that starts at one chord. Null when the picker is closed.
   *
   * The chord is held by ID rather than by index so the popover cannot end up
   * editing a different chord than the one it opened over. The anchor is the
   * rect of whatever opened it, because the picker is positioned fixed — inside
   * the chart it would be clipped by the scrolling body and painted over by the
   * chords.
   */
  const [keyCenterPicker, setKeyCenterPicker] = useState<
    | { scope: 'global'; anchor: PickerAnchor }
    | { scope: 'chord'; chordId: string; anchor: PickerAnchor }
    | null
  >(null);

  /**
   * Opens the picker under `element`, or closes it if it is already open on the
   * same thing, so the badge and the header key toggle rather than reopen.
   */
  const toggleKeyCenterPicker = (
    target: { scope: 'global' } | { scope: 'chord'; chordId: string },
    element: HTMLElement
  ) => {
    const openOnSameTarget = keyCenterPicker !== null &&
      keyCenterPicker.scope === target.scope &&
      (target.scope === 'global' ||
        (keyCenterPicker.scope === 'chord' && keyCenterPicker.chordId === target.chordId));
    if (openOnSameTarget) {
      setKeyCenterPicker(null);
      return;
    }
    const rect = element.getBoundingClientRect();
    const anchor = { bottom: rect.bottom, centerX: rect.left + rect.width / 2 };
    if (target.scope === 'global') {
      setKeyCenterPicker({ scope: 'global', anchor });
    } else {
      setKeyCenterPicker({ scope: 'chord', chordId: target.chordId, anchor });
    }
  };

  const applyPickedKeyCenter = (picked: KeyCenter, keepOpen: boolean = false) => {
    if (keyCenterPicker === null) return;
    if (keyCenterPicker.scope === 'global') {
      setGlobalKeyCenter(picked);
    } else {
      const chordId = keyCenterPicker.chordId;
      setKeyCenterRegions(prev => setKeyCenterAtChord(chordChart, prev, chordId, picked));
    }
    // Picking a key is the end of the errand; switching Major/Minor is not, so
    // that one applies with the picker left standing.
    if (!keepOpen) setKeyCenterPicker(null);
  };

  /**
   * "Use global key": from this chord on the chart's global key applies,
   * whatever it is, ending any local key in force; the chords before keep the
   * key they had. A marker that stays where it is put, so a key set earlier
   * later still ends here.
   */
  const applyUseGlobalKey = () => {
    if (keyCenterPicker === null || keyCenterPicker.scope !== 'chord') return;
    const chordId = keyCenterPicker.chordId;
    setKeyCenterRegions(prev => setNoKeyCenterAtChord(chordChart, prev, chordId));
    setKeyCenterPicker(null);
  };

  /**
   * "Clear": on a chord, the key change set there goes and the key before it
   * carries on, a MIDI file's own too (the chart then differs from the file,
   * which shows as a marker on the player's key lane until the user settles
   * it); on the chart's own key, no global key.
   */
  const applyClearKeyCenter = () => {
    if (keyCenterPicker === null) return;
    if (keyCenterPicker.scope === 'global') {
      setGlobalKeyCenter(null);
    } else {
      setKeyCenterRegions(clearKeyCenterAtChord(chordChart, keyCenterRegions, keyCenterPicker.chordId));
    }
    setKeyCenterPicker(null);
  };

  // A chord added at the end of the chart, after anything but a barline,
  // starts a new bar: true when that barline has to go in first.
  const barlineBeforeAddedChord = (): boolean => {
    const at = selectedChordIndex === null ? chordChart.length - 1 : selectedChordIndex;
    if (at !== chordChart.length - 1 || at < 0) return false;
    const entry = chordChart[at];
    return !entry.isSeparator || entry.name !== SEPARATORS.barline;
  };

  // A region's chord as the chart holds it: its notes and the name READ / FEEL
  // shows, dated to its start. Null when the region has no name to chart.
  const fileChordEntry = (fileKey: string, region: ChordRegion): ChordChartEntry | null => {
    const segment = shownSegment(region, settings.preferNotationLegible);
    if (!segment) return null;
    return {
      id: newEntryId(),
      segment,
      name: chordSymbolText(segment, lockedTonicNote, lockedTonicMode),
      voicing: regionVoicing(region).slice(),
      isSeparator: false,
      midiSource: { fileKey, seconds: region.start },
    };
  };

  /**
   * Adds MIDI file chord regions to the chart (the player's promote button):
   * a chord for each region not yet charted, dated to its start. Each goes
   * beside the chord of the region before it when that one is charted, else
   * after the selected entry or at the end (chartFileLink.ts placePromoted).
   * Regions with no chord to chart, a single pitch class or a scale, are left
   * out and the user told so. The last one added is selected, where the next
   * promotion goes.
   */
  // `replace`: the file's chords become the whole chart, its keys the chart's
  // keys, in one chart edit (undo brings the old chart back). `keysNow`: the
  // file's keys when they are newer than the last report's. `select`: 'first'
  // when the whole file is charted on load, selecting its first chord, where
  // the file's playhead is
  const addChordsFromFile = (fileKey: string, fileName: string, regions: ChordRegion[], atEnd: boolean = false, replace: boolean = false, keysNow: FileKeys | null = null, select: 'first' | 'last' = 'last') => {
    const notationLegible = settings.preferNotationLegible;
    const base = replace ? [] : chordChart;
    const uncharted = regions.filter(r => isEnabled(r) && !isCharted(base, fileKey, r));
    const chartable = uncharted.filter(r => !r.is_scale && shownSegment(r, notationLegible));
    const leftOut = uncharted.length - chartable.length;
    if (leftOut > 0) {
      showChartNotice(`${leftOut} chord region${leftOut === 1 ? '' : 's'} left out: a single note or a scale has no chord symbol to chart.`);
    }
    if (chartable.length === 0) return;
    autoOpenChartPanel();
    rememberChartMidiFile(fileKey, fileName);
    const added = chartable.map(region => ({ region, entry: fileChordEntry(fileKey, region) as ChordChartEntry }));
    const allRegions = fileRegions !== null && fileRegions.fileKey === fileKey ? fileRegions.regions : regions;
    const barline: ChordChartEntry = { id: newEntryId(), name: SEPARATORS.barline, voicing: [], isSeparator: true };
    const placed = placePromoted(base, fileKey, allRegions, added, atEnd || replace ? null : selectedChordIndex, barline,
      entry => entry.isSeparator && entry.name === SEPARATORS.barline);
    const keys = keysNow !== null ? keysNow : (fileKeys !== null && fileKeys.fileKey === fileKey ? fileKeys : null);
    if (replace) {
      setChordChart(placed.entries);
      setGlobalKeyCenter(keys !== null ? keys.globalKey : null);
      setKeyCenterRegions(keys !== null ? syncFileKeyRegions([], placed.entries, [], null, keys) : []);
    } else {
      applyChartEditWithFileKeys(placed.entries, keys);
    }
    skipFollowSeekRef.current = true;
    setSelectedChordIndex(select === 'first' ? placed.entries.indexOf(added[0].entry) : placed.last);
  };

  // A MIDI file's analysis has arrived. Into an empty chart, with the setting
  // on, its chords go straight in; otherwise the user is asked. A chart that
  // already has chords is always asked about, setting or not: there is one
  // chart, and two songs in it would mix.
  const [midiChartOffer, setMidiChartOffer] = useState<{ fileKey: string; fileName: string; regions: ChordRegion[]; chartEmpty: boolean } | null>(null);
  const [midiChartEveryTime, setMidiChartEveryTime] = useState(false);
  const handleFileAnalyzed = (fileKey: string, fileName: string, regions: ChordRegion[], keys: FileKeys) => {
    // A file just loaded holds the place until the chart is touched, so its
    // key shows at once
    setFileDrivesKey(true);
    if (regions.length === 0) return;
    const chartEmpty = chordChart.length === 0;
    if (chartEmpty && settings.chartMidiOnLoad) {
      addChordsFromFile(fileKey, fileName, regions, false, false, keys, 'first');
      return;
    }
    setMidiChartEveryTime(false);
    setMidiChartOffer({ fileKey, fileName, regions, chartEmpty });
  };
  const acceptMidiChartOffer = (replace: boolean) => {
    if (midiChartOffer === null) return;
    const { fileKey, fileName, chartEmpty } = midiChartOffer;
    // The regions as they are now: they may have been edited since the offer
    const regions = fileRegions !== null && fileRegions.fileKey === fileKey ? fileRegions.regions : midiChartOffer.regions;
    if (chartEmpty && midiChartEveryTime) handleSettingChange('chartMidiOnLoad', true);
    addChordsFromFile(fileKey, fileName, regions, true, replace, null, 'first');
    setMidiChartOffer(null);
  };

  // The loaded MIDI file's regions as the player last reported them: which
  // chart chords are orphans, and where a promoted region's neighbour is
  const [fileRegions, setFileRegions] = useState<{ fileKey: string; regions: ChordRegion[] } | null>(null);

  // Set when an edit only moves the selected entry to another index, or Follow
  // selects the chord the file has reached, so the selection effect keeps the
  // keys as they are rather than putting the chord's notes on them (and
  // seeking the file) as a new selection does
  const keepNotesOnReselectRef = useRef(false);

  // Follow: while the file drives (it moved last, or is playing), the chord
  // it has reached is the chart's selection. The file's notes stay on the
  // keys, already applied as far as the Compass is concerned. Only the file
  // moving runs this, not the selection changing: a separator selected while
  // the file is paused stays selected until the file moves again.
  useEffect(() => {
    if (!chartFollowOn || !(fileDrivesKey || midiFilePlaying) || firstFollowedIndex < 0) return;
    if (firstFollowedIndex === selectedChordIndex) return;
    keepNotesOnReselectRef.current = true;
    setSelectedChordIndex(firstFollowedIndex);
    setNotesAppliedFor(firstFollowedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartFollowOn, fileDrivesKey, midiFilePlaying, firstFollowedIndex]);

  // The loaded MIDI file's keys as the player last reported them
  const [fileKeys, setFileKeys] = useState<FileKeys | null>(null);

  /**
   * applyChartEdit with the file's keys flowed into the chart's key centers
   * in the same step (chartFileLink.ts syncFileKeyRegions): what changed in
   * the file's keys since the chart last took them flows in where the chart
   * still matched, new chords take the file's keys, and a chart with no
   * global key takes the file's.
   */
  const applyChartEditWithFileKeys = (nextChart: ChordChartEntry[], keys: FileKeys | null, chartGlobal: KeyCenter | null = globalKeyCenter) => {
    if (keys === null) {
      applyChartEdit(nextChart);
      return;
    }
    const nextGlobal = chartGlobal !== null ? chartGlobal : keys.globalKey;
    const reconciled = reconcileRegions(chordChart, nextChart, keyCenterRegions);
    setChordChart(nextChart);
    if (nextGlobal !== globalKeyCenter) setGlobalKeyCenter(nextGlobal);
    setKeyCenterRegions(syncFileKeyRegions(chordChart, nextChart, reconciled, fileKeys, keys));
  };

  // The chart goes back to the file's key at a chord: the chart's key change
  // there goes, and the file's, if one lands there, takes its place
  const returnChartKeyToFile = (chordId: string) => {
    if (fileKeys !== null) setKeyCenterRegions(returnToFileKeyAt(chordChart, keyCenterRegions, fileKeys, chordId));
  };
  // The same for the chart's global key: the user changed it, and only the
  // user can say which is right. Only a chart with chords from the file has
  // a global key of the file's to compare.
  const divergentChartGlobalKey = fileKeys !== null
    && chordChart.some(entry => entry.midiSource !== undefined && entry.midiSource.fileKey === fileKeys.fileKey)
    && !keyCentersEqual(globalKeyCenter, fileKeys.globalKey) ? { keyCenter: globalKeyCenter } : null;
  const returnChartGlobalKeyToFile = () => {
    if (fileKeys !== null) setGlobalKeyCenter(fileKeys.globalKey);
  };

  // Writes a chart that keeps the selected entry selected wherever it went,
  // or the entry before its place when it was deleted
  const applyChartEditKeepingSelection = (nextChart: ChordChartEntry[], keys: FileKeys | null = null, chartGlobal: KeyCenter | null = globalKeyCenter) => {
    const selectedId = selectedChordIndex !== null && chordChart[selectedChordIndex] ? chordChart[selectedChordIndex].id : null;
    applyChartEditWithFileKeys(nextChart, keys, chartGlobal);
    if (selectedChordIndex === null || selectedId === null) return;
    const found = nextChart.findIndex(entry => entry.id === selectedId);
    const nextIndex = found >= 0 ? found : Math.min(selectedChordIndex, nextChart.length) - 1;
    if (nextIndex === selectedChordIndex) return;
    keepNotesOnReselectRef.current = true;
    setSelectedChordIndex(nextIndex >= 0 ? nextIndex : null);
  };

  // The player's report of committed region changes: chart chords dated to
  // the file follow them as chartFileLink.ts says, all in one chart edit.
  // Follow off holds the reports back; the one sent when it is turned on
  // again (`afterUnlinked`) is asked about first, if it would change the chart.
  type RegionReport = { fileKey: string; changes: RegionChange[]; regions: ChordRegion[]; keys: FileKeys; toChart: ChordRegion[] };
  const [heldRegionReport, setHeldRegionReport] = useState<RegionReport | null>(null);
  const handleRegionsSynced = (fileKey: string | null, changes: RegionChange[], regions: ChordRegion[], keys: FileKeys | null, toChart: ChordRegion[], afterUnlinked: boolean, chartKeeps: boolean) => {
    // Undoing an edit the player took from the chart: the chart keeps what it
    // has, as "Keep chart" does, so the difference shows as a marker again
    if (fileKey === null || keys === null || chartKeeps) {
      setFileKeys(keys);
      return;
    }
    const report = { fileKey, changes, regions, keys, toChart };
    if (afterUnlinked && regionReportEdit(report, true) !== null) {
      setHeldRegionReport(report);
      return;
    }
    applyRegionReport(report, false);
  };
  // Settling the held report: the chart takes it, spelled as the player now
  // shows, or keeps what it has (the differences then show as markers)
  const syncHeldRegionReport = (update: boolean) => {
    if (heldRegionReport === null) return;
    if (update) applyRegionReport(heldRegionReport, true);
    else setFileKeys(heldRegionReport.keys);
    setHeldRegionReport(null);
  };
  const applyRegionReport = (report: RegionReport, respell: boolean) => {
    setFileKeys(report.keys);
    const edit = regionReportEdit(report, respell);
    if (edit !== null) applyChartEditKeepingSelection(edit.next, report.keys, edit.chartGlobal);
  };
  // The chart edit a report makes, or null when it changes nothing. `respell`:
  // chords following their regions take the name the player shows.
  const regionReportEdit = ({ fileKey, changes, regions, keys, toChart }: RegionReport, respell: boolean) => {
    const nameOf = (segment: ChordSegment) => chordSymbolText(segment, lockedTonicNote, lockedTonicMode);
    const shownOf = (region: ChordRegion) => shownSegment(region, settings.preferNotationLegible);
    let next = applyRegionChanges(chordChart, fileKey, changes, nameOf, shownOf);
    if (respell) next = respellFollowed(next, fileKey, regions, shownOf, nameOf);
    // A region split off a charted one, or shown again after a hide deleted
    // its chord, is charted beside its neighbour, in the same edit; single
    // notes and scales have no chord to chart
    const added = toChart
      .filter(region => !region.is_scale && !isCharted(next, fileKey, region))
      .map(region => ({ region, entry: fileChordEntry(fileKey, region) }))
      .filter((a): a is { region: ChordRegion; entry: ChordChartEntry } => a.entry !== null);
    if (added.length > 0) {
      const barline: ChordChartEntry = { id: newEntryId(), name: SEPARATORS.barline, voicing: [], isSeparator: true };
      next = placePromoted(next, fileKey, regions, added, null, barline,
        entry => entry.isSeparator && entry.name === SEPARATORS.barline).entries;
    }
    // Only a chart with chords from this file has keys of the file's to take
    if (!chordChart.some(entry => entry.midiSource && entry.midiSource.fileKey === fileKey)) return null;
    // The chart's global key follows the file's while the user has left it
    // as the file had it
    const previous = fileKeys !== null && fileKeys.fileKey === fileKey ? fileKeys.globalKey : undefined;
    const chartGlobal = previous !== undefined && keyCentersEqual(globalKeyCenter, previous) ? keys.globalKey : globalKeyCenter;
    const reconciled = reconcileRegions(chordChart, next, keyCenterRegions);
    const rebuilt = syncFileKeyRegions(chordChart, next, reconciled, fileKeys, keys);
    if (next === chordChart && keyCentersEqual(chartGlobal, globalKeyCenter)
      && JSON.stringify(rebuilt) === JSON.stringify(keyCenterRegions)) return null;
    return { next, chartGlobal };
  };

  // Follow on, switching READ / FEEL respells the chart's chords that follow
  // their regions at once; regions not named in the new setting yet follow
  // in the report that names them. Only the switch runs this: a chart chord
  // respelled in the chart afterwards stays as the user spelled it.
  useEffect(() => {
    if (!chartFollowOn || fileRegions === null) return;
    const next = respellFollowed(chordChart, fileRegions.fileKey, fileRegions.regions,
      region => shownSegment(region, settings.preferNotationLegible),
      segment => chordSymbolText(segment, lockedTonicNote, lockedTonicMode));
    if (next !== chordChart) applyChartEdit(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.preferNotationLegible]);

  // A region's marker: every chart chord dated to it takes its notes and the
  // name the READ / FEEL setting shows, keeping its id and date
  const updateChartFromRegion = (fileKey: string, region: ChordRegion) => {
    const segment = shownSegment(region, settings.preferNotationLegible);
    if (!segment || region.is_scale) return;
    // Only the chords holding other notes: a linked one that still holds the
    // region's notes keeps its name, which is the user's spelling
    setChordChart(prev => prev.map(entry => (linkedTo(entry, fileKey, region) && !holdsNotesOf(entry, region)
      ? { ...entry, voicing: regionVoicing(region).slice(), segment, name: chordSymbolText(segment, lockedTonicNote, lockedTonicMode) }
      : entry)));
  };

  // A passing notice in the status banner, gone after a few seconds
  const [chartNotice, setChartNotice] = useState<string | null>(null);
  const chartNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showChartNotice = (message: string) => {
    if (chartNoticeTimerRef.current) clearTimeout(chartNoticeTimerRef.current);
    setChartNotice(message);
    chartNoticeTimerRef.current = setTimeout(() => setChartNotice(null), 4000);
  };

  // WORKS!!! But adding new method below:
  const addEntryToChart =(input: number | string, separator: boolean, lockedTonicNote: number | null = null, lockedTonicMode: number | null = null) => {
    autoOpenChartPanel();
    if (separator) {
      const separatorName = typeof input === 'string' ? input : SEPARATORS.barline;
      const insertionIndex = selectedChordIndex === null ? chordChart.length : selectedChordIndex + 1;
      // Built out here rather than in a state updater, which React may run more
      // than once and would mint a different ID each run. Same for every entry
      // built below.
      const newEntry = { id: newEntryId(), name: separatorName, voicing: [], isSeparator: true };
      applyChartEdit([
        ...chordChart.slice(0, insertionIndex),
        newEntry,
        ...chordChart.slice(insertionIndex)
      ]);
      setSelectedChordIndex(insertionIndex);
      return;
    }

    // Special case for blank chord
    if (input === -1) {
      const insertionIndex = selectedChordIndex === null ? chordChart.length : selectedChordIndex + 1;
      const newEntry = { id: newEntryId(), name: '\u2001', voicing: [], isSeparator: false };  // Blank chord entry
      applyChartEdit([
        ...chordChart.slice(0, insertionIndex),
        newEntry,
        ...chordChart.slice(insertionIndex)
      ]);
      setSelectedChordIndex(insertionIndex);
      return;
    }

    const isNoChordSelected = selectedChordIndex === null;
    const shouldAddBarline = barlineBeforeAddedChord();

    // const segment = csOptionsSegments[segmentIndex];
    const segment = csOptionsSegments[input as number];
    const allNotes = [...activeNotes, ...heldNotes, ...toggledNotes];
    // Notes the MIDI file put on the keys date the chord to the region at
    // the playhead
    const fileSource = notesFromFileRef.current && midiControllerRef.current
      ? midiControllerRef.current.fileSourceAt()
      : null;
    if (fileSource) rememberChartMidiFile(fileSource.fileKey, fileSource.fileName);
    const newEntry: ChordChartEntry = {
      id: newEntryId(),
      segment,
      name: chordSymbolText(segment, lockedTonicNote, lockedTonicMode),
      voicing: allNotes,
      isSeparator: false,
      ...(fileSource ? { midiSource: { fileKey: fileSource.fileKey, seconds: fileSource.seconds } } : {}),
    };

    const insertionIndex = isNoChordSelected ? chordChart.length : selectedChordIndex + 1;
    const leadingBarline = shouldAddBarline
      ? [{ id: newEntryId(), name: SEPARATORS.barline, voicing: [], isSeparator: true }]
      : [];
    // A chord from the file brings the file's keys with it
    applyChartEditWithFileKeys([
      ...chordChart.slice(0, insertionIndex),
      ...leadingBarline,
      newEntry,
      ...chordChart.slice(insertionIndex)
    ], fileSource && fileKeys !== null && fileKeys.fileKey === fileSource.fileKey ? fileKeys : null);

    // Selected in the same update that adds it, so nothing renders the new
    // entry in the chart but not yet selected.
    setSelectedChordIndex(insertionIndex + (shouldAddBarline ? 1 : 0));
  };

  const replaceChordInChart = (optionIndex: number, exitEditMode: boolean = true) => {
    if (selectedChordIndex === null) return;
    const segment = csOptionsSegments[optionIndex];
    if (!segment) return;
    const allNotes = [...activeNotes, ...heldNotes, ...toggledNotes];
    const updatedEntry = {
      segment,
      name: chordSymbolText(segment, lockedTonicNote, lockedTonicMode),
      voicing: allNotes,
      isSeparator: false
    };
    // The ID comes from the entry being replaced: editing a chord's symbol or
    // its notes is not a new entry, and anything anchored to it stays anchored.
    // So does its MIDI timestamp: a corrected chord still came from there.
    setChordChart(prevChart => prevChart.map((entry, idx) =>
      idx === selectedChordIndex ? { ...updatedEntry, id: entry.id, ...(entry.midiSource ? { midiSource: entry.midiSource } : {}) } : entry
    ));
    if (exitEditMode) setChordEditMode(false);
  };

  /**
   * Select a chart chord and put it straight into chord edit mode — the batch
   * review's Edit, for a chord it found no substitute for. The selection puts
   * the chord's notes on the keys; their analysis then picks the option with
   * the chord's charted root, since the notes match the original voicing set
   * here. The option index is cleared meanwhile, as when stepping in edit mode,
   * so the previous notes' options never show against this chord.
   */
  const editChartChord = (index: number) => {
    const entry = chordChart[index];
    if (entry === undefined || entry.isSeparator) return;
    const voicing = [...new Set(entry.voicing)].sort((a, b) => a - b);
    editModeOriginalVoicingRef.current = voicing;
    setEditModeOriginalVoicing(voicing);
    setSelectedChordOptionIndex(-1);
    setChordEditMode(true);
    setSelectedChordIndex(index);
  };

  const handleChordEditAction = () => {
    const allNotes = [...activeNotes, ...heldNotes, ...toggledNotes];
    if (selectedChordIndexRef.current !== null &&
      chordChart[selectedChordIndexRef.current] &&
      chordChart[selectedChordIndexRef.current].isSeparator) {
      const idx = selectedChordIndexRef.current;
      let prevChord: ChordChartEntry | null = null;
      for (let i = idx - 1; i >= 0; i--) {
        if (!chordChart[i].isSeparator) { prevChord = chordChart[i]; break; }
      }
      let nextChord: ChordChartEntry | null = null;
      for (let i = idx + 1; i < chordChart.length; i++) {
        if (!chordChart[i].isSeparator) { nextChord = chordChart[i]; break; }
      }
      // If notes are actively held, always add a new chord regardless of adjacent voicings
      if (activeNotesRef.current.length === 0) {
        const currentNotes = Array.from(new Set([...heldNotesRef.current, ...toggledNotesRef.current])).sort((a, b) => a - b);
        const voicingMatches = (voicing: number[]) => {
          const sorted = Array.from(new Set(voicing)).sort((a, b) => a - b);
          return sorted.length === currentNotes.length && sorted.every((n, i) => n === currentNotes[i]);
        };
        if ((prevChord && voicingMatches(prevChord.voicing)) || (nextChord && voicingMatches(nextChord.voicing))) {
          // Notes match adjacent chord - enter separator edit mode to change separator type.
          // Not while the chart is hidden: there is no separator on screen to edit.
          if (!chartPanelShown) return;
          const currentType = chordChart[idx].name;
          const typeIndex = SEPARATOR_TYPES.indexOf(currentType);
          setSelectedSeparatorTypeIndex(typeIndex >= 0 ? typeIndex : 0);
          setSeparatorEditMode(true);
          setSeparatorMenuExpanded(prev => !prev);
          return;
        }
      }
      // New notes on a separator add their chord after it.
      if (csOptionsSegments.length > 0) addEntryToChart(0, false, lockedTonicNote, lockedTonicMode);
      return;
    }
    if (chordEditModeRef.current) {
      replaceChordInChart(selectedChordOptionIndexRef.current);
    } else if ( // If no notes are held, active, or toggled, and the current chord is not a separator, enter chord edit mode
      selectedChordIndexRef.current !== null && !chordEditModeRef.current && allNotes.length === 0 &&
      chordChart[selectedChordIndexRef.current] && !chordChart[selectedChordIndexRef.current].isSeparator) {
      // Chord edit mode needs the chart or the chord symbol panel on screen to
      // show the chord being edited
      if (!chartPanelShown && !chordSymbolPanelShown) return;
      const chartVoicing = [...chordChart[selectedChordIndexRef.current].voicing].sort((a, b) => a - b);
      setToggledNotes(chartVoicing);
      const v1 = [...new Set(chordChart[selectedChordIndexRef.current].voicing)].sort((a, b) => a - b);
      editModeOriginalVoicingRef.current = v1;
      setEditModeOriginalVoicing(v1);
      setChordEditMode(true);
      return;
    } else if (selectedChordIndexRef.current !== null && csOptionsSegments.length > 0 &&
      chordChart[selectedChordIndexRef.current] && !chordChart[selectedChordIndexRef.current].isSeparator) {
      const chartVoicing = [...chordChart[selectedChordIndexRef.current].voicing].sort((a, b) => a - b);
      const currentNotes = [...toggledNotesRef.current].sort((a, b) => a - b);
      const voicingMatches = chartVoicing.length === currentNotes.length &&
        chartVoicing.every((n, i) => n === currentNotes[i]);
      if (voicingMatches) {
        // As above: no chord edit mode with neither panel on screen
        if (!chartPanelShown && !chordSymbolPanelShown) return;
        const chartRootPc = chordChart[selectedChordIndexRef.current].segment && chordChart[selectedChordIndexRef.current].segment!.root_pc;
        const matchIndex = csOptionsSegments.findIndex(seg => seg.root_pc === chartRootPc);
        setSelectedChordOptionIndex(matchIndex >= 0 ? matchIndex : 0);
        const v2 = [...new Set(chartVoicing)].sort((a, b) => a - b);
        editModeOriginalVoicingRef.current = v2;
        setEditModeOriginalVoicing(v2);
        setChordEditMode(true);
      } else {
        addEntryToChart(0, false, lockedTonicNote, lockedTonicMode);
      }
    } else if (csOptionsSegments.length > 0) {
      addEntryToChart(0, false, lockedTonicNote, lockedTonicMode);
    }
  };

  const navigateChordEditOptions = (direction: 'up' | 'down') => {
    if (!chordEditModeRef.current || csOptionsSegments.length === 0) return;
    setSelectedChordOptionIndex(prev =>
      direction === 'up'
        ? (prev - 1 + csOptionsSegments.length) % csOptionsSegments.length
        : (prev + 1) % csOptionsSegments.length
    );
  };

  const replaceSeparatorInChart = (typeIndex: number, exitEditMode: boolean = true) => {
    if (selectedChordIndexRef.current === null) return;
    const newType = SEPARATOR_TYPES[typeIndex];
    if (!newType) return;
    updateSeparator(selectedChordIndexRef.current, newType);
    if (exitEditMode) setSeparatorEditMode(false);
  };

  const navigateSeparatorEditTypes = (direction: 'up' | 'down') => {
    if (!separatorEditModeRef.current) return;
    setSelectedSeparatorTypeIndex(prev =>
      direction === 'up'
        ? (prev - 1 + SEPARATOR_TYPES.length) % SEPARATOR_TYPES.length
        : (prev + 1) % SEPARATOR_TYPES.length
    );
  };

  // WILL ADD THIS Transpose function later
  // function transposeChordChart(semitones: number) {
  //   setChordChart(prevChart => prevChart.map(entry => {
  //     if (entry.isSeparator) return entry;

  //     const segment = entry.segment;
  //     const transposed = {
  //       ...segment,
  //       root_pc: (segment.root_pc + semitones) % 12,
  //       bass_pc: (segment.bass_pc + semitones) % 12
  //     };

  //     return {
  //       ...entry,
  //       segment: transposed,
  //       name: assembleChordSymbol(transposed)
  //     };
  //   }));
  // }




  const updateSeparator = (index: number, newSeparator: string) => {
    const updatedChart = chordChart.map((entry, idx) => {
      if (idx === index && entry.isSeparator) {
        return { ...entry, name: newSeparator };
      }
      return entry;
    });
    setChordChart(updatedChart);
    setSelectedChordIndex(index);
  };


  const spacer = "\u00A0" // options ⁘ ⁞ ⋮ ⋰ ⠿ ⠀ ⬚ ∴ ⋮ ▭ ⬜ ⎵ … ☐ □   ╎ ┆ ┊ ◌ ♢ ♭ ♯ ⸬ 𝄆 𝄄 𝄀 𝄁 𝄃 𝄇


  // let playTimeouts: NodeJS.Timeout[] = [];

  useEffect(() => {
    if (playSongOn) {
      playSong();
    } else {
      stopSong();
    }
  }, [playSongOn]); // Dependency array includes playSongOn



  const playIntervalRef = useRef<any>(null);

  const playSong = () => {
    let index = (selectedChordIndex !== null && selectedChordIndex < chordChart.length - 1) ? selectedChordIndex : 0;
    const tempoValue = parseInt(tempo.value, 10);
    if (isNaN(tempoValue) || tempoValue <= 0) {
      console.error("Invalid tempo value");
      return;
    }
    const interval = (60000 / tempoValue) * timeSigDuration; // Interval for one bar
    const advanceIndex = () => {
      index++;
      while (index < chordChart.length && chordChart[index].isSeparator) {
        index++; // Skip separators
      }
      if (index < chordChart.length) {
        setSelectedChordIndex(index);
      } else {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
        setCurrentPlaySongIndex(null);
        setPlaySongOn(false);
        setSelectedChordIndex(null);
      }
    };
    if (index < chordChart.length && !chordChart[index].isSeparator) {
      if (selectedChordIndex !== index) {
        setSelectedChordIndex(index);
      } else {
        playChartChord();
      }
    }
    playIntervalRef.current = setInterval(advanceIndex, interval);
    setCurrentPlaySongIndex(index);
  };




  const stopSong = () => {
    clearInterval(playIntervalRef.current);
    playIntervalRef.current = null; // Reset the ref
    setCurrentPlaySongIndex(null);
    setPlaySongOn(false);
  };


  const handlePlaySongToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setPlaySongOn(prevState => !prevState); // Just toggle the state
  };



  // const deselectChord = () => {
  //   setSelectedChordIndex(null);
  // };

  const deleteChordFromChart = (index: number) => {
    if (!playSongOnRef.current) {
      const updatedChart = chordChart.filter((_, idx) => idx !== index);
      applyChartEdit(updatedChart);
      releaseToggledNotes();
      setToggledNotes([]);

      if (updatedChart.length === 0) {
        // If all chords are deleted, reset the selection
        setSelectedChordIndex(null);
      } else if (index === chordChart.length - 1) {
        // If the last chord is deleted, select the new last chord
        setSelectedChordIndex(updatedChart.length - 1);
      } else if (index === 0) {
        // If the first chord is deleted, select the new first chord
        setSelectedChordIndex(index);
      } else if (index === selectedChordIndex) {
        // If the deleted chord was selected, move the selection to the previous index
        setSelectedChordIndex(index - 1);
      }
    }
  };

  const toggleEditTitle = () => {
    setSongTitle({ ...songTitle, isEditable: !songTitle.isEditable });
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSongTitle({ ...songTitle, title: event.target.value });
  };

  const toggleEditComposer = () => {
    setComposer({ ...composer, isEditable: !composer.isEditable });
  };

  const handleComposerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComposer({ ...composer, name: event.target.value });
  };

  // const toggleEditTempo = () => {
  //   setTempo({ ...tempo, isEditable: !tempo.isEditable });
  // };

  const handleTempoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempo({ ...tempo, value: event.target.value });
  };

  const MAX_LENGTH_TO_REDUCE = 800


  useEffect(() => {
    // Drives the chart's font scaling. A chord counts as one however long its
    // symbol is; separators and blank chords count their own character.
    const currentChartLength = chordChart.reduce((acc, chord) => {
      return acc + (chord.segment ? 1 : chord.name.length);
    }, 0);

    setChartLength(Math.min(currentChartLength, MAX_LENGTH_TO_REDUCE));
    setSongTitleLength(songTitle.title.length);
  }, [chordChart, songTitle]);


  const concatenateChartData = () => {
    let dataString = `${songTitle.title}, `;
    dataString += `by ${composer.name}\n`;
    dataString += `${timeSigBeats}/${timeSigDuration} || `;

    dataString += chordChart.map(entry => {
      if (entry.isSeparator || !entry.segment) {
        return entry.name; // Return separator or blank chord name as-is
      }

      // For chord entries with segments
      const { root_pc, third_quality, extension, bass_pc, mode, chord_qual } = entry.segment;
      const rootName = midiToName(root_pc, false, root_pc, chord_qual);
      const bassName = bass_pc !== root_pc ? `/${midiToName(bass_pc, false, bass_pc, chord_qual)}` : '';
      // Method to check accidentals of rootName to use for bassName. Doesn't work for chords like D^7/Db
      // const bassName = (bass_pc != null) && bass_pc !== root_pc
      //   ? `${lockedTonicNote === null
      //     ? midiBassToName(bass_pc, rootName, mode)
      //     : midiBassToName(bass_pc, rootName, lockedTonicMode === null ? mode : lockedTonicMode)}`
      //   : '';
      return `${rootName}${third_quality}${extension}${bassName}`;
    }).join("  ");

    dataString += "  ||";
    return dataString;
  };

  const copyChordChartToClipboard = async () => {
    const dataToCopy = concatenateChartData();
    try {
      await navigator.clipboard.writeText(dataToCopy);
      alert('Data copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  /**
   * Analysis settings for every candidate lookup, batch or per-chord.
   *
   * One place on purpose: the settings are part of the cache key, so a prefetch
   * and the provider that reads its results must agree on them exactly, or the
   * prefetch fills entries nothing ever looks up.
   */
  const candidateOptions = () => ({
    server,
    keyCenter: lockedTonicNote !== null ? lockedTonicNote : -1,
    keyMode: lockedTonicMode,
    notationLegible: settings.preferNotationLegible,
    preferModeBorrowing: settings.preferModeBorrowing,
    cache: candidateCacheRef.current,
  });

  /**
   * The review dialog's "incomplete result" warning, or undefined when every
   * chord was checked. The reason comes from the failure itself rather than
   * being assumed — see describeLookupError.
   */
  const lookupWarning = (result: ChartResolutionResult) => {
    if (result.lookupFailures === 0) return undefined;
    const reason = result.lookupFailureReason || 'analysis server unreachable';
    return `${result.lookupFailures} chord(s) could not be checked — ${reason}.`;
  };

  /**
   * Respells every chord in the chart to the top-ranked spelling in one
   * ordering, then opens the review dialog. Nothing is applied until the user
   * confirms — Cancel simply closes it.
   *
   * Every chord needs an analysis lookup here (unlike iReal Pro respelling,
   * which can skip chords that already render natively), so the shared voicing
   * cache does the heavy lifting on charts that repeat voicings.
   */
  const respellAllChords = async (ordering: 'read' | 'feel') => {
    if (batchRunning) return;
    setBatchRunning(true);

    const anchor = anchorRectOf(batchButtonRef);
    const label = ordering === 'read' ? 'legibility' : 'feeling';
    try {
      const options = candidateOptions();
      const provider = createCandidateProvider(options);

      // Every chord needs a lookup for this operation, so fetch the whole
      // chart in one request; the provider then reads it all from the cache.
      await prefetchCandidates(chordChart, options);

      const result = await respellChartByOrdering(chordChart, provider, ordering);
      const changes = changesOnly(result);
      const chordCount = result.resolutions.length;

      setBatchReview({
        title: `Respell chords by ${label}`,
        summary: `${changes.length} of ${chordCount} ${chordCount === 1 ? 'chord' : 'chords'} would change`,
        changes,
        warning: lookupWarning(result),
        apply: (accepted) => applyToChart(accepted),
        anchor,
        width: 'narrow',
      });
    } catch (err) {
      console.error('[batch edit] respell failed:', err);
      setServerUnreachable(true);
    } finally {
      setBatchRunning(false);
    }
  };

  /** Sends a chart to iReal Pro. Takes the chart so callers can export a
   *  respelled copy without touching the live chordChart. */
  const sendChartToIrealPro = (chart: ChordChartEntry[]) => {
    // Read the opening/closing bar selector values set by the user in the chart display.
    // '𝄆' (U+1D106) = repeat start |:   → iRealPro '{' barline
    // '𝄃' (U+1D103) = double barline ||  → no special barline (section open '[' suffices)
    // '𝄇' (U+1D107) = repeat end   :|   → iRealPro '}' barline on last measure
    // '𝄂' (U+1D102) = final barline      → default Z end (no special barline)
    const openingBarValue = openingBarSelectRef.current ? openingBarSelectRef.current.value : '𝄃';
    const closingBarValue = closingBarSelectRef.current ? closingBarSelectRef.current.value : '𝄂';
    console.log('[iRealPro export] openingBar:', openingBarValue, ' closingBar:', closingBarValue);
    openInIrealPro(chart, {
      title: songTitle.title,
      composer: composer.name,
      key: getIrealKey(),
      bpm: parseInt(tempo.value) || 120,
      timeSignature: `${timeSigBeats}${timeSigDuration}` as '44' | '34',
      style: 'Medium Swing',                 // TODO: wire to chartSettings.style
      minorSymbol: 'm',                      // TODO: wire to chartSettings.minorSymbol
      openingBar: openingBarValue === '𝄆' ? 'repeatStart' : 'doubleBar',
      closingBar: closingBarValue === '𝄇' ? 'repeatEnd' : 'doubleBar',
    });
  };

  /**
   * Finds iReal Pro-recognised spellings for chords iReal Pro cannot render, and
   * returns the proposed changes. Shared by the export flow and the batch menu.
   */
  /**
   * Builds the review-dialog rows and summary for an iReal Pro respelling.
   *
   * Rows include chords with no substitute, not just the fixable ones — the
   * honest headline is how many chords iReal Pro cannot render, and reporting
   * only the fixable subset understated it ("3 of 4" when all 4 were
   * unsupported).
   */
  /**
   * Viewport rect of a control, captured before any await so the dialog anchors
   * to where the button actually is.
   */
  const anchorRectOf = (ref: React.RefObject<HTMLButtonElement>) => {
    if (!ref.current) return null;
    const rect = ref.current.getBoundingClientRect();
    return { bottom: rect.bottom, right: rect.right };
  };

  const describeRespelling = (result: { resolutions: ChartResolution[]; lookupFailures: number }) => {
    const rows = result.resolutions.filter(r => r.resolution.kind !== 'unchanged');
    return {
      rows,
      summary: rows.length === 0
        ? 'All chords are supported by iReal Pro'
        : `${rows.length} ${rows.length === 1 ? "chord isn't" : "chords aren't"} supported by iReal Pro`,
    };
  };

  const findIrealRespellings = async () => {
    const options = candidateOptions();
    const provider = createCandidateProvider(options);

    // Only chords iReal Pro cannot already render need alternates, and
    // resolveChord with no candidates is exactly that test ('fallback' means
    // "candidates would help"). Prefetching only those keeps the batch request
    // as small as the work actually requires.
    const needsLookup = chordChart.filter(entry =>
      !entry.isSeparator && entry.segment &&
      resolveChord(entry.segment, []).kind === 'fallback');
    await prefetchCandidates(needsLookup, options);

    return resolveChart(chordChart, provider);
  };

  /**
   * Export entry point. Respells first, and only prompts when there is
   * something to decide — a chart iReal Pro can already render exports straight
   * through with no dialog.
   *
   * Export must never be blocked by respelling: a failed lookup leaves the
   * chord on Harmona's spelling (the exporter wraps it in *...* as before) and
   * is surfaced as a warning in the dialog, not as a failed export.
   */
  const exportToIrealPro = async () => {
    if (batchRunning) return;

    // Fast path, and the reason it matters: openInIreal Pro navigates via
    // window.location.href, which browsers may block for a custom scheme once
    // the user-gesture context is gone — and awaiting a network call loses it.
    // resolveChord with no candidates is synchronous, so a chart iReal Pro can
    // already render exports inside the click that triggered it.
    // Every other path ends at the dialog, whose button click is a fresh
    // gesture, so no export ever fires outside one.
    const previewNeedsReview = chordChart.some(entry =>
      !entry.isSeparator && entry.segment &&
      resolveChord(entry.segment, []).kind !== 'unchanged');

    if (!previewNeedsReview) {
      sendChartToIrealPro(chordChart);
      return;
    }

    const exportAnchor = anchorRectOf(exportButtonRef);
    setBatchRunning(true);

    try {
      const result = await findIrealRespellings();
      const { rows, summary } = describeRespelling(result);

      setBatchReview({
        title: 'Respell chords for iReal Pro',
        summary,
        changes: rows,
        warning: lookupWarning(result),
        applyLabel: 'Change Selections & Export',
        keepLabel: 'Export Without Changes',
        // Export-only: the live chart is never modified by this path.
        apply: (accepted) => sendChartToIrealPro(applyResolutions(chordChart, accepted)),
        keepAll: () => sendChartToIrealPro(chordChart),
        anchor: exportAnchor,
      });
    } catch (err) {
      // Respelling failed outright — export anyway rather than losing the user's action.
      console.error('[iReal Pro export] respelling failed, exporting as-is:', err);
      sendChartToIrealPro(chordChart);
    } finally {
      setBatchRunning(false);
    }
  };

  /**
   * Batch-menu counterpart to the export respelling: applies the same
   * suggestions to the real chart rather than to an exported copy.
   */
  const respellChartForIrealPro = async () => {
    if (batchRunning) return;
    const anchor = anchorRectOf(batchButtonRef);
    setBatchRunning(true);

    try {
      const result = await findIrealRespellings();
      const { rows, summary } = describeRespelling(result);

      setBatchReview({
        title: 'Respell chords for iReal Pro',
        summary,
        changes: rows,
        warning: lookupWarning(result),
        apply: (accepted) => applyToChart(accepted),
        anchor,
        width: 'narrow',
      });
    } catch (err) {
      console.error('[batch edit] respell failed:', err);
      setServerUnreachable(true);
    } finally {
      setBatchRunning(false);
    }
  };

  /**
   * Writes accepted resolutions into the live chart.
   *
   * applyResolutions rewrites `segment` only — it has no access to
   * chordSymbolText or the locked key — so `name` is rebuilt here for the
   * entries it touched.
   *
   * `id` is untouched, so key center regions and anything else anchored to an
   * entry survive a batch respelling: the chord is the same chord, spelled
   * differently.
   */
  const applyToChart = (accepted: ChartResolution[]) => {
    const touched = new Set(accepted.map(a => a.index));
    const nextChart = applyResolutions(chordChart, accepted).map((entry, i) => {
      if (!touched.has(i) || !entry.segment) return entry;
      return {
        ...entry,
        name: chordSymbolText(entry.segment, lockedTonicNote, lockedTonicMode),
      };
    });
    setChordChart(nextChart);
  };


  const getIrealKey = (): string => {
    if (lockedTonicNote === null) return 'C';
    const noteName = midiToName(lockedTonicNote, false, lockedTonicNote, lockedTonicMode);
    // iReal Pro uses plain ASCII b, not Unicode ♭
    const ascii = noteName.replace('♭', 'b').replace('♯', '#');
    return lockedTonicMode === 1 ? `${ascii}-` : ascii;
  };


  // NEW ADDITION ACRYLICODE
  // ///////////////////////
  // const saveSong = async (song: Song) => {
  //   const usersRef = collection(firestore, 'users')
  //   const userDoc = doc(usersRef, email)

  //   getDoc(userDoc).then(async snapshot => {
  //     const data = snapshot.data()
  //     const userSongs = data && data["songs"] ? data['songs'] : []

  //     const songsWithSameStarting = userSongs.filter((userSong: Song) => userSong.songTitle.startsWith(song.songTitle))
  //     const songNumber = songsWithSameStarting.length + 1
  //     const songTitle = `${song.songTitle} ${songNumber}`
  //     song.songTitle = songTitle
  //     const allUserSongs = [...userSongs, song]
  //     await setDoc(userDoc, { songs: allUserSongs }, { merge: true })
  //     setUserSongs(allUserSongs)
  //     setAllSongs([...defaultSongs, ...allUserSongs])
  //   })
  // }



  const [showWarning, setShowWarning] = useState(false); // finish this!
  const toggleWarning = () => {
    setShowWarning(prev => !prev);
  };

  // Finish this!
  // const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  // const toggleTooltip = () => {
  //   setIsTooltipVisible(prev => !prev);
  // };



  // const renderChordSymbol = (segment: ChordSegment, isSvg: boolean = false) => {
  //   return assembleChordSymbol(segment, isSvg);
  // };

  /**
   * Spells the parts of a chord symbol that depend on the locked tonic rather
   * than on the segment alone: the root name (or its numeral, in SVG), the
   * slash, and the bass name.
   *
   * Shared by assembleChordSymbol, which styles these, and chordSymbolText,
   * which does not, so the text symbol stored on a chart entry can never
   * disagree with the symbol drawn over it.
   *
   * With a tonic locked, names are spelled in the mode of the scale the
   * sounding notes imply (tonicScalesNotes), which suits the chord being
   * played. `spellingMode` overrides that for a chord that is not the one
   * sounding — a MIDI file's chord regions pass the locked mode — so its
   * spelling cannot change with whatever the playhead is on.
   */
  const spellChordSymbol = (segment: ChordSegment, isSvg: boolean, lockedTonicNote: number | null, lockedTonicMode: number | null, spellingMode?: number) => {
    const { root_pc, bass_pc, mode, chord_qual } = segment;
    const impliedMode = spellingMode !== undefined ? spellingMode : getModeFromPcSet(tonicScalesNotes[0]);
    // const effectiveMode = lockedTonicMode !== null ? lockedTonicMode : mode;
    const rootName = root_pc != null
      ? lockedTonicNote === null
        ? midiToName(root_pc, false, root_pc, chord_qual)
        : isSvg
          // ? midiToRomanNumeral(root_pc, lockedTonicNote, lockedTonicMode === null
          ? settings.showNashvilleNumber
            ? midiToNashvilleNumber(root_pc, lockedTonicNote, lockedTonicMode === null
              ? mode
              : impliedMode, mode)
            : midiToRomanNumeral(root_pc, lockedTonicNote, lockedTonicMode === null
              ? mode
              : impliedMode, mode)
          : midiToName(root_pc, false, lockedTonicNote, lockedTonicMode === null
            ? chord_qual
            : impliedMode) // replaced lockedTonicMode
      : '';

    const slash = (bass_pc != null) && bass_pc !== root_pc ? '\u2044' : '';
    const bassName = (bass_pc != null) && bass_pc !== root_pc
      ? lockedTonicNote === null
        ? midiToName(bass_pc, false, root_pc, chord_qual)
        : midiToName(bass_pc, false, lockedTonicNote, lockedTonicMode === null
          ? chord_qual
          : impliedMode) // replaced lockedTonicMode with impliedMode because the implied scale may flip modes

      // Replaced below method with above method to rely on PieChordModes mode for chord and bass note spelling.
      // ? `${lockedTonicNote === null
      //   ? midiToName(bass_pc, false, root_pc, mode)
      //   : midiToName(bass_pc, false, lockedTonicNote, lockedTonicMode === null ? mode : lockedTonicMode)}`

      // Method to check accidentals of rootName to use for bassName. Doesn't work for chords like D^7/Db
      // ? midiBassToName(bass_pc, rootName, mode)
      // : midiBassToName(bass_pc, rootName, lockedTonicMode === null ? mode : mode)}`

      : '';

    // console.log("******** bassname:", bassName, "root_pc:", root_pc, "lockedTonicMode:", lockedTonicMode)

    return { rootName, slash, bassName };
  };

  /**
   * The chord symbol as plain text — what a chart entry stores in `name`.
   *
   * The same pieces assembleChordSymbol lays out below, in the same order,
   * without the styling: root, third quality, extension, slash, bass. Two
   * deliberate differences from the drawn symbol: ± stays as the one character
   * the segment carries rather than becoming stacked ♯/♭ glyphs, and an
   * unapproved chord spells out in full rather than showing the PRO lock, which
   * is view state and has no business in stored data.
   */
  const chordSymbolText = (segment: ChordSegment, lockedTonicNote: number | null = null, lockedTonicMode: number | null = null): string => {
    const { third_quality, extension } = segment;
    const { rootName, slash, bassName } = spellChordSymbol(segment, false, lockedTonicNote, lockedTonicMode);
    return `${rootName}${third_quality}${extension}${slash}${bassName}`;
  };

  // ADDED HANDLING OF chord symbol display using segments
  const assembleChordSymbol = (segment: ChordSegment, isSvg: boolean = false, lockedTonicNote: number | null = null, lockedTonicMode: number | null = null, spellingMode?: number): JSX.Element => {
    const { root_pc, third_quality, third_quality_wbn, extension, extension_wbn } = segment;
    const isApprovedChord = checkApprovedChord(activePcs, root_pc);
    const { rootName, slash, bassName } = spellChordSymbol(segment, isSvg, lockedTonicNote, lockedTonicMode, spellingMode);

    const mainPart = root_pc != null ? `${rootName}${third_quality}` : '';

    // Function to replace ± with stacked ♯/♭ symbols
    const replaceSymbols = (text: string): JSX.Element[] => {
      if (!text.includes('±')) return [<>{text}</>];

      const parts = text.split('±');
      const result: JSX.Element[] = [];

      for (let i = 0; i < parts.length; i++) {
        if (i > 0) {
          // Add the stacked ♯/♭ symbol between parts
          result.push(
            <span style={{ display: 'inline-block', position: 'relative', width: '0.33em', height: '1em' }}>
              <span style={{ position: 'absolute', top: '0.55em', left: '0.03em', fontSize: '0.8em' }}>♯</span>
              <span style={{ position: 'absolute', bottom: '0.0em', left: '0.07em', fontSize: '0.8em' }}>♭</span>
            </span>
          );
        }
        if (parts[i]) {
          result.push(<>{parts[i]}</>);
        }
      }

      return result;
    };

    return isSvg ? (
      <>
        <tspan fontSize={lockedTonicNote === null ? '1em' : '1.15em'} dy={lockedTonicNote === null ? '0em' : '0.15em'}>{rootName}</tspan>
        {(lockedTonicNote === null || (settings.showNashvilleNumber ? chordQual === 1 : '') || chordQual === 3 || chordQual === 4) &&
          <tspan fontSize={
            third_quality === "°"
              ? '1.0em'
              : '0.65em'} dy={third_quality === "+"
                ? '-0.4em'
                : lockedTonicNote !== null && third_quality === "°"
                  ? '-0.12em'
                  : '0em'}
          >{!isApprovedChord
            ? ''
            : lockedTonicNote !== null
              ? third_quality_wbn !== "m" || settings.showNashvilleNumber
                ? third_quality_wbn
                : ''
              : third_quality_wbn}</tspan>}
        <tspan fontSize={
          !isApprovedChord ? '0.6em'
            : lockedTonicNote === null
              ? '0.6em' : '0.55em'} dy={lockedTonicNote === null
                ? '-0.5em' : third_quality === "°" ? '-0.65em' : '-0.95em'}>
          {!isApprovedChord
            ? (<a href="https://your-subscribe-url.com" target="_blank" style={{ cursor: 'pointer' }}>
              <tspan filter="url(#blur)" letterSpacing="-0.05em">▒▒</tspan>
              <tspan fontSize="0.1dvw" fill="#888">PRO</tspan></a>) // 🔒 TODO: replace with PRO icon
            : extension_wbn.includes('±') ? (
              (() => {
                // Process all ± symbols one at a time
                let result: JSX.Element[] = [];
                const parts = extension_wbn.split('±');

                // Add first part
                result.push(<tspan key="part-0">{parts[0]}</tspan>);

                // Add each remaining part with the ♯/♭ symbol before it
                for (let i = 1; i < parts.length; i++) {
                  result.push(
                    <React.Fragment key={`symbol-${i}`}>
                      <tspan dy="-0.4em" dx="0.05em" fontSize="0.8em">♯</tspan>
                      <tspan dy="0.7em" dx="-0.28em" fontSize="0.8em">♭</tspan>
                      <tspan dy="-0.23em" dx="0.0em">{parts[i]}</tspan>
                    </React.Fragment>
                  );
                }

                return <>{result}</>;
              })()
            ) : extension_wbn}
        </tspan>
        {/* <tspan fontSize="0.65em" dy={third_quality_wbn === "o" ? "-0.5em" : "0em"}>{third_quality_wbn}</tspan> */}
        {/* <tspan fontSize="0.6em" dy={third_quality_wbn === "o" ? "-0em" : "-0.5em"}>{extension_wbn}</tspan> */}
      </>
    ) : (

      // return isSvg ? (
      //   <>
      //     <tspan fontSize={lockedTonicNote === null ? '1em' : '1.15em'} dy={lockedTonicNote === null ? '0em' : '0.15em'}>{rootName}</tspan>
      //     {(lockedTonicNote === null || chordQual === 3 || chordQual === 4) && <tspan fontSize={third_quality === "°"
      //       ? '1.0em' : '0.65em'} dy={third_quality === "+"
      //         ? '-0.4em' : lockedTonicNote !== null && third_quality === "°"
      //           ? '-0.12em' : '0em'}>{lockedTonicNote !== null ? third_quality_wbn !== "m" ? third_quality_wbn : '' : third_quality_wbn}</tspan>}
      //     <tspan fontSize={lockedTonicNote === null
      //       ? '0.6em' : '0.55em'} dy={lockedTonicNote === null
      //         ? '-0.5em' : third_quality === "°" ? '-0.65em' : '-0.95em'}>
      //       {extension_wbn.includes('±') ? (
      //         (() => {
      //           const [before, after] = extension_wbn.split('±', 2);
      //           return (
      //             <>
      //               <tspan>{before}</tspan>
      //               <tspan dy="-0.4em" dx="0.05em" fontSize="0.8em">♯</tspan>
      //               <tspan dy="0.7em" dx="-0.28em" fontSize="0.8em">♭</tspan>
      //               <tspan dy="-0.23em" dx="0.0em">{after}</tspan>
      //             </>
      //           );
      //         })()
      //       ) : extension_wbn}
      //     </tspan>
      //     {/* <tspan fontSize="0.65em" dy={third_quality_wbn === "o" ? "-0.5em" : "0em"}>{third_quality_wbn}</tspan> */}
      //     {/* <tspan fontSize="0.6em" dy={third_quality_wbn === "o" ? "-0em" : "-0.5em"}>{extension_wbn}</tspan> */}
      //   </>
      // ) : (

      <span style={{ display: 'inline-block', verticalAlign: 'baseline' }}>
        {rootName}<span style={{
          // fontSize: '0.65em',
          fontSize: third_quality === "°" ? '1.0em' : '0.65em',
          verticalAlign: third_quality === "+" ? '0.4em' : '0.0em'
          // verticalAlign: third_quality === "o" ? '0.5em' : '0.0em'
        }}>{!isApprovedChord ? '' : third_quality}</span><span style={{
          fontSize: !isApprovedChord ? '0.5em' : '0.6em',
          verticalAlign: '0.5em', lineHeight: 0
        }}>
          {!isApprovedChord ? (
            <a href="https://your-subscribe-url.com" target="_blank" rel="noopener"><span style={{
              filter: 'blur(0.25dvw)',
              opacity: 1.0,
              cursor: 'pointer',
              textDecoration: 'none',
              color: 'inherit',
            }}>▒▒</span><span style={{ fontSize: '0.8dvw', fontStyle: 'italic' }}>PRO</span></a> // 🔒 TODO: replace with PRO icon
          ) : extension.includes('±') ? (
            <>
              {replaceSymbols(extension).map((element, i) => (
                <React.Fragment key={i}>{element}</React.Fragment>
              ))}
            </>
          ) : !isApprovedChord ? '[locked]' : extension}
        </span><span style={{
          fontSize: '0.6em', fontWeight: 'bold',
          verticalAlign: '-0.1em', letterSpacing: '0.1em'
        }}>{slash}</span><span style={{
          fontSize: '0.65em',
          verticalAlign: '-0.2em', letterSpacing: '0.0em'
        }}>{bassName}</span>
      </span>
    );
  };


  const assembleScaleName = (scaleRoot: number, mode: number) => {
    if (activePcs.length > 0) {
      return (
        <>
          {midiToName(scaleRoot, false, lockedTonicNote !== null ? lockedTonicNote : rootNote, lockedTonicNote !== null ? lockedTonicMode : chordQual)}
          {chordSymbol}
        </>
      )
    } else {
      return <></>;
    }
  }



  // Palettes Start
  const [selectedPalette, setSelectedPalette] = useState(defaultPalettes[0].label);

  const [allPalettes, setAllPalettes] = useState<Palette[]>(defaultPalettes);
  const [userPalettes, setUserPalettes] = useState<Palette[]>([]);
  const [customPaletteName, setCustomPaletteName] = useState<string>('');

  const updatePalettes = async (email: string, label: string, majorLabels: string[], minorLabels: string[]) => {
    const usersRef = collection(firestore, 'users')
    const newUserDoc = doc(usersRef, email)
    const palette = allPalettes.find(palette => palette.label === label)

    const palettesToUpdate = [...userPalettes]
    if (!palette) {
      palettesToUpdate.push({ label, majorLabels, minorLabels })
    } else {
      const index = palettesToUpdate.indexOf(palette)
      palettesToUpdate[index] = { label, majorLabels, minorLabels }
    }

    // merge: true so palette saves never wipe other fields on the user doc (e.g. future songs/settings)
    await setDoc(newUserDoc, { labelSets: palettesToUpdate }, { merge: true })
    syncPalettes(email)
  }

  const deletePalette = async (email: string, label: string) => {
    const usersRef = collection(firestore, 'users')
    const newUserDoc = doc(usersRef, email)
    const palettesToUpdate = userPalettes.filter(palette => palette.label !== label)
    await setDoc(newUserDoc, { labelSets: palettesToUpdate }, { merge: true })
    syncPalettes(email)
  }
  // Palettes END


  // Compares against the user's own lock, not the chart's: locking the key a
  // chart lock is showing takes it over as the user's lock rather than toggling
  // anything off. lockedTonicMode is the chart's mode in that case, so the
  // user's lock keeps it.
  const handleToggleLock = (note: number | null) => {
    let newKeyCenter = userLockedTonicNote === note ? null : note;
    let newTonicMode = isScale ? mode : (lockedTonicMode !== null ? lockedTonicMode : mode);

    setLockedTonicNote(newKeyCenter);
    setLockedTonicMode(newTonicMode);
    setKeyCenter(newKeyCenter ? newKeyCenter : 0);

    // new menu selection logic
    if (newKeyCenter !== null) {
      if (!settings.anchorToChordRoot) setRootNote(newKeyCenter); // This line triggers update to pie chart/chord compass states
      setMode(newTonicMode);

      const targetPitchClass = newKeyCenter % 12;
      const matchIndex = fifthsOrderedPitchClasses.findIndex(entry =>
        entry.pitchClass === targetPitchClass &&
        entry.showInModes.includes(newTonicMode)
      );
      if (matchIndex !== -1) {
        setSelectedKeyCenterIndex(matchIndex);
      }
    }
  };


  const handleModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMode = parseInt(event.target.value, 10);
    if (lockedTonicNote === null) {
      setMode(selectedMode);
    } else {
      // Changing the mode of a chart lock makes it the user's lock, in the new mode
      if (isChartLocked) setLockedTonicNote(lockedTonicNote);
      setLockedTonicMode(selectedMode);
      // new menu selection update for mode changes
      const targetPitchClass = lockedTonicNote % 12;
      const matchIndex = fifthsOrderedPitchClasses.findIndex(entry =>
        entry.pitchClass === targetPitchClass &&
        entry.showInModes.includes(selectedMode)
      );
      if (matchIndex !== -1) {
        setSelectedKeyCenterIndex(matchIndex);
      }
    }
  };
  // if (selectedMode === 1) {
  //   transformPalette(0, 100, 255, 6);
  // } else {
  //   transformPalette(255, 200, 0, 6);
  // }


  // Guitar and bass lay the computer keyboard out as strings (rows) and
  // frets (columns); the key holding each string, see handleKeyEvent
  const isStringLayout = selectedInstrument == 'AcousticGuitar' || selectedInstrument == 'BassSixString';
  const heldStringKeysRef = useRef<Map<number, { key: string; column: number; midiNote: number }>>(new Map());
  useEffect(() => {
    heldStringKeysRef.current.clear();
  }, [selectedInstrument]);
  useEffect(() => {
    const forget = () => heldStringKeysRef.current.clear();
    window.addEventListener('blur', forget);
    return () => window.removeEventListener('blur', forget);
  }, []);

  const getMidiNoteFromKey = (key: string): number | null => {
    // z is a guitar's low E (E2) at the default octave, and the bass's an
    // octave below (E1)
    const transposition = selectedInstrument == 'BassSixString' ? -32 : -20;
    const baseMap: { [key: string]: number } = selectedInstrument == 'AcousticGuitar' || selectedInstrument == 'BassSixString' ? {
      // GUITAR LAYOUT if Acoustic Guitar or Bass Six String selected
      'z': 60 + transposition,
      'x': 61 + transposition,
      'c': 62 + transposition,
      'v': 63 + transposition,
      'b': 64 + transposition,
      'n': 65 + transposition,
      'm': 66 + transposition,
      ',': 67 + transposition,
      '.': 68 + transposition,
      '/': 69 + transposition,
      'a': 65 + transposition,
      's': 66 + transposition,
      'd': 67 + transposition,
      'f': 68 + transposition,
      'g': 69 + transposition,
      'h': 70 + transposition,
      'j': 71 + transposition,
      'k': 72 + transposition,
      'l': 73 + transposition,
      ';': 74 + transposition,
      '\'': 75 + transposition,
      'q': 70 + transposition,
      'w': 71 + transposition,
      'e': 72 + transposition,
      'r': 73 + transposition,
      't': 74 + transposition,
      'y': 75 + transposition,
      'u': 76 + transposition,
      'i': 77 + transposition,
      'o': 78 + transposition,
      'p': 79 + transposition,
      '[': 80 + transposition,
      ']': 81 + transposition,
      '\\': 82 + transposition,
      '1': 75 + transposition,
      '2': 76 + transposition,
      '3': 77 + transposition,
      '4': 78 + transposition,
      '5': 79 + transposition,
      '6': 80 + transposition,
      '7': 81 + transposition,
      '8': 82 + transposition,
      '9': 83 + transposition,
      '0': 84 + transposition,
      '-': 85 + transposition,
      '=': 86 + transposition
    } : {
      // PIANO LAYOUT if Piano or Rhodes selected
      'a': 60,
      'w': 61,
      's': 62,
      'e': 63,
      'd': 64,
      'f': 65,
      't': 66,
      'g': 67,
      'y': 68,
      'h': 69,
      'u': 70,
      'j': 71,
      'k': 72,
      'o': 73,
      'l': 74,
      'p': 75,
      ';': 76,
      '\'': 77,
      ']': 78
    };
    return baseMap[key] ? baseMap[key] + octaveOffset : null;
  };

  const masterVolume = -11

  // Samplers connect straight to the destination on purpose. A master limiter
  // would be a clean guard against dense moments clipping the output, but
  // Tone.Limiter wraps a DynamicsCompressorNode, and Chrome gives that a fixed
  // ~6ms pre-delay whether or not it is compressing - too costly on a chain
  // tuned for live MIDI latency (see lookAhead below). Note density is handled
  // at the source instead: see the batch velocity trim in MidiController and
  // the retrigger cooldown in midiAudioHandler.

  // Samplers are created lazily on the first Sound On (see the [soundOn] effect
  // below) instead of on mount, so visitors don't download ~30MB of samples
  // before ever enabling sound.
  const samplersCreatedRef = useRef(false);

  // Tone.js keeps downloading (and later fires onload) even after a sampler is
  // disposed, so switching instruments mid-download would let the OLD
  // instrument's onload fire late and corrupt state (set samplerLoaded early,
  // rebuild the scale sampler for the wrong instrument). Each loadSamplers call
  // gets a session number; onload callbacks bail if they're no longer current.
  const loadSessionRef = useRef(0);

  // Audio context setup runs once on mount so MidiAudioHandler (created later)
  // captures the same context the samplers will use.
  useEffect(() => {
    if (Tone.getContext().state === 'suspended' || !Tone.getContext().state) {
      Tone.setContext(new AudioContext({
        latencyHint: "playback", // More aggressive setting than "interactive"
        sampleRate: 48000,
      }));

      // Additional aggressive latency optimization
      Tone.getContext().lookAhead = 0.005; // More aggressive setting than 0.001

      // Alternative: Set destination properties directly
      Tone.getDestination().volume.value = -1; // Reduce if needed
    }
  }, []);

  // Loads only the small C3-C6 scale-preview sampler for the current instrument
  // (quiet layer only, no loudBaseUrl — on purpose). Called on demand by
  // playScale, which works with the Sound toggle off, and by loadSamplers once
  // the main sampler has finished downloading (so its files come from the
  // browser cache — no second download, no extra hosting bandwidth).
  const loadScaleSampler = (onReady?: () => void) => {
    if (scaleSamplerRef.current) {
      scaleSamplerRef.current.dispose();
    }
    scaleSamplerRef.current = new Tone.Sampler({
      urls: scaleSampleUrls,
      release: instrumentConfigs[selectedInstrument].release,
      baseUrl: instrumentConfigs[selectedInstrument].baseUrl,
      attack: 0,
      onload: () => {
        if (onReady) onReady();
      },
      onerror: (err) => {
        console.error('Scale sampler failed to load:', err);
        setLoadingScaleKey(null);
      }
    }).toDestination();
    scaleSamplerRef.current.volume.value = masterVolume;
  };

  const loadSamplers = () => {
    // Invalidate any previous load that's still downloading (see loadSessionRef)
    const session = ++loadSessionRef.current;

    // A scale-preview spinner may be waiting on a sampler we're about to
    // dispose; its onReady will never fire, so clear the spinner
    setLoadingScaleKey(null);

    // Dispose of existing sampler if changing instruments
    if (samplerRef.current) {
      samplerRef.current.dispose();
      samplerRef.current = null;
    }

    // Dispose of loud velocity layer sampler if it exists
    if (samplerLoudRef.current) {
      samplerLoudRef.current.dispose();
      samplerLoudRef.current = null;
    }

    // Dispose of scale sampler if it exists
    if (scaleSamplerRef.current) {
      scaleSamplerRef.current.dispose();
      scaleSamplerRef.current = null;
    }

    // Set sampler as not loaded while loading new instrument
    setSamplerLoaded(false);

    // Define sample URLs (same for all instruments and velocity layers)
    const sampleUrls = {
      "C2": "C2.mp3",
      "C1": "C1.mp3",
      "C3": "C3.mp3",
      "C4": "C4.mp3",
      "C5": "C5.mp3",
      "C6": "C6.mp3",
      "C7": "C7.mp3",
      "C8": "C8.mp3",
      "D#1": "Ds1.mp3",
      "D#2": "Ds2.mp3",
      "D#3": "Ds3.mp3",
      "D#4": "Ds4.mp3",
      "D#5": "Ds5.mp3",
      "D#6": "Ds6.mp3",
      "D#7": "Ds7.mp3",
      "F#1": "Fs1.mp3",
      "F#2": "Fs2.mp3",
      "F#3": "Fs3.mp3",
      "F#4": "Fs4.mp3",
      "F#5": "Fs5.mp3",
      "F#6": "Fs6.mp3",
      "F#7": "Fs7.mp3",
      "A1": "A1.mp3",
      "A2": "A2.mp3",
      "A3": "A3.mp3",
      "A4": "A4.mp3",
      "A5": "A5.mp3",
      "A6": "A6.mp3",
      "A7": "A7.mp3",
    };

    // Track loading state for both samplers
    const hasLoudLayer = !!instrumentConfigs[selectedInstrument].loudBaseUrl;
    let baseSamplerLoaded = false;
    let loudSamplerLoaded = false;

    const checkBothLoaded = () => {
      if (!hasLoudLayer && baseSamplerLoaded) {
        setSamplerLoaded(true);
      } else if (hasLoudLayer && baseSamplerLoaded && loudSamplerLoaded) {
        setSamplerLoaded(true);
      }
    };

    samplerRef.current = new Tone.Sampler({
      urls: sampleUrls,
      release: instrumentConfigs[selectedInstrument].release,
      baseUrl: instrumentConfigs[selectedInstrument].baseUrl,
      attack: 0,            // ✅ This one exists - immediate sample start
      // curve: "linear"
      onload: () => {
        if (session !== loadSessionRef.current) return; // superseded load
        baseSamplerLoaded = true;
        checkBothLoaded();

        // (Re)create the scale sampler now that the base download is done, so
        // its files come straight from the browser cache
        loadScaleSampler();
      },
      onerror: (err) => {
        if (session !== loadSessionRef.current) return;
        console.error('Sampler failed to load (' + selectedInstrument + '):', err);
      }
    }).toDestination();

    samplerRef.current.volume.value = masterVolume; // sets the master volume
    setCurrentSampler(samplerRef.current);

    // Create loud velocity layer sampler if the instrument has one (Grand Piano only)
    if (hasLoudLayer) {
      samplerLoudRef.current = new Tone.Sampler({
        urls: sampleUrls,
        release: instrumentConfigs[selectedInstrument].release,
        baseUrl: instrumentConfigs[selectedInstrument].loudBaseUrl,
        attack: 0,
        onload: () => {
          if (session !== loadSessionRef.current) return; // superseded load
          loudSamplerLoaded = true;
          checkBothLoaded();
        },
        onerror: (err) => {
          if (session !== loadSessionRef.current) return;
          console.error('Loud sampler failed to load (' + selectedInstrument + '):', err);
        }
      }).toDestination();

      samplerLoudRef.current.volume.value = masterVolume;
    }
  };

  // First time sound is enabled (via the Sound button, Play Chord, or MIDI file
  // playback — anything that sets soundOn), create the samplers. The button shows
  // "Loading <instrument>" via samplerLoaded until the download finishes; all
  // playback paths already guard on sampler.loaded.
  useEffect(() => {
    if (soundOn && !samplersCreatedRef.current) {
      samplersCreatedRef.current = true;
      loadSamplers();
    }
  }, [soundOn]);

  // Instrument changed: reload samplers only if sound is currently on.
  // With sound off, just dispose whatever is loaded (it belongs to the previous
  // instrument) and defer any downloading until sound is next turned on or a
  // scale preview is played.
  useEffect(() => {
    if (soundOn && samplersCreatedRef.current) {
      loadSamplers();
      return;
    }
    samplersCreatedRef.current = false;
    if (samplerRef.current) {
      samplerRef.current.dispose();
      samplerRef.current = null;
    }
    if (samplerLoudRef.current) {
      samplerLoudRef.current.dispose();
      samplerLoudRef.current = null;
    }
    if (scaleSamplerRef.current) {
      scaleSamplerRef.current.dispose();
      scaleSamplerRef.current = null;
    }
    setCurrentSampler(null);
  }, [selectedInstrument]);

  // iOS silent mode workaround for iOS 16 or older: Create silent audio element on mount
  // useEffect(() => {
  //   // Create a silent audio element that loops
  //   // This "unlocks" Web Audio API on iOS even in silent mode
  //   const silentAudio = new Audio();
  //   // Tiny silent MP3 data URL (0.1 second of silence)
  //   silentAudio.src = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAA4T/////////////////////////////////////////////////////////////////////////////////';
  //   silentAudio.loop = true;
  //   silentAudio.volume = 0;
  //   silentAudioRef.current = silentAudio;
  // }, []);


  const handleSoundToggle = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!soundOn) { // If the sound is currently off
      // Fix for iOS silent mode: Set audio session to playback (iOS 17+)
      if ('audioSession' in navigator) {
        try {
          (navigator as any).audioSession.type = 'playback';
        } catch (e) {
          console.log('Could not set audioSession type:', e);
        }
      }

      // iOS silent mode workaround for iOS 16 or older: Play silent audio to unlock Web Audio API
      // if (silentAudioRef.current) {
      //   try {
      //     silentAudioRef.current.play().catch(e => console.log('Silent audio play failed:', e));
      //   } catch (e) {
      //     console.log('Could not play silent audio:', e);
      //   }
      // }

      // If Tone.js hasn't been initialized yet, start it
      if (!audioInitialized) {
        await Tone.start();
        // console.log("Tone.js started successfully");
        setAudioInitialized(true);
      }
      Tone.getTransport().start();
      // console.log("Tone.js started/resumed");

      // Trigger a silent note to unlock audio
      // if (samplerRef.current) {
      //   samplerRef.current.triggerAttackRelease('C4', '16n', undefined, 0); // 0 volume
      // }

    } else { // If the sound is currently on
      Tone.getTransport().pause();
      // console.log("Tone.js paused");
      if (samplerRef.current) {
        samplerRef.current.releaseAll();
      }
    }

    // Toggle the sound state
    // console.log("Current soundOn state:", soundOn);
    setSoundOn(prevState => prevState);
  };


  useEffect(() => {
    isScrubbingRef.current = isScrubbing;
  }, [isScrubbing]);

  useEffect(() => {
    isMidiFilePausedRef.current = isMidiFilePaused;
  }, [isMidiFilePaused]);


  useEffect(() => {
    shouldPlaySoundRef.current = soundOn && audioInitialized;
    setShouldPlaySound(soundOn && audioInitialized);
    // console.log("shouldPlaySoundRef state updated:", shouldPlaySoundRef.current);
  }, [soundOn, audioInitialized]);


  // function playChord(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
  function playChord() {
    // From the refs, not state: Space calls this from the key handler, which is
    // only rebuilt when its dependencies change, and the notes change on every
    // MIDI note — too often to rebuild it for. State there could be a note
    // behind what the piano shows.
    const allNotes = [...new Set([...activeNotesRef.current, ...heldNotesRef.current, ...toggledNotesRef.current])];
    // If sound is off, turn it on
    if (!soundOn && allNotes.length > 0) {
      if (!audioInitialized) {
        Tone.start();
        setAudioInitialized(true);
      }
      setSoundOn(true);
    }
    if (samplerRef.current && samplerRef.current.loaded) {
      allNotes.forEach(note => {
        if (allNotes.includes(note)) {
          samplerRef.current!.triggerRelease(Tone.Midi(note).toNote());
        }
      });
      // if (isArpeggiateOn || isScale || selectedInstrument == 'AcousticGuitar' || selectedInstrument == 'BassSixString') {
      let uniqueMidiNotes = Array.from(new Set(allNotes)); // remove any duplicates
      let sortedMidiNotes = uniqueMidiNotes.sort((a, b) => !(isScale && scaleDescending) ? a - b : b - a); // Sort notes from low to high or high to low if scale and desc.
      // let arpSpeed = 10000 / parseInt(tempo.value, 10) / (1 + sortedMidiNotes.length / 50);
      let arpSpeed = (isScale ?
        100000 : // for scales base tempo is slower: default 100000, higher number is slower
        isArpeggiateOn ?
          30000 : // Arpeggio speed here, default 30000, lower number is faster
          selectedInstrument == 'AcousticGuitar' ?
            6000 : // strummed chords
            selectedInstrument == 'BassSixString' ?
              3500 : // finger picking chords
              1500 // Piano and Rhodes slightly imperfect block chords
      ) / 120 / (1 + sortedMidiNotes.length / 2) // more notes arpeggiate/strum faster ... 120 is default tempo

      startChordNotes(sortedMidiNotes, arpSpeed);
      // } else {
      //   allNotes.forEach(note => {
      //     samplerRef.current!.triggerAttack(Tone.Midi(note).toNote(), undefined, 0.65);
      //   });
      // }
    }
  }

  function playScale(scaleNotes: number[], rootPcVal: number, scaleKey: 'tonic' | 'relative' | 'chord') {
    // Previews work with the Sound toggle off: they load and use only the small
    // scale sampler, and don't turn sound on or fetch the full instrument samples.
    if (!audioInitialized) { Tone.start(); setAudioInitialized(true); }
    if (!scaleSamplerRef.current) {
      // First preview before any samples were loaded: fetch the scale sampler,
      // show an hourglass on this play button, then play when ready
      setLoadingScaleKey(scaleKey);
      loadScaleSampler(() => {
        setLoadingScaleKey(null);
        playScale(scaleNotes, rootPcVal, scaleKey);
      });
      return;
    }
    if (!scaleSamplerRef.current.loaded) return; // still downloading (hourglass showing)
    // Activate the selected scale
    setSettings(s => scaleKey === 'tonic'
      ? { ...s, showTonicScale: true, showRelativeChordScale: false, showChordScale: false }
      : scaleKey === 'relative'
        ? { ...s, showRelativeChordScale: true, showTonicScale: false, showChordScale: false }
        : { ...s, showChordScale: true, showTonicScale: false, showRelativeChordScale: false }
    );
    const scaleDelay = 25000 / 120; // consistent speed regardless of note count
    const baseNote = 48 + (rootPcVal % 12); // always in range 48–59
    const notePairs: [number, number][] = [
      ...scaleNotes.map(interval => [baseNote + interval, baseNote + interval + 12] as [number, number]),
      [baseNote + 12, baseNote + 24], // root repeated one octave up at the end
    ];
    // Cancel any currently playing scale
    scaleNoteTimeoutsRef.current.forEach(id => clearTimeout(id));
    scaleNoteTimeoutsRef.current = [];
    if (scaleSamplerRef.current) scaleSamplerRef.current.releaseAll();

    // Flash icon in sync with note steps
    if (scalePlayIntervalRef.current) clearInterval(scalePlayIntervalRef.current);
    setPlayingScale(scaleKey);
    setPlayIconFlash(true);
    scalePlayIntervalRef.current = setInterval(() => setPlayIconFlash(v => !v), scaleDelay);
    scaleNoteTimeoutsRef.current.push(setTimeout(() => {
      if (scalePlayIntervalRef.current) { clearInterval(scalePlayIntervalRef.current); scalePlayIntervalRef.current = null; }
      setPlayingScale(null);
      setPlayIconFlash(true);
    }, (notePairs.length + 0.5) * scaleDelay));

    const bottomNote = Tone.Midi(notePairs[0][0]).toNote();
    const lastReleaseTime = (notePairs.length - 1) * scaleDelay + 1.2 * scaleDelay;

    notePairs.forEach(([lower, upper], index) => {
      scaleNoteTimeoutsRef.current.push(setTimeout(() => {
        if (!scaleSamplerRef.current || !scaleSamplerRef.current.loaded) return;
        scaleSamplerRef.current.triggerAttack(Tone.Midi(lower).toNote(), undefined, 0.65);
        scaleSamplerRef.current.triggerAttack(Tone.Midi(upper).toNote(), undefined, 0.65);
      }, index * scaleDelay));
      scaleNoteTimeoutsRef.current.push(setTimeout(() => {
        if (!scaleSamplerRef.current || !scaleSamplerRef.current.loaded) return;
        scaleSamplerRef.current.triggerRelease(Tone.Midi(upper).toNote());
        if (index > 0) scaleSamplerRef.current.triggerRelease(Tone.Midi(lower).toNote());
      }, index * scaleDelay + 1.2 * scaleDelay));
    });

    // Release the held bottom note at the end of the scale
    scaleNoteTimeoutsRef.current.push(setTimeout(() => {
      if (!scaleSamplerRef.current || !scaleSamplerRef.current.loaded) return;
      scaleSamplerRef.current.triggerRelease(bottomNote);
    }, lastReleaseTime));
  }

  // onCLick
  // keepSounding: notes whose samples should keep ringing through the clear.
  // Resuming playback clears the note lists so they can be rebuilt correctly for
  // the new playing state, but a note that is sounding now and should still be
  // sounding at the resume point has no reason to be released and struck again -
  // it just needs to move into the right list.
  const clearAllNotes = (keepSounding?: number[]) => {
    const keepSoundingSet = new Set(keepSounding || []);
    const allNotes = [...new Set([...activeNotes, ...heldNotes, ...toggledNotes])];

    // Turn off sound to stop any stuck sampler notes
    const wasSoundOn = soundOn && audioInitialized && Tone.getContext().state === 'running'; // Remember the original state
    if (wasSoundOn) { Tone.getTransport().pause(); }

    if (sustainRef.current) {
      sustainRef.current = false;
      if (midiEventMergerRef.current) {
        const sustainOffData = [0xB0, 64, 0]; // CC 64 (sustain) value 0 (off)
        midiEventMergerRef.current.handleFileMidi(sustainOffData, false, false);
        // Clear all sustain states in the merger to prevent hanging notes
        midiEventMergerRef.current.clearAllSustainStates();
      }
    }
    // Nothing is sounding from MIDI after this point, so any note-off that
    // arrives later (playback resuming past notes that started earlier) must
    // not be treated as a sounding note the pedal can hold. The audio handler
    // has to forget too, or it would read the next attack of a note we just
    // released as a restrike and skip it.
    midiActiveNotesRef.current.clear();
    if (audioHandlerRef.current) {
      audioHandlerRef.current.clearNoteTracking(keepSounding);
    }

    if (allNotes.length > 0) {
      setActiveNotes([]);
      setHeldNotes([]);
      setToggledNotes([]);
      setIsScale(false);
      setChordSymbol('');
      setCsOptionsSegments([]);
      setPcWeightsSorted([]);
      if (shouldPlaySoundRef.current && samplerRef.current) {
        allNotes.forEach(note => {
          if (keepSoundingSet.has(note)) return; // deliberately left ringing
          const noteName = Tone.Midi(note).toNote();
          samplerRef.current!.triggerRelease(noteName);
          // Loud-layer notes (velocity >= 100) live in the other sampler and
          // would otherwise keep ringing after everything else is cleared
          if (samplerLoudRef.current && samplerLoudRef.current.loaded) {
            samplerLoudRef.current.triggerRelease(noteName);
          }
        });
      }
    }

    // Turn sound back on if it was originally on. Small delay to ensure the sound off takes effect first
    if (wasSoundOn) { setTimeout(() => Tone.getTransport().start(), 10); }

  };



  const nudgeToggledNotes = (amount: number) => {
    const allNotes = [...new Set([...heldNotes, ...toggledNotes])];
    if (heldNotes.length > 0) {
      setHeldNotes(heldNotes.map(note => note + amount));
    }
    if (toggledNotes.length > 0) {
      setToggledNotes(toggledNotes.map(note => note + amount));
    }
    if (shouldPlaySoundRef.current && samplerRef.current) {
      allNotes.forEach(note => {
        // if (activeNotes.includes(note) || heldNotes.includes(note) || toggledNotes.includes(note)) {
        samplerRef.current!.triggerRelease(Tone.Midi(note).toNote());
        // }
      });
    }
  };

  const syncPalettes = (email: string) => {
    const usersRef = collection(firestore, 'users')
    const userDoc = doc(usersRef, email)
    getDoc(userDoc).then(snapshot => {
      const data = snapshot.data()
      if (!data) return
      const userPalettes = data['labelSets']
      setUserPalettes(userPalettes)
      setAllPalettes([...defaultPalettes, ...userPalettes])
    })
  }

  // NEW ADDITION ACRYLICODE
  // /////////////////
  // const syncSongs = (email: string) => {
  //   const usersRef = collection(firestore, 'users')
  //   const userDoc = doc(usersRef, email)
  //   getDoc(userDoc).then(snapshot => {
  //     const data = snapshot.data()
  //     if (!data) return
  //     const songs = data['songs']
  //     setUserSongs(songs)
  //     setAllSongs([...defaultSongs, ...userSongs])
  //   })
  // }


  const navigate = useNavigate()

  const clearAllEmos = () => {
    setEmoClassLabelsMajor(defaultEmoClassLabelsMajor);
    setEmoClassLabelsMinor(defaultEmoClassLabelsMinor);
  };

  const [email, setEmail] = useState('')
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        if (user && user.email) {
          setEmail(user.email)
          syncPalettes(user.email)
          // NEW ADDITION ACRYLICODE
          // syncSongs(user.email)
        } else {
          setEmail('')
          clearAllEmos()
          navigate("/login") // Comment out for offline work
        }
      },
      (e) => {
        // console.log('error', e)
      }
    )
    return unsubscribe
  }, [])

  {/*  Pie Chart/Chord Compass Below */ }
  const pcClass = ['p-root', 'p-fifth', 'maj-second', 'maj-sixth', 'maj-third', 'maj-seventh', 'tritone', 'min-second', 'min-sixth', 'min-third', 'min-seventh', 'p-fourth'];
  // const pcClass = ['p-root', 'min-second', 'maj-second', 'min-third', 'maj-third', 'p-fourth', 'tritone', 'p-fifth', 'min-sixth', 'maj-sixth', 'min-seventh', 'maj-seventh'];
  const emoClass = ['emo-p-root', 'emo-p-fifth', 'emo-maj-second', 'emo-maj-sixth', 'emo-maj-third', 'emo-maj-seventh', 'emo-tritone', 'emo-min-second', 'emo-min-sixth', 'emo-min-third', 'emo-min-seventh', 'emo-p-fourth'];

  // abbreviated pitch class labels
  const pcClassNameLabels = ['Root', 'Perf 5', 'Maj 2', 'Maj 6', 'Maj 3', 'Maj 7', 'Tritone', 'Min 2', 'Min 6', 'Min 3', 'Min 7', 'Perf 4'];
  const pcMajClassNameLabels = ['Root', 'Perf 5', 'Maj 2', 'Maj 6', 'Maj 3', 'Maj 7', 'Aug 4', 'Min 2', 'Min 6', 'Min 3', 'Dom 7', 'Perf 4'];
  const pcMinClassNameLabels = ['Root', 'Perf 5', 'Maj 2', 'Maj 6', 'Maj 3', 'Maj 7', 'Dim 5', 'Min 2', 'Min 6', 'Min 3', 'Min 7', 'Perf 4'];

  //  abbreviated pitch class labels
  //  const pcClassLabels = ['Root', 'P5', 'M2', 'M6', 'M3', 'M7', 'TT', 'm2', 'm6', 'm3', 'm7', 'P4'];
  //  const pcMajClassLabels = ['R', 'P5', 'M2', 'M6', 'M3', 'M7', 'A4', 'm2', 'm6', 'm3', 'D7', 'P4'];
  //  const pcMinClassLabels = ['R', 'P5', 'M2', 'M6', 'M3', 'M7', 'd5', 'm2', 'm6', 'm3', 'm7', 'P4'];

  // pitch class labels: Arabic Numbers
  const pcClassLabels = ['Root', '5th', '2nd', '6th', '3rd', '7th', '♯4th', '♭2nd', '♭6th', '♭3rd', '♭7th', '4th'];
  const pcMajClassLabels = ['Root', '5th', '2nd', '6th', '3rd', '7th', '♯4th', '♭2nd', '♭6th', '♭3rd', '♭7th', '4th'];
  const pcMinClassLabels = ['Root', '5th', '2nd', '6th', '3rd', '7th', '♭5th', '♭2nd', '♭6th', '♭3rd', '♭7th', '4th'];

  // abbreviated pitch class labels: Arabic Numbers
  // const pcClassLabels = ['Root', '5', '2', '6', '3', '7', '♯4', '♭2', '♭6', '♭3', '♭7', '4'];
  // const pcMajClassLabels = ['1', '5', '2', '6', '3', '7', '♯4', '♭2', '♭6', '♭3', '♭7', '4'];
  // const pcMinClassLabels = ['1', '5', '2', '6', '3', '7', '♭5', '♭2', '♭6', '♭3', '♭7', '4'];

  // // abbreviated scale degree labels
  // const sdMajClassLabels = ['Tonic', 'Perf 5', 'Maj 2', 'Maj 6', 'Maj 3', 'Maj 7', 'Aug 4', 'Min 2', 'Min 6', 'Min 3', 'Dom 7', 'Perf 4'];
  // const sdMinClassLabels = ['Tonic', 'Perf 5', 'Maj 2', 'Maj 6', 'Maj 3', 'Maj 7', 'Dim 5', 'Min 2', 'Min 6', 'Min 3', 'Min 7', 'Perf 4'];

  // // abbreviated scale degree labels: Roman Numerals
  // const sdMajClassLabels = ['I', 'V', 'II', 'VI', 'III', 'VII', '♯IV', '♭II', '♭VI', '♭III', '♭VII', 'IV'];
  // const sdMinClassLabels = ['I', 'V', 'II', 'VI', 'III', 'VII', '♭V', '♭II', '♭VI', '♭III', '♭VII', 'IV'];

  // // abbreviated scale degree labels: Arabic Numbers
  // const sdMajClassLabels = ['1', '5', '2', '6', '3', '7', '♯4', '♭2', '♭6', '♭3', '♭7', '4'];
  // const sdMinClassLabels = ['1', '5', '2', '6', '3', '7', '♭5', '♭2', '♭6', '♭3', '♭7', '4'];

  // abbreviated scale degree labels: Arabic Numbers
  const sdMajClassLabels = ['Tonic', '5th', '2nd', '6th', '3rd', '7th', '♯4th', '♭2nd', '♭6th', '♭3rd', '♭7th', '4th'];
  const sdMinClassLabels = ['Tonic', '5th', '2nd', '6th', '3rd', '7th', '♭5th', '♭2nd', '♭6th', '♭3rd', '♭7th', '4th'];

  // const defaultEmoClassLabelsMajor = ['Peace', 'Brightness', 'Yearning', 'Enchantment', 'Eeriness', 'Mysticism', 'Fortitude', 'Anticipation', 'Suspense', '\u00A0existence', '\u00A0prominence', 'Hope']
  // const defaultEmoClassLabelsMinor = ['Bewitchment', 'Rebellion', 'Despair', 'Dread', 'Gloom', 'Apprehension', 'Sadness', 'Pensiveness', 'Wistfulness', '\u00A0perserverance', '\u00A0solemnity', 'Longing']

  // create state for each descriptor
  const [emoClassLabelsMajor, setEmoClassLabelsMajor] = useState<string[]>(defaultPalettes[0].majorLabels);
  const [emoClassLabelsMinor, setEmoClassLabelsMinor] = useState<string[]>(defaultPalettes[0].minorLabels);

  // create a handler for changes
  const handleInputChangeMajor = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmoClassLabelsMajor = [...emoClassLabelsMajor];
    newEmoClassLabelsMajor[index] = event.target.value;
    setEmoClassLabelsMajor(newEmoClassLabelsMajor);
  };

  const handleInputChangeMinor = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmoClassLabelsMinor = [...emoClassLabelsMinor];
    newEmoClassLabelsMinor[index] = event.target.value;
    setEmoClassLabelsMinor(newEmoClassLabelsMinor);
  };

  // Choose the appropriate labels based on the mode
  const [emoClassLabels, setEmoClassLabels] = useState<string[]>(mode === 0 ? defaultEmoClassLabelsMajor : defaultEmoClassLabelsMinor);


  // const reorder = [9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8];  // adjust as needed
  const reorder = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];  // adjust as needed
  // const reorder = [9, 4, 11, 6, 1, 8, 3, 10, 5, 0, 7, 2];  // adjust as needed


  // const rootNote = Math.min(...midiNotes); // Math.min uses the lowest midi note
  // const [chordSymbols, setChordSymbols] = useState<string | null>('No Chord');
  const [chordSymbols, setChordSymbols] = useState<string>("loading...");
  const [chordSymbol, setChordSymbol] = useState<string>(" ");
  const [chordSymbolTwo, setChordSymbolTwo] = useState<string>(" ");
  const [rootNote, setRootNote] = useState<number>(0);  // default root note
  const [scaleRoot, setScaleRoot] = useState<number>(0);  // default scale root note
  const [csOptions, setCsOptions] = useState<string[]>([]);
  type ChordSegment = {
    root_pc: number;
    third_quality: string;
    third_quality_wbn: string;
    extension: string;
    extension_wbn: string;
    bass_pc: number;
    weight: number;
    mode: number;
    chord_qual: number;
  };
  const [csOptionsSegments, setCsOptionsSegments] = useState<ChordSegment[]>([]);
  // The notes the current analysis (csOptionsSegments and the rest) is of.
  const [analyzedNotes, setAnalyzedNotes] = useState<number[]>([]);
  // const root_pc = rootNote % 12;


  // Convert chromatic pitch classes to circle of 5ths pitch classes
  const chromaticToFifths = function (note: number, rootNote: number = 0) {
    return ((note + 12 - rootNote) * 7) % 12;
  };

  // Convert MIDI notes to pitch classes
  const midiToPcs = (midiNotes: number[]): number[] => {
    const pitchClasses = midiNotes.map(note => note % 12);
    return [...new Set(pitchClasses)]; // Remove duplicates
  };



  // useEffects Below //


  // CSS variables for theme colors
  useEffect(() => {
    const backgroundColor = settings.appTheme === 'dark' ? 'rgb(25, 33, 35)' :
      settings.appTheme === 'light' ? 'rgb(89, 89, 89)' :
        'rgb(44, 44, 42)'; // warm
    document.documentElement.style.setProperty('--app-background-color', backgroundColor);
    setSettings(prev => ({
      ...prev,
      preferWhiteStaffPaper: settings.appTheme === 'dark' ? false : true,
    }));
  }, [settings.appTheme]);

  // Active Notes Ref
  // Pressing a key over a selected chart chord lets go of the chord's notes.
  // Only a press: the MIDI file moves notes between lists without one, and the
  // chart keeps its selection through the file's playing. Pausing moves the
  // file's notes out of activeNotes into toggledNotes, which this must not
  // clear; starting play moves notes already ringing into activeNotes in the
  // render that empties toggledNotes, while toggledNotesRef still lists them,
  // so releasing here would cut them off.
  useEffect(() => {
    const previous = activeNotesRef.current;
    activeNotesRef.current = activeNotes;
    const pressed = activeNotes.some(note => !previous.includes(note));
    const filePlaying = midiControllerRef.current !== null && midiControllerRef.current.isPlaying();
    // A region being corrected keeps its notes on the keys under a press
    const editingRegion = midiControllerRef.current !== null && midiControllerRef.current.isEditingRegion();
    if (pressed && !filePlaying && !editingRegion && selectedChordIndexRef.current !== null && !chordEditModeRef.current) {
      releaseToggledNotes();
      setToggledNotes([]);
    }
  }, [activeNotes]);

  // Toggled Notes Ref
  useEffect(() => {
    toggledNotesRef.current = toggledNotes;
  }, [toggledNotes]);

  // Held Notes Ref
  useEffect(() => {
    heldNotesRef.current = heldNotes;
  }, [heldNotes]);

  useEffect(() => {
    chordEditModeRef.current = chordEditMode;
  }, [chordEditMode]);

  useEffect(() => {
    selectedChordOptionIndexRef.current = selectedChordOptionIndex;
  }, [selectedChordOptionIndex]);

  useEffect(() => {
    separatorEditModeRef.current = separatorEditMode;
  }, [separatorEditMode]);

  useEffect(() => {
    selectedSeparatorTypeIndexRef.current = selectedSeparatorTypeIndex;
  }, [selectedSeparatorTypeIndex]);


  // Clear Chord Symbol(s) function
  const clearChordData = () => {
    setChordSymbols('');
    setChordSymbol('');
    setChordSymbolTwo('');
    setCsOptionsSegments([]);
    setPcWeightsSorted([]); //
    setPcSorted([]); //
    setActiveEmoLabels([]);
    setMidiNotes([]); //
  };

  // Clear Chord Symbol(s) data when no notes are present
  useEffect(() => {
    if (!sustain && activeNotes.length === 0 && heldNotes.length === 0 && toggledNotes.length === 0) {
      clearChordData();
    }
  }, [sustain, activeNotes, heldNotes, toggledNotes]);

  // Monitor csOptionsSegments changes
  // useEffect(() => {
  //   console.log("csOptionsSegments updated:", csOptionsSegments);
  // }, [csOptionsSegments]);

  // SustainRef
  useEffect(() => {
    sustainRef.current = sustain;
  }, [sustain]);


  const lastNoteOffTimeRef = useRef<number>(0);


  // restoreOnly: this event is restoring a note that is already sounding into the
  // correct list (see MidiEventMerger.handleFileMidi). There is no onset, so the
  // key must not flash - the flash animation starts on a black overlay, which on
  // a key that is already lit reads as the note blinking off and back on.
  const handleMidiData = useCallback((data: number[], restoreOnly: boolean = false) => {

    // Log the MIDI data
    // console.log('MIDI data', data); // MIDI data [144, 63, 73]

    if (data[0] === 248 || data[0] === 254 || data[0] === 214) { // If it's a timing clock or active sensing message
      return; // Just ignore it and exit the function early
    }
    // if (data[0] !== 248 && data[0] !== 254 && data[0] !== 214) {
    //   // console.log('MIDI data', data); // *Latest Debugging*
    // }

    if (data.length < 2) {
      console.error('Unexpected MIDI data:', data);
      return;
    }

    if (typeof data[1] !== 'number' || data[1] < 0 || data[1] > 127) {
      console.error('Invalid MIDI note number - data[1]:', data[1], 'data[0]:', data[0], 'Full data:', data, 'data.length:', data.length);
      return;
    }

    let status = data[0] & 0xF0; // Mask out the channel bits

    // if ((data[0] === 144 || data[0] === 150 || data[0] === 148) && data[2] > 0) {  // Note on event, 144 for most midi controllers, 150 for old Alesis Vi controller
    if (status === 144 && data[2] > 0) { // Note On event for any channel
      midiActiveNotesRef.current.add(data[1]);

      if (!restoreOnly) {
        // Flash the played note and its overtones if enabled
        const notesToFlash = showOvertonesRef.current ?
          [data[1], ...getOvertoneNotes([data[1]])] :
          [data[1]];
        setKeyflash(prevNotes => [...prevNotes, ...notesToFlash]);

        // Calculate all pitch classes to flash (primary + overtones)
        const pcsToFlash = notesToFlash.map(midiNote => chromaticToFifths(midiNote, rootNoteRef.current));

        // Flash all pitch classes at once
        setLabelFlashes(prev => {
          let updated = { ...prev };
          pcsToFlash.forEach(pc => updated[pc] = true);
          return updated;
        });
        setPcLabelFlashes(prev => {
          let updated = { ...prev };
          pcsToFlash.forEach(pc => updated[pc] = true);
          return updated;
        });

        setTimeout(() => {
          setKeyflash(prevNotes => prevNotes.filter(note => !notesToFlash.includes(note)));

          // Clear all pitch class flashes at once
          setLabelFlashes(prev => {
            let updated = { ...prev };
            pcsToFlash.forEach(pc => updated[pc] = false);
            return updated;
          });
          setPcLabelFlashes(prev => {
            let updated = { ...prev };
            pcsToFlash.forEach(pc => updated[pc] = false);
            return updated;
          });
        }, 65);
      }

      setActiveNotes(prevNotes => [...prevNotes, data[1]]);
      // setToggledNotes([]); // Clear toggledNotes, OLD METHOD, now handled in midiAudioHandler.ts and keeps toggled chart chord active
      // if (selectedChordIndexRef.current !== null) {
      //   setToggledNotes([]);
      // }

      // if (shouldPlaySoundRef.current) { // Audio moved to midiAudioHandler.ts July 24, 2025

      //   // const noteName = midiToName(data[1], true);
      //   const noteName = Tone.Midi(data[1]).toNote(); // Audio moved to midiAudioHandler.ts July 24, 2025
      //   // const volumeDb = velocityToDb(data[2]); // convert MIDI velocity to dB for tone.js
      //   const velocityNormd = Math.min(Math.max((data[2] + 1) / (127 + 20), 0), 1); // Audio moved to midiAudioHandler.ts July 24, 2025

      //   // console.log("Note on event captured:", noteName); // Debug log // *Latest Debugging*
      //   // console.log("Note volume in dB", volumeDb)
      //   // console.log("samplerRef.current, shouldPlaySoundRef", samplerRef.current, shouldPlaySoundRef.current) // *Latest Debugging*
      //   // sampler.triggerAttack(noteName, undefined, velocity);

      //   // Check if the note is already active, held, or toggled (sustainRef tells us that)
      //   // and release the sounding sample to prep for retriggering
      //   if (activeNotes.includes(data[1]) && sustainRef.current && samplerRef.current && shouldPlaySoundRef.current) { // Audio moved to midiAudioHandler.ts July 24, 2025
      //     samplerRef.current.triggerRelease(Tone.Midi(data[1]).toNote()); // Audio moved to midiAudioHandler.ts July 24, 2025
      //   } // Audio moved to midiAudioHandler.ts July 24, 2025

      //   if (samplerRef.current && shouldPlaySoundRef.current) { // Audio moved to midiAudioHandler.ts July 24, 2025
      //     // samplerRef.current.volume.value = volumeDb;
      //     // samplerRef.current.volume.value = -13;
      //     // samplerRef.current.triggerAttack(Tone.Midi(data[1]).toNote());
      //     samplerRef.current.triggerAttack(Tone.Midi(data[1]).toNote(), undefined, velocityNormd); // Audio moved to midiAudioHandler.ts July 24, 2025
      //   } // Audio moved to midiAudioHandler.ts July 24, 2025
      // } // Audio moved to midiAudioHandler.ts July 24, 2025

      // } else if (data[0] === 128 || data[0] === 134 || ((data[0] === 144 || data[0] === 148) && data[2] === 0)) { // Note off event
    } else if ((status === 128 || (status === 144 && data[2] === 0))) { // Note Off event for any channel
      lastNoteOffTimeRef.current = Date.now();
      // Only a note that is actually sounding can be carried by the pedal.
      // Resuming playback after scrubbing replays the note-offs of notes that
      // started before the resume point (their note-ons are behind the stream's
      // start, and clearAllNotes already silenced them), so without this check
      // those stale note-offs light up notes that will never sound.
      const wasSounding = midiActiveNotesRef.current.delete(data[1]);
      if (sustainRef.current && !isScrubbingRef.current && wasSounding) {
        // If sustain is on, move note to heldNotes
        // setHeldNotes(prevNotes => [...prevNotes, data[1]]);
        setHeldNotes(prevNotes => {
          if (!prevNotes.includes(data[1])) {
            return [...prevNotes, data[1]];
          } else {
            return prevNotes;
          }
        });

      }
      // Remove note from activeNotes
      setActiveNotes(prevNotes => prevNotes.filter(note => note !== data[1]));

      // if (shouldPlaySoundRef.current && (!sustainRef.current || isScrubbingRef.current)) { // Audio moved to midiAudioHandler.ts July 24, 2025
      //   // const noteName = midiToName(data[1], true);
      //   const noteName = Tone.Midi(data[1]).toNote(); // Audio moved to midiAudioHandler.ts July 24, 2025
      //   // sampler.triggerRelease(noteName);
      //   if (samplerRef.current && shouldPlaySoundRef.current) { // Audio moved to midiAudioHandler.ts July 24, 2025
      //     samplerRef.current.triggerRelease(Tone.Midi(data[1]).toNote()); // Audio moved to midiAudioHandler.ts July 24, 2025
      //   } // Audio moved to midiAudioHandler.ts July 24, 2025
      // } // Audio moved to midiAudioHandler.ts July 24, 2025

    } else if (data[0] === 176 && data[1] === 64) { // Sustain pedal CC message

      if (data[2] >= 64) { // Pedal down (typically 127 for full press)
        sustainRef.current = true;
        setSustain(true); // Update visual indicator
      } else { // Pedal up (0)
        lastNoteOffTimeRef.current = Date.now();
        sustainRef.current = false;
        setSustain(false); // Update visual indicator

        // When pedal is released, clear heldNotes
        setHeldNotes([]);
        // setToggledNotes([]); // Clear toggledNotes

        // if (samplerRef.current && shouldPlaySoundRef) { // Audio moved to midiAudioHandler.ts July 24, 2025
        //   // const noteNames = heldNotesRef.current.map(midiNote => Tone.Midi(midiNote).toNote());
        //   const noteNames = heldNotesRef.current // Audio moved to midiAudioHandler.ts July 24, 2025
        //     .filter(midiNote => !activeNotesRef.current.includes(midiNote)) // Audio moved to midiAudioHandler.ts July 24, 2025
        //     .map(midiNote => Tone.Midi(midiNote).toNote()); // Audio moved to midiAudioHandler.ts July 24, 2025
        //   samplerRef.current.triggerRelease(noteNames); // Audio moved to midiAudioHandler.ts July 24, 2025
        // } // Audio moved to midiAudioHandler.ts July 24, 2025
      }
    }
  }, [
    setKeyflash, setLabelFlashes, setPcLabelFlashes, setActiveNotes, setToggledNotes, setHeldNotes,
    activeNotes, sustainRef, samplerRef, shouldPlaySoundRef, activeNotesRef, heldNotesRef,
    chromaticToFifths, rootNoteRef, selectedChordIndexRef, lastNoteOffTimeRef
  ]);

  // Handler for paused file MIDI - updates toggledNotes instead of activeNotes
  const handlePausedFileMidi = useCallback((data: number[]) => {
    if (data[0] === 248 || data[0] === 254 || data[0] === 214) {
      return;
    }

    if (data.length < 2) {
      console.error('Unexpected MIDI data:', data);
      return;
    }

    if (typeof data[1] !== 'number' || data[1] < 0 || data[1] > 127) {
      console.error('Invalid MIDI note number:', data[1]);
      return;
    }

    let status = data[0] & 0xF0;

    if (status === 144 && data[2] > 0) { // Note On
      let pc = chromaticToFifths(data[1], rootNoteRef.current);
      const notesToFlash = settings.showOvertones ?
        [data[1], ...getOvertoneNotes([data[1]])] :
        [data[1]];
      setKeyflash(prevNotes => [...prevNotes, ...notesToFlash]);
      setLabelFlashes(prev => ({ ...prev, [pc]: true }));
      setPcLabelFlashes(prev => ({ ...prev, [pc]: true }));
      setTimeout(() => setKeyflash(prevNotes => prevNotes.filter(note => !notesToFlash.includes(note))), 65);
      setTimeout(() => {
        setLabelFlashes(prev => {
          let updated = { ...prev };
          updated[pc] = false;
          return updated;
        });
        setPcLabelFlashes(prev => {
          let updated = { ...prev };
          updated[pc] = false;
          return updated;
        });
      }, 65);

      // Update toggledNotes instead of activeNotes when paused
      // The playhead owns this note again, so the pedal no longer does
      setHeldNotes(prevNotes => prevNotes.filter(note => note !== data[1]));
      setToggledNotes(prevNotes => [...prevNotes, data[1]]);

    } else if ((status === 128 || (status === 144 && data[2] === 0))) { // Note Off
      // The file has moved past this note. With a live sustain pedal down it is
      // still sounding (midiAudioHandler declines to release it), so it has to
      // stay visible too - the pedal is carrying it now, exactly as it carries a
      // note released on the keyboard, which is what heldNotes means. Dragging
      // the scrub bar is excluded so notes don't pile up across a fast drag,
      // matching the release rule on the audio side.
      if (sustainRef.current && !isScrubbingRef.current) {
        setHeldNotes(prevNotes => {
          if (!prevNotes.includes(data[1])) {
            return [...prevNotes, data[1]];
          } else {
            return prevNotes;
          }
        });
      }
      // Remove note from toggledNotes
      setToggledNotes(prevNotes => prevNotes.filter(note => note !== data[1]));
    }
    // Ignore sustain messages for paused file MIDI
  }, [
    setKeyflash, setLabelFlashes, setPcLabelFlashes, setToggledNotes, setHeldNotes,
    chromaticToFifths, rootNoteRef, settings.showOvertones, getOvertoneNotes
  ]);

  useEffect(() => {
    const audioHandler = new MidiAudioHandler(
      samplerRef,
      samplerLoudRef,
      shouldPlaySoundRef,
      sustainRef,
      isScrubbingRef,
      activeNotesRef,
      heldNotesRef,
      selectedInstrumentRef,
      toggledNotesRef
    );
    audioHandlerRef.current = audioHandler;
    // Create merger once, it will use current handleMidiData via closure
    midiEventMergerRef.current = new MidiEventMerger((data: number[], restoreOnly?: boolean) => {
      // Always use the latest handleMidiData function
      handleMidiData(data, restoreOnly);
    }, audioHandler);
    // Register paused file callback
    midiEventMergerRef.current.setPausedFileCallback((data: number[]) => {
      handlePausedFileMidi(data);
    });
  }, []); // No dependencies - create once

  // Single MIDI message handler function - defined outside useEffect to prevent duplicates
  const getMIDIMessage = useCallback((midiMessage: WebMidi.MIDIMessageEvent) => {
    // Check if this message is from the selected device
    // Filter out messages from devices that don't match the selected input device
    if (selectedInputIdRef.current !== "" && selectedInputIdRef.current !== null && selectedInputIdRef.current !== "no-device") {
      const target = midiMessage.target as WebMidi.MIDIInput;
      if (target && target.id !== selectedInputIdRef.current) {
        return; // Ignore messages from non-selected devices
      }
    }

    // let data = midiMessage.data; // commented out to: Skip any data conversion - direct array access for minimal latency
    const data = [midiMessage.data[0], midiMessage.data[1], midiMessage.data[2]]; // direct array access for minimal latency

    // Use the merger for live MIDI
    if (midiEventMergerRef.current && selectedInputIdRef.current !== "no-device") {
      // midiEventMergerRef.current.handleLiveMidi(Array.from(data)); // direct array access for minimal latency below
      midiEventMergerRef.current.handleLiveMidi(data); // direct array access for minimal latency
    }
  }, []);

  // MIDI Events
  useEffect(() => {
    if (!navigator.requestMIDIAccess) {
      return;
    }

    function setupMidiHandlers(midiAccess: WebMidi.MIDIAccess) {
      // Release stuck MIDI notes (activeNotes/heldNotes) when changing devices,
      // but preserve toggledNotes so chord chart selection stays visible.
      const midiStuckNotes = [...new Set([...activeNotesRef.current, ...heldNotesRef.current])];
      if (sustainRef.current) {
        sustainRef.current = false;
        if (midiEventMergerRef.current) {
          const sustainOffData = [0xB0, 64, 0];
          midiEventMergerRef.current.handleFileMidi(sustainOffData, false, false);
          midiEventMergerRef.current.clearAllSustainStates();
        }
      }
      if (midiStuckNotes.length > 0) {
        setActiveNotes([]);
        setHeldNotes([]);
        if (shouldPlaySoundRef.current && samplerRef.current) {
          midiStuckNotes.forEach(note => {
            samplerRef.current!.triggerRelease(Tone.Midi(note).toNote());
          });
        }
      }

      // Clear all existing handlers first - use null to completely remove handlers
      for (let input of midiAccess.inputs.values()) {
        input.onmidimessage = null as any;
      }

      // Set up handlers based on selectedInputId
      if (selectedInputId === "no-device") {
        return;
      } else if (selectedInputId === "" || selectedInputId === null) {
        for (let input of midiAccess.inputs.values()) {
          input.onmidimessage = getMIDIMessage;
        }
      } else {
        // Specific device selected
        const selectedInput = Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId);
        if (selectedInput) {
          selectedInput.onmidimessage = getMIDIMessage;
        }
      }
    }

    function onMIDISuccess(midiAccess: WebMidi.MIDIAccess) {
      setMidiAccess(midiAccess); // This triggers re-render which will call setupMidiHandlers via useEffect
    }

    function onMIDIFailure() {
      console.log('Could not access your MIDI devices.');
    }

    // If midiAccess already exists (device selection change), just update handlers
    if (midiAccess) {
      setupMidiHandlers(midiAccess);
    } else {
      // Initial setup - request MIDI access
      navigator.requestMIDIAccess({ sysex: false }).then(onMIDISuccess, onMIDIFailure);
    }
  }, [selectedInputId, midiAccess]); // Re-run when device selection OR midiAccess changes

  // Keep selectedInputIdRef updated
  useEffect(() => {
    selectedInputIdRef.current = selectedInputId;
  }, [selectedInputId]);

  // Keep selectedInstrumentRef updated
  useEffect(() => {
    selectedInstrumentRef.current = selectedInstrument;
  }, [selectedInstrument]);

  useEffect(() => {
    rootNoteRef.current = rootNote; // Update the ref with the latest rootNote
  }, [rootNote]); // Run this effect whenever rootNote changes

  useEffect(() => {
    showOvertonesRef.current = settings.showOvertones;
  }, [settings.showOvertones]);

  // Update currentCirclePosition when rotation completes
  useEffect(() => {
    setCurrentCirclePosition(rootNote);
  }, [rootNote]);



  const getShortestFifthsDistance = (from: number, to: number) => {
    const fromFifths = chromaticToFifths(from, 0);
    const toFifths = chromaticToFifths(to, 0);

    // Calculate the direct distance
    let directDist = toFifths - fromFifths;

    // Instead of wrapping to stay within -6 to 6,
    // we return the actual distance
    return directDist;
  };

  useEffect(() => {
    setPreviousRootNote(currentCirclePosition);  // Save the current position before starting new transition

    // Calculate the shortest distance for rotation
    const distance = getShortestFifthsDistance(currentCirclePosition, rootNote);

    // Update innerLabelRotation based on this distance
    const rotation = distance * (Math.PI / 6);

    // After transition duration, update the current position
    const transitionDuration = 300; // matches CSS transition duration
    const timer = setTimeout(() => {
      setCurrentCirclePosition(rootNote);
    }, transitionDuration);

    return () => {
      clearTimeout(timer);  // Clean up timer if component unmounts or rootNote changes again
    };
  }, [rootNote]);

  // Use useEffect to handle the state update
  useEffect(() => {
    if (previousRootNote !== rootNote) {
      let distToPositive = Math.abs(baseRotation - prevActualRotation);
      let distToNegative = Math.abs(negativeRotation - prevActualRotation);

      // Choose the closer rotation
      let newRotation = (distToNegative < distToPositive) ? negativeRotation : baseRotation;

      // Store the chosen rotation for next time
      setPrevActualRotation(newRotation);
    }
  }, [rootNote, previousRootNote]); // Only run when these values change



  useEffect(() => {
    const combinedMidi = [...activeNotes, ...heldNotes, ...toggledNotes];
    const newActivePcs = [...new Set(midiToPcs(combinedMidi))];
    setActivePcs(newActivePcs);
    if (settings.showOvertones) {
      const overtoneNotes = getOvertoneNotes(combinedMidi);
      const overtonePcs = midiToPcs(overtoneNotes);
      setActivePlusOvertonePcs([...new Set([...newActivePcs, ...overtonePcs])]);
    }
    if (newActivePcs.length == 0 && isScale == true) {
      setIsScale(false)
      // alert("set Scale to False")
    }
    setIsImpliedScale(newActivePcs.length > 0)
  }, [activeNotes, heldNotes, toggledNotes, settings.showOvertones]); // Run this effect whenever activeNotes, heldNotes, toggledNotes changes

  // const velocityNormd = Math.min(Math.max((data[2] + 1) / (127 + 20), 0), 1);

  useEffect(() => {
    const handleKeyEvent = (event: KeyboardEvent) => {

      if (!songTitle.isEditable && !composer.isEditable && !isInputtingTextRef.current) {

        // console.log("MASTER USEEFFECT HANDLEKEYEVENT: isInputtingTextRef.current", isInputtingTextRef.current)

        const midiNote = getMidiNoteFromKey(event.key);
        if (midiNote !== null) {

          if (event.repeat) return;  // Ignore repeated keydown events

          // Guitar and bass: each row of keys is a string, so one note per
          // row sounds, the one furthest right (the highest fret), as a
          // fretting hand allows. A key pressed under one held further right
          // is not heard; one pressed further right takes over the string.
          const stringPosition = isStringLayout ? STRING_KEY_POSITIONS[event.key] : undefined;

          if (event.type === "keydown") {
            if (stringPosition !== undefined) {
              const held = heldStringKeysRef.current.get(stringPosition.row);
              if (held && held.key !== event.key) {
                if (held.column > stringPosition.column) return;
                setActiveNotes(prevNotes => prevNotes.filter(note => note !== held.midiNote));
                if (shouldPlaySoundRef.current && samplerRef.current) {
                  samplerRef.current.triggerRelease(Tone.Midi(held.midiNote).toNote());
                }
              }
              heldStringKeysRef.current.set(stringPosition.row, { key: event.key, column: stringPosition.column, midiNote });
            }

            // console.log("Key pressed:", event.key); // *Latest Debugging*

            // Flash the played note and its overtones if enabled
            const notesToFlash = settings.showOvertones
              ? [midiNote, ...getOvertoneNotes([midiNote])]
              : [midiNote];

            // Calculate pitch classes for all notes to flash (including overtones)
            const pcsToFlash = notesToFlash.map(note => chromaticToFifths(note, rootNoteRef.current));

            setKeyflash(prevNotes => [...prevNotes, ...notesToFlash]);

            // Flash labels for all pitch classes (including overtones)
            pcsToFlash.forEach(pc => {
              setLabelFlashes(prev => ({ ...prev, [pc]: true }));
              setPcLabelFlashes(prev => ({ ...prev, [pc]: true }));
            });
            setTimeout(() => {
              setKeyflash(prevNotes => prevNotes.filter(note => !notesToFlash.includes(note)));
              pcsToFlash.forEach(pc => {
                setLabelFlashes(prev => {
                  let updated = { ...prev };
                  updated[pc] = false;
                  return updated;
                });
                setPcLabelFlashes(prev => {
                  let updated = { ...prev };
                  updated[pc] = false;
                  return updated;
                });
              });
            }, 65);

            setActiveNotes(prevNotes => [...prevNotes, midiNote]);
            // setToggledNotes([]);

            if (shouldPlaySoundRef.current) {

              const noteName = Tone.Midi(midiNote).toNote();
              // const noteName = midiToName(midiNote, true);

              // console.log("Key down event captured:", noteName); // *Latest Debugging*
              // console.log("samplerRef.current, shouldPlaySoundRef", samplerRef.current, shouldPlaySoundRef.current); // *Latest Debugging*

              if (activeNotes.includes(midiNote) && sustainRef.current && samplerRef.current) {
                samplerRef.current.triggerRelease(noteName);
              }

              if (samplerRef.current && samplerRef.current.loaded) {
                samplerRef.current.triggerAttack(noteName, undefined, 0.75);
              }
            }

          } else if (event.type === "keyup") {
            if (stringPosition !== undefined) {
              // A key that was not heard, or was taken over, has nothing to release
              const held = heldStringKeysRef.current.get(stringPosition.row);
              if (!held || held.key !== event.key) return;
              heldStringKeysRef.current.delete(stringPosition.row);
            }
            setActiveNotes(prevNotes => prevNotes.filter(note => note !== midiNote));

            if (shouldPlaySoundRef.current && samplerRef.current) {
              samplerRef.current.triggerRelease(Tone.Midi(midiNote).toNote());
            }
          }

        } else if (event.type === "keydown" && !isInputtingTextRef.current) {
          // console.log("isInputtingTextRef.current", isInputtingTextRef.current)
          if (event.key === 'V' && midiControllerRef.current && midiControllerRef.current.hasFile()) {
            // Shift+V shows or hides the selected chord regions, as V does
            // visibility in MuseScore and Dorico (lowercase v is a note)
            midiControllerRef.current.toggleSelectedEnabled();
            return;
          } else if (event.key === 'z' || event.key === 'x') {
            if (event.key === 'z' && octaveOffset > -48) {
              setOctaveOffset(octaveOffset - 12);
            } else if (event.key === 'x' && octaveOffset < 36) {
              setOctaveOffset(octaveOffset + 12);
            }
            return;
          } else if (event.shiftKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            // Shift+arrow slides the panel stack, as the scroll controls do;
            // the plain arrows stay with the chart.
            event.preventDefault();
            scrollPanels(event.key === 'ArrowLeft' ? -1 : 1);
          } else if (event.key === 'ArrowLeft') {
            if (separatorEditModeRef.current) {
              replaceSeparatorInChart(selectedSeparatorTypeIndexRef.current, true);
            } else if (chordEditModeRef.current) {
              replaceChordInChart(selectedChordOptionIndexRef.current, false);
            }
            chartNavigation('backward');
          } else if (event.key === 'ArrowRight') {
            if (separatorEditModeRef.current) {
              replaceSeparatorInChart(selectedSeparatorTypeIndexRef.current, true);
            } else if (chordEditModeRef.current) {
              replaceChordInChart(selectedChordOptionIndexRef.current, false);
            }
            chartNavigation('forward');
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (separatorEditModeRef.current) {
              navigateSeparatorEditTypes('up');
            } else if (chordEditModeRef.current) {
              navigateChordEditOptions('up');
            } else {
              chartNavigation('jump-backward');
            }
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (separatorEditModeRef.current) {
              navigateSeparatorEditTypes('down');
            } else if (chordEditModeRef.current) {
              navigateChordEditOptions('down');
            } else {
              chartNavigation('jump-forward');
            }
          } else if (event.key === 'Enter') {
            event.preventDefault();
            if (midiControllerRef.current && midiControllerRef.current.isEditingRegion()) {
              // Done correcting the region's notes
              midiControllerRef.current.stopEditingRegion();
            } else if (separatorEditModeRef.current) {
              replaceSeparatorInChart(selectedSeparatorTypeIndexRef.current, true);
            } else {
              handleChordEditAction();
            }
          } else if (event.key === '/') {
            if (separatorEditModeRef.current) {
              replaceSeparatorInChart(selectedSeparatorTypeIndexRef.current, true);
            }
            addEntryToChart(-1, true);
            setSelectedSeparatorTypeIndex(0);
            setSeparatorEditMode(true);
          } else if (event.key === '=') {
            event.preventDefault();
            addEntryToChart(-1, false);
          } else if (event.key === ' ' && event.shiftKey) {
            event.preventDefault();
            // With a MIDI file loaded, Shift+Space belongs to its player
            if (midiControllerRef.current && midiControllerRef.current.hasFile()) {
              midiControllerRef.current.togglePlayback();
            } else if (chordChart.length > 0) {
              if (chordEditModeRef.current) {
                playChord();
              } else {
                setPlaySongOn(prevState => !prevState);
              }
            } else {
              playChord();
            }
          } else if (event.key === ' ') {
            event.preventDefault();
            playChord();
          } else if (event.key === 'Backspace' || event.key === 'Delete') {
            // Only with the chart on screen, where the deleted entry can be seen
            if (selectedChordIndex !== null && chartPanelShown) {
              deleteChordFromChart(selectedChordIndex);
              releaseToggledNotes();
              setToggledNotes([]);
            }
          } else if (event.key === 'Escape') {
            if (midiControllerRef.current && midiControllerRef.current.isEditingRegion()) {
              midiControllerRef.current.stopEditingRegion();
            } else if (separatorEditModeRef.current) {
              setSeparatorEditMode(false);
            } else if (chordEditModeRef.current) {
              setChordEditMode(false);
            } else if (selectedChordIndex != null) {
              setSelectedChordIndex(null);
              releaseToggledNotes();
              setToggledNotes([]);
            }
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyEvent);
    window.addEventListener("keyup", handleKeyEvent);
    return () => {
      window.removeEventListener("keydown", handleKeyEvent);
      window.removeEventListener("keyup", handleKeyEvent);
    };
  }, [octaveOffset, selectedChordIndex, chordChart, chordSymbol, csOptionsSegments, songTitle, composer, isInputtingTextRef, lockedTonicNote, lockedTonicMode, isArpeggiateOn, scaleDescending, isScale, chartPanelShown, chordSymbolPanelShown, soundOn, audioInitialized, selectedInstrument]);


  // True while chord-analysis requests are failing, so the user knows why the display stopped updating
  const [serverUnreachable, setServerUnreachable] = useState(false);

  // Hybrid debounce: resets on each note change (MIDI_DEBOUNCE_WAIT), but fires unconditionally after MIDI_DEBOUNCE_MAX_WAIT
  const MIDI_DEBOUNCE_WAIT = 25;
  const MIDI_DEBOUNCE_MAX_WAIT = 70;
  const lastRequestTimeRef = useRef<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceStartTimeRef = useRef<number>(0);

  // Analysis answers already received, by request, so notes heard before —
  // stepping back through a chart, a repeated voicing — show without waiting
  // on the server. The answer depends only on what is sent, and the server
  // sorts the notes it is sent, so the key sorts them too. A hit moves to the
  // back, so past the cap the answer dropped is the one used least recently.
  const ANALYSIS_CACHE_MAX = 500;
  const analysisCacheRef = useRef(new Map<string, any>());
  // Each request's number, and the number of the newest answer shown. A cached
  // answer arrives at once, so an older request's answer can land after it;
  // it must not replace what is showing.
  const analysisRequestSeqRef = useRef(0);
  const analysisShownSeqRef = useRef(0);

  useEffect(() => {
    const computedMidiNotes = [...new Set([...activeNotes, ...heldNotes, ...toggledNotes])];
    setMidiNotes(computedMidiNotes);

    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    } else {
      debounceStartTimeRef.current = Date.now(); // start of a new burst
    }

    if (computedMidiNotes.length >= 1) { // >= 1
      // Include overtones if showOvertones setting is enabled
      const finalNotes = settings.showOvertones
        ? [...computedMidiNotes, ...getOvertoneNotes(computedMidiNotes)]
        : computedMidiNotes;
      const data = { notes: finalNotes, keyCenter: lockedTonicNote !== null ? lockedTonicNote : -1, keyMode: lockedTonicMode, notationLegible: settings.preferNotationLegible, preferModeBorrowing: settings.preferModeBorrowing };
      // console.log('#### data: ############', data); // *Latest Debugging*

      const elapsed = Date.now() - debounceStartTimeRef.current;

      const fireRequest = () => {
        debounceStartTimeRef.current = Date.now(); // reset for next burst
        lastRequestTimeRef.current = Date.now();

        // Send a POST request to server

        // axios.post('http://localhost:5000/sentiserver', data)
        // axios.post('http://127.0.0.1:5000/sentiserver', data)

        const seq = ++analysisRequestSeqRef.current;
        const cache = analysisCacheRef.current;
        const cacheKey = JSON.stringify({ ...data, notes: [...data.notes].sort((a, b) => a - b) });
        const cached = cache.get(cacheKey);
        let request: Promise<{ data: any }>;
        if (cached !== undefined) {
          cache.delete(cacheKey);
          cache.set(cacheKey, cached);
          request = Promise.resolve({ data: cached });
        } else {
          console.log('Sending data to server:', data.notes, performance.now().toFixed(0) + 'ms');
          request = axios.post(server, data).then(response => {
            setServerUnreachable(false);
            cache.set(cacheKey, response.data);
            if (cache.size > ANALYSIS_CACHE_MAX) cache.delete(cache.keys().next().value);
            return response;
          });
        }
        request
          .then(response => {

            // An answer to a newer request is already showing
            if (seq < analysisShownSeqRef.current) return;

            // Discard stale responses: accept only if there's overlap between request notes and currently playing notes
            const requestNoteSet = new Set(computedMidiNotes);
            const currentNoteSet = new Set([...activeNotesRef.current, ...heldNotesRef.current, ...toggledNotesRef.current]);
            const hasOverlap = [...requestNoteSet].some(n => currentNoteSet.has(n));
            if (currentNoteSet.size <= 1 || !hasOverlap) {
              return;
            }
            analysisShownSeqRef.current = seq;

            // Handle the response data here
            // console.log('Complete server response:', response); // *Latest Debugging*
            // console.log('Received response from server:', response.data); // *Latest Debugging*
            // const chordSymbols = response.data.cs_display;
            // const rootNote = response.data.root_pc;
            const { bass_note_pc, is_scale, scale_code } = response.data;
            // Which notes this analysis is of, so the Compass can tell a fresh
            // analysis from one still describing the notes before.
            setAnalyzedNotes([...computedMidiNotes].sort((a, b) => a - b));
            // console.log('Initial check - is_scale:', is_scale, 'settings:', settings.preferNotationLegible); // *Latest Debugging*

            setIsScale(is_scale);
            setScaleCode(scale_code);
            // console.log('#### scaleCode: ############', scaleCode); // *Latest Debugging*

            // console.log('scaleCode:', scaleCode); // *Latest Debugging*
            // console.log('response.data#########################################', response.data); // *Latest Debugging*

            if (is_scale) {
              setChordSymbol(response.data.cs_options[0]);
              setChordSymbols(response.data.cs_options.join('\n'));
              setPcWeightsSorted([]);
              setScaleRoot(bass_note_pc);
              setRootNote(anchorTonicNote !== null ? anchorTonicNote : bass_note_pc);
              if (lockedTonicNote === null) {  // Only set key center if no key is locked
                setKeyCenter(bass_note_pc);
                // console.log("if is_scale true and no lockedTonicNote then setKeyCenter = ", keyCenter)
              }
              const newMode = response.data.minmaj;
              setMode(newMode);
              setModes([newMode]); // Set modes array for scales too, so table colors update correctly
              setPieChordModes([newMode]);
              setPieSliceMode(anchorTonicNote !== null ? lockedTonicMode : newMode);
              // setPieSliceMode((lockedTonicNote !== null && lockedTonicMode === 0) || (lockedTonicNote === null && pieChordModes[0] === 0) ? 0 : 1);
              // console.log("if is_scale true and no lockedTonicNote then setPieSliceMode = ", pieSliceMode)
              setRootPc(response.data.root_pc);
              // console.log("if is_scale true then rootPc = ", rootPc)
              // console.log("response.data.cs_options[0]", response.data.cs_options[0]); // *Latest Debugging*
              // console.log("response.data.cs_options_notation[0]", response.data.cs_options_notation[0]); // *Latest Debugging*
              if (scale_code !== "") {
                setScaleUrl("https://ianring.com/musictheory/scales/" + scale_code);
              } else {
                console.warn('Scale code is undefined');
                // Handle the scenario where scale_code is undefined
              }

              setTonicScales(response.data.tonic_scales);
              setTonicScalesNotes(response.data.tonic_scales_notes);
              setRelativeChordScales(response.data.relative_chord_scales);
              setRelativeChordScalesNotes(response.data.relative_chord_scales_notes);
              setChordScales(response.data.chord_roots_tonicizing_scales);
              setChordScalesNotes(response.data.chord_roots_tonicizing_scales_notes);

              return;

            }

            // Determine which datasets to use
            const isNotation = settings.preferNotationLegible;
            const csOptions = isNotation ? response.data.cs_options_notation : response.data.cs_options;
            const csOptionsSegments = isNotation ? response.data.cs_options_segments_notation : response.data.cs_options_segments;

            setTonicScales(response.data.tonic_scales);
            setTonicScalesNotes(response.data.tonic_scales_notes);
            setRelativeChordScales(response.data.relative_chord_scales);
            setRelativeChordScalesNotes(response.data.relative_chord_scales_notes);
            setChordScales(response.data.chord_roots_tonicizing_scales);
            setChordScalesNotes(response.data.chord_roots_tonicizing_scales_notes);
            console.log("tonicScales", tonicScales);
            console.log("relativeChordScales", relativeChordScales);
            console.log("chordScales", chordScales);

            // Debug: log full response to inspect all fields
            console.log("Full response object:", response);
            console.log("response.data:", response.data);
            console.log("Keys in response.data:", Object.keys(response.data));
            console.log("response.data.relative_chord_scales:", response.data.relative_chord_scales);

            // Use csOptions data if available
            if (csOptions) {
              setCsOptions(csOptions);
              setChordSymbol(csOptions[0]);
              if (csOptions[1]) setChordSymbolTwo(csOptions[1]);
              setChordSymbols(csOptions.join('\n'));
              setTonicScales(response.data.tonic_scales);
              setTonicScalesNotes(response.data.tonic_scales_notes);
              setRelativeChordScales(response.data.relative_chord_scales);
              setRelativeChordScalesNotes(response.data.relative_chord_scales_notes);
              setChordScales(response.data.chord_roots_tonicizing_scales);
              setChordScalesNotes(response.data.chord_roots_tonicizing_scales_notes);
            } else {
              console.warn('response.data.cs_options is undefined');
            }

            // Use csOptionsSegments data if available
            if (csOptionsSegments) {
              setCsOptionsSegments(csOptionsSegments);
              // Atomically update selectedChordOptionIndex in the same batch to avoid a flash
              if (chordEditModeRef.current && selectedChordIndexRef.current !== null) {
                const current = [...new Set([...activeNotesRef.current, ...heldNotesRef.current, ...toggledNotesRef.current])].sort((a, b) => a - b);
                const orig = editModeOriginalVoicingRef.current;
                const hasChanges = current.length !== orig.length || current.some((n, i) => n !== orig[i]);
                if (hasChanges) {
                  setSelectedChordOptionIndex(0);
                } else {
                  const chartEntry = chordChart[selectedChordIndexRef.current];
                  const chartRootPc = chartEntry && chartEntry.segment && chartEntry.segment.root_pc;
                  const matchIndex = csOptionsSegments.findIndex((seg: ChordSegment) => seg.root_pc === chartRootPc);
                  setSelectedChordOptionIndex(matchIndex >= 0 ? matchIndex : 0);
                }
              }
            } else {
              console.warn(
                `response.data.${isNotation ? 'cs_options_segments_notation' : 'cs_options_segments'} is undefined`
              );
            }

            // Set rootNote, keyCenter, and mode based on available segment data

            // if (!segmentsData && !isScale) {
            //   setPieChordModes([response.data.minmaj]);
            // }

            // The key and its mode follow the analysis's first option. The
            // Compass's own anchoring — its root, slices and colours — is the
            // layout effect beside displaySegment, which follows a selected
            // chart chord or an option being browsed in edit mode. The key
            // must not: with no key locked, a change of mode here sets
            // lockedTonicMode, which re-runs the analysis and would undo the
            // browsing.
            if (csOptionsSegments && csOptionsSegments.length > 0) {
              setMode(csOptionsSegments[0].mode);
              if (lockedTonicNote === null) {
                setKeyCenter(csOptionsSegments[0].root_pc);
              }
            }

            // isScale ? setPieChordModes([mode]) : '';
            // console.log("PIE CHORD MODES!!!!!!!!!", pieChordModes);

            // Set modes, pcSorted, and pcWeightsSorted based on notation preference
            if (isNotation) {
              setModes(response.data.modes_notation);
              setChordQuals(response.data.chord_quals_notation);
              setPcSorted(response.data.pc_sorted_notation);
              setPcWeightsSorted(response.data.pc_weights_sorted_notation);
            } else {
              setModes(response.data.modes);
              setChordQuals(response.data.chord_quals);
              setPcSorted(response.data.pc_sorted);
              setPcWeightsSorted(response.data.pc_weights_sorted);
              // setTonicScales(response.data.tonic_scales);
              // setTonicScalesNotes(response.data.tonic_scales_notes);
            }

          })
          .catch(error => {
            // Handle any errors here
            console.error('Error sending request to server:', error);
            setServerUnreachable(true);
          });
      };

      if (elapsed >= MIDI_DEBOUNCE_MAX_WAIT) {
        // MaxWait exceeded: fire synchronously so it cannot be cancelled by the next note event
        fireRequest();
      } else {
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          fireRequest();
        }, MIDI_DEBOUNCE_WAIT);
      }
    } else { // if no notes active or toggled or midi notes playing a lockedTonicMode may still be changed so adjust pieSliceMode
      if (anchorTonicNote !== null) {
        setRootNote(anchorTonicNote);
        setPieSliceMode(lockedTonicMode);
      }
      if (lockedTonicNote !== null) {
        setKeyCenter(lockedTonicNote);
        // setPieChordModes([lockedTonicMode]);
        setMode(lockedTonicMode);
        // } else {
        //   setPieSliceMode(lockedTonicMode);
      }
    }
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [activeNotes, heldNotes, toggledNotes, lockedTonicNote, lockedTonicMode, anchorTonicNote, settings.preferModeBorrowing, settings.preferNotationLegible, settings.showOvertones]);


  // Emo Labels
  // Choose the appropriate Emo labels based on the mode
  useEffect(() => {
    // pieSliceMode is the anchor's mode: the chord's, or the locked key's under
    // the Key-Center Anchor setting (see anchorTonicNote). Not `mode`, which a
    // key lock sets to the key's mode whenever the notes are released.
    setEmoClassLabels(pieSliceMode === 0 ? emoClassLabelsMajor : emoClassLabelsMinor);
  }, [pieSliceMode, emoClassLabelsMajor, emoClassLabelsMinor]);


  // Sentiments Priority for Emo Labels (mode-dependent)
  const getEmotionDisplayPriority = (mode: number) => {
    if (mode === 0) {
      // Major mode
      return [3, 6, 8, 1, 9, 2, 11, 10, 4, 7, 0];
    } else {
      // Minor mode
      return [4, 6, 8, 1, 9, 2, 11, 10, 3, 7, 0];
    }
  };

  // Active Emo Labels
  useEffect(() => {
    let emoLabelsWithPc: Array<{ pc: number, label: string }> = [];

    // Get highest and lowest MIDI notes' pitch classes
    const combinedNotes = [...activeNotes, ...heldNotes, ...toggledNotes];
    const allNotesWithOvertones = settings.showOvertones
      ? [...combinedNotes, ...getOvertoneNotes(combinedNotes)]
      : combinedNotes;
    const highestNote = Math.max(...combinedNotes);
    const lowestNote = Math.min(...combinedNotes);
    const highestNotePc = ((highestNote % 12) - rootNote + 12) % 12;
    const lowestNotePc = ((lowestNote % 12) - rootNote + 12) % 12;

    // Show root/fifth sentiment only when 2 PCs present: root+fifth (0,7), root+maj3 (0,4), or root+min3 (0,3)
    const relativeActivePcs = [...new Set(combinedNotes.map(n => ((n % 12) - rootNote + 12) % 12))];
    const isNeutralDyad = !settings.showRootFifthSentiment &&
      relativeActivePcs.length === 2 && relativeActivePcs.includes(0) &&
      (relativeActivePcs.includes(7) || relativeActivePcs.includes(4) || relativeActivePcs.includes(3));
    const isSimpleTriad = !settings.showRootFifthSentiment &&
      relativeActivePcs.length === 3 && relativeActivePcs.includes(0) && relativeActivePcs.includes(7) &&
      (relativeActivePcs.includes(3) || relativeActivePcs.includes(4) || relativeActivePcs.includes(5));

    // Find highest and lowest notes excluding root (0) and 5th (7)
    const notesExcludingRootAnd5th = combinedNotes.filter(note => {
      const pc = ((note % 12) - rootNote + 12) % 12;
      return pc !== 0 && pc !== 7;
    });
    const highestNoteExcluding = notesExcludingRootAnd5th.length > 0 ? Math.max(...notesExcludingRootAnd5th) : null;
    const highestNotePcExcluding = highestNoteExcluding !== null ? ((highestNoteExcluding % 12) - rootNote + 12) % 12 : null;

    // First set of emotions (primary root)
    pcClass.forEach((pc, i) => {
      const isActive = settings.showOvertones
        ? allNotesWithOvertones.map(note => chromaticToFifths(note, rootNote)).includes(i)
        : combinedNotes.map(note => chromaticToFifths(note, rootNote)).includes(i);
      // const isActive = activeNotes.map(note => chromaticToFifths(note, rootNote)).includes(i) ||
      //   heldNotes.map(note => chromaticToFifths(note, rootNote)).includes(i) ||
      //   toggledNotes.map(note => chromaticToFifths(note, rootNote)).includes(i);
      if (isActive) {
        // Calculate relative pitch class for primary root
        const chromaticPc = ((i * 7) % 12);
        // When showRootFifthSentiment is off: exclude root (0) and fifth (7) unless it's a pure power chord
        if (settings.showRootFifthSentiment || chromaticPc !== 0 && chromaticPc !== 7 || isNeutralDyad || isSimpleTriad && chromaticPc === 7) {
          emoLabelsWithPc.push({
            pc: chromaticPc,
            label: emoClassLabels[i]
          });
        }
      }
    });

    // Get primary root mode
    const primaryMode = pieSliceMode;
    const primaryPriority = getEmotionDisplayPriority(primaryMode);

    // Modified sort to prioritize lowest note first, then highest note (excluding root and 5th)
    emoLabelsWithPc.sort((a, b) => {
      if (a.pc === lowestNotePc && lowestNotePc !== 0) return -1;
      if (b.pc === lowestNotePc && lowestNotePc !== 0) return 1;
      if (a.pc === highestNotePc && highestNotePc !== 0 && highestNotePc !== 7) return -1;
      if (b.pc === highestNotePc && highestNotePc !== 0 && highestNotePc !== 7) return 1;
      // if (highestNotePcExcluding !== null && a.pc === highestNotePcExcluding) return -1;
      // if (highestNotePcExcluding !== null && b.pc === highestNotePcExcluding) return 1;
      return primaryPriority.indexOf(a.pc) - primaryPriority.indexOf(b.pc);
    });

    const newActiveEmoLabels = emoLabelsWithPc.map(item => item.label);
    setActiveEmoLabels(newActiveEmoLabels);

    if (pcSorted.length > 1 && pcWeightsSorted[1] >= settings.secondaryChordThreshold) {
      const secondRootNote = pcSorted[1];
      const secondRootMode = modes[1];

      // Calculate highest and lowest notes' PCs relative to next root
      const highestNotePcSecondRoot = ((highestNote % 12) - secondRootNote + 12) % 12;
      const lowestNotePcSecondRoot = ((lowestNote % 12) - secondRootNote + 12) % 12;

      // Find highest note excluding root (0) and 5th (7) relative to next root
      const highestNotePcSecondRootExcluding = highestNoteExcluding !== null ? ((highestNoteExcluding % 12) - secondRootNote + 12) % 12 : null;

      let emoClassLabelsSecondRootTemp = secondRootMode === 0 ? emoClassLabelsMajor : emoClassLabelsMinor;
      setEmoClassLabelsSecondRoot(emoClassLabelsSecondRootTemp);

      const secondRootPriority = getEmotionDisplayPriority(secondRootMode);

      let emoLabelsSecondRootWithPc: Array<{ pc: number, label: string }> = [];

      pcClass.forEach((pc, i) => {
        const isActive = settings.showOvertones
          ? allNotesWithOvertones.map(note => chromaticToFifths(note, secondRootNote)).includes(i)
          : combinedNotes.map(note => chromaticToFifths(note, secondRootNote)).includes(i);
        if (isActive) {
          const chromaticPc = ((i * 7) % 12);
          // When showRootFifthSentiment is off: exclude root (0) and fifth (7) unless it's a pure power chord
          if (settings.showRootFifthSentiment || chromaticPc !== 0 && chromaticPc !== 7 || isNeutralDyad || isSimpleTriad && chromaticPc === 7) {
            emoLabelsSecondRootWithPc.push({
              pc: chromaticPc,
              label: emoClassLabelsSecondRootTemp[i]
            });
          }
        }
      });

      // Modified sort for secondary root emotions
      emoLabelsSecondRootWithPc.sort((a, b) => {
        if (a.pc === lowestNotePcSecondRoot && lowestNotePcSecondRoot !== 0) return -1;
        if (b.pc === lowestNotePcSecondRoot && lowestNotePcSecondRoot !== 0) return 1;
        if (a.pc === highestNotePcSecondRoot && highestNotePcSecondRoot !== 0 && highestNotePcSecondRoot !== 7) return -1;
        if (b.pc === highestNotePcSecondRoot && highestNotePcSecondRoot !== 0 && highestNotePcSecondRoot !== 7) return 1;
        // if (highestNotePcSecondRootExcluding !== null && a.pc === highestNotePcSecondRootExcluding) return -1;
        // if (highestNotePcSecondRootExcluding !== null && b.pc === highestNotePcSecondRootExcluding) return 1;
        return secondRootPriority.indexOf(a.pc) - secondRootPriority.indexOf(b.pc);
      });

      const newActiveEmoLabelsSecondRoot = emoLabelsSecondRootWithPc.map(item => item.label);
      setActiveEmoLabelsSecondRoot(newActiveEmoLabelsSecondRoot);
    } else {
      setActiveEmoLabelsSecondRoot([]);
    }
  }, [modes, activeNotes, heldNotes, toggledNotes, pcSorted, rootNote, emoClassLabels, emoClassLabelsMajor, emoClassLabelsMinor, pcWeightsSorted, settings.showOvertones, settings.secondaryChordThreshold, settings.showRootFifthSentiment]);


  // Begin Sort Sentiments for Circle Visualization

  // Calculate combined sentiment strengths for circle visualization
  const [sortedSentiments, setSortedSentiments] = useState<Array<[string, { strength: number, emoClassName: string, rootIndex: number }]>>([]);

  useEffect(() => {
    const labelStrengthMap = new Map();

    // Use default weight of 1.0 when isScale is true (no pc weight data available)
    const primaryWeight = isScale ? 1.0 : pcWeightsSorted[0];
    const secondaryWeight = isScale ? 0 : pcWeightsSorted[1];

    // Primary root contributions
    activeEmoLabels.forEach((label, index) => {
      if (!label) return;
      const pcIndex = emoClassLabels.indexOf(label);
      const emoClassName = emoClass[pcIndex];
      const positionWeight = activeEmoLabels.length - index;
      const strength = primaryWeight * positionWeight;

      labelStrengthMap.set(label, {
        strength,
        emoClassName,
        rootIndex: 0
      });
    });

    // Secondary root contributions (combine if duplicate)
    if (pcSorted.length > 1 && secondaryWeight >= settings.secondaryChordThreshold) {
      activeEmoLabelsSecondRoot.forEach((label, index) => {
        if (!label) return;
        const pcIndex = emoClassLabelsSecondRoot.indexOf(label);
        const emoClassName = emoClass[pcIndex];
        const positionWeight = activeEmoLabelsSecondRoot.length - index;
        const strength = secondaryWeight * positionWeight * 0.9;

        const existing = labelStrengthMap.get(label);
        if (existing) {
          existing.strength += strength;
        } else {
          labelStrengthMap.set(label, { strength, emoClassName, rootIndex: 1 });
        }
      });
    }

    // Sort by strength (highest first) for circle rendering
    const sorted = Array.from(labelStrengthMap.entries())
      .sort((a, b) => b[1].strength - a[1].strength);
    // .slice(0, 7); // Uncomment to limit to top 7 circles

    setSortedSentiments(sorted);
  }, [activeEmoLabels, activeEmoLabelsSecondRoot, emoClassLabels, emoClassLabelsSecondRoot, pcSorted, pcWeightsSorted, settings.secondaryChordThreshold, isScale]);

  // Pre-calculate circle sizes for sentiment visualization
  const sentimentCircleData = React.useMemo(() => {
    if (sortedSentiments.length === 0) return null;

    const totalStrength = sortedSentiments.reduce((sum, [, data]) => sum + data.strength, 0);
    const maxRadius = 45;
    const bottomY = 95;

    // Calculate sizes by position
    const sizesByPosition = sortedSentiments.map((_, index) => {
      const cumulativeStrength = sortedSentiments
        .slice(index)
        .reduce((sum, [, d]) => sum + d.strength, 0);
      const radiusSquared = (cumulativeStrength / totalStrength) * (maxRadius * maxRadius);
      const radius = Math.sqrt(radiusSquared);
      const cy = bottomY - radius;
      return { radius, cy };
    });

    // Map sizes and text positions to labels so they follow the label, not the position
    const sizesByLabel = new Map();
    const textYByLabel = new Map();

    sortedSentiments.forEach(([label], index) => {
      sizesByLabel.set(label, sizesByPosition[index]);

      // Calculate text position for this label
      let textY;
      if (index < sortedSentiments.length - 1) {
        const nextRadius = sizesByPosition[index + 1].radius;
        const nextTopEdge = bottomY - 2 * nextRadius;
        textY = nextTopEdge + 0.8 - sizesByPosition[index].radius / 12;
      } else {
        textY = sizesByPosition[index].cy;
      }
      textYByLabel.set(label, textY);
    });

    return {
      sizesByLabel,
      textYByLabel,
      largestRadius: sizesByPosition[0].radius,
      largestCy: sizesByPosition[0].cy,
      maxRadius,
      bottomY
    };
  }, [sortedSentiments]);

  // End Sort Sentiments for Circle Visualization

  const hasChanges = () => {
    const currentPalette = allPalettes.find(({ label }) => label === selectedPalette);
    if (!currentPalette) return false
    const isPaletteEqual = currentPalette.majorLabels === emoClassLabelsMajor && currentPalette.minorLabels === emoClassLabelsMinor
    return isPaletteEqual
  }


  // Label Flashes
  useEffect(() => {
    let newPrimaryFlashes: Record<number, boolean> = {};
    let newSecondaryFlashes: Record<number, boolean> = {};
    const isThresholdMet = (pcSorted.length > 1 && pcWeightsSorted[1] >= settings.secondaryChordThreshold);

    for (let note of keyflash) {
      let primaryPc = chromaticToFifths(note, rootNote);
      let secondaryPc = chromaticToFifths(note - pcSorted[1], 0);

      newPrimaryFlashes[primaryPc] = true;
      if (isThresholdMet) {
        newSecondaryFlashes[secondaryPc] = true;
      }
    }

    setPrimaryLabelFlashes(newPrimaryFlashes);
    setSecondaryLabelFlashes(newSecondaryFlashes);

    const timerId = setTimeout(() => {
      setPrimaryLabelFlashes({});
      setSecondaryLabelFlashes({});
    }, 65);

    return () => clearTimeout(timerId);
  }, [keyflash, rootNote, pcSorted, pcWeightsSorted]);

  // Display Key for KeyCenterPanelKey display with enharmonic spelling from mode, top left 
  useEffect(() => {
    // // Determine the current mode based on lockedTonicNote and lockedTonicMode
    // const currentMode = lockedTonicNote !== null ? lockedTonicMode : mode;
    // const currentKeyCenter = lockedTonicNote !== null ? lockedTonicNote : keyCenter;
    // // Call midiToName with the necessary arguments
    // const newName = midiToName(keyCenter, false, currentKeyCenter, currentMode); // TESTING Mar 2 Why does this work? rootNote for keyCenter?
    // Add this useEffect to keep the selectedKeyCenterIndex in sync with keyCenter when unlocked
    if (lockedTonicNote === null) {
      // Find the matching index for the current keyCenter in the current mode
      const matchIndex = fifthsOrderedPitchClasses.findIndex(entry =>
        entry.pitchClass === keyCenter &&
        entry.showInModes.includes(mode)
      );
      if (matchIndex !== -1) {
        setSelectedKeyCenterIndex(matchIndex);
        setLockedTonicMode(mode);
      }
    }
  }, [keyCenter, lockedTonicNote, mode]);

  // // Display Key for KeyCenterPanelKey display with enharmonic spelling from mode, top left 
  // useEffect(() => {
  //   // Determine the current mode based on lockedTonicNote and lockedTonicMode
  //   const currentMode = lockedTonicNote !== null ? (lockedTonicMode !== null ? lockedTonicMode : 0) : mode;
  //   const currentKeyCenter = lockedTonicNote !== null ? (lockedTonicNote !== null ? lockedTonicNote : keyCenter) : keyCenter;
  //   // Call midiToName with the necessary arguments
  //   const newName = midiToName(keyCenter, false, currentKeyCenter, currentMode); // TESTING Mar 2 Why does this work? rootNote for keyCenter?
  //   // Update the state with the new name
  //   setKeyNameDisplay(newName);
  // }, [rootNote, keyCenter, mode, lockedTonicMode, lockedTonicNote]);


  useEffect(() => {
    // Determine the sentiment palette index based on the anchor (see anchorTonicNote)
    const paletteIndex = anchorTonicNote !== null ? 1 : 0;
    const newPalette = defaultPalettes[paletteIndex];

    setSelectedPalette(newPalette.label);
    setEmoClassLabelsMajor(newPalette.majorLabels);
    setEmoClassLabelsMinor(newPalette.minorLabels);
  }, [anchorTonicNote]);



  // Pie Chart/Chord Compass Center Circle Old unused Transition BELOW
  //  // Pie Chart/Chord Compass Center Circle Transition BELOW
  // const [isTransitioning, setIsTransitioning] = useState(false);
  // const [previousMode, setPreviousMode] = useState<'default' | 'major' | 'minor' | null>(null);

  // // Add an effect to detect mode changes
  // useEffect(() => {
  //   const currentMode = chordSymbol === ''
  //     ? 'default'
  //     : pieChordModes[0] === 0 ? 'major' : 'minor';

  //   const previousModeValue = previousMode;

  //   // If we have a real mode change
  //   if (previousModeValue !== currentMode && previousModeValue !== null) {
  //     setIsTransitioning(true);

  //     // End transition after animation completes
  //     const timer = setTimeout(() => {
  //       setIsTransitioning(false);
  //     }, 200); // Match your transition duration

  //     return () => clearTimeout(timer);
  //   }

  //   // Update the previous mode for next time
  //   setPreviousMode(currentMode);
  // }, [chordSymbol, pieChordModes[0], previousMode]);



  // MIDI note numbers for 7 octaves from A0 to C8
  const notes = Array.from({ length: 88 }, (_, i) => i + 21); // MIDI note 21 is A0, i is pitch class (0 = C)

  // Filter the note numbers into white and black notes
  const whiteNotes = notes.filter(note => ![1, 3, 6, 8, 10].includes(note % 12)); // starting at A0, Bb at position 1 is first
  const blackNotes = notes.filter(note => [1, 3, 6, 8, 10].includes(note % 12));


  // Positions of the black keys between the white keys
  const blackKeyPositions = [1, 3, 4, 6, 7];
  const oneOctblackKeyPos = [0, 1, 3, 4, 5]; // For KeyCenterPanelKey


  // Toggled Notes
  const toggleNote = (note: number) => {
    if (toggledNotes.includes(note)) {
      setToggledNotes(toggledNotes.filter(n => n !== note));
    } else {
      setToggledNotes([...toggledNotes, note]);
    }
  };

  const notesInOctave = Array.from({ length: 12 }, (_, i) => i + rootNote);
  // console.log('notesInOctave:', notesInOctave)
  const inactiveOpacity = 0.5;
  const activeOpacity = 1; // unused. Use for basic piano key display activity, no scale degrees or Emos


  // console.log('keyCenter', keyCenter, 'mode:', mode);

  // console.log("Rendering with activeEmoLabelsSecondRoot:", activeEmoLabelsSecondRoot);

  // The chart chords shown in the Key Center panel, because the chart itself
  // can be hidden: the current chord and the next. Separators and blank chords
  // are passed over (isChartChord) — the next entry worth showing is the next chord.
  // Kept above the `if (!email)` return below: hooks must run on every render.
  // A selected separator or blank chord goes on showing the chord the rest of
  // the app is still sounding (chartKeyChordId), as long as no other chord
  // lies between the two — which is always so when stepping. One clicked
  // further off shows no current chord, only the one after it.
  let peekCurrentIndex: number | null = null;
  if (selectedChordIndex !== null && selectedChartEntry !== null) {
    if (isChartChord(selectedChartEntry)) {
      peekCurrentIndex = selectedChordIndex;
    } else if (chartKeyChordId !== null) {
      const soundingIndex = chordChart.findIndex(entry => entry.id === chartKeyChordId);
      const from = Math.min(soundingIndex, selectedChordIndex);
      const to = Math.max(soundingIndex, selectedChordIndex);
      const chordBetween = chordChart.slice(from + 1, to).some(isChartChord);
      if (soundingIndex !== -1 && !chordBetween) peekCurrentIndex = soundingIndex;
    }
  }
  const peekCurrentEntry = peekCurrentIndex !== null && isChartChord(chordChart[peekCurrentIndex])
    ? chordChart[peekCurrentIndex]
    : null;
  let peekNextEntry: ChordChartEntry | null = null;
  if (selectedChordIndex !== null) {
    const after = peekCurrentIndex !== null ? peekCurrentIndex : selectedChordIndex;
    for (let i = after + 1; i < chordChart.length; i++) {
      if (isChartChord(chordChart[i])) {
        peekNextEntry = chordChart[i];
        break;
      }
    }
  }
  // While a chord is being edited the chart draws the voicing's new spelling,
  // so the panel does too.
  const peekCurrentSegment = peekCurrentEntry && peekCurrentIndex === selectedChordIndex && chordEditMode && csOptionsSegments[selectedChordOptionIndex]
    ? csOptionsSegments[selectedChordOptionIndex]
    : peekCurrentEntry && peekCurrentEntry.segment;
  // The current chord is highlighted only while the notes sounding are exactly
  // its voicing. Toggling a piano key, playing, or editing the chord dims it
  // with the rest, though the strip stays where it is.
  const soundingNotes = Array.from(new Set([...activeNotes, ...heldNotes, ...toggledNotes])).sort((a, b) => a - b);
  const peekCurrentVoicing = peekCurrentEntry !== null
    ? Array.from(new Set(peekCurrentEntry.voicing)).sort((a, b) => a - b)
    : [];
  const peekCurrentSounding = peekCurrentEntry !== null
    && peekCurrentVoicing.length === soundingNotes.length
    && peekCurrentVoicing.every((note, i) => note === soundingNotes[i]);

  // The panel draws every chart chord on one strip and slides it so the
  // current chord (or, with none, the next) sits just inside the window's left
  // fade. Stepping animates the slide, so chords pass out through the fade on
  // one side and in through the other.
  const peekChords = chordChart.filter(isChartChord);
  const peekAnchor = peekCurrentEntry !== null ? peekCurrentEntry : peekNextEntry;
  const peekAnchorPosition = peekAnchor !== null ? peekChords.indexOf(peekAnchor) : -1;
  const chartPeekStripRef = useRef<HTMLSpanElement>(null);
  const [chartPeekOffset, setChartPeekOffset] = useState(0);
  useLayoutEffect(() => {
    const strip = chartPeekStripRef.current;
    if (strip === null || peekAnchorPosition === -1) return;
    const anchor = strip.children[peekAnchorPosition] as HTMLElement | undefined;
    if (anchor) setChartPeekOffset(anchor.offsetLeft);
  }, [peekAnchorPosition, chordChart, peekCurrentSegment]);

  // The chord the Staff and the Chord Compass show. A chart chord shows as
  // charted — its own root, whatever READ/FEEL would put first in the
  // analysis — and while it is being edited, the option being chosen. That is
  // the Key Center panel's current chord (peekCurrentSegment), so a selected
  // separator goes on showing the chord before it, on the same terms.
  // Otherwise it is the analysis's first option.
  //
  // The keyboard always wins: the chart chord is the place in the chart
  // (chartChordAt), but it is shown only while it is what is sounding. Once the
  // notes stop being its voicing — a key played, released or toggled, outside
  // chord edit mode — the Key Center panel dims it, and here the analysis of
  // what is sounding takes over again: a chord not in the chart yet, pending,
  // which Enter or the Compass's plus would add after it. With nothing
  // sounding, nothing is shown.
  //
  // The render after a chord is selected, before its notes reach the keyboard
  // (notesAppliedFor), looks like a change of voicing and is not: the chart
  // chord shows through it. And new notes are only known once their analysis
  // is back; until then the Compass holds what it was showing — the chart
  // chord if that was it, the pending chord if that was, nothing if nothing
  // was — rather than show the last analysis as though it were of these notes.
  // So the chart chord never reappears once the notes have left it, and only
  // the move from the chart chord to a pending one animates: a pending chord
  // changes its symbol in place.
  //
  // While Follow has the MIDI file playing or scrubbing the chart's selection,
  // no chart chord is at the place as far as these panels go: the selection
  // walks the chart, and the Compass shows what is sounding, with no strip.
  const chartSegment = peekCurrentSegment ? peekCurrentSegment : undefined;
  const fileMovesSelection = chartFollowOn && (midiFilePlaying || isScrubbing);
  const chartChordAt = peekCurrentEntry !== null && peekCurrentEntry.voicing.length !== 0
    && chartSegment !== undefined && !fileMovesSelection;
  const notesSilent = soundingNotes.length === 0;
  const selectionApplied = selectedChartEntry === null || !isChartChord(selectedChartEntry)
    || notesAppliedFor === selectedChordIndex;
  const analysisFresh = analyzedNotes.length === soundingNotes.length
    && analyzedNotes.every((note, i) => note === soundingNotes[i]);
  // What the Compass showed at the last commit, for holding through a wait.
  const wasPendingRef = useRef(false);
  const wasShownRef = useRef(false);
  const notesLeftChart = selectionApplied && !chordEditMode && !peekCurrentSounding;
  const chartChordShown = chartChordAt && !notesSilent
    && (!notesLeftChart || (!analysisFresh && wasShownRef.current));
  const chartChordPending = chartChordAt && !notesSilent && !chartChordShown
    && (analysisFresh || wasPendingRef.current) && csOptionsSegments[0] !== undefined;
  useLayoutEffect(() => {
    wasShownRef.current = chartChordShown;
    wasPendingRef.current = chartChordPending;
  }, [chartChordShown, chartChordPending]);
  let displaySegment: ChordSegment | undefined = csOptionsSegments[0];
  if (chartChordShown) {
    displaySegment = chartSegment;
  } else if (chartChordAt && !chartChordPending && (notesSilent || !analysisFresh)) {
    displaySegment = undefined;
  } else if (!chartChordAt && selectedChordIndex !== null && chordEditMode) {
    displaySegment = csOptionsSegments[selectedChordOptionIndex];
  }

  // The Compass anchors on the chord it shows: its root, mode and quality turn
  // the wheel and colour the centre. A locked key still takes the root and
  // slice mode when the Compass is set to anchor on it (anchorTonicNote). This
  // is the one place a chord does that, so the wheel follows a chart chord, an
  // option being browsed in edit mode, or the analysis, whichever is showing.
  // The key and its mode stay with the analysis (see the response handler). A layout effect, so a new analysis and
  // the anchoring it causes reach the screen together. Only while notes sound:
  // with none, the last analysis is stale, and the branch at the foot of the
  // analysis effect keeps the lock in charge.
  const soundingCount = soundingNotes.length;
  useLayoutEffect(() => {
    if (isScale || displaySegment === undefined || soundingCount === 0) return;
    setRootPc(displaySegment.root_pc);
    setChordQual(displaySegment.chord_qual);
    setPieChordModes([displaySegment.mode]);
    setPieSliceMode(anchorTonicNote !== null ? lockedTonicMode : displaySegment.mode);
    setRootNote(anchorTonicNote !== null ? anchorTonicNote : displaySegment.root_pc);
  }, [displaySegment, isScale, soundingCount, anchorTonicNote, lockedTonicMode]);

  // A segment's with-bass-note symbol as the Compass draws it (the svg form of
  // assembleChordSymbol), as plain text: its length sizes the centre symbol,
  // and it tells the strip below when a chord's drawn width can have changed.
  const compassSymbolText = (segment: ChordSegment) =>
    spellChordSymbol(segment, true, anchorTonicNote, lockedTonicMode).rootName
    + segment.third_quality_wbn + segment.extension_wbn;
  const compassCentreSize = (segment: ChordSegment) => 6.5 - compassSymbolText(segment).length * 0.2;

  // At a place in the chart, the Compass's centre is a strip of the chart's
  // chords that slides as the selection steps (CompassChartStrip). The current
  // chord is drawn as shown — the option being chosen, in edit mode. A pending
  // chord goes in the strip where adding it would put it, after the selected
  // entry, and takes the middle: the chart chord slides aside as though the
  // new one were already charted.
  //
  // The strip stays for as long as there is a current chart chord. Live — the
  // chart chord sounding, or a pending chord in the middle — needs two or more
  // pitch classes sounding: a single note shows no chord. Otherwise (silent, a
  // single note, or other notes with nothing pending yet) it is dimmed, the
  // chart chord faint in the middle as the chart's chords are beside a
  // pending one. activePcs lags the keys by a render, so this counts the notes
  // sounding itself.
  const compassChordSounding = new Set(soundingNotes.map(note => note % 12)).size > 1;
  const compassPending = chartChordPending && compassChordSounding;
  const compassStripDimmed = chartChordAt && !compassPending && !(chartChordShown && compassChordSounding);
  const compassCentreSegment = compassStripDimmed ? chartSegment : displaySegment;
  const compassStrip = chartChordAt
    ? peekChords
      .filter(entry => entry.segment !== undefined)
      .map(entry => ({ id: entry.id, segment: (entry === peekCurrentEntry ? chartSegment : entry.segment) as ChordSegment }))
    : [];
  let compassStripCurrent = peekCurrentEntry !== null
    ? compassStrip.findIndex(chord => chord.id === peekCurrentEntry.id)
    : -1;
  if (compassPending && compassStripCurrent !== -1 && csOptionsSegments[0] !== undefined) {
    // A separator selected before the sounding chord inserts ahead of it.
    const ahead = peekCurrentIndex !== null && selectedChordIndex !== null && selectedChordIndex < peekCurrentIndex;
    if (!ahead) compassStripCurrent++;
    compassStrip.splice(compassStripCurrent, 0, { id: COMPASS_PENDING_ID, segment: csOptionsSegments[0] });
  }
  // Adds the pending chord, as Return does. The strip holds on to its
  // handlers (it re-renders only on its key), so it gets one stable function
  // that calls through to this render's addEntryToChart and its state.
  const addPendingRef = useRef(() => { });
  // The chart is placed first: addEntryToChart opens it in its own slot, which
  // does nothing once it is on screen.
  addPendingRef.current = () => {
    openChartBefore('chordCompass');
    addEntryToChart(0, false, lockedTonicNote, lockedTonicMode);
  };
  const addPendingChord = useCallback(() => addPendingRef.current(), []);
  const compassStripKey = compassStrip.map(chord => `${chord.id}:${compassSymbolText(chord.segment)}`).join('|')
    + `#${compassStripCurrent}#${anchorTonicNote}#${chordQual}`;

  // The anchor arrow at the Compass's bottom left is a button while a key is
  // locked, and wears a box to say so. The box is measured from the glyph
  // rather than drawn at fixed coordinates: the arrow's font size is in dvw,
  // which inside the svg is read as user units, so the glyph's size there
  // changes with the window, and its place moves with the key name beside it.
  const compassArrowRef = useRef<SVGTSpanElement>(null);
  const [compassArrowBox, setCompassArrowBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const compassRendered = isPanelRendered('chordCompass');
  useLayoutEffect(() => {
    const measure = () => {
      const arrow = compassArrowRef.current;
      if (arrow === null || arrow.getNumberOfChars() === 0) {
        setCompassArrowBox(null);
        return;
      }
      const glyph = arrow.getExtentOfChar(0);
      setCompassArrowBox({ x: glyph.x, y: glyph.y, width: glyph.width, height: glyph.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [lockedTonicNote, lockedTonicMode, anchorTonicNote, compassRendered]);

  if (!email) {
    return null; // Comment out for offline work
  }

  // const scaleFontMultiplier = isScale ? 1 : 0;
  // console.log('isScale:', isScale, 'fontSizeMultiplier:', scaleFontMultiplier); // Debugging

  const getEmoColor = (emoClassName: string, modesIndex: number = 0) => {
    const pcToColorKey: { [key: string]: string } = {
      'emo-p-root': 'R',
      'emo-min-second': 'm2',
      'emo-maj-second': 'M2',
      'emo-min-third': 'm3',
      'emo-maj-third': 'M3',
      'emo-p-fourth': 'P4',
      'emo-tritone': 'd5',
      'emo-p-fifth': 'P5',
      'emo-min-sixth': 'm6',
      'emo-maj-sixth': 'M6',
      'emo-min-seventh': 'm7',
      'emo-maj-seventh': 'M7'
    };

    const colorKey = pcToColorKey[emoClassName];
    if (!colorKey || !colors[colorKey as keyof ColorScheme]) {
      console.warn(`No color found for emo class: ${emoClassName}`);
      return 'rgb(200, 200, 200)'; // fallback color
    }
    return colors[colorKey as keyof ColorScheme][modes[modesIndex]];
  };



  // Inner Circle Note Name Labels Rotation
  // Calculate base rotation
  let baseRotation = chromaticToFifths(0, rootNote) * (Math.PI / 6);
  let negativeRotation = baseRotation - (2 * Math.PI);

  // Choose the rotation that's closest to the previous rotation
  let innerLabelRotation = baseRotation;
  if (previousRootNote !== rootNote) {
    // Calculate distances using the actual previous rotation
    let distToPositive = Math.abs(baseRotation - prevActualRotation);
    let distToNegative = Math.abs(negativeRotation - prevActualRotation);

    // Instead of choosing between just baseRotation and negativeRotation,
    // we'll add or subtract 2π as needed to get the closest continuous rotation
    if (distToPositive < distToNegative) {
      // If positive rotation is closer, adjust by adding/subtracting 2π if needed
      while (Math.abs(baseRotation - prevActualRotation) > Math.PI) {
        if (baseRotation > prevActualRotation) {
          baseRotation -= 2 * Math.PI;
        } else {
          baseRotation += 2 * Math.PI;
        }
      }
      innerLabelRotation = baseRotation;
    } else {
      // If negative rotation is closer, adjust by adding/subtracting 2π if needed
      while (Math.abs(negativeRotation - prevActualRotation) > Math.PI) {
        if (negativeRotation > prevActualRotation) {
          negativeRotation -= 2 * Math.PI;
        } else {
          negativeRotation += 2 * Math.PI;
        }
      }
      innerLabelRotation = negativeRotation;
    }
  }

  // Selected Chord Root PC
  const chartVoicingSorted = selectedChartEntry ? [...selectedChartEntry.voicing].sort((a, b) => a - b) : [];
  const toggledNotesSorted = [...toggledNotes].sort((a, b) => a - b);
  const selectedChartRootPc = chordEditMode && csOptionsSegments[selectedChordOptionIndex]
    ? csOptionsSegments[selectedChordOptionIndex].root_pc
    : selectedChartEntry
      && selectedChartEntry.segment
      && chartVoicingSorted.length === toggledNotesSorted.length
      && chartVoicingSorted.every((n, i) => n === toggledNotesSorted[i])
      ? selectedChartEntry.segment.root_pc
      : null;
  /**
   * What the key center panel shows as the current key: a locked tonic — the
   * user's, or the chart's — first, then the selected chart chord's root (any
   * chord root can be heard as a key center, especially when dwelt on).
   *
   * Computed once because the white-key and black-key rows are two copies of
   * the same expression.
   */
  const panelKeyCenterPc = selectedChartRootPc !== null && lockedTonicNote === null ? selectedChartRootPc : keyCenter;
  // The Key Center panel's root: the locked tonic while a key is locked. Not
  // rootNote, which is the Chord Compass's anchor and stays on the chord's root
  // under a lock unless the Key-Center Anchor setting is on.
  let panelRootNote = rootNote;
  if (selectedChartRootPc !== null) panelRootNote = selectedChartRootPc;
  if (lockedTonicNote !== null) panelRootNote = lockedTonicNote;

  /**
   * The region key center in force at each chart position, or null where only
   * the global key applies. A separator takes the key of the chord before it,
   * so a region's line draws continuously across barlines.
   *
   * One walk of the chart rather than a lookup per chord: the regions are
   * already in chart order, so the two can be walked together.
   */
  const regionKeyByEntryIndex = (() => {
    const perEntry: (KeyCenter | null)[] = [];
    const ordered = regionsInChartOrder(chordChart, keyCenterRegions);
    let nextRegion = 0;
    let current: KeyCenter | null = null;
    chordChart.forEach(entry => {
      const startsRegion = !entry.isSeparator &&
        nextRegion < ordered.length &&
        ordered[nextRegion].startChordId === entry.id;
      if (startsRegion) {
        current = ordered[nextRegion].keyCenter;
        nextRegion++;
      }
      perEntry.push(current);
    });
    return perEntry;
  })();

  /** Chords a region starts at, which wear their key center badge unselected. */
  const regionStartIds = new Set(keyCenterRegions.map(region => region.startChordId));

  /**
   * The key center lines above one chart entry: the chart's global key on top,
   * and below it — closest to the chords — the region covering this entry, if
   * one does.
   *
   * Absolutely positioned inside entries that are already position:relative, so
   * switching the lines on never moves a chord. Each segment spans its own
   * entry and they meet edge to edge, which is what makes a region read as one
   * bar however the chart wraps. Colour says mode and nothing else: yellowish
   * major, bluish minor, matching the chart's own chord backgrounds, since
   * other colours already mean scale degrees here.
   */
  const keyCenterLineStyle = (lineMode: 0 | 1, top: string, left: string = '0'): React.CSSProperties => ({
    position: 'absolute',
    left,
    // Overhangs to the right by more than the gap between entries
    // (.chord-space carries margin-right: 0.1dvw), so the segments meet.
    right: '-0.2dvw',
    top,
    height: '0.12dvw',
    backgroundColor: lineMode === 0 ? 'rgb(90,90,0)' : 'rgb(50,50,140)',
    pointerEvents: 'none',
  });

  // Whether the chart makes room for key center lines between its systems. A
  // chart with no key set anywhere has no lines to draw, so it keeps the tight
  // spacing until the first key goes in. Key centers are stored apart from the
  // entries, so this is a check of two values rather than a walk of the chart.
  const chartSpacesKeyCenterLines = settings.showKeyCenterRegions && chartHasKeyCenters;

  const keyCenterLines = (index: number) => {
    if (!settings.showKeyCenterRegions) return null;
    const regionKey = regionKeyByEntryIndex[index];
    return (
      <>
        {globalKeyCenter !== null && (
          <span style={keyCenterLineStyle(globalKeyCenter.mode, 'var(--key-center-line-global)')} />
        )}
        {regionKey !== null && (
          <span style={keyCenterLineStyle(regionKey.mode, 'var(--key-center-line-local)')} />
        )}
      </>
    );
  };

  /**
   * The chart key's line alone, for the blocks that come before the first chord
   * — the time signature and the opening barline.
   *
   * The chart key covers the whole chart, so its line starts where the chart
   * does rather than at the first chord, running back towards the key it names
   * in the header. No local line here: no region can begin before the chords.
   */
  const globalKeyCenterLine = () => {
    if (!settings.showKeyCenterRegions || globalKeyCenter === null) return null;
    return (
      <span style={keyCenterLineStyle(
        globalKeyCenter.mode,
        'var(--key-center-line-global)',
        'var(--key-center-global-line-reach)'
      )} />
    );
  };

  /**
   * An invisible strip over both key center lines that counts as hovering the
   * chord below it, and opens the picker when clicked.
   *
   * The badge sits well above the chord's own box, so this covers the band it
   * appears in. It needs no handlers of its own: it is a child of the chord's
   * wrapper, and mouseenter/mouseleave treat a descendant as part of the
   * element, so the wrapper's own hover tracking already sees it.
   */
  const keyCenterHoverStrip = (index: number, chordId: string) => (
    // Only where the gap between systems is there to hold it: in the tight
    // spacing it would lie over the row above and take its clicks. The badge
    // on the hovered chord still offers a key without it.
    !chartSpacesKeyCenterLines ? null : <span
      data-actionable="true"
      title="Key center from this chord on"
      onClick={(e) => {
        e.stopPropagation();
        toggleKeyCenterPicker({ scope: 'chord', chordId }, e.currentTarget);
      }}
      style={{
        position: 'absolute',
        left: 0,
        right: '-0.2dvw',
        top: 'var(--key-center-hover-top)',
        height: 'var(--key-center-hover-height)',
        cursor: 'pointer',
      }}
    />
  );

  /**
   * What the open picker is editing, resolved once so the render below reads as
   * one block rather than branching on scope in four places.
   */
  const keyCenterPickerView = (() => {
    if (keyCenterPicker === null) return null;
    if (keyCenterPicker.scope === 'global') {
      return {
        anchor: keyCenterPicker.anchor,
        value: globalKeyCenter,
        usesGlobal: false,
        canUseGlobal: false,
        canClear: globalKeyCenter !== null,
      };
    }
    const chordId = keyCenterPicker.chordId;
    const covering = regionCoveringChord(chordChart, keyCenterRegions, chordId);
    const startingHere = keyCenterRegions.find(region => region.startChordId === chordId);
    return {
      anchor: keyCenterPicker.anchor,
      // The key in force at the chord: a local key, or the global key a "use
      // global key" marker gives, set here or earlier. Where the global key
      // shows through with no marker, no key is picked yet
      value: covering !== null && covering.keyCenter === null ? globalKeyCenter : (covering === null ? null : covering.keyCenter),
      // "Use global key" set at this chord itself, not earlier
      usesGlobal: startingHere !== undefined && startingHere.keyCenter === null,
      // Offered on any chord, in the middle of a region too, and where no
      // local key is in force yet, since the marker stays
      canUseGlobal: true,
      // Any key change starting at this chord, a MIDI file's too
      canClear: startingHere !== undefined,
    };
  })();

  const scaleIndex = chordEditMode && csOptionsSegments[selectedChordOptionIndex]
    ? selectedChordOptionIndex
    : selectedChartRootPc !== null
      ? Math.max(0, csOptionsSegments.findIndex(seg => seg && seg.root_pc === selectedChartRootPc))
      : 0;
  const scaleRootDisplay = !isScale ? (selectedChartRootPc !== null ? selectedChartRootPc : pcSorted[0]) : scaleRoot;
  const scaleTonic = lockedTonicNote !== null ? lockedTonicNote : scaleRootDisplay;
  const scaleModeDisplay = lockedTonicNote !== null ? getModeFromPcSet(tonicScalesNotes[0]) : chordQual;
  const effectiveTonicScaleName = lockedTonicNote !== null ? tonicScales[0] : chordScales[scaleIndex];
  const effectiveTonicScaleNotes = lockedTonicNote !== null ? tonicScalesNotes[0] : chordScalesNotes[scaleIndex];






  // RETURN BELOW


  // The chart's own toolbar: separator/blank-chord, navigation, undo/redo,
  // batch edit and export. It hangs in the strip ABOVE the panel that the
  // registry's topInset reserves, so it goes through <Panel>'s overlay slot
  // rather than the scrolling body — the body is masked, and a mask applies to
  // its whole painted subtree no matter which element is the containing block.
  const chartControls = (
            <div className='chord-chart-controls'>
              <div>
                <button title="Insert Separator" className='add-separator' onClick={() => addEntryToChart(SEPARATORS.barline, true)}>
                  <span>/</span></button>
              </div>

              <div>
                <button
                  title="Insert Blank Chord" className='insert-blank-chord' onClick={() => addEntryToChart(-1, false)}>
                  <span>+</span></button>
              </div>

              <div>
                <button title="Batch Chord Edit" className='batch-edit'
                  ref={batchButtonRef}
                  onClick={() => setShowBatchMenu(prev => !prev)}>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '0.9', gap: '0' }}>
                    <img src="/images/batch-edit410x-black.png" alt="Batch Edit" style={{ width: '1.35dvw' }} />
                  </span>
                </button>
                {showBatchMenu && (() => {
                  const rect = batchButtonRef.current ? batchButtonRef.current.getBoundingClientRect() : null;
                  return (
                    <>
                      {/* Backdrop to close menu when clicking outside */}
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                        onClick={() => setShowBatchMenu(false)} />
                      {/* Batch menu — fixed position anchored below the button's right edge */}
                      <div style={{
                        position: 'fixed',
                        top: rect ? rect.bottom + 4 : 0,
                        left: rect ? rect.right - 176 : 0,
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '0.3dvw',
                        padding: '0.3dvw 0',
                        minWidth: '11dvw',
                        zIndex: 1000,
                        boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)'
                      }}>
                        {[
                          {
                            label: 'Respell chords by feeling…',
                            action: () => { setShowBatchMenu(false); respellAllChords('feel'); },
                            disabled: chordChart.length === 0 || batchRunning,
                          },
                          {
                            label: 'Respell chords by legibility…',
                            action: () => { setShowBatchMenu(false); respellAllChords('read'); },
                            disabled: chordChart.length === 0 || batchRunning,
                          },
                          {
                            label: 'Respell chords for iReal Pro…',
                            action: () => { setShowBatchMenu(false); respellChartForIrealPro(); },
                            disabled: chordChart.length === 0 || batchRunning,
                          },
                        ].map(item => (
                          <div
                            key={item.label}
                            onClick={item.disabled ? undefined : item.action}
                            onMouseEnter={(e) => { if (!item.disabled) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            style={{
                              padding: '0.3dvw 0.8dvw',
                              cursor: item.disabled ? 'not-allowed' : 'pointer',
                              fontSize: '0.8vw',
                              fontFamily: 'Trebuchet MS',
                              lineHeight: 'normal',
                              color: item.disabled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
                              borderBottom: '0.05dvw solid rgba(255,255,255,0.05)',
                            }}
                          >
                            {item.label}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              <div>
                <button title="Export Chart" className='export-chart'
                  ref={exportButtonRef}
                  onClick={() => setShowExportMenu(prev => !prev)}>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '0.9', gap: '0' }}>
                    {/* <span style={{ textAlign: 'center', width: '100%' }}>↓</span>
                    <span style={{ textAlign: 'center', width: '100%', marginTop: '-0.5em', fontSize: '0.7em' }}>▢</span> */}
                    <img src="/images/Download-Fill--Streamline-Sharp-Fill-Material-Symbols-black.png" alt="Export" style={{ width: '1.35dvw' }} />
                  </span>
                </button>
                {showExportMenu && (() => {
                  const rect = exportButtonRef.current ? exportButtonRef.current.getBoundingClientRect() : null;
                  return (
                    <>
                      {/* Backdrop to close menu when clicking outside */}
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                        onClick={() => setShowExportMenu(false)} />
                      {/* Export menu — fixed position anchored below the button's right edge */}
                      <div style={{
                        position: 'fixed',
                        top: rect ? rect.bottom + 4 : 0,
                        left: rect ? rect.right - 176 : 0,
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '0.3dvw',
                        padding: '0.3dvw 0',
                        minWidth: '11dvw',
                        zIndex: 1000,
                        boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)'
                      }}>
                        {[
                          { label: 'Copy to Clipboard', action: () => { copyChordChartToClipboard(); setShowExportMenu(false); }, disabled: false },
                          { label: 'iReal Pro', action: () => { exportToIrealPro(); setShowExportMenu(false); }, disabled: false },
                          { label: 'MusicXML', action: () => { }, disabled: true },
                          { label: 'PDF', action: () => { }, disabled: true },
                        ].map(item => (
                          <div
                            key={item.label}
                            onClick={item.disabled ? undefined : item.action}
                            onMouseEnter={(e) => { if (!item.disabled) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            style={{
                              padding: '0.3dvw 0.8dvw',
                              cursor: item.disabled ? 'not-allowed' : 'pointer',
                              fontSize: '0.8vw',
                              fontFamily: 'Trebuchet MS',
                              lineHeight: 'normal',
                              color: item.disabled ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
                              borderBottom: '0.05dvw solid rgba(255,255,255,0.05)',
                            }}
                          >
                            {item.disabled ? '🔜 ' : ''}{item.label}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>


              {/* <div> OLD COPY BUTTON
                <button title="Copy Chord Chart" className='copy-chart' onClick={copyChordChartToClipboard}>
                  <span>{'\u29C9'}</span></button>
              </div> */}


              {/* // NEW ADDITION ACRYLICODE */}
              {/* <div>
              <button title="Save" onClick={() => saveSong({
                songTitle: songTitle.title,
                composer: composer.name,
                chordChart: chordChart,
                timeSigBeats: timeSigBeats,
                timeSigDuration: timeSigDuration,
              })}>
                <span>{'\u29C9'}</span></button>
            </div> */}

              <div>
                <button data-actionable='true' className='chart-nav backward' title='Previous Chord'
                  onClick={() => chartNavigation("backward")} disabled={chordChart.length < 2}>{'\u276E'}</button>
                <button data-actionable='true' className='chart-nav forward' title='Next Chord'
                  onClick={() => chartNavigation("forward")} disabled={chordChart.length < 2}>{'\u276F'}</button>
              </div>
              {/* u2702 is scissors, \u2398 is paste, &larr; &rarr; are arrows */}

              <div>

                {/* {selectedChordIndex === index && ( */}
                <button data-actionable="true" title="Delete Entry" className={`${selectedChordIndex || selectedChordIndex === 0 ? 'delete-entry' : 'delete-disabled'}`}
                  onClick={() => { selectedChordIndex !== null ? deleteChordFromChart(selectedChordIndex) : '' }}>
                  <span>{' \u232B'}</span>
                </button>

                {/* Follow, beside the chart's play */}
                <FollowToggle on={chartFollowOn} onToggle={() => setChartFollowOn(on => !on)} size="0.8dvw" />
                <button
                  key={`play-chart-btn-${playSongOn}`}
                  // One driver: while the MIDI file plays, the chart only stops
                  disabled={chartFollowOn && midiFilePlaying && !playSongOn}
                  onClick={(event) => {
                    // setPlaySongOn(prev => !prev);
                    handlePlaySongToggle(event);
                  }}
                  style={{
                    color: playSongOn ? 'white' : 'black',
                    textShadow: playSongOn ? '0 0 0.7dvw #fff, 0 0 0.7dvw #fff, 0 0 1.4dvw #bbb, 0 0 1.8dvw #bbb,0 0 2.8dvw #bbb' : '',
                    animation: (() => {
                      const bpm = parseInt(tempo.value, 10) || 120; const flashMs = (60000 / bpm) * (bpm >= 160
                        ? 2 // play song button flashes cut time (1/2) for faster tempos
                        : 1); return playSongOn
                          ? `song-play-flash ${flashMs}ms linear ${-flashMs / 2.6}ms infinite` // offset the flash by 1/2.6 the flash duration so white lines up with chord
                          : 'none';
                    })()
                  }}
                  className='play-song' title={`${playSongOn ? 'Pause Song [Shift+Space]' : 'Play Song [Shift+Space]'}`}
                >{'\u25B6'}ll</button>
                {/* {playSongOn ? '\u25FC' : '\u25B6'} */}
              </div>

              <div>
                <button
                  data-actionable="true"
                  className='history undo'
                  title='Undo'
                  onClick={undo}>
                  <span>↺</span>
                </button>
                <button
                  data-actionable="true"
                  className={`history ${currentHistoryIndex >= chordChartHistory.length - 1 ? 'redo-disabled' : 'redo'}`}
                  title='Redo'
                  onClick={redo}
                  disabled={currentHistoryIndex >= chordChartHistory.length - 1}>
                  <span>↻</span>
                </button>
              </div>
            </div>
  );

  return (
    <div>

      {serverUnreachable && (
        <StatusBanner message="Analysis server unreachable — chord display paused. Retrying as you play…" />
      )}
      {/* Make a chart from the MIDI file just loaded, or add its chords after
          the chart there is, or put them in its place */}
      {midiChartOffer !== null && (
        <div className="midi-chart-offer" data-actionable="true">
          <div className="midi-chart-offer-question">
            {midiChartOffer.chartEmpty
              ? <>Make a chord chart from <strong>{midiChartOffer.fileName}</strong>?</>
              : <>Add the chords of <strong>{midiChartOffer.fileName}</strong>?</>}
          </div>
          {midiChartOffer.chartEmpty && (
            <label className="midi-chart-offer-every-time">
              <input type="checkbox" checked={midiChartEveryTime} onChange={(e) => setMidiChartEveryTime(e.target.checked)} />
              Do this every time I load a MIDI file
            </label>
          )}
          <div className="midi-chart-offer-buttons">
            <button onClick={() => acceptMidiChartOffer(false)}>{midiChartOffer.chartEmpty ? 'Make chart' : 'Add after the chart'}</button>
            {!midiChartOffer.chartEmpty && <button onClick={() => acceptMidiChartOffer(true)}>Replace the chart</button>}
            <button onClick={() => setMidiChartOffer(null)}>Not now</button>
          </div>
        </div>
      )}

      {/* Follow back on after the regions changed: the chart catches up, or
          keeps what it has and shows the differences as markers */}
      {heldRegionReport !== null && (
        <div className="midi-chart-offer" data-actionable="true">
          <div className="midi-chart-offer-question">
            The MIDI file's chord regions changed while unlinked. Update the chord chart to match?
          </div>
          <div className="midi-chart-offer-buttons">
            <button onClick={() => syncHeldRegionReport(true)}>Update chart</button>
            <button onClick={() => syncHeldRegionReport(false)}>Keep chart</button>
          </div>
        </div>
      )}

      {/* One banner at a time: they share a place on screen */}
      {!serverUnreachable && chartNotice !== null && (
        <StatusBanner message={chartNotice} tone="info" />
      )}

      {/* One picker for both the chart key and a chord's key center. Rendered
          here rather than inside the chart because it is positioned fixed from
          its opener's rect: inside the chart's scrolling body it would be
          clipped, and the chord symbols paint over it. */}
      {keyCenterPickerView !== null && (
        <KeyCenterPicker
          value={keyCenterPickerView.value}
          usesGlobal={keyCenterPickerView.usesGlobal}
          anchor={keyCenterPickerView.anchor}
          onPick={applyPickedKeyCenter}
          onUseGlobal={keyCenterPickerView.canUseGlobal ? applyUseGlobalKey : undefined}
          onClear={keyCenterPickerView.canClear ? applyClearKeyCenter : undefined}
          onDismiss={() => setKeyCenterPicker(null)}
        />
      )}

      {batchReview && (
        <ChordBatchReview
          title={batchReview.title}
          summary={batchReview.summary}
          changes={batchReview.changes}
          warning={batchReview.warning}
          applyLabel={batchReview.applyLabel}
          keepLabel={batchReview.keepLabel}
          onApply={(accepted) => { batchReview.apply(accepted); setBatchReview(null); }}
          onKeepAll={() => { if (batchReview.keepAll) batchReview.keepAll(); setBatchReview(null); }}
          onDismiss={() => setBatchReview(null)}
          onEdit={(index) => { setBatchReview(null); editChartChord(index); }}
          renderChord={(segment) => assembleChordSymbol(segment, false, lockedTonicNote, lockedTonicMode)}
          anchor={batchReview.anchor}
          width={batchReview.width}
        />
      )}

      <div className="piano-background" style={{ backgroundImage: `url('/images/pianobackingC1742x892${settings.appTheme}.jpg')` }}></div>
      <div className="piano-bot-background" style={{ backgroundImage: `url('/images/pianobottom4${settings.appTheme}.jpg')` }}></div>

      {/* Background approach 2: Using img tag (comment out one of these approaches) */}
      {/* <img src="/images/pianobacking.jpg" alt="Piano Background" className="background-img" /> */}

      {/* Add Settings component next to HelpPopup */}
      <div style={{ display: 'inline-block' }}>
        <HelpPopup settings={settings} /> {/* This will render the "Open Help" button and the modal functionality */}
        <Settings
          settings={settings}
          onSettingChange={handleSettingChange}
          panels={{
            isOn: isPanelOn,
            toggle: togglePanel,
            moveTo: movePanelTo,
            meter: panelMeter,
          }}
          onOpenSentimentCustomizer={() => {
            console.log('Customize clicked!', sentimentCustomizerRef.current);
            if (sentimentCustomizerRef.current) {
              sentimentCustomizerRef.current.openPopup();
            }
          }}
        />
      </div>


      {/* Sentiment Customizer Popup Below */}

      <SentimentCustomizer
        ref={sentimentCustomizerRef}
        email={email}
        reorder={reorder}
        pcClassLabels={pcClassLabels}
        pcMajClassLabels={pcMajClassLabels}
        pcMinClassLabels={pcMinClassLabels}
        pcClass={pcClass}
        emoClassLabelsMajor={emoClassLabelsMajor}
        emoClassLabelsMinor={emoClassLabelsMinor}
        selectedPalette={selectedPalette}
        customPaletteName={customPaletteName}
        defaultPalettes={defaultPalettes}
        userPalettes={userPalettes}
        allPalettes={allPalettes}
        handleInputChangeMajor={handleInputChangeMajor}
        handleInputChangeMinor={handleInputChangeMinor}
        handleInputFocus={handleInputFocus}
        handleInputBlur={handleInputBlur}
        setSelectedPalette={setSelectedPalette}
        setEmoClassLabelsMajor={setEmoClassLabelsMajor}
        setEmoClassLabelsMinor={setEmoClassLabelsMinor}
        updatePalettes={updatePalettes}
        deletePalette={deletePalette}
        setCustomPaletteName={setCustomPaletteName}
        hasChanges={hasChanges}
        navigate={navigate}
        signOut={signOut}
        auth={auth}
      />

      {/* The panel row. Panels are placed by their `order`, taken from
          panelOrder above, so their sequence here does not decide what the user
          sees. Each one is sized from the registry — no coordinates. */}
      <PanelRow>

        {/* Staff View Component */}

        {isPanelRendered('staff') && (
          <Panel
            id="staff"
            appTheme={settings.appTheme}
            {...panelPlacement('staff')}
            {...panelControls('staff')}
            style={{ zIndex: 1 }}
          >

            <StaffView
              midiNotes={[...new Set([...activeNotes, ...heldNotes, ...toggledNotes])]}
              keyCenter={keyCenter}
              mode={mode}
              width={'22.4dvw'}
              height={'31.5dvw'}
              lockedTonicNote={lockedTonicNote}
              lockedTonicMode={lockedTonicMode}
              // rootNote is the compass anchor, which stays on the chord's root
              // under a key lock by default; the staff spells against the key
              rootNote={lockedTonicNote !== null ? lockedTonicNote : rootNote}
              isScale={isScale}
              chordSymbols={chordSymbols}
              chordSymbol={chordSymbol}
              csOptionOneSegments={displaySegment}
              scaleRoot={scaleRoot}
              scaleUrl={scaleUrl}
              activePcs={activePcs}
              activeNotes={activeNotes}
              heldNotes={heldNotes}
              toggledNotes={toggledNotes}
              assembleChordSymbol={assembleChordSymbol}
              assembleScaleName={assembleScaleName}
              keyflash={keyflash}
              showNashvilleNumber={settings.showNashvilleNumber}
              chordQual={chordQual}
              preferWhiteStaffPaper={settings.preferWhiteStaffPaper}
              showOvertones={settings.showOvertones}
              appTheme={settings.appTheme}
            />
          </Panel>
        )}


        {/* Chord Symbol Panel Display Below */}

        {isPanelRendered('chordSymbol') ? (

          <Panel
            id="chordSymbol"
            appTheme={settings.appTheme}
            {...panelPlacement('chordSymbol')}
            {...panelControls('chordSymbol')}
            chrome
            domId="chord-container"
            className="chord-display"
          >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p className="title-text" style={{
              color: `${settings.appTheme === 'light'
                ? 'rgb(0, 0, 0)'
                : 'rgb(255, 255, 255)'}`,
              margin: 0
            }}><span style={{ fontSize: '1.1dvw', fontWeight: 'bold', color: 'rgb(69, 207, 207)' }}>&#x2B06; </span>Chord Symbol</p>

            <span style={{
              fontFamily: 'Arial Unicode MS, Bravura Text', fontSize: '0.8dvw', verticalAlign: '-0.1dvw'
            }}>
              {/* Sort Mode: */}
            </span>

            <button
              onClick={() => handleSettingChange('preferNotationLegible', !settings.preferNotationLegible)}
              style={{
                background: 'none',
                border: 'none',
                minWidth: '5dvw',
                height: '1.5dvw',
                verticalAlign: 'middle',
                // textAlign: 'left',
                justifyContent: 'left',
                cursor: 'pointer',
                fontFamily: 'Arial Unicode MS, Bravura Text',
                fontSize: '0.8dvw',
                color: settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)',
                padding: '0.0dvw 0.5dvw',
                fontWeight: 'bold',
              }}
              title={settings.preferNotationLegible ? 'Sorted by easiest to READ (Legibility)' : 'Sorted by how chords FEEL (Root Perception)'}
            >
              {/* 21A7 = arrow down with top base */}
              {settings.preferNotationLegible
                ? 'READ'
                : 'FEEL'}<span style={{ alignItems: 'left', verticalAlign: '0.04dvw' }}>&nbsp;<img src={`/images/${settings.preferNotationLegible
                  ? 'Open-Book--Streamline-Core-Solid'
                  : 'Heart--Streamline-Core'}-${settings.appTheme === 'light'
                    ? 'black'
                    : 'white'}.png`} alt={settings.preferNotationLegible
                      ? 'READ'
                      : 'FEEL'} style={{ width: '0.9dvw', height: 'auto', verticalAlign: 'middle' }} /></span>
              {/* {settings.preferNotationLegible ? 'G7♭9' : '100%'}<span style={{ fontSize: '1.2dvw', verticalAlign: '-0.1dvw' }}>&#x2193;</span> */}
            </button>
          </div>
          <hr style={{
            backgroundColor: '#fff', fontSize: '0.3dvw', width: '100%',
            border: `0.03dvw solid ${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'}`,
            boxShadow: `0 0 0.3dvw 0.2dvw ${settings.appTheme === 'light' ? 'rgba(255, 255, 255, 0.3' : 'rgb(68, 68, 68)'}`
          }}></hr>
          <p className="title-text"></p>
          <table
            //           title="* All possible chord symbols arranged by the most likely perceived root.&#13;&#13;
            // * Chord roots are prioritized by the way the chord feels which may not be the most practical way to read it on a chart. (Notation priority helps!)&#13;&#13;
            // * Scale names appear when all of its notes are played within one octave. Click the scale name for details."
            style={{
              fontSize: isScale
                ? `${2.7 - (chordSymbol.length * 0.04)}dvw`
                : `${4.0 - (chordSymbols.length * 0.001) - (chordSymbol.length * 0.1)}dvw`,
              paddingTop: isScale ? '1dvw' : '0.5dvw',
              fontStyle: isScale ? 'normal' : 'normal',
              border: "none",
              borderSpacing: "0.2dvw",
              margin: "0dvw",
              backgroundColor: "transparent"
            }}>
            <tbody>
              {(activeNotes.length > 1 || heldNotes.length > 1 || toggledNotes.length > 1) && activePcs.length > 1 && chordSymbols && chordSymbols.split('\n').map((symbol, index) => (
                <tr key={`${symbol}-${index}-${mode}`} className={(index === 0 ? "chord-symbol" : (pcWeightsSorted[index] >= settings.secondaryChordThreshold ? "chord-symbol-poly" : "chord-symbols"))}
                  style={index === 0 ? {
                    color: settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)',
                  } : {
                    color: settings.appTheme === 'light' ?
                      (chordEditMode && index === selectedChordOptionIndex) || (!chordEditMode && selectedChartRootPc !== null && csOptionsSegments[index] && csOptionsSegments[index].root_pc === selectedChartRootPc) ? 'rgb(0, 0, 0)' :
                        `rgb(
                    ${255 - (pcWeightsSorted[index] * 13.2 + 120)},
                    ${255 - (pcWeightsSorted[index] * 13.2 + 120)},
                    ${255 - (pcWeightsSorted[index] * 13.2 + 120)}` :
                      (chordEditMode && index === selectedChordOptionIndex) || (!chordEditMode && selectedChartRootPc !== null && csOptionsSegments[index] && csOptionsSegments[index].root_pc === selectedChartRootPc) ? 'rgb(255, 255, 255)' :
                        `rgb(
                    ${pcWeightsSorted[index] * 3.2 + 70},
                    ${pcWeightsSorted[index] * 3.2 + 70},
                    ${pcWeightsSorted[index] * 3.2 + 70})`,

                    fontSize: `${2.7 - (chordSymbols.length * 0.003) - (symbol.length * 0.03)}dvw`
                  }}
                >
                  <td className="chord-percent" style={{ border: "none", margin: "0", backgroundColor: "transparent" }}>
                    {(settings.showOvertones ? activePlusOvertonePcs.length : activePcs.length) > 1 && pcWeightsSorted[index] !== undefined ? `${pcWeightsSorted[index].toFixed(1)}%\u00A0` : ""}
                  </td>
                  <td
                    className={settings.showChordQualityColors && activePcs.length > 1 && pcSorted || isScale ? "selected-chord-symbol-row" : ""}
                    style={{
                      // border: "none",
                      padding: isScale ? '0.0dvw 0.45dvw' : '0.0dvw 0.0dvw',
                      margin: '0',
                      borderRadius: '0.7dvw',
                      border: `0.09dvw ${settings.showChordQualityColors && activePcs.length > 1 ? 'solid' : 'none'} rgba(${modes[index] === 0 ? '163, 153, 60' : '70, 100, 162'}, ${isScale ? 1 : (pcWeightsSorted[index] * 0.02 + 0.1)})`,
                      background: `rgba(${modes[index] === 0
                        ? (settings.appTheme === 'light'
                          ? '203, 193, 90'
                          : '123, 113, 20')
                        : (settings.appTheme === 'light'
                          ? '110, 140, 202'
                          : '30, 60, 122')}, ${(settings.showChordQualityColors || (chordEditMode && index === selectedChordOptionIndex) || (!chordEditMode && selectedChartRootPc !== null && csOptionsSegments[index] && csOptionsSegments[index].root_pc === selectedChartRootPc)) && activePcs.length > 1
                            ? isScale || (chordEditMode && index === selectedChordOptionIndex) || (!chordEditMode && selectedChartRootPc !== null && csOptionsSegments[index] && csOptionsSegments[index].root_pc === selectedChartRootPc)
                              ? 1.0
                              : (pcWeightsSorted[index] * 0.015 + 0.15)
                            : 0})`,

                      // backgroundColor: `rgba(${modes[index] === 0 ? '90, 75, 0' : '41, 49, 120'}, ${pcWeightsSorted[index] * 0.02 + 0.2})`,
                      // background: `linear-gradient(to bottom, rgba(${modes[index] === 0 ? '123, 113, 20' : '48, 60, 169'}, ${pcWeightsSorted[index] * 0.02 + 0.2}),
                      // rgba(${modes[index] === 0 ? '60, 45, 0' : '31, 39, 110'}, ${pcWeightsSorted[index] * 0.03 + 0.3}))`,
                      // Added text shadow to improve readability of chord symbol on light color background
                      textShadow: `0 0 0.2dvw ${settings.appTheme === 'light' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'}, 0 0 0.7dvw ${settings.appTheme === 'light' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'}`,
                      ...((chordEditMode && index === selectedChordOptionIndex) || (!chordEditMode && selectedChartRootPc !== null && csOptionsSegments[index] && csOptionsSegments[index].root_pc === selectedChartRootPc) ? { outline: `0.15dvw solid ${settings.appTheme === 'light' ? 'rgba(7, 92, 92, 0.9)' : 'rgba(100, 255, 255, 0.75)'}`, outlineOffset: '0.04dvw', outlineRadius: '0.1dvw', animation: chordEditMode ? 'pulse-outline-bw 0.5s linear infinite' : 'none' } : {})
                    }}>
                    {isScale ? (<a style={{
                      color: settings.appTheme === "light" ? "rgb(0, 0, 0)" : "rgb(255, 255, 255)", textDecoration: "none", fontFamily: 'Bravura Text', display: 'inline-block',
                    }}
                      href={`${scaleUrl}`} target="_blank" rel="noopener noreferrer">{assembleScaleName(scaleRoot, mode)} {/*} + " " + activePcs.sort((a, b) => a - b)} */}
                    </a>
                    ) : (<span className={activePcs.length === 1 ? "note" : "chord"} onClick={() => {
                      if (chordEditMode) {
                        replaceChordInChart(index);
                      } else {
                        // Placed first: addEntryToChart's own opening then finds it on screen
                        openChartBefore('chordSymbol');
                        addEntryToChart(index, false, lockedTonicNote, lockedTonicMode);
                      }
                    }}>
                      {
                        csOptionsSegments[index]
                          ? assembleChordSymbol(csOptionsSegments[index], false, lockedTonicNote, lockedTonicMode)
                          : symbol
                      }
                    </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>


          {/* Chord Chart Message. It explains how to get a chord INTO the chart,
              so it is only worth the space while the chart is on screen to
              receive one - and once it has been read and closed it does not
              come back, on this visit or any later one.
              To see it again (or to test it), in the browser console:
                localStorage.removeItem('harmona.chartHintDismissed')
              and reload. Setting that key to 'true' puts it back away. */}
          {chordChart.length > 0 && chartPanelShown && !chartHintDismissed ? (
            <div style={{
              padding: '1dvw', bottom: '0dvw', position: 'absolute', textAlign: 'center', fontFamily: 'Trebuchet MS', fontSize: '0.8dvw', fontWeight: '700', color: '#777'
            }}>

              {/* moved to the top of the chord panel <ToggleSwitch
                isChecked={settings.preferNotationLegible}
                onChange={(checked) => handleSettingChange('preferNotationLegible', checked)}
                labelOff="Sentiment Priority"
                labelOn="Notation Priority"
                appTheme={settings.appTheme}
              /> */}

              {/* <br></br> */}
              {"Hit the 'return' key to add the top chord or click any chord to add it to the chart"}

              {/* Out of the text flow, up in the box's own padding. Inline it
                  cost the last line a word's width and pushed one word onto a
                  line of its own. The message div is already absolute, so it is
                  this one's containing block. */}
              <span
                data-actionable="true"
                onClick={dismissChartHint}
                title="Got it - don't show this again"
                style={{
                  position: 'absolute',
                  top: '0.1dvw',
                  right: '0.15dvw',
                  cursor: 'pointer',
                  padding: '0dvw 0.2dvw',
                  fontSize: '0.75dvw',
                  color: '#999',
                  fontWeight: '700'
                }}
              >
                {'\u2716'}
              </span>

              {/* {"SAVE and EXPORT chart coming soon!"}<br></br> */}
              {/* {'For now use "Copy Chord Chart" '}<span style={{ verticalAlign: '-0.3dvw', fontSize: '1.4dvw' }}>{'\u29C9'}</span> */}
            </div>
          ) : (
            activePcs.length >= 0 ? (
              <div style={{
                padding: '1dvw', bottom: '0dvw', position: 'absolute', textAlign: 'center', fontFamily: 'Trebuchet MS', fontSize: '0.76dvw', fontWeight: '100', color: '#777'
              }}>
                {isScale ? (
                  "Click scale name above for more info"
                ) : (
                  <>

                    {/* moved to the top of the chord panel <ToggleSwitch
                      isChecked={settings.preferNotationLegible}
                      onChange={(checked) => handleSettingChange('preferNotationLegible', checked)}
                      labelOff="Sentiment Priority"
                      labelOn="Notation Priority"
                      appTheme={settings.appTheme}
                    /> */}

                    {/* <br></br> */}
                    {/* "Click chord symbols above to build charts" */}
                  </>
                )}
                {/* Chord Chart Message End */}
              </div>
            ) : (<span></span>)
          )}
          </Panel>

        ) : null}

      {/*
        \uD834\uDD34 = common time
        \uE08E = slash
        \u00A0 = space
        \uE084 = time signature 4
        \uD834\uDD03 = Double bar start
        \uD834\uDD02 = Double bar end
      */}




        {/* Emo Labels / Sentiment Panel Display below */}

        {
          isPanelRendered('sentiment') ? (

            <Panel
              id="sentiment"
              appTheme={settings.appTheme}
              {...panelPlacement('sentiment')}
              {...panelControls('sentiment')}
              chrome
              lightAlpha={0.6}
              padding="0.5dvw 0dvw"
              className="emo-display"
              style={{ fontSize: `${1.3 - (chordSymbols.length * 0.0001)}dvw` }}
            >
            <span className="title-text" style={{ padding: '0.5dvw', color: `${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'}` }}>Sentiment {'\u2001'}</span>

            {/* <span style={{
              fontSize: '0.9dvw', fontFamily: 'Trebuchet MS', color: 'grey'
            }}> [ OLD SENTIMENT CUSTOMIZER */}
            {/* </span> */}

            <hr style={{
              backgroundColor: '#fff', fontSize: '0.3dvw', width: '95%', padding: '0dvw 0.5dvw', marginLeft: '0.5dvw',
              border: `0.03dvw solid ${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'}`,
              boxShadow: `0 0 0.3dvw 0.2dvw ${settings.appTheme === 'light' ? 'rgba(255, 255, 255, 0.3' : 'rgb(68, 68, 68)'}`
            }}></hr>

            <div style={{ height: '0.0dvw' }}> </div>

            {/* Sentiment Circles Visualization */}
            {sentimentCircleData && activePcs.length > 0 && chordSymbols && (
              <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet"
                style={{
                  width: '22dvw',
                  height: '14dvw',
                  margin: '0dvw auto',
                  display: 'block'
                }}
              >
                <defs>
                  {/* Inset (inner) shadow for SVG circles.
                      drop-shadow() is outer-only; this builds a visible inner rim by:
                      eroding the alpha -> rim = alpha - eroded(alpha) -> blur/offset -> tint -> composite. */}
                  <filter
                    id="sentimentInsetDark"
                    filterUnits="userSpaceOnUse"
                    x="-20"
                    y="-20"
                    width="140"
                    height="140"
                  >
                    {/* Darken mainly the OUTER ring (inside edge), not the whole interior */}
                    <feMorphology in="SourceAlpha" operator="erode" radius="0.5" result="eroded" />
                    {/* rim = alpha - eroded(alpha) => thin band near the edge */}
                    <feComposite in="SourceAlpha" in2="eroded" operator="out" result="rim" />
                    {/* Crisp edge component: unblurred rim so the darkening hits the exact edge */}
                    <feFlood floodColor="rgb(0,0,0)" floodOpacity="0.58" result="edgeColor" />
                    <feComposite in="edgeColor" in2="rim" operator="in" result="shadowEdge" />

                    {/* Soft depth component: subtle blur/offset for an inner drop-shadow feel */}
                    <feGaussianBlur in="rim" stdDeviation="1.7" result="rimBlur" />
                    <feOffset in="rimBlur" dx="0" dy="-0.35" result="rimOffset" />
                    <feFlood floodColor="rgb(0,0,0)" floodOpacity="0.5" result="depthColor" />
                    <feComposite in="depthColor" in2="rimOffset" operator="in" result="shadowDepth" />

                    <feMerge result="shadow">
                      <feMergeNode in="shadowEdge" />
                      <feMergeNode in="shadowDepth" />
                    </feMerge>
                    <feBlend in="SourceGraphic" in2="shadow" mode="multiply" />
                  </filter>

                  <filter
                    id="sentimentInsetLight"
                    filterUnits="userSpaceOnUse"
                    x="-20"
                    y="-20"
                    width="140"
                    height="140"
                  >
                    <feMorphology in="SourceAlpha" operator="erode" radius="1.0" result="eroded" />
                    <feComposite in="SourceAlpha" in2="eroded" operator="out" result="rim" />
                    <feFlood floodColor="rgb(0,0,0)" floodOpacity="0.16" result="edgeColor" />
                    <feComposite in="edgeColor" in2="rim" operator="in" result="shadowEdge" />

                    <feGaussianBlur in="rim" stdDeviation="0.6" result="rimBlur" />
                    <feOffset in="rimBlur" dx="0" dy="0.45" result="rimOffset" />
                    <feFlood floodColor="rgb(0,0,0)" floodOpacity="0.30" result="depthColor" />
                    <feComposite in="depthColor" in2="rimOffset" operator="in" result="shadowDepth" />

                    <feMerge result="shadow">
                      <feMergeNode in="shadowEdge" />
                      <feMergeNode in="shadowDepth" />
                    </feMerge>
                    <feBlend in="SourceGraphic" in2="shadow" mode="multiply" />
                  </filter>
                </defs>

                {/* Static dark grey background circle - always matches largest circle size, never animates */}
                <circle
                  cx={50}
                  cy={sentimentCircleData.largestCy}
                  r={sentimentCircleData.largestRadius}
                  fill={settings.appTheme === 'light' ? '#ccc' : mode === 0 ? '#333' : '#222'}
                  stroke={settings.appTheme === 'light' ? '#ccc' : mode === 0 ? '#333' : '#222'}
                  strokeWidth="0.5"
                  opacity="1"
                  style={{
                    // Simple depth: drop-shadow per circle (theme-aware)
                    filter: settings.appTheme === 'light'
                      ? 'drop-shadow(0 0 0.35dvw rgba(0, 0, 0, 0.22)) drop-shadow(0 0 0.15dvw rgba(0, 0, 0, 0.16))'
                      : mode === 0
                        ? 'drop-shadow(0 0 0.06dvw rgb(39, 31, 0)) drop-shadow(0 0 0.5dvw rgb(87, 68, 0)) drop-shadow(0 0 0.99dvw rgb(255, 242, 168))'
                        : 'drop-shadow(0 0 0.06dvw rgb(6, 21, 56)) drop-shadow(0 0 0.5dvw rgb(13, 44, 94)) drop-shadow(0 0 0.99dvw rgb(95, 159, 255))',
                    transition: 'filter 0.3s ease-in-out'
                  }}
                />

                {sortedSentiments.map(([label, data], index) => {
                  if (!label) return null;
                  const { radius, cy } = sentimentCircleData.sizesByLabel.get(label);
                  const textY = sentimentCircleData.textYByLabel.get(label);

                  const minFontSize = 4.5;
                  const maxFontSize = 7;
                  const fontSize = Math.max(minFontSize, Math.min(maxFontSize, minFontSize + (radius / sentimentCircleData.maxRadius) * (maxFontSize - minFontSize)));

                  // Debug logging - remove after testing
                  // console.log(`Rendering circle ${label} at index ${index}, r=${radius.toFixed(2)}, cy=${cy.toFixed(2)}`);

                  const primaryPcIdx = emoClassLabels.indexOf(label);
                  const secondaryPcIdx = emoClassLabelsSecondRoot.indexOf(label);
                  const isCircleFlashing = !!(primaryLabelFlashes[primaryPcIdx] || secondaryLabelFlashes[secondaryPcIdx]);

                  return (
                    <g
                      key={`circle-${label}`}
                      style={{
                        transformOrigin: '50px 95px'
                      }}
                    >
                      {/* Single fill circle with true inset shading (no stroke). */}
                      <circle
                        className="sentiment-circle"
                        cx={50}
                        cy={cy}
                        r={radius}
                        filter={`url(#${settings.appTheme === 'light' ? 'sentimentInsetLight' : 'sentimentInsetDark'})`}
                        fill={getEmoColor(data.emoClassName, data.rootIndex)}
                        stroke="none"
                        opacity={0.9}
                        style={{
                          // Simple depth: drop-shadow per circle (theme-aware)
                          // filter: settings.appTheme === 'light'
                          //   ? 'drop-shadow(0 0 0.35dvw rgba(0, 0, 0, 0.22)) drop-shadow(0 0 0.15dvw rgba(0, 0, 0, 0.16))'
                          //   : 'drop-shadow(0 0 0.35dvw rgba(0, 0, 0, 0.55)) drop-shadow(0 0 0.15dvw rgba(0, 0, 0, 0.35))',
                          // transition: 'r 0.3s ease-in-out, cy 0.3s ease-in-out, opacity 0.3s ease-in-out'
                        }}
                      />
                      {/* Flash overlay — no SVG filter so fill animates correctly (black->white).
                          Conditionally mounted for the 65ms flash window, then unmounted. */}
                      {isCircleFlashing && (
                        <circle
                          cx={50}
                          cy={cy}
                          r={radius}
                          fill="black"
                          stroke="none"
                          className="flash-circle-overlay"
                        />
                      )}
                      <g className="sentiment-circle-text" style={{ transform: `translateY(${textY}px)` }}>
                        <text
                          x={50}
                          y={0}
                          textAnchor="middle"
                          fill="black"
                          fontSize={fontSize}
                          // fontWeight="bold"
                          className={`sentiment-circle-label${isCircleFlashing ? ' flash-circle-label' : ''}`}
                        >
                          {label}
                        </text>
                      </g>
                    </g>
                  );
                })}
              </svg>
            )}

            {/* <div style={{ height: '0.1dvw' }}> </div> */}

            <div style={{ marginLeft: '0.7dvw', marginRight: '0.5dvw', marginTop: '0.3dvw', marginBottom: '0.5dvw' }}>

              {chordSymbols && activePcs.length > 0 && (
                <>
                  {/* Add Chord Symbol or Scale Name to top of Sentiments */}
                  {activeEmoLabels.some(label => label) && (
                    <text fill="grey" className='musicfont'
                      style={{
                        fontSize: `${1.4 - (chordSymbols.length * 0.001)}dvw`,
                        // fontFamily: 'Trebuchet MS',
                        // fontStyle: "italic",
                        // fontWeight: "700",
                        // stroke: "rgb(160,160,160)",
                        // strokeWidth: "0.1",
                        // border: "none", 
                        borderSpacing: "0.0dvw",
                        color: `${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(185, 185, 185)'}`,
                        opacity: isScale ? 1 : `${Math.max(pcWeightsSorted[0] * .03, 0.5)}`,
                        margin: "0.1dvw 0dvw 0dvw 0dvw"
                      }}>
                      {/* {renderChordSymbol(chordSymbol)} */}
                      {isScale ? (
                        <>
                          <tspan x="0" dy="1.2em">&#x0a;
                            <a style={{ color: settings.appTheme === "light" ? "rgb(0, 0, 0)" : "rgb(256, 256, 256)", textDecoration: "none", fontFamily: 'Bravura Text' }}
                              href={`${scaleUrl}`} target="_blank" rel="noopener noreferrer">{assembleScaleName(scaleRoot, mode)}
                            </a></tspan><br></br>
                        </>
                      )
                        : (csOptionsSegments[0] && assembleChordSymbol(csOptionsSegments[0], false, anchorTonicNote, lockedTonicMode))}
                    </text>
                  )}

                  {/* For primary emotion labels */}
                  {activeEmoLabels.map((label, index) => {
                    const pcIndex = emoClassLabels.indexOf(label);
                    const emoClassName = emoClass[pcIndex];
                    // Convert from fifths representation to chromatic for comparison
                    const absoluteChromaticPc = ((pcIndex * 7) % 12 + rootNote) % 12;
                    const isOvertonePc = activePlusOvertonePcs.includes(absoluteChromaticPc) && !activePcs.includes(absoluteChromaticPc);
                    return (
                      <>
                        {/* {index === 0 && <br />} */}
                        <div key={index} className={`${primaryLabelFlashes[pcIndex] ? 'flash-emo' : ''} ${isOvertonePc ? 'overtone-pulse-emo' : ''}`} style={{
                          fontSize: `${1.0 - (chordSymbol.length * 0.0001)}dvw`,
                          fontWeight: "100",
                          fontStyle: "bold",
                          opacity: `${Math.max(pcWeightsSorted[0] * .03, 0.5)}`,
                          // textIndent: "0.5dvw",
                          margin: label === "" ? "0dvw" : "0.2dvw",
                          padding: label === "" ? "0dvw" : "0.05dvw 0.2dvw 0.1dvw 0.2dvw",
                          color: getEmoColor(emoClassName, 0),
                          backgroundColor: label === "" ? "transparent" : `${settings.appTheme === 'light' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(30, 30, 30, 0.95)'}`,
                          borderRadius: '0.5dvw',
                          border: label === "" ? "none" : settings.appTheme === 'light' ? '0.07dvw solid rgba(255, 255, 255, 0.99)' : '0.03dvw solid rgba(255, 255, 255, 0.4)',
                          // width: 'auto',
                          display: 'inline-block',
                        }}>{label}</div>
                      </>
                    )
                  })}
                  {/* <p></p> */}
                  <div>

                    {/* For secondary emotion labels */}
                    {(pcSorted.length > 1 && pcWeightsSorted[1] >= settings.secondaryChordThreshold) && (
                      <>
                        {/* Secondary labels content */}
                        <text fill="grey" className='musicfont'
                          style={{
                            fontSize: `${1.2 - (chordSymbolTwo.length * 0.001)}dvw`,
                            // fontFamily: 'Trebuchet MS',
                            // fontStyle: "italic",
                            // fontWeight: "700",
                            color: `${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(185, 185, 185)'}`,
                            opacity: `${Math.max(pcWeightsSorted[0] * .03, 0.5)}`,
                            margin: "0.1dvw 0dvw 0dvw 0dvw"
                          }}>
                          {/* {renderChordSymbol(chordSymbolTwo)} */}
                          {csOptionsSegments[1] && assembleChordSymbol(csOptionsSegments[1], false, anchorTonicNote, lockedTonicMode)}
                        </text>

                        {activeEmoLabelsSecondRoot.map((label, index) => {
                          const pcIndex = emoClassLabelsSecondRoot.indexOf(label);
                          const emoClassName = emoClass[pcIndex];
                          // Convert from fifths representation to chromatic for comparison
                          const secondRootNote = pcSorted[1] || rootNote;
                          const absoluteChromaticPc = ((pcIndex * 7) % 12 + secondRootNote) % 12;
                          const isOvertonePc = activePlusOvertonePcs.includes(absoluteChromaticPc) && !activePcs.includes(absoluteChromaticPc);
                          return (
                            <>
                              {/* {index === 0 && <br />} */}
                              <div key={`secondRoot-${index}`} className={`${secondaryLabelFlashes[pcIndex] ? 'flash-emo' : ''} ${isOvertonePc ? 'overtone-pulse-emo' : ''}`} style={{
                                fontSize: `${1.0 - (chordSymbols.length * 0.0009)}dvw`,
                                fontWeight: "100",
                                // textIndent: "2.6dvw",
                                lineHeight: "1.5dvw",
                                // background: 'rgb(30,30,30)',
                                opacity: secondaryLabelFlashes[pcIndex] === true ? "1" : `${pcWeightsSorted[1] * .02 + 0}`,
                                color: getEmoColor(emoClassName, 1),
                                margin: label === "" ? "0dvw" : "0.2dvw",
                                padding: label === "" ? "0dvw" : "0.05dvw 0.2dvw 0.1dvw 0.2dvw",
                                backgroundColor: label === "" ? "transparent" : `${settings.appTheme === 'light' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(30, 30, 30, 0.99)'}`,
                                borderRadius: '0.5dvw',
                                border: label === "" ? "none" : settings.appTheme === 'light' ? '0.07dvw solid rgba(255, 255, 255, 0.99)' : '0.03dvw solid rgba(255, 255, 255, 0.4)',
                                // width: 'auto',
                                display: 'inline-block',
                              }}>{label}</div>
                            </>
                          );
                        })
                        }
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            </Panel>

          ) : null
        }

        {/* end Emo Labels Display */}


        {/*  Pie Chart/Chord Compass Below */}

        {
          isPanelRendered('chordCompass') ? (

          // OLD GRADIENTS
          //  <div className='pie-chart' style={{
          //   backgroundImage: (lockedTonicNote !== null)
          //     ? (lockedTonicMode === 0
          //       ? 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(255, 254, 214) 15%, rgb(243, 197, 157) 47.5%, rgb(139, 134, 96) 50.5%, rgb(91, 70, 39) 69%, rgb(0, 0, 0) 100%)'  // Major gradient
          //       : 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(214, 217, 255) 15%,rgb(140, 163, 212) 47.5%, rgb(100, 119, 167) 50.5%, rgb(39, 49, 91) 69%, rgb(0, 0, 0) 100%)')  // Minor gradient
          //     : (pieChordModes[0] === 0 && chordSymbol != '')
          //       ? 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(255, 254, 214) 15%, rgb(243, 197, 157) 47.5%, rgb(139, 134, 96) 50.5%, rgb(91, 70, 39) 69%, rgb(0, 0, 0) 100%)'  // Major gradient
          //       : (pieChordModes[0] === 1 && chordSymbol != '')
          //         ? 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(214, 217, 255) 15%,rgb(140, 163, 212) 47.5%, rgb(100, 119, 167) 50.5%, rgb(39, 49, 91) 69%, rgb(0, 0, 0) 100%)'  // Minor gradient
          //         : 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(255, 254, 214) 15%, rgb(243, 197, 157) 47.5%, rgb(107, 117, 142) 50.5%, rgb(39, 49, 91) 69%, rgb(0, 0, 0) 100%)'  // Default gradient
          // }}>

          <Panel
            id="chordCompass"
            appTheme={settings.appTheme}
            {...panelPlacement('chordCompass')}
            {...panelControls('chordCompass')}
            className="pie-chart"
          >
            {/* Base/Default gradient layer */}
            <div className='pie-chart' style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(255, 254, 214) 15%, rgb(243, 197, 157) 47.5%, rgb(107, 117, 142) 50.5%, rgb(39, 49, 91) 69%, rgb(0, 0, 0) 100%)',
              opacity: 1,
              zIndex: 0,
              boxShadow: `0 0 0.6dvw 0.35dvw rgba(0, 0, 0, ${settings.appTheme === "light" ? 0.2 : 0.5}), inset 0dvw 0dvw 5dvw 0.5dvw rgba(27, 20, 13, ${settings.appTheme === "light" ? 0.6 : 0.99})`
            }} />


            {/* Major gradient layer. The panel background shows the locked key's
                mode whenever a key is locked, whatever the anchor setting; the
                inner circle below always shows the current chord's mode. */}
            <div className='pie-chart' style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(255, 254, 214) 15%, rgb(243, 197, 157) 47.5%, rgb(139, 134, 96) 50.5%, rgb(91, 70, 39) 69%, rgb(0, 0, 0) 100%)',
              opacity: ((lockedTonicNote !== null && lockedTonicMode === 0) || (lockedTonicNote === null && pieSliceMode === 0 || (lockedTonicNote === null && isScale && mode === 0)) && chordSymbol !== '') ? 1 : 0,
              transition: 'opacity 0.4s ease-in-out',
              zIndex: 0,
              boxShadow: `inset 0dvw 0dvw 4dvw 0.0dvw rgba(255, 255, 255, ${settings.appTheme === "light" ? 0.0 : 0.5})`
            }} />

            {/* Minor gradient layer */}
            <div className='pie-chart' style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'linear-gradient(to bottom, rgb(255, 255, 255) 0%, rgb(214, 217, 255) 15%,rgb(140, 163, 212) 47.5%, rgb(100, 119, 167) 50.5%, rgb(39, 49, 91) 69%, rgb(0, 0, 0) 100%)',
              opacity: ((lockedTonicNote !== null && lockedTonicMode === 1) || (lockedTonicNote === null && pieSliceMode === 1 || (lockedTonicNote === null && isScale && mode === 1)) && chordSymbol !== '') ? 1 : 0,
              transition: 'opacity 0.4s ease-in-out',
              zIndex: 0,
              boxShadow: `inset 0dvw 0dvw 5dvw 2.0dvw rgba(0, 0, 0, ${settings.appTheme === "light" ? 0.2 : 0.99})`
            }} />

            {/* The global `svg` rule in App.css sets height: 37dvw, and a
                presentation attribute loses to any author rule — so height="100%"
                is ignored and this box is 37dvw tall inside a 31.5dvw panel,
                hanging 5.5dvw below it. The wheel is drawn entirely inside the
                panel, but the overhanging box was still a pointer target, which
                stretched the panel's hover area down over the piano keys.
                Nothing here is interactive, so it opts out of hit testing. */}
            <svg viewBox="0 5 64 64" preserveAspectRatio="xMidYMid meet" width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              <defs>
                <linearGradient id="p-root" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={colors.R[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.R[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="p-rootActive" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={colors.R[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.R[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="min-second" gradientTransform="rotate(200, 0.43, 0.5)">
                  <stop offset="0%" stopColor={colors.m2[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="20%" stopColor={colors.m2[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="min-secondActive" gradientTransform="rotate(200, 0.43, 0.50)">
                  <stop offset="0%" stopColor={colors.m2[pieSliceMode]} stopOpacity="1" />
                  <stop offset="30%" stopColor={colors.m2[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="maj-second" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M2[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.M2[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="maj-secondActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M2[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.M2[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="min-third" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.m3[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.m3[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="min-thirdActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.m3[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.m3[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="maj-third" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M3[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.M3[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="maj-thirdActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M3[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.M3[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="p-fourth" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.P4[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.P4[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="p-fourthActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.P4[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.P4[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="tritone" gradientTransform="rotate(83, 0.5, 0.5)">
                  <stop offset="15%" stopColor={colors.d5[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="45%" stopColor={colors.d5[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="tritoneActive" gradientTransform="rotate(83, 0.5, 0.5)">
                  <stop offset="15%" stopColor={colors.d5[pieSliceMode]} stopOpacity="1" />
                  <stop offset="45%" stopColor={colors.d5[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="p-fifth" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.P5[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.P5[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="p-fifthActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.P5[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.P5[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="min-sixth" gradientTransform="rotate(135, 0.9, 0.92)">
                  <stop offset="50%" stopColor={colors.m6[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="75%" stopColor={colors.m6[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="min-sixthActive" gradientTransform="rotate(135, 0.9, 0.92)">
                  <stop offset="50%" stopColor={colors.m6[pieSliceMode]} stopOpacity="1" />
                  <stop offset="75%" stopColor={colors.m6[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="maj-sixth" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M6[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.M6[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="maj-sixthActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M6[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.M6[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="min-seventh" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.m7[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.m7[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="min-seventhActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.m7[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.m7[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                <linearGradient id="maj-seventh" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M7[pieSliceMode]} stopOpacity={inactiveOpacity} />
                  <stop offset="100%" stopColor={colors.M7[pieSliceMode]} stopOpacity={inactiveOpacity} />
                </linearGradient>
                <linearGradient id="maj-seventhActive" gradientTransform="rotate(90)">
                  <stop offset="0%" stopColor={colors.M7[pieSliceMode]} stopOpacity="1" />
                  <stop offset="100%" stopColor={colors.M7[pieSliceMode]} stopOpacity="1" />
                </linearGradient>

                {/* <linearGradient id="majorGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: "rgb(80,80,100)", stopOpacity: 1 }} />
                <stop offset="60%" style={{ stopColor: "rgb(220,200,160)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* DEFAULTGRADIENT Left to Right */}
                {/* <linearGradient id="defaultGradient" x1="130%" y1="0%" x2="-35%" y2="0%">
                <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                <stop offset="0%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                <stop offset="45.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                <stop offset="51.5%" style={{ stopColor: "rgb(107, 117, 142)", stopOpacity: 1 }} />
                <stop offset="99%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* DEFAULTGRADIENT Top to Bottom */}
                <linearGradient id="defaultGradient" x1="0%" y1="-33%" x2="0%" y2="132%">
                  <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                  <stop offset="10%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                  <stop offset="46.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                  <stop offset="51.5%" style={{ stopColor: "rgb(107, 117, 142)", stopOpacity: 1 }} />
                  <stop offset="99%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
                </linearGradient>

                {/* <linearGradient id="majorGradient" x1="56%" y1="0%" x2="-99%" y2="0%">
                <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                <stop offset="1%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                <stop offset="8.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                <stop offset="91.5%" style={{ stopColor: "rgb(107, 117, 142)", stopOpacity: 1 }} />
                <stop offset="99%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* <linearGradient id="majorGradient" x1="60%" y1="-10%" x2="-140%" y2="190%">
                <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                <stop offset="15%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                <stop offset="25.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                <stop offset="37.5%" style={{ stopColor: "rgb(107, 117, 142)", stopOpacity: 1 }} />
                <stop offset="74%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* MAJORGRADIENT Left to Right */}
                {/* <linearGradient id="majorGradient" x1="49%" y1="0%" x2="-70%" y2="0%">
                <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                <stop offset="1%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                <stop offset="30.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                <stop offset="61.5%" style={{ stopColor: "rgb(97, 107, 132)", stopOpacity: 1 }} />
                <stop offset="79%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* MAJORGRADIENT Top to Bottom */}
                <linearGradient id="majorGradient" x1="0%" y1="53%" x2="0%" y2="-66%" gradientTransform="rotate(180, 0.5, 0.5)">
                  <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                  <stop offset="1%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                  <stop offset="30.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                  <stop offset="61.5%" style={{ stopColor: "rgb(97, 107, 132)", stopOpacity: 1 }} />
                  <stop offset="79%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
                </linearGradient>

                {/* <linearGradient id="minorGradient" x1="60%" y1="-80%" x2="-10%" y2="20%">
                <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                <stop offset="15%" style={{ stopColor: "rgb(255, 254, 214)", stopOpacity: 1 }} />
                <stop offset="30.5%" style={{ stopColor: "rgb(243, 197, 157)", stopOpacity: 1 }} />
                <stop offset="61.5%" style={{ stopColor: "rgb(107, 117, 142)", stopOpacity: 1 }} />
                <stop offset="89%" style={{ stopColor: "rgb(39, 49, 91)", stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: "rgb(0, 0, 0)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* MINORGRADIENT Left To Right */}
                {/* <linearGradient id="minorGradient" x1="145%" y1="0%" x2="0%" y2="0%">
                <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                <stop offset="49%" style={{ stopColor: "rgb(107, 117, 162)", stopOpacity: 1 }} />
                <stop offset="63.5%" style={{ stopColor: "rgb(56, 67, 132)", stopOpacity: 1 }} />
                <stop offset="67%" style={{ stopColor: "rgb(34, 49, 121)", stopOpacity: 1 }} />
                <stop offset="70%" style={{ stopColor: "rgb(29, 39, 101)", stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: "rgb(0, 5, 56)", stopOpacity: 1 }} />
              </linearGradient> */}

                {/* MINORGRADIENT Top to Bottom */}
                <linearGradient id="minorGradient" x1="0%" y1="140%" x2="0%" y2="5%" gradientTransform="rotate(180, 0.5, 0.5)">
                  <stop offset="0%" style={{ stopColor: "rgb(255, 255, 255)", stopOpacity: 1 }} />
                  <stop offset="49%" style={{ stopColor: "rgb(107, 117, 162)", stopOpacity: 1 }} />
                  <stop offset="63.5%" style={{ stopColor: "rgb(56, 67, 132)", stopOpacity: 1 }} />
                  <stop offset="67%" style={{ stopColor: "rgb(34, 49, 121)", stopOpacity: 1 }} />
                  <stop offset="70%" style={{ stopColor: "rgb(29, 39, 101)", stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: "rgb(0, 5, 56)", stopOpacity: 1 }} />
                </linearGradient>

                <filter id="rectShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow
                    dx="0.2"
                    dy="0.2"
                    stdDeviation="0.35"
                    floodColor="black"
                    floodOpacity="0.8"
                  />
                </filter>

                {/* Modern subtle radial gradient overlay */}
                <radialGradient id="sliceHighlight" cx="0.5" cy="0.5" r="0.5" fx="0.4" fy="0.4">
                  <stop offset="0%" stopColor="#fff" stopOpacity="0.17" />
                  <stop offset="100%" stopColor="#aaa" stopOpacity="0" />
                </radialGradient>

                {/* Very subtle noise texture */}
                {/* <filter id="modernNoise" x="0%" y="0%" width="100%" height="100%">
                <feTurbulence type="fractalNoise" baseFrequency="0.4" numOctaves="2" seed="5" stitchTiles="stitch" result="noise" />
                <feColorMatrix type="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 0.03 0" in="noise" result="coloredNoise" />
                <feComposite operator="in" in="coloredNoise" in2="SourceGraphic" result="noiseIn" />
                <feBlend mode="soft-light" in="SourceGraphic" in2="noiseIn" />
              </filter> */}

                {/* Clean drop shadow */}
                <filter id="cleanShadow" x="-10%" y="-10%" width="120%" height="120%">
                  <feDropShadow dx="0.2" dy="0.2" stdDeviation="0.3" flood-color="black" flood-opacity="0.45" />
                </filter>

                <mask id="excludeInner" maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse">
                  <rect x="-100" y="-100" width="200" height="200" fill="white" />
                  <circle cx={0} cy={0} r={20.2} fill="black" />
                </mask>

                <filter id="blur">
                  <feGaussianBlur stdDeviation="0.5" />
                </filter>

              </defs>

              {pcClass.map((pc, i) => {
                // const isActive = activeNotes.includes(i % 12) || heldNotes.includes(i % 12) || toggledNotes.includes(i % 12);
                const isDirectlyActive = activeNotes.map(note => chromaticToFifths(note, rootNote)).includes(i) ||
                  heldNotes.map(note => chromaticToFifths(note, rootNote)).includes(i) ||
                  toggledNotes.map(note => chromaticToFifths(note, rootNote)).includes(i);

                // Check for overtone activity if showOvertones is enabled
                let overtoneStrength: number | false = false;
                let isOvertoneActive = false;

                if (settings.showOvertones && !isDirectlyActive) {
                  const baseNotes = [...activeNotes, ...heldNotes, ...toggledNotes];
                  const overtoneStrengths: number[] = [];

                  // Collect all overtone strengths for this pitch class
                  for (let midiNote = 33; midiNote <= 108; midiNote++) {
                    if (chromaticToFifths(midiNote, rootNote) === i) {
                      const strength = getOvertoneInfo(midiNote, baseNotes);
                      if (strength !== false) {
                        overtoneStrengths.push(strength as number);
                      }
                    }
                  }
                  if (overtoneStrengths.length > 0) {
                    isOvertoneActive = true;

                    // Screen blending: 1 - product of all inverses
                    const blendedStrength = 1 - overtoneStrengths.reduce((product, strength) => {
                      return product * (1 - strength);
                    }, 1);

                    // Ensure overtone opacity is never less than inactive opacity
                    overtoneStrength = Math.max(blendedStrength, 0.4); // was 0.3, sets the minimum overtone opacity to 0.4
                  }
                }

                const isActive = isDirectlyActive || isOvertoneActive;

                if (isActive && activeNotes.length > 0) {
                  const pcClassLabel = ['Root', '5th', 'Maj2', 'Maj6', 'Maj3', 'Maj7', 'Tri', 'min2', 'min6', 'min3', 'min7', '4th'][i];
                }

                // Calculate slice opacity based on whether it's a direct note or overtone
                const sliceOpacity = isDirectlyActive
                  ? 1
                  : (overtoneStrength !== false
                    ? overtoneStrength
                    : 0.85);

                const reduction = settings.showPieSentiments ? 6.5 : 2.7;
                const radius = 23 - reduction;  // pie chart/chord compass radius adjust for text labels Root, Maj 2, etc, default 23 - 6.5
                const radius_two = 26.5 // pie chart/chord compass radius adjust for text labels two, Emotions, default 27
                const midAngle = ((i) / pcClass.length) * 2 * Math.PI + rotation;  // mid angle for each slice

                const labelX = radius * Math.cos(midAngle);  // labelX depends on midAngle
                const labelY = radius * Math.sin(midAngle);  // labelY depends on midAngle
                const labelX_two = 0.99 * radius_two * Math.cos(midAngle);  // labelX depends on midAngle
                const labelY_two = 0.9 * radius_two * Math.sin(midAngle);  // labelY depends on midAngle

                // The chart strip stands in for the centre symbol at a place in
                // the chart, live or dimmed (see compassStripDimmed).
                const compassStripShown = chartChordAt && !isScale && compassStripCurrent !== -1;
                // The staff lines under the centre bring the chart into view, so
                // they are only there while the chart is out of view
                const compassChartIcon = compassStripShown && !compassPending && !isPanelVisible('chordChart');
                // Dimmed, the centre keeps the chart chord's mode gradient: it
                // is still the place in the chart, whatever is sounding.
                const dimmedChartMode = compassStripShown && compassStripDimmed && compassCentreSegment !== undefined
                  ? compassCentreSegment.mode
                  : null;
                const centreMajor = dimmedChartMode !== null
                  ? dimmedChartMode === 0
                  : ((!isScale && pieChordModes[0] === 0) || (isScale && mode === 0)) && chordSymbol !== '';
                const centreMinor = dimmedChartMode !== null
                  ? dimmedChartMode === 1
                  : ((!isScale && pieChordModes[0] === 1) || (isScale && mode === 1)) && chordSymbol !== '';
                // Ink for the inner circle's icons: white on a minor chord's dark
                // gradient, black otherwise — the same rule as the chord symbol.
                const centreInkWhite = dimmedChartMode !== null
                  ? dimmedChartMode === 1
                  : activePcs.length !== 1 && (pieChordModes[0] === 1 || (isScale && mode === 1));
                const centerInk = centreInkWhite ? 'white' : 'black';


                return (
                  <g key={i} transform="translate(32, 32)">
                    <path
                      key={i}
                      d={getPieSlicePath(i, pcClass.length, settings.showPieSentiments)}
                      fill={`url(#${pc}${isActive ? 'Active' : ''})`}
                      className={`pie-slice ${isActive ? 'active-slice' : ''} ${isOvertoneActive ? 'overtone-pulse-svg' : ''}`}
                      opacity={sliceOpacity}
                      // stroke="#444"
                      // strokeWidth="0.1"
                      // strokeOpacity="0.3"
                      filter={isActive ? "url(#cleanShadow)" : "none"}
                    />
                    {/* Add a subtle highlight overlay */}
                    <path
                      d={getPieSlicePath(i, pcClass.length, settings.showPieSentiments)}
                      fill="url(#sliceHighlight)"
                      pointerEvents="none"
                    // mask="url(#excludeInner)"
                    // filter="url(#modernNoise)"
                    />
                    <text x={labelX} y={labelY}
                      // fill="#000"
                      className={pcLabelFlashes[i] ? 'flash-pc-label' : ''}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={isActive ? "2.2" : "2.1"}
                      // fontFamily='Trebuchet MS'
                      // fontFamily="Futura" // Trebuchet MS, Segoe UI
                      fontFamily="Arial Unicode MS, Bravura Text"
                      fontWeight={isActive ? '700' : '100'}
                      fill={isActive ? (isOvertoneActive ? "rgb(60,60,60)" : "black") : "rgb(40,40,40)"}
                    >
                      {anchorTonicNote !== null
                        ? (getModeFromPcSet(tonicScalesNotes[0]) === 0 // replaced lockedTonicMode with getModeFromPcSet(tonicScalesNotes[0]) throughout
                          ? sdMajClassLabels[i]
                          : sdMinClassLabels[i])
                        : pieSliceMode === 0 || (isScale && mode === 0)
                          // PIE CHART/CHORD COMPASS SLICE LABELS below
                          ? settings.showIntervalLabels ? pcMajClassNameLabels[i] : pcMajClassLabels[i]
                          : settings.showIntervalLabels ? pcMinClassNameLabels[i] : pcMinClassLabels[i]}
                    </text>

                    {/* Conditional Sentiment Labels on Pie Chart/Chord Compass */}
                    {settings.showPieSentiments && (
                      <>
                        {/* <rect x={labelX_two - emoClassLabels[i].length * 1.1 / 2} y={labelY_two - 2 / 2} width={emoClassLabels[i].length * 1.1} height={2} */}
                        <rect
                          x={labelX_two - (emoClassLabels[i].length === 0 ? 0 : Math.max(2, Math.min(14, 3 + emoClassLabels[i].length * 0.75))) / 2}
                          y={labelY_two - 2 / 2}
                          width={emoClassLabels[i].length === 0 ? 0 : Math.max(2, Math.min(14, 3 + emoClassLabels[i].length * 0.75))}
                          height={2}
                          rx={0.7}
                          ry={0.7}
                          filter="url(#rectShadow)"
                          color={isActive ? 'grey' : 'white'}
                          className={pcLabelFlashes[i] ? 'flash-pc-label' : ''}
                          fill={(() => {
                            const result = activePcs.length > 1
                              ? (isActive
                                ? 'black'
                                : (anchorTonicNote !== null
                                  ? (lockedTonicMode === 0 ? colors.M3[0] : colors.m3[1])
                                  : !isScale
                                    ? (pieSliceMode === 0 ? colors.M3[pieSliceMode] : colors.m3[pieSliceMode])
                                    : mode === 0 ? colors.M3[0] : colors.m3[1]))
                              : 'white';

                            return result;
                          })()}
                          fillOpacity={activePcs.length > 1 ? isActive ? sliceOpacity : '0.15' : '0.15'} // sliceOpacity was 0.65
                          stroke="rgb(90,90,90)"
                          strokeOpacity={isActive ? '0.7' : '0'}
                          strokeWidth="0.1"
                        />
                        <text x={labelX_two} y={labelY_two}
                          className={isActive ? `${emoClass[i]}` : ''}
                          fill={activePcs.length > 1
                            ? (isActive
                              ? (anchorTonicNote !== null
                                ? (lockedTonicMode === 0 ? colors.M3[0] : colors.m3[1]) // || (lockedTonicMode === 0 && isScale && mode === 0)
                                : !isScale
                                  ? (pieSliceMode === 0 ? colors.M3[pieSliceMode] : colors.m3[pieSliceMode])
                                  : mode === 0 ? colors.M3[0] : colors.m3[1])
                              : 'black')
                            : 'black'}
                          fillOpacity={isActive ? '0.8' : '0.5'}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={isActive ? "1.65" : "1.65"}
                          // fontFamily='Trebuchet MS'
                          // fontFamily='Bricolage Grotesque'
                          fontFamily="Futura"
                          dy={anchorTonicNote !== null ? "0.1em" : "0.03em"}
                          fontStyle='normal'
                          // textDecoration={isActive ? '' : '0 0 10px #fff, 0 0 10px #fff, 0 0 22px #bbb, 0 0 42px #bbb,0 0 62px #bbb'}
                          // fontStyle={isActive ? 'normal' : 'italic'}
                          // stroke={isActive ? 'black' : 'grey'}
                          stroke={activePcs.length > 1 ? isActive ? 'rgb(255,255,255)' : 'rgb(0,0,0)' : 'rgb(0,0,0)'}
                          strokeOpacity="0.2"
                          strokeWidth="0.0035dvw"
                          fontWeight={isActive ? '700' : '100'}
                        >
                          {emoClassLabels[i]}
                        </text>
                      </>
                    )}

                    {/* OLD CIRCLE */}
                    {/* <rect x={-12} y={-4} width={24} height={8} */}
                    {/* <circle cx={0} cy={0} r={11.7} // r=11
                    color="white"
                    // fill={isActive ? 'white' : 'grey'}
                    fill={
                      (pieChordModes[0] === 0 && chordSymbol != '')
                        ? 'url(#majorGradient)'
                        : (pieChordModes[0] === 1 && chordSymbol != '')
                          ? 'url(#minorGradient)'
                          : (chordSymbol === ''
                            ? 'url(#defaultGradient)'
                            : 'rgb(100,100,100)')
                      // Option to show center circle as Major or Minor locked Mode
                      // : (chordSymbol === '' ? lockedTonicNote === null ? 'url(#defaultGradient)' : lockedTonicMode === 0 ? 'url(#majorGradient)' : 'url(#minorGradient)' : 'rgb(100,100,100)')
                    }
                    fillOpacity={isTransitioning ? 0 : 1} // Fade in
                    stroke="rgb(170,170,170)"
                    strokeOpacity="0.21"
                    strokeWidth="0.26"
                    style={{ transition: 'fill-opacity 0.3s ease' }}
                  /> */}

                    <>
                      {/* <rect x={-12} y={-4} width={24} height={8} */}


                      {/* Base layer (default gradient) - always visible */}
                      <circle
                        cx={0}
                        cy={0}
                        r={10.2}
                        fill="url(#defaultGradient)"
                        fillOpacity={1}
                        stroke="rgb(170,170,170)"
                        strokeOpacity="0.21"
                        strokeWidth="0.06"
                      />

                      {/* Major gradient layer - always rendered but opacity controlled */}
                      <circle
                        cx={0}
                        cy={0}
                        r={10.2}
                        fill="url(#majorGradient)"
                        fillOpacity={centreMajor ? 1 : 0}
                        stroke="rgb(170,170,170)"
                        strokeOpacity="0.21"
                        strokeWidth="0.26"
                        style={{ transition: 'fill-opacity 0.2s ease-in-out' }}
                      />

                      {/* Minor gradient layer - always rendered but opacity controlled */}
                      <circle
                        cx={0}
                        cy={0}
                        r={10.2}
                        fill="url(#minorGradient)"
                        fillOpacity={centreMinor ? 1 : 0}
                        stroke="rgb(170,170,170)"
                        strokeOpacity="0.21"
                        strokeWidth="0.26"
                        style={{ transition: 'fill-opacity 0.2s ease-in-out' }}
                      />
                    </>

                    {/* With a chart chord showing, the centre is a strip of the
                        chart's chords (CompassChartStrip), drawn here: on the
                        inner circle's ground, under the note ring. */}
                    {compassStripShown && compassCentreSegment !== undefined && (
                      <CompassChartStrip
                        chords={compassStrip}
                        current={compassStripCurrent}
                        reach={CHART_JUMP + 1}
                        centreSize={compassCentreSize(compassCentreSegment)}
                        ink={centerInk}
                        drawSymbol={segment => assembleChordSymbol(segment, true, anchorTonicNote, lockedTonicMode)}
                        centreAddable={compassPending}
                        dimmed={compassStripDimmed}
                        onAddCentre={addPendingChord}
                        stripKey={`${compassStripKey}#${centerInk}#${compassPending}#${compassStripDimmed}`}
                      />
                    )}

                    {/* Inner circle note name labels */}
                    <g
                      transform={`rotate(${innerLabelRotation * (180 / Math.PI)})`}
                      style={{
                        transition: 'transform 0.3s ease-out'
                      }}
                    >

                      {/* Inner circle for note name labels - always visible */}
                      <circle
                        cx={0}
                        cy={0}
                        r={settings.showPieSentiments ? 12.7 : 14.7}
                        fill="rgb(0, 0, 0)"
                        fillOpacity="0.015"
                        stroke="rgb(156, 156, 156)"
                        strokeOpacity="0.32"
                        strokeWidth="0.1"
                      />

                      {/* <g transform={`rotate(${((-rootNote % 12) * 30) % 360})`}> */}
                      {pcClass.map((_, i) => {
                        const angle = ((i % 12) * Math.PI * 1) / 6;
                        const radius = settings.showPieSentiments ? 11.5 : 12.4;
                        const positionRotation = innerLabelRotation * (1 / Math.PI);
                        // const FIFTHS_ROTATION_FACTOR = 1 / 7;  // or approximately 0.142857
                        // const positionRotation = ((12 - (rootNote % 12)) % 12) * FIFTHS_ROTATION_FACTOR;
                        // const radius = rootNote === 6 ? 8.5 : 10.2;
                        const x = Math.cos(angle) * radius;
                        const y = Math.sin(angle) * radius;
                        const pitchClass = (rootNote + i) % 12;  // Calculate pitch class based on rootNote
                        // const combinedRotation = (angle * 1 / Math.PI) - (innerLabelRotation * 180 / Math.PI);

                        const combinedRotation = (angle * 1 / Math.PI) - (innerLabelRotation * 180 / Math.PI);
                        return (
                          <text
                            key={`inner-label-${i}`}
                            x={x}
                            y={y}
                            textAnchor="middle"
                            alignmentBaseline="middle"
                            className={pcLabelFlashes[(i + 12 - chromaticToFifths(rootNote, 0) + 6) % 12] ? 'flash-pc-label' : ''}
                            // fill={
                            // fill="rgb(0,0,0)"
                            style={(() => {
                              const pc = chromaticToFifths(i, 6);
                              const isIncluded = activePcs.includes(pc);

                              return {
                                fontSize: pc === rootPc && isIncluded
                                  // ? settings.showPieSentiments ? '0.1dvw' : '0.15dvw'
                                  ? settings.showPieSentiments ? '1.6' : '2'
                                  : isIncluded
                                    // ? settings.showPieSentiments ? '0.085dvw' : '0.11dvw'
                                    // : settings.showPieSentiments ? '0.08dvw' : '0.105dvw',
                                    ? settings.showPieSentiments ? '1.4' : '1.9'
                                    : settings.showPieSentiments ? '1.3' : '1.67',
                                fontFamily: 'Arial Unicode MS, Bravura Text',
                                // fontFamily: 'Helvetica',
                                fontWeight: isIncluded ? '700' : '100',
                                // fontWeight: chromaticToFifths(i, 6) === rootPc ? '700' : '100',
                                fill: isIncluded ? 'rgb(40, 40, 40)' : 'rgb(255, 255, 255)',
                                opacity: isIncluded ? '0.85' :
                                  // !activePcs.includes(chromaticToFifths(i, 6)) && chromaticToFifths(i, 6) === rootPc ? '0.05' : // not needed?
                                  (i + 12 - chromaticToFifths(rootNote, 0) + 6) % 12 >= 6 ? '0.07' : '0.12'
                              };
                            })()}

                            // transition: 'transform 0.3s ease-in-out',

                            // transform: 'translateZ(0)',  // Force GPU acceleration
                            // backfaceVisibility: 'hidden',
                            // perspective: 'none',

                            // transformOrigin: 'center center',  // Add this
                            // transformOrigin: '50% 50%', // makes the circle rotate off of the screen for most rootNotes.
                            // transformBox: 'fill-box',           // And this
                            // transform: 'scale(1)',  // Force consistent scaling during animation
                            // backfaceVisibility: 'hidden',  // This can help prevent unwanted 3D effects
                            // perspective: '1000px'  // Control the 3D space

                            // transform={`rotate(${90 + (angle * 180) / Math.PI}, ${x}, ${y})`}
                            // transform={`rotate(${-(innerLabelRotation) / Math.PI + (angle * i) / Math.PI + 90 + (-i * 30)}, ${x}, ${y})`}

                            // transform={`rotate(${innerLabelRotation * (180 / Math.PI) + (rootNote % 12) * 30})`}

                            // transform={`rotate(${(innerLabelRotation < 0 ? -1 : 1) * (Math.abs(innerLabelRotation) / Math.PI) +
                            //   ((innerLabelRotation < 0 ? -1 : 1) * (rootNote % 12) * 1) +
                            //   (getShortestFifthsDistance(0, rootNote) * 30)
                            // }, ${x}, ${y})`}

                            // (getShortestFifthsDistance(currentCirclePosition, rootNote) > 0 ? -1 : 1) * (Math.abs(innerLabelRotation) / Math.PI) +
                            // ((getShortestFifthsDistance(currentCirclePosition, rootNote) > 0 ? -1 : 1) * (rootNote % 12) * 1) +
                            // (chromaticToFifths(rootNote, 0) * 30)

                            // -(innerLabelRotation) / Math.PI +
                            // (angle) / Math.PI +
                            // ((-rootNote % 12) * 1 +

                            // (innerLabelRotation < 0 ? -1 : 1) * (Math.abs(innerLabelRotation) / Math.PI) +
                            // ((innerLabelRotation < 0 ? -1 : 1) * (rootNote % 12) * 1) +
                            // (chromaticToFifths(rootNote, 0) * 30 - (chromaticToFifths(rootNote, 0) * 0.5))

                            // (chromaticToFifths(rootNote, 0) * 30))


                            transform={`rotate(${combinedRotation}, ${x}, ${y})`}

                          // transform-origin={`${x}px ${y}px`}
                          // transform={`rotate(${-positionRotation}deg)`}

                          // transform={`
                          //     rotate(${-innerLabelRotation * (180 / Math.PI)}, ${x}, ${y})  
                          //     rotate(${(rootNote % 12) * 1}, ${x}, ${y})
                          // `}

                          // }, ${x}, ${y})`

                          >
                            {midiToName((chromaticToFifths(i, 6)), false, rootNote, anchorTonicNote !== null ? getModeFromPcSet(tonicScalesNotes[0]) : chordQual)}
                          </text>
                        );
                      })}
                      {/* </g> */}
                    </g>

                    {/* Add Chord Symbol to center of Pie Chart/Chord Compass Below */}
                    {!compassStripShown && <text className="pie-chord-symbol" x={0} y={1.6}
                      fill={ // removed lockedTonicNote !== null check so chord symbol background is chord quality color
                        activePcs.length === 1
                          ? 'black'
                          : (pieChordModes[0] && pieChordModes[0] === 0) || (isScale && mode === 0)
                            ? 'black'
                            : (pieChordModes[0] && pieChordModes[0] === 1) || (isScale && mode === 1)
                              ? 'white'
                              : 'black'
                        // : 'black'
                      }
                      // fontSize: isScale
                      // ? `${2.7 - (chordSymbol.length * 0.04)}dvw`
                      // : `${4.0 - (chordSymbols.length * 0.001) - (chordSymbol.length * 0.1)}dvw`,
                      style={{
                        fontSize: isScale ? `${4.3 - (chordSymbol.length * 0.1)}` : `${displaySegment ? compassCentreSize(displaySegment) : 6.5}`,
                        borderSpacing: "0.0dvw",
                        margin: "0dvw"
                      }}>
                      {/* {chordSymbol} */}
                      {/* {renderChordSymbol(chordSymbol, true)} */}
                      {/* {csOptionsSegments[0] && assembleChordSymbol(csOptionsSegments[0], true)} */}

                      {/* {activePcs.length === 1 ? midiToSingleNote(midiNotes[0], false, rootNote, mode) : */}

                      {activePcs.length === 1 ? "" :

                        isScale ? assembleScaleName(scaleRoot, mode) : (displaySegment && assembleChordSymbol(displaySegment, true, anchorTonicNote, lockedTonicMode))}
                    </text>}
                    {/* Add Chord Symbol to center of Pie Chart/Chord Compass End */}

                    {/* The control under the centre symbol. The svg opts out of
                        hit testing, so each opts back in on its own, and
                        data-actionable keeps handleGlobalClick from dropping the
                        chart selection.
                        Sort toggle: the same setting as the READ/FEEL button atop
                        the Chord Symbol panel, icon only, shown on the same terms
                        as the chord symbol or scale name above it, and at a place
                        in the chart, unless the staff lines below have the slot. */}
                    {!compassChartIcon && (compassStripShown || (activePcs.length !== 1 && (isScale || csOptionsSegments[0]))) && <image
                      data-actionable="true"
                      href={`/images/${settings.preferNotationLegible ? 'Open-Book--Streamline-Core-Solid' : 'Heart--Streamline-Core'}-${centerInk}.png`}
                      x={-0.9} y={7.2} width={1.8} height={1.8}
                      className="compass-sort-toggle"
                      style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                      onClick={() => handleSettingChange('preferNotationLegible', !settings.preferNotationLegible)}
                    >
                      <title>{settings.preferNotationLegible ? 'Easiest to READ (Legibility)' : 'How chords FEEL (Root Perception)'}</title>
                    </image>}
                    {/* At a place in the chart, with the chart chord showing or
                        dimmed and the chart out of view, the staff lines bring
                        the chart into view (the same mark as on the Key Center
                        lock). A pending chord keeps the sort toggle; it is added
                        by clicking it (CompassChartStrip). */}
                    {compassChartIcon && <text
                      data-actionable="true"
                      x={0} y={9.1}
                      style={{ fill: centerInk, transition: 'fill 0.2s ease-in-out', fontFamily: 'Bravura Text', fontWeight: 'bold', fontSize: '3.6px', textAnchor: 'middle', pointerEvents: 'auto', cursor: 'pointer' }}
                      onClick={() => openChartBefore('chordCompass')}
                    >
                      <title>Show the chord chart</title>
                      {'\uD834\udd18'}
                    </text>}



                    {/* Add Key Indication to Left of Pie Chart/Chord Compass Below */}
                    {/* The box that marks the arrow as clickable, in the key
                        center selector's border and corner (0.08dvw, 0.4dvw),
                        converted to the svg's units: its box is 36dvw across
                        for 64 units (the global svg rule sets the size), so
                        one unit is 0.5625dvw. Drawn once, under the arrow. */}
                    {i === 0 && lockedTonicNote !== null && compassArrowBox !== null && <rect
                      x={compassArrowBox.x - 0.3}
                      y={compassArrowBox.y - 0.5}
                      width={compassArrowBox.width + 0.6}
                      height={compassArrowBox.height + 0.6}
                      rx={0.4 / 0.5625}
                      fill="none"
                      stroke="rgb(69, 207, 207)"
                      strokeWidth={0.08 / 0.5625}
                      style={{ pointerEvents: 'none' }}
                    />}
                    <text className="pie-chord-symbol" x={lockedTonicNote !== null ? -24 : -29.9} y={lockedTonicMode === 0 ? 26.0 : 26.0} // -23.75 for major
                      fill={ // removed lockedTonicNote !== null check so chord symbol background is chord quality color
                        // activePcs.length === 1
                        //   ? 'black'
                        (lockedTonicNote !== null && lockedTonicMode === 0)
                          ? 'rgba(140, 130, 54, 0.15)'
                          : 'rgba(57, 111, 178, 0.11)'
                      }
                      style={{
                        fontSize: "3",
                        fontStyle: "italic",
                        fontWeight: "bold",
                        borderSpacing: "0.0",
                        margin: "0",
                        wordSpacing: "1",
                        letterSpacing: "-0.1",
                      }}>
                      {/* The arrow toggles the anchor setting while a key is locked
                          (there is no key to anchor to otherwise). The svg opts out
                          of hit testing, so the arrow opts back in on its own;
                          data-actionable keeps handleGlobalClick from dropping the
                          chart selection and its toggled notes.
                          The key's name shows whenever a key is locked, whichever
                          way the arrow points. */}
                      {(csOptionsSegments[0] || lockedTonicNote !== null) && <tspan ref={i === 0 ? compassArrowRef : undefined} data-actionable="true" onClick={() => handleSettingChange('anchorToChordRoot', !settings.anchorToChordRoot)} style={{ fontWeight: 'bold', fontSize: '0.13dvw', fontStyle: 'normal', fill: 'rgb(69, 207, 207)', pointerEvents: lockedTonicNote !== null ? 'auto' : 'none', cursor: 'pointer' }}>{anchorTonicNote !== null ? '⮕' : '⬆'} </tspan>}
                      {lockedTonicNote !== null && <>{midiToName(lockedTonicNote, false, lockedTonicNote, lockedTonicMode)} {lockedTonicMode === 0 ? 'Major' : 'Minor'}</>}
                    </text>
                    {/* Add Key Indication to Left of Pie Chart/Chord Compass End */}


                    {/* Add Key Indication to center of Pie Chart/Chord Compass Below */}
                    {/* <text className="pie-chord-symbol" x={0} y={7.2}
                    fill={ // removed lockedTonicNote !== null check so chord symbol background is chord quality color
                      activePcs.length === 1
                        ? 'black'
                        : (pieChordModes[0] && pieChordModes[0] === 0) || (isScale && mode === 0)
                          ? 'black'
                          : (pieChordModes[0] && pieChordModes[0] === 1) || (isScale && mode === 1)
                            ? 'white'
                            : 'black'
                    }
                    style={{
                      fontSize: "0.13dvw",
                      borderSpacing: "0.0dvw",
                      margin: "0dvw",
                      wordSpacing: "0.02dvw"
                    }}>
                    {lockedTonicNote !== null && activePcs.length > 1
                      ? `in ${midiToName(lockedTonicNote, false, lockedTonicNote, lockedTonicMode)} ${lockedTonicMode === 0 ? 'Major' : 'Minor'}`
                      : ''
                    }
                  </text> */}
                    {/* Add Key Indication to center of Pie Chart/Chord Compass End */}

                  </g>
                );
              })}
            </svg>
          </Panel>
          ) : null
        }

        {/*  Pie Chart/Chord Compass End */}

        {
          isPanelRendered('luxTonnetz') ? (
            <Panel
              id="luxTonnetz"
              appTheme={settings.appTheme}
              {...panelPlacement('luxTonnetz')}
              {...panelControls('luxTonnetz')}
              style={{
                borderRadius: '1dvw',
                boxShadow: `0 0 0.6dvw 0.35dvw rgba(0, 0, 0, ${settings.appTheme === "light" ? 0.2 : 0.5})`,
              }}
            >
              <LuxTonnetz
                keyCenter={tonnetzKeyCenter}
                activeScaleDegrees={tonnetzDegrees}
                appTheme={settings.appTheme}
              />
              {/* The right arrow at the bottom left: the grid is laid out
                  around the key center, so what it shows is heard relative to
                  the key (horizontal), as the same arrow says on the Chord
                  Compass. Drawn in the Compass's own coordinate box — same
                  viewBox and same size attributes, which the global svg rule
                  turns into the same 36 x 37dvw box in a panel the same size —
                  so it lands at the Compass arrow's exact spot and size. */}
              <svg viewBox="0 5 64 64" preserveAspectRatio="xMidYMid meet" width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                <g transform="translate(32, 32)">
                  <text className="pie-chord-symbol" x={-29.9} y={26.0} style={{ fontSize: '3' }}>
                    <tspan style={{ fontWeight: 'bold', fontSize: '0.13dvw', fill: 'rgb(69, 207, 207)' }}>{'⮕'} </tspan>
                  </text>
                </g>
              </svg>
            </Panel>
          ) : null
        }


        {/* Chord Chart Display. Visibility is not read from chordChart.length
            any more — an effect above puts the chart into the layout when it
            gains entries and takes it out when it loses them, so that it goes
            through the same row and budget as every other panel. */}
        {
          isPanelRendered('chordChart') ? (
            <Panel
              id="chordChart"
              appTheme={settings.appTheme}
              {...panelPlacement('chordChart')}
              {...panelControls('chordChart')}
              className="chord-chart"
              bodyClassName={`chord-chart-body${chartHasMoreAbove ? ' more-above' : ''}${chartHasMoreBelow ? ' more-below' : ''}${chartSpacesKeyCenterLines ? ' key-center-lines' : ''}`}
              bodyRef={chartBodyRef}
              onBodyScroll={(e) => measureChartOverflow(e.currentTarget)}
              onBodyClick={handleChartBackgroundClick}
              overlay={
                <>
                  {chartControls}
                  <div
                    className={`chord-chart-more ${chartHasMoreBelow ? '' : 'at-end'}`}
                    aria-hidden="true"
                  >
                    <span className="chord-chart-arrow">{'▾'}</span>
                  </div>
                </>
              }
              style={{
                backgroundColor: `rgba(0, 0, 0, ${chartPaperAlpha()})`,
                fontSize: `${1.9 / (1 + chartLength * 0.0005)}dvw`,
                // Sets the height of each chord's box, which is its selection
                // background and the origin the key center lines are drawn
                // from. Row spacing is set separately, by .chord-chart-body.
                lineHeight: `${3.1 / (1 + chartLength * 0.0008)}dvw`
              }}
            >
            <div className="song-and-composer-container">

              {/* Tempo. Absolute so the slider can overlay the chart rather than
                  shoving the title sideways — but anchored to the song/composer
                  container (which scrolls) instead of the panel (which does
                  not), so the BPM scrolls away with the title and composer
                  beside it. 0.65dvw where it used to be 0.95: the container sits
                  0.3dvw inside the panel's padding, which the old containing
                  block included.
                  The BPM stays put while the slider is open, so the number you
                  are dragging never moves. */}
              <div style={{ top: '0.65dvw', position: 'absolute' }}>
                <div
                  style={{ fontSize: `${1.4 / (1 + chartLength * 0.0005)}dvw` }}
                  className="song-tempo"
                  onClick={toggleEditTempo}
                  title="Set tempo"
                >
                  {tempo.value}
                </div>

                {/* The old control was a range input rotated -90°, hung 5.8dvw
                    to the left of the tempo and closed by the first mouse-up, so
                    it could not be fine-tuned. It also depended on escaping the
                    chart's overflow, which anchoring the tempo to the scrolling
                    container took away. This is the Settings slider's shape
                    instead — horizontal track, round thumb, value chip — in a
                    popover that stays open until it is dismissed.
                    The backdrop catches the click that closes it, the same way
                    the separator menu below does. */}
                {showSlider && (
                  <>
                    <div
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                      onClick={() => setShowSlider(false)}
                    />
                    {/* Name and track, and no value readout: the BPM it drives
                        is on screen right above, unmoving, so a chip in here
                        would be the same number twice. */}
                    <div className="chart-tempo-slider" data-actionable="true">
                      <span className="chart-tempo-label">BPM</span>
                      <input
                        className="settings-slider"
                        type="range"
                        min="5"
                        max="500"
                        value={tempo.value}
                        onChange={handleSliderChange}
                      />
                    </div>
                  </>
                )}

                {/* The chart's own key center, under the tempo: both are
                    properties of the song, so both belong in its header. This
                    is not the locked tonic, which says what key the app is
                    rendering in and stays in the Key Center panel.

                    It gets its own class rather than borrowing .song-tempo,
                    whose width is 0.1dvw — the BPM's text overflows that box
                    harmlessly, but a control has to be as wide as the thing you
                    are meant to click, or the click lands on the song title
                    underneath and opens the title editor. */}
                <div
                  className="chart-key-center"
                  data-actionable="true"
                  title="Chart Key-center - the key of the song"
                  style={{
                    fontSize: `${1.2 / (1 + chartLength * 0.0005)}dvw`,
                    opacity: globalKeyCenter === null ? 0.5 : 1,
                    ...(globalKeyCenter !== null ? { color: keyCenterColor(globalKeyCenter.mode) } : {}),
                  }}
                  onClick={(e) => toggleKeyCenterPicker({ scope: 'global' }, e.currentTarget)}
                >
                  {globalKeyCenter === null ? 'key' : keyCenterLabel(globalKeyCenter)}
                  {'▾'}
                </div>
              </div>

              {/* Song Title and Composer. Each is a layout box taking its share
                  of the header, with the text inside it as the click target.
                  Both hand the input the same size the text is drawn at, so the
                  header holds still when editing starts. */}
              <div className="song-title-box">
                {songTitle.isEditable ? (
                  <input
                    className="song-title-input"
                    style={{ fontSize: `${1.8 / (1 + songTitleLength * 0.01)}dvw` }}
                    type="text"
                    value={songTitle.title}
                    onChange={handleTitleChange}
                    onBlur={toggleEditTitle}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                  />
                ) : (
                  <span data-actionable="true" style={{ fontSize: `${1.8 / (1 + songTitleLength * 0.01)}dvw` }}
                    className="song-title" title="Edit song title" onClick={toggleEditTitle}>
                    {songTitle.title}
                  </span>
                )}
              </div>

              <div className="composer-box">
                {composer.isEditable ? (
                  <input
                    className="composer-input"
                    style={{ fontSize: `${1.4 / (1 + songTitleLength * 0.02)}dvw` }}
                    type="text"
                    value={composer.name}
                    onChange={handleComposerChange}
                    onBlur={toggleEditComposer}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                  />
                ) : (
                  <span data-actionable="true" style={{ fontSize: `${1.4 / (1 + songTitleLength * 0.02)}dvw` }}
                    className="composer" title="Edit composer" onClick={toggleEditComposer}>
                    {composer.name}
                  </span>
                )}
              </div>
            </div>

            {/* Gap under the header. This has no width, so it shares the
                header's row and the taller of the two sets that row's height.
                With the key center lines on it collapses, leaving the header's
                own height to set the gap — which is how the first system's
                lines come to sit level with the chart key. */}
            <div style={chartSpacesKeyCenterLines ? { marginTop: 0, height: 0 } : { marginTop: '2dvw' }}>
              <p></p>
            </div>

            {/* Time Signature. The chart key's line starts at the opening
                barline to the right of this, clear of the key's own label in
                the header above. */}
            <div>
              <ChartDropdown
                className="time-signature"
                style={{ position: 'relative', fontSize: `${1.7 / (1 + chartLength * 0.001)}dvw` }}
                menuFontSize={`${1.4 / (1 + chartLength * 0.001)}dvw`}
                title="Beats per bar"
                value={String(timeSigBeats)}
                options={beatOptions.map(beat => ({ value: String(beat), label: String(beat) }))}
                onChange={(beat) => setTimeSigBeats(Number(beat))}
              /><span style={{ position: 'relative', left: '-0.1dvw', fontSize: `${1.7 / (1 + chartLength * 0.0015)}dvw` }} className='time-signature'>/</span>
              <ChartDropdown
                className="time-signature"
                style={{ position: 'relative', fontSize: `${1.7 / (1 + chartLength * 0.001)}dvw` }}
                menuFontSize={`${1.4 / (1 + chartLength * 0.001)}dvw`}
                title="Beat value"
                value={String(timeSigDuration)}
                options={durationOptions.map(dur => ({ value: String(dur), label: String(dur) }))}
                onChange={(dur) => setTimeSigDuration(Number(dur))}
              />
            </div>

            <span style={{ verticalAlign: "0.1dvw" }}></span>
            <div style={{ position: 'relative' }}>
              {globalKeyCenterLine()}
              {'\u00A0'}
              <select
                ref={openingBarSelectRef}
                style={{ position: 'relative', fontSize: `${1.98 / (1 + chartLength * 0.001)}dvw` }}
                className='time-signature'
              >
                <option value="𝄃">𝄃</option>
                <option value="𝄆">𝄆</option>
              </select>
              {'\u00A0\u00A0\u00A0'}
            </div>

            {/* // NEW ADDITION ACRYLICODE */}
            {/* {selectedSong.chordChart.map((entry, index) => ( */}
            {chordChart.map((entry, index) => {
              const startsRegionHere = regionStartIds.has(entry.id);
              return (
              <React.Fragment key={entry.id}>

                {/* {!entry.isSeparator || entry.name !== SEPARATORS.barline ?
                <span className="chord-space"></span> : null} */}

                {/* {entry.isSeparator ? (
                // Render a select menu for separators with click handler for selection
                <select
                  tabIndex={1}
                  onFocus={() => setSelectedChordIndex(index)}
                  style={{ position: 'relative', bottom: '0px', fontSize: `${2.5 / (1 + chartLength * 0.001)}dvw` }}
                  className={`${selectedChordIndex === index ? 'selected-separator' : 'separator'}`}
                  value={entry.name}
                  onChange={(e) => updateSeparator(index, e.target.value)}
                >
                  {Object.entries(SEPARATORS).map(([key, separator]) => (
                    <option key={key} value={separator}>{separator}</option>
                  ))}
                </select>
              ) : (
                // Render the chord with selection functionality
                <span
                  tabIndex={0}
                  className={`chord ${selectedChordIndex === index ? 'selected-chord' : ''}`}
                  title={`${selectedChordIndex === index ? 'Play Chord' : 'Select Chord'}`}
                  style={selectedChordIndex === index && entry.name !== '\u2001'
                    ? { backgroundColor: /m|°/.test(entry.name) ? 'rgb(50,50,140)' : 'rgb(90,90,0)' } // when mode is 0 major or 1 minor and chord is selected
                    : {} // No backgroundColor applied when the chord is not selected
                  }
                  onClick={() => handleChordSelection(index)}
                >
                  {renderChordSymbol(entry.name)}
                </span>
              )} */}

                {/* New Separator section for Segments */}

                {entry.isSeparator ? (
                  <span
                    tabIndex={1}
                    data-chart-index={index}
                    onFocus={() => setSelectedChordIndex(index)}
                    onClick={() => {
                      setSelectedChordIndex(index);
                      setSeparatorMenuExpanded(separatorMenuExpanded && selectedChordIndex === index ? false : true);
                    }}
                    style={{
                      position: 'relative', bottom: '0px', fontSize: `${2.5 / (1 + chartLength * 0.001)}dvw`,
                      cursor: 'pointer', userSelect: 'none',
                      ...(separatorEditMode && selectedChordIndex === index ? { outline: '0.12dvw solid white', outlineOffset: '0.05dvw', animation: 'pulse-outline-bw 0.5s linear infinite' } : {})
                    }}
                    className={`${selectedChordIndex === index ? 'selected-separator' : 'separator'}`}
                  >
                    {keyCenterLines(index)}
                    {separatorEditMode && selectedChordIndex === index
                      ? SEPARATOR_TYPES[selectedSeparatorTypeIndex]
                      : entry.name}
                    {separatorMenuExpanded && selectedChordIndex === index && (
                      <>
                        <div
                          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                          onClick={() => setSeparatorMenuExpanded(false)}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '0.4dvw',
                          padding: '0dvw 0.3dvw',
                          zIndex: 1000,
                          boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)',
                          whiteSpace: 'nowrap'
                        }}>
                          {Object.entries(SEPARATORS).map(([key, separator]) => (
                            <div
                              key={key}
                              onClick={() => {
                                updateSeparator(index, separator);
                                setSeparatorMenuExpanded(false);
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                              style={{
                                padding: '0dvw 0.3dvw',
                                borderRadius: '0.4dvw',
                                cursor: 'pointer',
                                fontFamily: 'Bravura Text',
                                fontSize: `${2.5 / (1 + chartLength * 0.001)}dvw`,
                                color: entry.name === separator ? '#fff' : 'rgba(255,255,255,0.7)',
                                fontWeight: entry.name === separator ? 'bold' : 'normal',
                              }}
                            >
                              {separator}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </span>
                ) : (
                  <span // hoveredChordIndex is used to display the edit mode button
                    style={{ position: 'relative', display: 'inline-block' }}
                    data-chart-index={index}
                    onMouseEnter={() => setHoveredChordIndex(index)}
                    onMouseLeave={() => setHoveredChordIndex(null)}
                  >
                    {keyCenterLines(index)}
                    {keyCenterHoverStrip(index, entry.id)}

                    {/* Key center badge, on the key center lines above the
                        chord. It labels every chord that starts a region, so a
                        key change is readable while playing, and appears as a
                        bare arrow on the hovered chord, where it is the control
                        for setting one. A "use global key" marker shows the
                        global key, or a bare arrow with no global key.

                        It goes with the lines when the regions are hidden: the
                        chart draws no key centers then, and offers none. */}
                    {(() => {
                      if (!settings.showKeyCenterRegions) return null;
                      if (!startsRegionHere && hoveredChordIndex !== index) return null;

                      // A "use global key" marker shows the global key where
                      // it changes the key; the arrow alone where the key
                      // before it was the global key already, or there is none
                      const usesGlobal = regionKeyByEntryIndex[index] === null;
                      const keyBefore = index > 0 && regionKeyByEntryIndex[index - 1] !== null ? regionKeyByEntryIndex[index - 1] : globalKeyCenter;
                      const startedKey = usesGlobal ? globalKeyCenter : regionKeyByEntryIndex[index];
                      const named = startsRegionHere && startedKey !== null && !(usesGlobal && keyCentersEqual(keyBefore, globalKeyCenter));
                      return (
                        <span
                          className="key-center-badge"
                          data-actionable="true"
                          title="Key center from this chord on"
                          style={{
                            fontSize: `${1.15 / (1 + chartLength * 0.001)}dvw`,
                            opacity: startsRegionHere ? 1 : 0.6,
                            ...(named ? { color: keyCenterColor(startedKey!.mode) } : {}),
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleKeyCenterPicker({ scope: 'chord', chordId: entry.id }, e.currentTarget);
                          }}
                        >
                          {named && keyCenterLabel(startedKey!)}
                          {'▾'}
                        </span>
                      );
                    })()}

                    <span
                      tabIndex={0}
                      className={`chord ${selectedChordIndex === index ? 'selected-chord' : ''}`}
                      title={`${selectedChordIndex === index ? 'Play Chord' : 'Select Chord'}`}
                      style={selectedChordIndex === index && entry.segment
                        ? (displaySegment => ({
                          backgroundColor: displaySegment.mode === 0
                            ? 'rgb(90,90,0)' : 'rgb(50,50,140)',
                          ...(chordEditMode ? { outline: `0.16dvw solid white`, outlineOffset: '0.0dvw', outlineRadius: '0.1dvw', animation: 'pulse-outline-bw 0.5s linear infinite' } : {})
                        }))(chordEditMode && csOptionsSegments[selectedChordOptionIndex]
                          ? csOptionsSegments[selectedChordOptionIndex]
                          : entry.segment)
                        : {}}
                      onClick={() => handleChordSelection(index)}
                    >
                      {entry.segment
                        ? assembleChordSymbol(
                          chordEditMode && selectedChordIndex === index && csOptionsSegments[selectedChordOptionIndex]
                            ? csOptionsSegments[selectedChordOptionIndex]
                            : entry.segment
                        )
                        : entry.name === '\u2001'
                          ? '\u2001'  // Blank chord
                          : entry.name  // Fallback
                      }
                    </span>

                    {/* An orphan: dated to the loaded MIDI file, at no chord
                        region's start any more (chartFileLink.ts isOrphan) */}
                    {fileRegions !== null && isOrphan(entry, fileRegions.fileKey, fileRegions.regions) && (
                      <span className="chord-orphan-dot" title="From the MIDI file, but its chord region was merged or moved" />
                    )}

                    {/* Chord Edit Mode Button + Discard Changes Button */}
                    {selectedChordIndex === index && (hoveredChordIndex === index || chordEditMode) && (() => {
                      let hasEditChanges = false;
                      if (chordEditMode) {
                        const current = [...new Set([...activeNotes, ...heldNotes, ...toggledNotes])].sort((a, b) => a - b);
                        hasEditChanges = current.length !== editModeOriginalVoicing.length || current.some((n, i) => n !== editModeOriginalVoicing[i]);
                      }
                      return (
                        <>
                          <span
                            title={chordEditMode ? (hasEditChanges ? 'Save chord' : 'Stop chord edit') : 'Edit chord'}
                            className="edit-chord-icon"
                            onClick={(e) => { e.stopPropagation(); handleChordEditAction(); }}
                          >{chordEditMode && hasEditChanges ? '\u2713' : <img src="/images/Pencil--Streamline-Core-Remix-white.png" alt={chordEditMode ? 'Stop chord edit' : 'Edit chord'} />}</span>
                          {chordEditMode && hasEditChanges && (
                            <span
                              title="Discard changes"
                              className="discard-chord-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setToggledNotes(editModeOriginalVoicing);
                                toggledNotesRef.current = editModeOriginalVoicing;
                                setChordEditMode(false);
                              }}
                            >&#x2715;</span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                )}

                {!entry.isSeparator && entry.name !== '\u2001' && ( // shouldn't this be entry.name === '\u2001' since that is a chord space?
                  // The gap after a chord carries the key center lines too, or
                  // a region would read as a row of dashes instead of a bar.
                  <span className="chord-space" style={{ position: 'relative' }}>
                    {keyCenterLines(index)}
                  </span>
                )}
              </React.Fragment>
              );
            })}



            <div>
              {'\u00A0\u00A0\u00A0'}
              <select
                ref={closingBarSelectRef}
                style={{ position: 'relative', fontSize: `${1.98 / (1 + chartLength * 0.001)}dvw` }}
                className='time-signature'
              >
                <option value="𝄂">𝄂</option>
                <option value="𝄇">𝄇</option>
              </select>
            </div>

            </Panel>

          ) : null
        }

        {/* Scroll controls, one at each end, faded out while nothing waits
            beyond their edge. */}
        <StackScroller side="left" stack={stack} beyond={offLeft} appTheme={settings.appTheme} onScroll={() => scrollPanels(-1)} left={placed.regionLeft} />
        <StackScroller side="right" stack={stack} beyond={offRight} appTheme={settings.appTheme} onScroll={() => scrollPanels(1)} />

      </PanelRow>
      {/* Chord Display End */}

      {/*
        \uD834\uDD34 = common time
        \uE08E = slash
        \u00A0 = space
        \uE084 = time signature 4
        \uD834\uDD03 = Double bar start
        \uD834\uDD02 = Double bar end
      */}



      {/* App Name and Title Text Below */}
      <div id="app-name-bg-gradient" style={{
        position: 'absolute', top: '0.0dvw', left: '0.0dvw', height: '3.5dvw', width: '100%',
        background: settings.appTheme === "light" ?
          'linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.15) 20%, rgba(245, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0.7) 100%)'
          : 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.5) 55%, rgba(0, 0, 0, 0.0) 100%)',
        border: `0.0dvw solid ${settings.appTheme === 'light' ? 'rgb(255, 255, 255)' : 'rgba(0, 0, 0, 0.6)'}`,
        zIndex: 0
      }}></div>

      <div id="image-container" style={{ position: 'absolute', top: '0.3dvw', left: '0.8dvw', transform: 'translate(-0dvh, -0dvh)' }}>
        <span className='app-name-text' style={{ fontSize: '1.9dvw' }}><span style={{
          fontWeight: '500',
          color: `${settings.appTheme === "light" ? 'rgb(0, 149, 149)' : 'rgb(69, 207, 207)'}`,
          textShadow: `${settings.appTheme === "light" ? '0 0 0.1dvw rgba(0, 0, 0, 0.95)' : '0 0 0.1dvw rgba(255, 255, 255, 0.5)'}`
        }}>Harmona </span></span>
        <span className='title-text' style={{
          fontSize: '0.8dvw',
          color: `${settings.appTheme === "light" ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'}`,
          textShadow: `${settings.appTheme === "light" ? '0 0 0.1dvw rgba(0, 0, 0, 0.5)' : '0 0 0.1dvw rgba(255, 255, 255, 0.5)'}`
        }}> by Sentisonics</span>
        {/* {<img src="/images/sentisonics-logo.jpg" alt="sentisonics" style={{ width: '12dvw' }} />}<text style={{ verticalAlign: '0.8em', fontSize: '0.8dvw', color: 'grey' }}>BETA</text> */}
      </div>



      {/* Key-Center (KeyCenterPanelKey) display Below */}

      <div className="color-key-container" style={{
        width: '15.1dvw', height: '26.0dvw', padding: '0.5dvw',
        backgroundColor: `${settings.appTheme === "light" ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.75)'}`,
        marginLeft: '0.5dvw', marginRight: '0dvw', marginTop: '1dvw', marginBottom: '1.0dvw',
        borderStyle: 'solid', borderColor: '#555', borderSpacing: '2dvw', borderRadius: '1dvw', borderWidth: '0.05dvw',
        boxShadow: `0 0 0.6dvw 0.35dvw rgba(0, 0, 0, ${settings.appTheme === "light" ? 0.2 : 0.5})`
      }}>
        <p className="title-text" style={{ color: `${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'}` }}><span style={{ fontSize: '1.1dvw', fontWeight: 'bold', color: 'rgb(69, 207, 207)' }}>&#x2B95; </span>Key-Center</p>
        <hr style={{
          backgroundColor: '#fff', fontSize: '0.3dvw', width: '100%',
          border: `0.03dvw solid ${settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)'}`,
          boxShadow: `0 0 0.3dvw 0.2dvw ${settings.appTheme === 'light' ? 'rgba(255, 255, 255, 0.3' : 'rgb(68, 68, 68)'}`
        }}></hr>

        <div className="key-display" style={{
          color: lockedTonicNote !== null ? '#fff' : '#bbb',
        }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <div
              data-actionable="true"
              onClick={() => setKeyCenterMenuExpanded(!keyCenterMenuExpanded)}
              title="Key-Center Selection"
              style={{
                background: keyCenterMenuExpanded ? '#1a1a1a' : 'transparent',
                color: lockedTonicNote !== null
                  ? 'rgb(255, 255, 255)'
                  : activePcs.length > 1
                    ? 'rgb(168, 168, 168)'
                    : '#666',
                border: lockedTonicNote !== null ? '0.08dvw solid #fff' : '0.08dvw solid #222',
                borderRadius: '0.4dvw',
                // padding: '0',
                margin: '0',
                textAlign: 'center',
                transition: 'none',
                position: 'static',
                fontSize: '1.3dvw',
                fontFamily: 'inherit',
                cursor: 'pointer',
                padding: '0.2dvw 0.0dvw',
                width: '2.2dvw',
                height: '2.05dvw',
                boxSizing: 'border-box'
              }}
            >
              {fifthsOrderedPitchClasses[selectedKeyCenterIndex].label}
            </div>

            {keyCenterMenuExpanded && (
              <>
                {/* Backdrop to close menu when clicking outside */}
                <div data-actionable="true" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                  onClick={() => setKeyCenterMenuExpanded(false)} />

                {/* Key-Center menu (opens downward: this panel sits at the top of the window) */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '0.3dvw',
                  backgroundColor: '#1a1a1a',
                  border: '0.05dvw solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.3dvw',
                  padding: '0.3dvw 0',
                  minWidth: '2dvw',
                  zIndex: 1000,
                  boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)',
                  textShadow: 'none'
                }}>
                  {fifthsOrderedPitchClasses
                    .map((entry, index) => ({ entry, index }))
                    .filter(({ entry }) => entry.showInModes.includes(lockedTonicMode))
                    .map(({ entry, index }) => (
                      <div
                        data-actionable="true"
                        key={index}
                        onClick={() => handleKeyCenterSelect(index)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = selectedKeyCenterIndex === index ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                        }}
                        style={{
                          padding: '0.2dvw 0.6dvw',
                          cursor: 'pointer',
                          fontSize: '1.2dvw',
                          color: selectedKeyCenterIndex === index && lockedTonicNote !== null ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                          fontWeight: selectedKeyCenterIndex === index && lockedTonicNote !== null ? 'bold' : 'normal',
                          backgroundColor: selectedKeyCenterIndex === index ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>{entry.label}</span>
                        {selectedKeyCenterIndex === index && (
                          <span style={{ marginLeft: '0.8dvw', opacity: lockedTonicNote !== null ? 0.75 : 0.3, fontFamily: 'Trebuchet MS, sans-serif' }}>✓</span>
                        )}
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Key-Center Implied Scale Desplay BEGIN */}


        <span style={{
          position: 'absolute',
          fontSize: '0.85dvw',
          fontFamily: 'Arial Unicode MS',
          // top: lockedTonicNote !== null ? '14.25dvw' : '16dvw',
          top: '14.25dvw',
          left: '0.4dvw',
          color: lockedTonicNote !== null && settings.showTonicScale ? 'rgb(69, 207, 207)' : 'grey',
          opacity: lockedTonicNote !== null && settings.showTonicScale ? '1' : '0.7',
          transition: 'top 0.3s ease-in-out, opacity 0.3s ease-in-out',
        }}><span style={{ fontSize: '0.8dvw', textAlign: 'center', fontWeight: 'bold', color: 'rgb(69, 207, 207)' }}>&#x2B95; </span>
          Tonic Scale</span>

        <div
          data-actionable="true"
          style={{
            width: '14.4dvw', height: '2.4dvw', padding: '0.4dvw 0.3dvw',
            marginLeft: '0.3dvw', marginRight: '0dvw', marginTop: '0dvw', marginBottom: '0dvw',
            position: 'absolute',
            // top: lockedTonicNote !== null ? '15.5dvw' : '17.3dvw',
            top: '15.5dvw',
            opacity: lockedTonicNote !== null ? '1' : '0.5',
            left: '0dvw',
            color: settings.showTonicScale ? settings.appTheme === "light" ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)' : settings.appTheme === "light" ? 'rgb(81, 81, 81)' : 'rgb(185, 215, 215)',
            fontSize: (effectiveTonicScaleName
              ? `${1.55 - (effectiveTonicScaleName.length * 0.021)}dvw`
              : '1dvw'),
            fontFamily: 'Bravura Text',
            cursor: 'pointer',
            background: `rgba(70, 175, 175, ${settings.appTheme === "light" ? settings.showTonicScale ? 0.4 : 0.3 : settings.showTonicScale ? 0.2 : 0.1})`,
            border: `0.1dvw solid rgba(75, 149, 149, ${settings.showTonicScale ? 0.99 : 0.2})`,
            borderRadius: '0.8dvw',
            boxShadow: settings.showTonicScale
              ? '0 0 0.4dvw rgba(0, 147, 176, 0.5), inset -0dvw -0dvw 1.3dvw rgba(75, 149, 149, 0.7)'
              : '0 0 0.4dvw rgba(0, 147, 176, 0.3)',
            // textShadow: '0 0 1dvw rgba(0, 247, 255, 0.8)',
            transition: 'border 0.3s ease, boxShadow 0.3s ease, color 0.3s ease, top 0.3s ease-in-out, opacity 0.3s ease-in-out',
            zIndex: 10,

          }}
          onClick={() => {
            const newSettings = { ...settings, showTonicScale: !settings.showTonicScale, showRelativeChordScale: false, showChordScale: false };
            setSettings(newSettings);
          }}
          onMouseEnter={() => setHoveredScaleBtn('tonic')}
          onMouseLeave={() => { setHoveredScaleBtn(null); setHoveredPlayBtn(null); }}>
          {effectiveTonicScaleName && activePcs.length > 0 && (lockedTonicNote !== null || pcSorted.length > 0) // check if tonicScale and pcSorted exists: avoids showing "undefined"
            ? lockedTonicNote !== null ?
              `${midiToName(lockedTonicNote, false, lockedTonicNote, getModeFromPcSet(tonicScalesNotes[0]))} ` :
              `${midiToName(scaleRootDisplay, false, scaleRootDisplay, chordQual)} ` :
            ''}
          {'\u00a0'} {effectiveTonicScaleName && activePcs.length > 0 && (lockedTonicNote !== null || pcSorted.length > 0) // check if tonicScale and pcSorted exists: avoids showing "undefined"
            ? effectiveTonicScaleName
            : ''}
          {/* {'\u00a0'} {lockedTonicNote !== null ? tonicScales[0] : activePcs.length > 1 && relativeChordScales ? relativeChordScales[0] : ''} */}
          {(hoveredScaleBtn === 'tonic' || settings.showTonicScale) && effectiveTonicScaleNotes && (
            <span
              title="Play Scale"
              onMouseEnter={(e) => { e.stopPropagation(); setHoveredPlayBtn('tonic'); }}
              onMouseLeave={(e) => { e.stopPropagation(); setHoveredPlayBtn(null); }}
              onClick={(e) => {
                e.stopPropagation();
                const root = lockedTonicNote !== null ? lockedTonicNote % 12 : keyCenter;
                playScale(effectiveTonicScaleNotes, root, 'tonic');
              }}
              style={{
                position: 'absolute', right: '0.4dvw', top: '50%', transform: 'translateY(-50%)',
                fontSize: hoveredPlayBtn === 'tonic' ? '0.95dvw' : '0.8dvw',
                color: 'white',
                cursor: 'pointer',
                opacity: playingScale === 'tonic' ? (playIconFlash ? 1 : 0) : 1,
                transition: 'font-size 0.1s ease',
                textShadow: hoveredPlayBtn === 'tonic' ? '0 0 0.4dvw white, 0 0 0.8dvw white' : 'none',
              }}
            >{loadingScaleKey === 'tonic' ? scaleLoadingSpinner : '\u25B6'}</span>
          )}
        </div>



        <span style={{
          position: 'absolute',
          fontSize: '0.85dvw',
          fontFamily: 'Arial Unicode MS',
          // top: lockedTonicNote !== null ? '18.3dvw' : '16.0dvw',
          top: '18.0dvw',
          left: '0.4dvw',
          color: lockedTonicNote !== null && settings.showRelativeChordScale ? 'rgb(69, 207, 207)' : 'grey',
          opacity: lockedTonicNote !== null && settings.showRelativeChordScale ? '1' : '0.7',
          transition: 'top 0.3s ease'
        }}><span style={{ fontSize: '0.8dvw', textAlign: 'center', fontWeight: 'bold', color: 'rgb(69, 207, 207)' }}>&#x2B06; </span>
          Relative Chord Scale</span>

        <div
          data-actionable="true"
          style={{
            width: '14.4dvw', height: '2.4dvw', padding: '0.4dvw 0.3dvw',
            marginLeft: '0.3dvw', marginRight: '0dvw', marginTop: '0dvw', marginBottom: '0dvw',
            position: 'absolute',
            // top: lockedTonicNote !== null ? '19.5dvw' : '17.3dvw',
            top: '19.2dvw',
            opacity: lockedTonicNote !== null ? '1' : '0.5',
            left: '0dvw',
            color: settings.showRelativeChordScale ? settings.appTheme === "light" ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)' : settings.appTheme === "light" ? 'rgb(81, 81, 81)' : 'rgb(185, 215, 215)',
            fontSize: (relativeChordScales[scaleIndex]
              ? `${1.55 - (relativeChordScales[scaleIndex].length * 0.021)}dvw`
              : '1dvw'),
            fontFamily: 'Bravura Text',
            cursor: 'pointer',
            background: `rgba(70, 175, 175, ${settings.appTheme === "light" ? settings.showRelativeChordScale ? 0.4 : 0.3 : settings.showRelativeChordScale ? 0.2 : 0.1})`,
            border: `0.1dvw solid rgba(75, 149, 149, ${settings.showRelativeChordScale ? 0.99 : 0.2})`,
            borderRadius: '0.8dvw',
            boxShadow: settings.showRelativeChordScale
              ? '0 0 0.4dvw rgba(0, 147, 176, 0.5), inset -0dvw -0dvw 1.3dvw rgba(75, 149, 149, 0.7)'
              : '0 0 0.4dvw rgba(0, 147, 176, 0.3)',
            // textShadow: '0 0 1dvw rgba(0, 247, 255, 0.8)',
            transition: 'border 0.3s ease, boxShadow 0.3s ease, color 0.3s ease, top 0.3s ease',
            zIndex: 10,

          }}
          onClick={() => {
            const newSettings = { ...settings, showRelativeChordScale: !settings.showRelativeChordScale, showTonicScale: false, showChordScale: false };
            setSettings(newSettings);
          }}
          onMouseEnter={() => setHoveredScaleBtn('relative')}
          onMouseLeave={() => { setHoveredScaleBtn(null); setHoveredPlayBtn(null); }}>
          {relativeChordScales[scaleIndex] && activePcs.length > 0 && pcSorted.length > 0 // check if relativeChordScale and pcSorted exists: avoids showing "undefined"
            ? `${midiToName(scaleRootDisplay, false, scaleTonic, scaleModeDisplay)} ` :
            ''}
          {'\u00a0'}
          {relativeChordScales[scaleIndex] && activePcs.length > 0 && pcSorted.length > 0 // check if relativeChordScale and pcSorted exists: avoids showing "undefined"
            ? relativeChordScales[scaleIndex]
            : ''}
          {/* {'\u00a0'} {lockedTonicNote !== null ? tonicScales[0] : activePcs.length > 1 && relativeChordScales ? relativeChordScales[0] : ''} */}
          {(hoveredScaleBtn === 'relative' || settings.showRelativeChordScale) && relativeChordScalesNotes[scaleIndex] && (
            <span
              title="Play Scale"
              onMouseEnter={(e) => { e.stopPropagation(); setHoveredPlayBtn('relative'); }}
              onMouseLeave={(e) => { e.stopPropagation(); setHoveredPlayBtn(null); }}
              onClick={(e) => {
                e.stopPropagation();
                const root = activePcs.length === 1 ? activePcs[0] : selectedChartRootPc !== null ? selectedChartRootPc : rootPc !== null ? rootPc % 12 : activePcs.length > 0 ? activePcs[0] : keyCenter;
                playScale(relativeChordScalesNotes[scaleIndex], root, 'relative');
              }}
              style={{
                position: 'absolute', right: '0.4dvw', top: '50%', transform: 'translateY(-50%)',
                fontSize: hoveredPlayBtn === 'relative' ? '0.95dvw' : '0.8dvw',
                color: 'white',
                cursor: 'pointer',
                opacity: playingScale === 'relative' ? (playIconFlash ? 1 : 0) : 1,
                transition: 'font-size 0.1s ease',
                textShadow: hoveredPlayBtn === 'relative' ? '0 0 0.4dvw white, 0 0 0.8dvw white' : 'none',
              }}
            >{loadingScaleKey === 'relative' ? scaleLoadingSpinner : '\u25B6'}</span>
          )}
        </div>


        <span style={{
          position: 'absolute',
          fontSize: '0.85dvw',
          fontFamily: 'Arial Unicode MS',
          // top: lockedTonicNote !== null ? '18.3dvw' : '16.0dvw',
          top: '21.7dvw',
          left: '0.4dvw',
          color: settings.showChordScale ? 'rgb(69, 207, 207)' : 'grey',
          opacity: settings.showChordScale ? '1' : '0.7',
          transition: 'top 0.3s ease'
        }}><span style={{ fontSize: '0.8dvw', textAlign: 'center', fontWeight: 'bold', color: 'rgb(69, 207, 207)' }}>&#x2B06; </span>
          Chord Scale</span>

        <div
          data-actionable="true"
          style={{
            width: '14.4dvw', height: '2.4dvw', padding: '0.4dvw 0.3dvw',
            marginLeft: '0.3dvw', marginRight: '0dvw', marginTop: '0dvw', marginBottom: '0dvw',
            position: 'absolute',
            // top: lockedTonicNote !== null ? '19.5dvw' : '17.3dvw',
            top: '22.9dvw',
            left: '0dvw',
            color: settings.showChordScale ? settings.appTheme === "light" ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)' : settings.appTheme === "light" ? 'rgb(81, 81, 81)' : 'rgb(185, 215, 215)',
            fontSize: (chordScales[scaleIndex]
              ? `${1.55 - (chordScales[scaleIndex].length * 0.021)}dvw`
              : '1dvw'),
            fontFamily: 'Bravura Text',
            cursor: 'pointer',
            background: `rgba(70, 175, 175, ${settings.appTheme === "light" ? settings.showChordScale ? 0.4 : 0.3 : settings.showChordScale ? 0.2 : 0.1})`,
            border: `0.1dvw solid rgba(75, 149, 149, ${settings.showChordScale ? 0.99 : 0.2})`,
            borderRadius: '0.8dvw',
            boxShadow: settings.showChordScale
              ? '0 0 0.4dvw rgba(0, 147, 176, 0.5), inset -0dvw -0dvw 1.3dvw rgba(75, 149, 149, 0.7)'
              : '0 0 0.4dvw rgba(0, 147, 176, 0.3)',
            // textShadow: '0 0 1dvw rgba(0, 247, 255, 0.8)',
            transition: 'border 0.3s ease, boxShadow 0.3s ease, color 0.3s ease, top 0.3s ease',
            zIndex: 10,

          }}
          onClick={() => {
            const newSettings = { ...settings, showChordScale: !settings.showChordScale, showTonicScale: false, showRelativeChordScale: false };
            setSettings(newSettings);
          }}
          onMouseEnter={() => setHoveredScaleBtn('chord')}
          onMouseLeave={() => { setHoveredScaleBtn(null); setHoveredPlayBtn(null); }}>
          {chordScales[scaleIndex] && activePcs.length > 0 && pcSorted.length > 0 // check if chordScale and pcSorted exists: avoids showing "undefined"
            ? `${midiToName(scaleRootDisplay, false, scaleTonic, scaleModeDisplay)} ` :
            ''}
          {'\u00a0'}
          {chordScales[scaleIndex] && activePcs.length > 0 && pcSorted.length > 0 // check if chordScale and pcSorted exists: avoids showing "undefined"
            ? chordScales[scaleIndex]
            : ''}
          {/* {'\u00a0'} {lockedTonicNote !== null ? tonicScales[0] : activePcs.length > 1 && rchordScales ? chordScales[0] : ''} */}
          {(hoveredScaleBtn === 'chord' || settings.showChordScale) && chordScalesNotes[scaleIndex] && activePcs.length > 1 && (
            <span
              title="Play Scale"
              onMouseEnter={(e) => { e.stopPropagation(); setHoveredPlayBtn('chord'); }}
              onMouseLeave={(e) => { e.stopPropagation(); setHoveredPlayBtn(null); }}
              onClick={(e) => {
                e.stopPropagation();
                const root = activePcs.length === 1 ? activePcs[0] : selectedChartRootPc !== null ? selectedChartRootPc : rootPc !== null ? rootPc % 12 : activePcs.length > 0 ? activePcs[0] : keyCenter;
                playScale(chordScalesNotes[scaleIndex], root, 'chord');
              }}
              style={{
                position: 'absolute', right: '0.4dvw', top: '50%', transform: 'translateY(-50%)',
                fontSize: hoveredPlayBtn === 'chord' ? '0.95dvw' : '0.8dvw',
                color: 'white',
                cursor: 'pointer',
                opacity: playingScale === 'chord' ? (playIconFlash ? 1 : 0) : 1,
                transition: 'font-size 0.1s ease',
                textShadow: hoveredPlayBtn === 'chord' ? '0 0 0.4dvw white, 0 0 0.8dvw white' : 'none',
              }}
            >{loadingScaleKey === 'chord' ? scaleLoadingSpinner : '\u25B6'}</span>
          )}
        </div>


        {/* Key-Center Implied Scale Desplay END */}


        {/* OLD KEY DISPLAY */}
        {/* <div className="key-display" style={{
          color: lockedTonicNote !== null ? '#fff' : '#bbb',
        }}>{keyNameDisplay}
          {/* {midiToName(rootNote, false, keyCenter, displayedMode)} ***
          <div className="boxed" style={{
            border: lockedTonicNote !== null || isScale !== false ? '0.08dvw solid #fff' : '0.08dvw solid #000',
            // textShadow: lockedTonicNote !== null ? '0 0 15px #fff, 0 0 15px #fff, 0 0 32px #bbb, 0 0 42px #bbb,0 0 62px #bbb' : undefined,
          }}>
          </div>
        </div> */}

        {/* <p style={{ color: 'white' }}>{rootNote}</p> */}

        <div className="mode-select-container">
          <button
            data-actionable="true"
            className="mode-select"
            style={{
              fontSize: '1.2vw',
              fontFamily: 'Bravura Text',
              cursor: 'pointer',
              color: lockedTonicNote !== null
                ? lockedTonicMode === 0
                  ? 'rgb(255, 246, 142)'
                  : 'rgb(150, 194, 255)'
                : mode === 0
                  ? activePcs.length > 1
                    ? 'rgb(176, 170, 97)'
                    : '#666'
                  : activePcs.length > 1
                    ? 'rgb(118, 153, 201)'
                    : '#666',
              // color: isScale ? '#444' : (lockedTonicNote !== null ? '#fff' : '#bbb'),
              border: lockedTonicNote !== null ? lockedTonicMode === 0 ? '0.08dvw solid rgb(255, 246, 142)' : '0.08dvw solid rgb(150, 194, 255)' : '0.08dvw solid #222',
              // border: lockedTonicNote !== null ? '0.08dvw solid #fff' : '0.08dvw solid #222',
              width: '4.2dvw',
              height: '2.1dvw',
              backgroundColor: 'transparent',
              // Override default button styles
              background: lockedTonicNote !== null ? 'black' : 'transparent',
              boxShadow: 'none',
              borderRadius: '0.4dvw',
              padding: '0',
              margin: '0',
              textAlign: 'center',
              transition: 'none',
              position: 'static',
              display: 'inline-block'
            }}
            id="mode-select"
            onClick={() => {
              const currentMode = lockedTonicNote === null ? mode : (lockedTonicMode === null ? 0 : lockedTonicMode);
              const newMode = currentMode === 0 ? 1 : 0;

              if (lockedTonicNote === null) {
                // Enable lock and switch mode
                handleToggleLock(keyCenter);
                handleModeChange({ target: { value: newMode.toString() } } as React.ChangeEvent<HTMLSelectElement>);
              } else {
                // Just switch mode
                handleModeChange({ target: { value: newMode.toString() } } as React.ChangeEvent<HTMLSelectElement>);
              }
            }}
          >
            {lockedTonicNote === null
              ? (mode === 0 ? 'Major' : 'Minor')
              : (lockedTonicMode === 0 ? 'Major' : 'Minor')
            }
          </button>
        </div>

        {/* id="mode-select" OLD SELECT METHOD
            onChange={handleModeChange}
            value={lockedTonicNote === null ? mode.toString() : (lockedTonicMode === null ? '' : lockedTonicMode.toString())}
          >
            <option key="major" value="0">Major</option>
            <option key="minor" value="1">Minor</option>
          </select> */}

        <span
          data-actionable="true"
          // A chart lock is not the user's to unlock here (the chart setting or
          // the selection lets go of it), so clicking it makes it the user's lock.
          onClick={() => userLockedTonicNote !== null ? setLockedTonicNote(null) : handleToggleLock(keyCenter)}
          title={(() => {
            if (isFileLocked) return "Locked to the MIDI file's key center";
            if (isChartLocked) return "Locked to the chart's key center";
            return lockedTonicNote !== null ? 'Unlock Key-Center' : 'Lock Key-Center';
          })()}

          // onClick={() => {
          //   if (lockedTonicNote !== null) {
          //     setLockedTonicNote(null);
          //   } else {
          //     handleToggleLock(rootPc);
          //     handleModeChange({ target: { value: mode.toString() } } as React.ChangeEvent<HTMLSelectElement>);
          //   }
          // }}

          style={{
            // width: '1.5dvw',
            // height: '2.2dvw',
            // textAlign: 'center',
            // verticalAlign: '-2.8',
            // border: lockedTonicNote !== null ? '0.08dvw solid rgb(178, 77, 77)' : '',
            cursor: 'pointer',
            // borderRadius: '0.4dvw',
            padding: '0.1dvw 0.1dvw',
            // backgroundColor: 'black',
            // color: lockedTonicNote !== null ? 'rgb(178, 77, 77)' : 'rgba(0,0,0,0',
            // fontSize: '0.2dvw',
            marginLeft: '7.6dvw', // just clear of the Major/Minor button, leaving room for the chart peek
            position: 'relative',
            top: '0.9dvw'
          }}
        >
          {/* The lock is boxed like the key and Major/Minor buttons beside it:
              the same dark #222 border while unlocked, and the mode's colour
              once the user locks a key, since that lock overrides the chart's.
              A chart lock has no box. The box is the buttons' 2.1dvw height,
              its own element because the lock's filter would recolour a
              border drawn on the image. The wrapper is inline-block with the
              image on its baseline, so the image sits where it did unwrapped;
              the unlocked image is dimmed itself rather than the whole span,
              which would dim its box too. */}
          <span style={{ position: 'relative', display: 'inline-block' }}>
            {lockedTonicNote !== null ?
              <img src="/images/Icons/lock-locked-512.gif" alt="" style={{
                width: '1.4dvw', filter: lockedTonicMode === 0
                  ? `invert(0%) sepia(100%) saturate( ${settings.appTheme === "light" ? 9550 : 700}%) hue-rotate(-10deg) ${settings.appTheme === "light" ? 'brightness(70%)' : 'brightness(100%)'} contrast(100%)`
                  : `invert(20%) sepia(12%) saturate(3530%) hue-rotate(175deg) ${settings.appTheme === "light" ? 'brightness(80%)' : 'brightness(100%)'} contrast(100%)`
              }} />
              : <img src="/images/Icons/lock-unlocked-512.gif" alt="" style={{
                width: '1.4dvw',
                opacity: 0.3,
                filter: `invert(12%) sepia(100%) saturate(0%) hue-rotate(90deg) ${settings.appTheme === "light" ? 'brightness(0%)' : 'brightness(100%)'}  contrast(100%)`
              }} />
              // <img src="/images/Icons/padlock-locked.png" alt="" style={{width: '1.6dvw'}} />
              // : <img src="/images/Icons/padlock-unlocked.png" alt="" style={{width: '1.6dvw'}} />
            }
            {!isChartLocked && <span style={{
              position: 'absolute',
              left: '-0.2dvw',
              top: '-0.36dvw',
              width: '1.8dvw',
              height: '2.1dvw',
              boxSizing: 'border-box',
              border: `0.08dvw solid ${(() => {
                if (lockedTonicNote === null) return '#222';
                return lockedTonicMode === 0 ? 'rgb(255, 246, 142)' : 'rgb(150, 194, 255)';
              })()}`,
              borderRadius: '0.4dvw',
              pointerEvents: 'none'
            }} />}
          </span>
          {/* A bass clef on the lock marks it as the chart's (Bravura fClef, U+E062) or 3 staff lines (U+1D118) which is \uD834\udd18  */}
          {isChartLocked && <span style={{
            position: 'absolute',
            left: '0.6dvw',
            top: '0.4dvw',
            fontFamily: 'Bravura Text',
            fontSize: '1.15dvw',
            fontWeight: 'bold',
            lineHeight: 1,
            color: 'black',
            pointerEvents: 'none'
          }}>{'\uD834\udd18'}</span>}
        </span>

        {/* The current chart chord and the next (see peekCurrentEntry), on a
            strip that slides through the window (see chartPeekOffset). Shown
            only while a chord or separator is selected in the chart: the chart
            can be hidden or scrolled off, and this is then the only sign that
            a selection is still held (Reset clears the piano but keeps it).
            Selected but not sounding, it stays, all dimmed. Clicking a chord
            selects it in the chart, hidden or not; data-actionable keeps
            handleGlobalClick from deselecting first. */}
        <span
          data-actionable="true"
          className="key-center-chart-peek"
          style={{
            color: settings.appTheme === 'light' ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)',
            visibility: peekChords.length > 0 && selectedChartEntry !== null ? 'visible' : 'hidden',
          }}
        >
          <span
            ref={chartPeekStripRef}
            className="key-center-chart-peek-strip"
            style={{ transform: `translateX(calc(var(--chart-peek-fade-left) - ${chartPeekOffset}px))` }}
          >
            {peekChords.map(entry => {
              const isCurrent = entry === peekCurrentEntry;
              const isHighlighted = isCurrent && peekCurrentSounding;
              const segment = isCurrent ? peekCurrentSegment : entry.segment;
              return (
                <span
                  key={entry.id}
                  className={`key-center-chart-peek-chord${isHighlighted ? ' current' : ''}`}
                  title="Select Chord"
                  onClick={() => handleChordSelection(chordChart.indexOf(entry))}
                  // The current chord's box in its mode's color, as the chart
                  // draws the selected chord. Off for now; restore the line below
                  // (and the box lines in .key-center-chart-peek-chord.current).
                  // style={isHighlighted && segment ? { backgroundColor: segment.mode === 0 ? 'rgb(90,90,0)' : 'rgb(50,50,140)' } : undefined}
                >{segment ? assembleChordSymbol(segment) : entry.name}</span>
              );
            })}
          </span>
        </span>

        {/* <div style={{width: '13.5dvw'}}>
          <span onClick={() => setLockedTonicNote(null)} title='Unlock Key-Center' style={{
            textAlign: 'center', border: lockedTonicNote !== null ? 'groove' : '', verticalAlign: '-0.8dvw', cursor: 'pointer', borderRadius: '0.4dvw', padding: '0.1dvw', backgroundColor: 'black', color: lockedTonicNote !== null ? 'rgb(220,220,220)' : 'rgb(70,70,70)', fontSize: '0.7dvw'
          }}>
           {'\u00a0Locked\u00a0\u2716'}
          </span>
        </div> */}

        <div id="color-key" style={{ height: '10dvw' }}>
          {(() => {
            const standardWhiteKeys = [0, 2, 4, 5, 7, 9, 11]; // C, D, E, F, G, A, B

            const effectiveRootPcForDisplay = selectedChartRootPc !== null ? selectedChartRootPc : rootPc !== null ? rootPc % 12 : null;
            const effectiveChordRootW = activePcs.length === 1 ? activePcs[0] : effectiveRootPcForDisplay;
            const rootPcValue = effectiveChordRootW !== null && (lockedTonicNote === null || (settings.showRelativeChordScale && isImpliedScale) || (settings.showChordScale && activePcs.length > 1))
              ? effectiveChordRootW
              : lockedTonicNote !== null
                ? lockedTonicNote % 12
                : 0;

            let keysToRender: number[] = [];

            // Determine which sequence of white keys to display based on rootPc range
            if (rootPcValue >= 0 && rootPcValue <= 2) { // C, C#/Db, D
              keysToRender = [0, 2, 4, 5, 7, 9, 11, 0 + 12, 2 + 12]; // Start with C (pc=0)
            }
            else if (rootPcValue >= 3 && rootPcValue <= 4) { // D#/Eb, E
              keysToRender = [2, 4, 5, 7, 9, 11, 0, 2 + 12, 4 + 12]; // Start with E (pc=4)
            }
            else if (rootPcValue >= 5 && rootPcValue <= 8) { // F, F#/Gb, G, G#/Ab
              keysToRender = [5, 7, 9, 11, 0, 2, 4, 5 + 12, 7 + 12]; // Start with F (pc=5)
            }
            else { // A, A#/Bb, B
              keysToRender = [9, 11, 0, 2, 4, 5, 7, 9 + 12, 11 + 12]; // Start with A (pc=9)
            }

            // Find where the actual root note is in our keysToRender array
            let rootNoteIndex = keysToRender.findIndex(note => note % 12 === rootPcValue);
            if (rootNoteIndex === -1) rootNoteIndex = 0; // Fallback if not found

            // Get the physical position of the root note
            const rootPhysicalPosition = rootNoteIndex;

            // Replace activePcs with transposed scale notes based on active scale mode
            let effectiveActivePcs = activePcs;
            if ((settings.showTonicScale || settings.showRelativeChordScale) && isImpliedScale || (settings.showChordScale && activePcs.length > 1)) {
              const scaleNotes = settings.showTonicScale // && lockedTonicNote !== null
                ? effectiveTonicScaleNotes
                : settings.showRelativeChordScale
                  ? relativeChordScalesNotes[scaleIndex]
                  : settings.showChordScale
                    ? chordScalesNotes[scaleIndex]
                    : undefined;
              // const scaleNotes = lockedTonicNote !== null ? tonicScalesNotes[0] : relativeChordScalesNotes[0];
              if (scaleNotes && scaleNotes.length > 1) {
                const transpositionRoot = (settings.showChordScale || settings.showRelativeChordScale || (settings.showTonicScale && lockedTonicNote === null))
                  ? (activePcs.length === 1 ? activePcs[0] : effectiveRootPcForDisplay !== null ? effectiveRootPcForDisplay : lockedTonicNote !== null ? lockedTonicNote % 12 : keyCenter)
                  : lockedTonicNote !== null ? lockedTonicNote % 12 : keyCenter;
                effectiveActivePcs = scaleNotes.map(note => (note + transpositionRoot) % 12);
              }
            }

            // Filter white keys based on their physical position relative to root
            const whiteKeysWithActivatablePitchClasses = keysToRender.filter((note, index) => {
              const pc = note % 12;

              // Only include if this pitch class is actually active
              if (!effectiveActivePcs.includes(pc)) return false;

              // Calculate distance from root in pitch class space
              const distanceFromRoot = (pc - rootPcValue + 12) % 12;

              // Check if rootPcValue is a black key
              const isRootBlackKey = [1, 3, 6, 8, 10].includes(rootPcValue);


              // For black key roots, we need to be more careful about pitch class distances
              if (isRootBlackKey) {
                // Calculate the precise distance in pitch class space
                const distanceFromRoot = (pc - rootPcValue + 12) % 12;

                // When root is a black key, we need to consider:
                // 1. Physical layout of the keyboard - white keys physically appear before black keys
                // 2. Pitch class relationship - whether the pc is "to the right" of rootPc in pitch class space

                // If this white key's pitch class is less than or equal to the rootPc,
                // it would physically appear to the left of the root black key
                const isPitchClassLeft = pc <= rootPcValue;

                // If pitch class is left of rootPc AND it's already in higher octave, include it
                if (note >= 12) {
                  return true;
                }

                // Check if there's a higher octave version of this note
                const hasHigherOctaveVersion = keysToRender.some(n => n >= 12 && n % 12 === pc);

                // For white keys with pitch classes less than or equal to root black key's pitch class,
                // ONLY include if it's the higher octave version
                if (isPitchClassLeft) {
                  if (hasHigherOctaveVersion) {
                    return false;
                  } else {
                    // No higher octave available but we need it in higher octave - use this as fallback
                    return true;
                  }
                }
                // For white keys with pitch classes greater than the root black key's pitch class,
                // they are naturally to the right, so include directly
                else {
                  return true;
                }
              }
              // For white key roots, continue with the existing logic
              else {
                // If note is physically to the right or at the root position
                if (index >= rootPhysicalPosition) {
                  const isToTheRight = true;
                  return isToTheRight;
                }
                // If note is in the next octave, include if active
                else if (note >= 12) {
                  return true;
                }
                // If note is physically left but has higher octave version, exclude
                else {
                  const hasHigherOctaveVersion = keysToRender.some(n => n >= 12 && n % 12 === pc);
                  if (hasHigherOctaveVersion) {
                    return false;
                  }
                  // No higher version available, include it
                  return true;
                }
              }
            });

            // Only the first occurrence of each activatable pitch class should be actually activatable
            const uniqueActivatableWhiteKeys: number[] = [];
            const seenPitchClasses = new Set<number>();


            // Process white keys in physical order
            for (const note of whiteKeysWithActivatablePitchClasses) {
              const pc = note % 12;
              if (!seenPitchClasses.has(pc)) {
                uniqueActivatableWhiteKeys.push(note);
                seenPitchClasses.add(pc);
              }
            }

            // Render the white keys with the correct pitch classes
            return keysToRender.map((note, index) => {
              // Get the pitch class (0-11) for the note
              const pc = note % 12;

              // A note is activatable if it's in uniqueActivatableWhiteKeys AND activePcs includes its pitch class
              const isActivatable = uniqueActivatableWhiteKeys.includes(note) && effectiveActivePcs.includes(pc);

              // Highlight the locked tonic at its correct octave position (whichever passed the filter)
              const scaleIsActive = (settings.showTonicScale || settings.showRelativeChordScale) && isImpliedScale || (settings.showChordScale && activePcs.length > 1);
              const tonicInActiveWhiteKeys = lockedTonicNote !== null && uniqueActivatableWhiteKeys.some(n => n % 12 === lockedTonicNote % 12);
              const isFirstOccurrence = lockedTonicNote !== null &&
                pc === lockedTonicNote % 12 &&
                (tonicInActiveWhiteKeys
                  ? uniqueActivatableWhiteKeys.includes(note)
                  : !scaleIsActive && note === keysToRender.find(n => n % 12 === lockedTonicNote % 12));

              return (
                <KeyCenterPanelKey
                  key={`white-${note}-${index}`}
                  note={note}
                  isBlack={false}
                  rootNote={panelRootNote}
                  setRootNote={setRootNote}
                  rootPc={selectedChartRootPc !== null ? selectedChartRootPc : rootPc}
                  keyCenter={panelKeyCenterPc}
                  setKeyCenter={setKeyCenter}
                  isActive={isActivatable}
                  isFirstOccurrence={isFirstOccurrence}
                  isScale={isScale}
                  toggleLock={lockedTonicNote}
                  setToggleLock={handleToggleLock}
                  mode={mode}
                  chordQual={chordQual}
                  lockedTonicNote={lockedTonicNote}
                  lockedTonicMode={lockedTonicMode}
                  isChartLocked={isChartLocked}
                  appTheme={settings.appTheme}
                  showTonicScale={settings.showTonicScale}
                  showRelativeChordScale={settings.showRelativeChordScale}
                  showChordScale={settings.showChordScale}
                  tonicScalesNotes={tonicScalesNotes}
                  relativeChordScalesNotes={relativeChordScalesNotes}
                  chordScalesNotes={chordScalesNotes}
                  scaleIndex={scaleIndex}

                />
              );
            });
          })()}

          {(() => {
            // Define black keys for the 15-note layout (6 black keys total - 5 standard + 1 octave up)
            const standardBlackKeys = [1, 3, 6, 8, 10]; // C#, D#, F#, G#, A#

            // Determine correct black key positions based on rootPc
            // const rootPitch = rootPc !== null && lockedTonicNote === null ? rootPc % 12 : lockedTonicNote !== null ? lockedTonicNote % 12 : 0;

            const effectiveRootPcForDisplay = selectedChartRootPc !== null ? selectedChartRootPc : rootPc !== null ? rootPc % 12 : null;
            const effectiveChordRootB = activePcs.length === 1 ? activePcs[0] : effectiveRootPcForDisplay;
            const rootPitch = effectiveChordRootB !== null && (lockedTonicNote === null || (settings.showRelativeChordScale && isImpliedScale) || (settings.showChordScale && activePcs.length > 1))
              ? effectiveChordRootB
              : lockedTonicNote !== null
                ? lockedTonicNote % 12
                : 0;

            // Position arrays for each rootPc with 6 positions for 6 black keys
            let positionArray: any[];

            if (rootPitch === 0 || rootPitch === 1 || rootPitch === 2) { // C or C#/Db or D
              positionArray = [0, 1, 3, 4, 5, 7];
            } else if (rootPitch === 3 || rootPitch === 4) { // D#/Eb or E
              positionArray = [0, 2, 3, 4, 6, 7];
            } else if (rootPitch === 5 || rootPitch === 6 || rootPitch === 7 || rootPitch === 8) { // F, F#/Gb, G, G#/Ab
              positionArray = [0, 1, 2, 4, 5, 7];
            } else { // A, A#/Bb, B
              positionArray = [0, 2, 3, 5, 6, 7];
            }

            // Define which black keys should be rendered based on the rootPitch
            let blackKeysToRender: number[] = [];

            if (rootPitch === 0 || rootPitch === 1 || rootPitch === 2) { // C, C#/Db, D
              // For C-based layout, we need C#, D#, F#, G#, A#, high C#
              blackKeysToRender = [1, 3, 6, 8, 10, 13]; // 13 is C# one octave up (1+12)
            } else if (rootPitch === 3 || rootPitch === 4) { // D#/Eb, E
              // For D-based layout, we need D#, F#, G#, A#, high C#, high D#
              blackKeysToRender = [3, 6, 8, 10, 13, 15]; // 13 is C#+12, 15 is D#+12
            } else if (rootPitch === 5 || rootPitch === 6 || rootPitch === 7 || rootPitch === 8) { // F, F#/Gb, G, G#/Ab
              // For F-based layout, we need F#, G#, A#, high C#, high D#, high F#
              blackKeysToRender = [6, 8, 10, 13, 15, 18]; // 18 is F#+12
            } else { // A, A#/Bb, B
              // For A-based layout, we need A#, high C#, high D#, high F#, high G#, high A#
              blackKeysToRender = [10, 13, 15, 18, 20, 22]; // 20 is G#+12, 22 is A#+12
            }

            // Find the physical position of the root among black keys
            const rootBlackKeyIndex = blackKeysToRender.findIndex(note => note % 12 === rootPitch);

            // Replace activePcs with transposed scale notes based on active scale mode
            let effectiveActivePcs = activePcs;
            // if (settings.showRelativeChordScale && isImpliedScale) {
            //   const scaleNotes = lockedTonicNote !== null ? tonicScalesNotes[0] : relativeChordScalesNotes[0];
            if ((settings.showTonicScale || settings.showRelativeChordScale) && isImpliedScale || (settings.showChordScale && activePcs.length > 1)) {
              const scaleNotes = settings.showTonicScale // && lockedTonicNote !== null
                ? effectiveTonicScaleNotes
                : settings.showRelativeChordScale
                  ? relativeChordScalesNotes[scaleIndex]
                  : settings.showChordScale
                    ? chordScalesNotes[scaleIndex]
                    : undefined;
              if (scaleNotes && scaleNotes.length > 1) {
                const transpositionRoot = (settings.showChordScale || settings.showRelativeChordScale || (settings.showTonicScale && lockedTonicNote === null))
                  ? (activePcs.length === 1 ? activePcs[0] : effectiveRootPcForDisplay !== null ? effectiveRootPcForDisplay : lockedTonicNote !== null ? lockedTonicNote % 12 : keyCenter)
                  : lockedTonicNote !== null ? lockedTonicNote % 12 : keyCenter;
                effectiveActivePcs = scaleNotes.map(note => (note + transpositionRoot) % 12);
              }
            }

            // Filter black keys based on their physical position relative to root
            const blackKeysWithActivatablePitchClasses = blackKeysToRender.filter((note, index) => {
              // If root is a black key, use its position as reference
              let shouldInclude = false;

              const pc = note % 12;
              // Only include if this pitch class is actually active
              if (!effectiveActivePcs.includes(pc)) return false;

              if (rootBlackKeyIndex !== -1) {
                // If root is a black key, use physical position
                shouldInclude = index >= rootBlackKeyIndex;
              } else {
                // For white key roots, apply the "to the right of" rule based on pitch class
                // PC order relative to the root is what matters
                const distanceFromRoot = (pc - rootPitch + 12) % 12;

                // When root is a white key, black keys have special positioning:
                // 1. Black keys with pc > rootPc naturally appear to the right
                // 2. Black keys with pc < rootPc might appear to the left (depending on exact pitch class)

                // For black keys, check if pitch class position is to the right of root pitch
                const isPitchClassRight = pc > rootPitch;

                // If note is already in higher octave, include it
                if (note >= 12) {
                  shouldInclude = true;
                }
                // If note's pitch class is less than rootPc, check for higher octave version
                else if (!isPitchClassRight) {
                  const hasHigherOctaveVersion = blackKeysToRender.some(n => n >= 12 && n % 12 === pc);

                  // If a higher octave version exists, exclude this lower one
                  if (hasHigherOctaveVersion) {
                    shouldInclude = false;
                  } else {
                    // No higher version available - use as fallback
                    shouldInclude = true;
                  }
                }
                // If pitch class is naturally to the right, include it
                else {
                  shouldInclude = true;
                }
              }

              return shouldInclude;
            });

            // Only the first occurrence of each activatable pitch class should be actually activatable
            const uniqueActivatableBlackKeys: number[] = [];
            const seenBlackPitchClasses = new Set<number>();


            // Process black keys in physical order
            for (const note of blackKeysWithActivatablePitchClasses) {
              const pc = note % 12;
              if (!seenBlackPitchClasses.has(pc)) {
                uniqueActivatableBlackKeys.push(note);
                seenBlackPitchClasses.add(pc);
              }
            }

            // Now render the black keys with proper positioning
            return blackKeysToRender.map((note, index) => {
              // Calculate actual note to use (modulo 12 to get the pitch class)
              const actualNote = note;
              const pc = note % 12;

              // Get the position of this black key for visual layout
              const positionInOctave = positionArray[index];

              // Adjust for 15 notes - position the black keys correctly
              const leftOffset = ((positionInOctave / 1.515) * 15) + 6.2;

              if (index === 0) {
              }

              // A note is activatable if it's in uniqueActivatableBlackKeys AND effectiveActivePcs includes its pitch class
              const isBlackKeyActivatable = uniqueActivatableBlackKeys.includes(note) && effectiveActivePcs.includes(pc);

              // Highlight the locked tonic at its correct octave position (whichever passed the filter)
              const scaleIsActive = (settings.showTonicScale || settings.showRelativeChordScale) && isImpliedScale || (settings.showChordScale && activePcs.length > 1);
              const tonicInActiveBlackKeys = lockedTonicNote !== null && uniqueActivatableBlackKeys.some(n => n % 12 === lockedTonicNote % 12);
              const isFirstOccurrence = lockedTonicNote !== null &&
                pc === lockedTonicNote % 12 &&
                (tonicInActiveBlackKeys
                  ? uniqueActivatableBlackKeys.includes(note)
                  : !scaleIsActive && note === blackKeysToRender.find(n => n % 12 === lockedTonicNote % 12));

              return (
                <KeyCenterPanelKey
                  key={`black-${note}-${index}`}
                  note={actualNote}
                  isBlack={true}
                  rootNote={panelRootNote}
                  setRootNote={setRootNote}
                  rootPc={selectedChartRootPc !== null ? selectedChartRootPc : rootPc}
                  keyCenter={panelKeyCenterPc}
                  setKeyCenter={setKeyCenter}
                  isActive={isBlackKeyActivatable}
                  isFirstOccurrence={isFirstOccurrence}
                  isScale={isScale}
                  style={{ left: `${leftOffset}%` }}
                  toggleLock={lockedTonicNote}
                  setToggleLock={handleToggleLock}
                  mode={mode}
                  chordQual={chordQual}
                  lockedTonicNote={lockedTonicNote}
                  lockedTonicMode={lockedTonicMode}
                  isChartLocked={isChartLocked}
                  appTheme={settings.appTheme}
                  showTonicScale={settings.showTonicScale}
                  showRelativeChordScale={settings.showRelativeChordScale}
                  showChordScale={settings.showChordScale}
                  tonicScalesNotes={tonicScalesNotes}
                  relativeChordScalesNotes={relativeChordScalesNotes}
                  chordScalesNotes={chordScalesNotes}
                  scaleIndex={scaleIndex}

                />
              );
            });
          })()}
        </div>
      </div >
      {/* KeyCenterPanelKey display End */}


      {
        // settings.showMidiPlayer ? (
          <div>
            <MidiController
              ref={midiControllerRef}
              midiEventMerger={midiEventMergerRef.current}
              clearAllNotes={clearAllNotes}
              onSustainStateChange={(sustainState: boolean) => {
                setSustain(sustainState);
                sustainRef.current = sustainState;
              }}
              onScrubbingStateChange={(scrubbing: boolean) => {
                setIsScrubbing(scrubbing);
                // Set the ref here rather than only in its effect: scrub end
                // resumes playback synchronously, and the note handling it does
                // reads this ref (a note-off while scrubbing is treated very
                // differently from one during playback)
                isScrubbingRef.current = scrubbing;
              }}
              onPausedStateChange={(paused: boolean) => {
                setIsMidiFilePaused(paused);
                setMidiFilePlaying(!paused);
              }}
              followOn={chartFollowOn}
              onToggleFollow={() => setChartFollowOn(on => !on)}
              onFollowTime={(fileKey, seconds) => setFollowTime(fileKey !== null && seconds !== null ? { fileKey, seconds } : null)}
              reportsHeld={heldRegionReport !== null}
              onFileRegions={(fileKey, regions) => setFileRegions(fileKey === null ? null : { fileKey, regions })}
              chartSelectionSeconds={!chartFollowOn && selectedChartEntry && selectedChartEntry.midiSource
                && fileRegions !== null && selectedChartEntry.midiSource.fileKey === fileRegions.fileKey
                ? selectedChartEntry.midiSource.seconds : null}
              playLocked={chartFollowOn && playSongOn}
              onFileInteraction={() => {
                // As Escape does: the edit is dropped, the selection kept.
                // The player clears and refills the notes itself.
                setChordEditMode(false);
                setSeparatorEditMode(false);
                notesFromFileRef.current = true;
                setFileDrivesKey(true);
              }}
              onPromoteRegions={addChordsFromFile}
              onRegionsSynced={handleRegionsSynced}
              divergentChartKeys={fileKeys !== null ? divergentChartKeys(chordChart, keyCenterRegions, fileKeys, globalKeyCenter) : []}
              onReturnChartKey={returnChartKeyToFile}
              divergentChartGlobalKey={divergentChartGlobalKey}
              onReturnChartGlobalKey={returnChartGlobalKeyToFile}
              chartChords={chordChart.filter(entry => entry.midiSource !== undefined)}
              onUpdateChartFromRegion={updateChartFromRegion}
              onFileAnalyzed={handleFileAnalyzed}
              soundOn={soundOn}
              setSoundOn={setSoundOn}
              audioInitialized={audioInitialized}
              setAudioInitialized={setAudioInitialized}
              midiAccess={midiAccess}
              selectedInputId={selectedInputId}
              setSelectedInputId={setSelectedInputId}
              activeNotes={activeNotes}
              toggledNotes={toggledNotes}
              heldNotes={heldNotes}
              setActiveNotes={setActiveNotes}
              setToggledNotes={setToggledNotes}
              setHeldNotes={setHeldNotes}
              server={server}
              notationLegible={settings.preferNotationLegible}
              chordSymbol={(segment, key, spellingMode) => assembleChordSymbol(segment, false, key ? key.pitchClass : null, key ? key.mode : 0, spellingMode !== undefined ? spellingMode : (key ? key.mode : 0))}
              chordRoot={(segment, key, spellingMode) => spellChordSymbol(segment, false, key ? key.pitchClass : null, key ? key.mode : 0, spellingMode !== undefined ? spellingMode : (key ? key.mode : 0)).rootName}
              noteName={(pc, key) => midiToName(pc, false, key ? key.pitchClass : pc, key ? key.mode : 0)}
              onFileKeyAt={setFileKeyCenter}
              onPlayChordNotes={playChordNotes}
              // The user's own lock only: the file spells by its own keys
              lockedTonicNote={userLockedTonicNote}
              lockedTonicMode={userLockedTonicMode}
              preferModeBorrowing={settings.preferModeBorrowing}
            />
          </div>
        // ) : ( // MIDI Player always shown, not turned off with settings.showMidiPlayer
        //   <div></div>
        // )
      }

      {/* Keyboard Reset, MIDI Device Select */}
      <div id="keyboard-inputs">


        {/* <div style={{ position: 'absolute', top: '5.4dvw', left: '0.4dvw', color: 'grey', fontSize: '1dvw' }}>
          
        </div> */}

        {/* <div style={{ position: 'absolute', top: '5.5dvw', left: '0.4dvw', color: 'grey', fontSize: '1dvw' }}>
          <button style={{ width: "12dvw" }} data-actionable="true" onClick={playChord}>Play Chord {'\u25B6'}</button>
        </div> */}

        <div style={{
          position: 'absolute', top: '7.5dvw', left: '0.4dvw',
          display: 'flex', // Use flexbox for horizontal layout
          alignItems: 'center', // Align items vertically in the center
          color: 'grey', fontSize: '1dvw'
        }}>
          <button
            onClick={(event) => {
              setSoundOn(prev => !prev);
              handleSoundToggle(event);
            }}
            style={{
              // fontWeight: soundOn ? 'bold' : 'normal',
              width: '9.9dvw',
              color: soundOn ? 'white' : 'black',
              textShadow: soundOn ? '0 0 0.7dvw #fff, 0 0 0.7dvw #fff, 0 0 1.4dvw #bbb, 0 0 1.8dvw #bbb,0 0 2.8dvw #bbb' : '',
              // backgroundColor: soundOn ? '#888' : '#555'
            }}
          >
            {!samplerLoaded && <span className="loading-label">{"Loading " + instrumentConfigs[selectedInstrument].name}</span>}
            {/* {samplerLoaded && soundOn && ": : : \u00A0 Sound On \u00A0 : : :"}
            {samplerLoaded && !soundOn && ": : : \u00A0 Sound Off \u00A0 : : :"} */}
            {samplerLoaded && soundOn && <>Sound On <img src='/images/sound-on-white.png' alt='Sound On' style={{ width: '1.2dvw', verticalAlign: '-0.3dvw' }} /></>}
            {samplerLoaded && !soundOn && <>Sound Off <img src='/images/sound-off.png' alt='Sound Off' style={{ width: '1.3dvw', verticalAlign: '-0.35dvw' }} /></>}
          </button>
          {/* Arpeggiate Toggle Button */}
          <button title={isScale ? "Scale Direction" : "Arpeggiate"} onClick={() => isScale ? setScaleDescending(prev => !prev) : setIsArpeggiateOn(prev => !prev)} data-actionable="true"
            style={{
              width: '4.6dvw',
              // height: "2.85dvw",
              color: isArpeggiateOn || isScale ? 'white' : 'black',
              // transform: isScale && !scaleDescending ? 'scale(1, -1)' : '', // Doesn't work, fix
              fontSize: !isScale ? '0.9dvw' : '1.1dvw',
              textShadow: isArpeggiateOn || isScale ? '0 0 0.7dvw #fff, 0 0 0.7dvw #fff, 0 0 1.4dvw #bbb, 0 0 1.8dvw #bbb, 0 0 2.8dvw #bbb' : '',
            }}
          >
            {isArpeggiateOn && !isScale ? (
              "Arp \u266C"
            ) : isScale ? (
              <img
                src={scaleDescending ? '/images/downstairs-white.png' : '/images/upstairs-white.png'}
                alt={scaleDescending ? 'Scale descending' : 'Scale ascending'}
                style={{ width: '1.05dvw', verticalAlign: '-0.15dvw' }}
              />
            ) : (
              "Arp \u266C"
            )}
          </button>
        </div>

        <div style={{ position: 'absolute', top: '9.6dvw', left: '0.4dvw', height: '1dvw' }}>
          <button style={{ width: "7.1dvw", color: 'rgb(130, 0, 0)', backgroundColor: 'rgb(130, 0, 0)' }} data-actionable="true" title="Reset Keyboard" onClick={() => clearAllNotes()}
          // style={{ color: 'black', fontSize: '1.3dvw', border: '10px', fontFamily: 'Trebuchet MS', fontStyle: 'bold', backgroundColor: 'rgb(110,110,140)' }}
          >Reset <span style={{ fontSize: '1.4dvw', verticalAlign: '-0.1dvw' }}>{'\u21Ba'}</span>
          </button>
          {/* Play Chord Button */}
          <button
            style={{
              width: "7.4dvw",
              // height: "2.85dvw",
              marginRight: '0.0dvw',
              cursor: !samplerLoaded ? 'not-allowed' : 'pointer',
              // opacity: !samplerLoaded ? 0.85 : 1
            }}
            data-actionable="true"
            title="Play Chord [Space Bar]"
            onClick={playChord}
            disabled={!samplerLoaded}
          >
            Play {!isScale ? `Chord` : `Scale`} {'\u25B6'}
          </button>
        </div>


        <div style={{ position: 'absolute', top: '11.8dvw', left: '0.4dvw', height: '1dvw', zIndex: 1 }}>

          <button data-actionable="true" title="Octave Down" onClick={() => nudgeToggledNotes(-12)}
            style={{ width: '3.4dvw' }}
          // style={{ color: 'black', fontSize: '1.3dvw', border: '10px', fontFamily: 'Trebuchet MS', fontStyle: 'bold', backgroundColor: 'rgb(110,110,140)' }}
          ><span style={{ fontSize: '0.9dvw', fontWeight: '100' }}>-12</span></button><span style={{ fontSize: '0.9dvw' }}>&nbsp;</span>

          <button data-actionable="true" title="Step Down" onClick={() => nudgeToggledNotes(-1)}
            style={{ width: '3.4dvw' }}
          // style={{ color: 'black', fontSize: '1.3dvw', border: '10px', fontFamily: 'Trebuchet MS', fontStyle: 'bold', backgroundColor: 'rgb(110,110,140)' }}
          ><span style={{ fontSize: '0.9dvw', fontWeight: '100' }}>-1</span></button><span style={{ fontSize: '0.9dvw' }}>&nbsp;</span>

          <button data-actionable="true" title="Step Up" onClick={() => nudgeToggledNotes(1)}
            style={{ width: '3.4dvw' }}
          // style={{ color: 'black', fontSize: '1.3dvw', border: '10px', fontFamily: 'Trebuchet MS', fontStyle: 'bold', backgroundColor: 'rgb(110,110,140)' }}
          ><span style={{ fontSize: '0.9dvw', fontWeight: '100' }}>+1</span></button><span style={{ fontSize: '0.9dvw' }}>&nbsp;</span>

          <button data-actionable="true" title="Octave Up" onClick={() => nudgeToggledNotes(12)}
            style={{ width: '3.4dvw' }}
          // style={{ color: 'black', fontSize: '1.3dvw', border: '10px', fontFamily: 'Trebuchet MS', fontStyle: 'bold', backgroundColor: 'rgb(110,110,140)' }}
          ><span style={{ fontSize: '0.9dvw', fontWeight: '100' }}>+12</span></button>
        </div>

      </div>




      {/* Main Piano display Below */}

      <div data-actionable="true" id="piano" style={{ height: '20dvw' }}>
        {whiteNotes.map((note) => (
          <PianoKey
            key={note}
            note={note}
            isBlack={false}
            keyflash={keyflash.includes(note)}
            // style={{ '--activeColor': activeColor } as any}
            isActive={activeNotes.includes(note) || heldNotes.includes(note) || toggledNotes.includes(note)}
            rootNote={rootNote}
            rootPc={rootPc}
            showTonicLabel={false}
            showScaleDegrees={settings.showScaleDegrees}
            lockedTonicNote={anchorTonicNote} // the keys read the chord like the Chord Compass does
            lockedTonicMode={lockedTonicMode}
            toggleNote={toggleNote}
            toggledNotes={toggledNotes}
            activeNotes={activeNotes}
            heldNotes={heldNotes}
            keyCenter={keyCenter}
            mode={mode}
            chordQual={chordQual}
            csOptions={csOptions}
            activePcs={activePcs}
            sampler={currentSampler}
            shouldPlaySound={shouldPlaySoundRef.current}
            // selectedInstrument={selectedInstrument}
            isScale={isScale}
            showOvertones={settings.showOvertones}
            showNashvilleNumber={settings.showNashvilleNumber}
            appTheme={settings.appTheme}
            tonicScalesNotes={tonicScalesNotes}
          // shouldPlaySoundRef={shouldPlaySoundRef.current}
          />

        ))}
        {blackNotes.map((note, index) => {
          // Calculate the left offset based on the position of the black key
          const octave = Math.floor((note - 21) / 12); // 21 is A0
          const positionInOctave = blackKeyPositions[index % 5];
          const leftOffset = ((octave * 7 + positionInOctave) / 88) * 169.2 - 0.62; // 7 white keys per octave, 88 total keys

          return (
            <PianoKey
              key={note}
              note={note}
              isBlack={true}
              keyflash={keyflash.includes(note)}
              // style={{ left: `${leftOffset}%`, '--activeColor': activeColor } as any}
              style={{ left: `${leftOffset}%` }}
              isActive={activeNotes.includes(note) || heldNotes.includes(note) || toggledNotes.includes(note)}
              rootNote={rootNote}
              rootPc={rootPc}
              showTonicLabel={false}
              showScaleDegrees={settings.showScaleDegrees}
              lockedTonicNote={anchorTonicNote}
              lockedTonicMode={lockedTonicMode}
              toggleNote={toggleNote}
              toggledNotes={toggledNotes}
              activeNotes={activeNotes}
              heldNotes={heldNotes}
              keyCenter={keyCenter}
              mode={mode}
              chordQual={chordQual}
              csOptions={csOptions}
              activePcs={activePcs}
              sampler={currentSampler}
              shouldPlaySound={shouldPlaySoundRef.current}
              // selectedInstrument={selectedInstrument}
              isScale={isScale}
              showOvertones={settings.showOvertones}
              showNashvilleNumber={settings.showNashvilleNumber}
              appTheme={settings.appTheme}
              tonicScalesNotes={tonicScalesNotes}
            // shouldPlaySoundRef={shouldPlaySoundRef.current}
            />
          );
        })}
      </div>
      {/* Main Piano display End */}



      {/* Sustain Pedal Visual Indicator */}
      <div
        onClick={(e) => {
          e.stopPropagation(); // Prevent handleGlobalClick from deselecting the chord
          const newSustainState = !sustainRef.current;
          sustainRef.current = newSustainState;
          setSustain(newSustainState);

          // If turning sustain off, release samples and clear held notes
          if (!newSustainState) {
            // Release held notes (same logic as audio handler)
            if (samplerRef.current) {
              const notesToRelease = heldNotesRef.current
                .filter(midiNote => !activeNotesRef.current.includes(midiNote));
              const noteNames = notesToRelease.map(midiNote => Tone.Midi(midiNote).toNote());
              samplerRef.current.triggerRelease(noteNames);
            }
            setHeldNotes([]);
          }
        }}
        style={{
          position: 'absolute',
          top: '51.28dvw',
          left: '46.5dvw',
          width: '6dvw',
          height: '0.8dvw',
          backgroundColor: sustain ? 'rgb(0, 210, 210)' : settings.appTheme === 'light' ? 'rgb(97, 119, 119)' : 'rgb(41, 74, 74)',
          borderBottomLeftRadius: '0.7dvw',
          borderBottomRightRadius: '0.7dvw',
          cursor: 'pointer',
          boxShadow: sustain
            ? '0 0.0dvw 0.7dvw rgba(255, 255, 255, 0.2), 0 0.0dvw 1.1dvw rgba(255, 255, 255, 0.5)'
            : '0 0.3dvw 0.5dvw rgba(0, 0, 0, 0.3), 0 0.4dvw 0.5dvw rgba(0, 0, 0, 0.6)',
          transition: 'background-color 0.1s, box-shadow 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.5dvw',
          color: sustain ? '#666' : '#aaa',
          fontWeight: '500',
          userSelect: 'none',
          zIndex: 1
        }}
        title="Sustain Pedal (Click to toggle)"
      >

      </div>


      {/* Instrument Selector and MIDI Device Selector Below */}

      <div data-actionable="true" style={{ position: 'absolute', top: '52.5dvw', left: '0.5dvw' }}>
        <div style={{ position: 'relative' }}>
          <div data-actionable="true"
            onClick={() => setInstrumentMenuExpanded(!instrumentMenuExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              fontSize: '0.8vw',
              fontFamily: 'Trebuchet MS',
              cursor: 'pointer',
              width: '7.3dvw',
              height: '1.6dvw',
              padding: '0.3dvw 0.5dvw',
              backgroundColor: instrumentMenuExpanded ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,1.0)',
              color: '#999',
              border: '0.05dvw solid rgb(145, 145, 145)',
              borderRadius: '0.4dvw'
            }}
            title="Instrument Selection"
          >
            {instrumentConfigs[selectedInstrument].name}
          </div>

          {instrumentMenuExpanded && (
            <>
              {/* Backdrop to close menu when clicking outside */}
              <div data-actionable="true" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                onClick={() => setInstrumentMenuExpanded(false)} />

              {/* Instrument menu */}
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                marginBottom: '0.3dvw',
                backgroundColor: '#1a1a1a',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.3dvw',
                padding: '0.3dvw 0',
                minWidth: '12dvw',
                zIndex: 1000,
                boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)'
              }}>
                {Object.entries(instrumentConfigs).map(([key, config]) => (
                  <div data-actionable="true"
                    key={key}
                    onClick={() => {
                      setSelectedInstrument(key as keyof InstrumentConfigs);
                      setInstrumentMenuExpanded(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    style={{
                      padding: '0.3dvw 0.8dvw',
                      cursor: 'pointer',
                      fontSize: '0.8vw',
                      color: selectedInstrument === key ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      fontWeight: selectedInstrument === key ? 'bold' : 'normal'
                    }}
                  >
                    {config.name}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div data-actionable="true" style={{ position: 'absolute', top: '54.5dvw', left: '0.5dvw' }}>
        {midiAccess && (
          <div data-actionable="true" style={{ position: 'relative' }}>
            <div
              data-actionable="true"
              onClick={() => setMidiMenuExpanded(!midiMenuExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                fontSize: '0.8vw', fontFamily: 'Trebuchet MS',
                cursor: 'pointer',
                width: '7.3dvw',
                height: '1.6dvw',
                padding: '0.3dvw 0.5dvw',
                backgroundColor: midiMenuExpanded ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,1.0)',
                color: '#999',
                border: '0.05dvw solid rgb(145, 145, 145)',
                borderRadius: '0.4dvw',
                opacity: selectedInputId === 'no-device' ? 0.6 : 1
              }}
              title="MIDI Device Selection"
            >
              {(() => {
                if (selectedInputId === 'no-device') return 'No device';
                if (!selectedInputId || selectedInputId === '') return 'All MIDI devices';
                const device = Array.from(midiAccess.inputs.values()).find(input => input.id === selectedInputId);
                return device ? device.name : 'MIDI Input';
              })()}
              {/* &nbsp;<span style={{ fontSize: '1.1dvw' }}>{selectedInputId === 'no-device' ? '\u21CD' : '\u21E0'}</span> */}
            </div>

            {midiMenuExpanded && (
              <>
                {/* Backdrop to close menu when clicking outside */}
                <div data-actionable="true" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                  onClick={() => setMidiMenuExpanded(false)} />

                {/* MIDI device menu */}
                <div data-actionable="true" style={{
                  position: 'absolute', bottom: '100%', left: 0, marginBottom: '0.3dvw', backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '0.3dvw',
                  padding: '0.3dvw 0', minWidth: '12dvw', zIndex: 1000, boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)'
                }}>
                  <div
                    data-actionable="true"
                    onClick={() => {
                      setSelectedInputId('');
                      setMidiMenuExpanded(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    style={{
                      padding: '0.3dvw 0.8dvw', cursor: 'pointer', fontSize: '0.8vw',
                      color: !selectedInputId ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      fontWeight: !selectedInputId ? 'bold' : 'normal'
                    }}
                  >
                    All MIDI devices
                  </div>
                  <div
                    data-actionable="true"
                    onClick={() => {
                      setSelectedInputId('no-device');
                      setMidiMenuExpanded(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    style={{
                      padding: '0.3dvw 0.8dvw', cursor: 'pointer', fontSize: '0.8vw',
                      color: selectedInputId === 'no-device' ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      fontWeight: selectedInputId === 'no-device' ? 'bold' : 'normal'
                    }}
                  >
                    No device
                  </div>
                  {Array.from(midiAccess.inputs.values()).map(input => (
                    <div data-actionable="true"
                      key={input.id}
                      onClick={() => {
                        setSelectedInputId(input.id);
                        setMidiMenuExpanded(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      style={{
                        padding: '0.3dvw 0.8dvw',
                        cursor: 'pointer',
                        fontSize: '0.8vw',
                        color: selectedInputId === input.id ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                        fontWeight: selectedInputId === input.id ? 'bold' : 'normal'
                      }}
                    >
                      {input.name}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>


      {/* Piano bars display Below */}

      {/* <div className="piano-bars">
        {notes.map((note) => (
          <Bar
            key={note}
            note={note}
            isActive={activeNotes.includes(note) || heldNotes.includes(note) || toggledNotes.includes(note)}
            rootNote={rootNote}
            width={100 / 12}
          />
        ))}
      </div> */}

      {/* <div className="piano-bars-key">
        {notes.map((note) => (
          <Bar
            key={note}
            note={note}
            isActive={activeNotes.includes(note) || heldNotes.includes(note) || toggledNotes.includes(note)}
            rootNote={rootNote}
            width={100 / 12}
          />
        ))}
      </div> */}

      {/* Piano bars display End */}


    </div>
  );
}

export default App;

// function useCallback(arg0: (data: number[]) => void, arg1: never[]) {
//   throw new Error('Function not implemented.');
// }

